/** API client for dashboard analytics. */

// Dashboard scope paths use plural dim names; the API uses singular. The map
// lives in scopeDims.js (a leaf module) so the scout scope-enforcement
// helpers share it without importing this module's whole graph.
import { DIM_SINGULAR } from "./scopeDims.js";
import { readFleetSource, readFleetAssociations, readFleetEndpointUse, readFleetPerson } from "./fleetEvidence.js";
import { readEndpointSessions, readFleetEndpointAgents, readFleetLinks, readFleetLocal, readFleetBreakdown, readFleetRuntimes, withEndpointSessions } from "./fleetConsoleReads.js";
import { RUNTIME_PRODUCT_NAMES } from "./fleetConsole.js";
import { promptCount } from "./promptMetrics.js";
import { countPromptGroups } from "./logicalPromptGroups.js";

// The sidebar only exposes dimensions the analytics planner can express.
const SUPPORTED_DIMS = new Set(ANALYTICS_SUPPORTED_DIM_PLURALS);

// Activity-surface layer colors. Reuse the dashboard's rainbow palette
// (saturated, distinct hues) so layers stay visually separable and the
// white "total" line stays clearly distinct from any per-layer stroke.
// Indexed by descending-activity rank: top agent gets the brand-green
// `inference` slot, then code/network/files/command/memory.
import { layerPalette } from "./theme.js";
import { DIMENSIONS, DIMENSION_KEYS, loadLabels, scopeStepLabel } from "./dimensionDefs.js";
import { SORT_FEWEST, compareDimEntries } from "./dimSort.js";
import { ANALYTICS_SUPPORTED_DIM_PLURALS, traceCaptureSourceEnabled } from "./featureFlags.js";
import { shapeSessionOrigin } from "./sessionOrigin.js";
import { DENIAL_REASON, parseDenial } from "./denialReason.js";
import {
  bucketSecondsForRange,
  buildPromptRootAndFilter,
  buildUsageRootAndFilter,
  buildUsageCollapse,
  densifyBuckets,
  describeSchema,
  dimFieldRef,
  dimIsArray,
  dimIsSynthetic,
  dimSignalNamespace,
  dimNeedsClusterJoin,
  dimNeedsAssignmentJoin,
  dimNeedsUsageJoin,
  dimValueIsLabel,
  isAnalyticsSupportedDim,
  promptRootDimRef,
  rangeBoundsMs,
  rangeToTimeRange,
  rowsToObjects,
  runQuery as runAnalyticsQuery,
  runSignalValueRows,
  unsupportedScopeDims,
  usageScopeFieldFor,
} from "./analyticsClient.js";
import { runFpNeedleJoin, fingerprintEntityAvailable, sealedTaskCarriers } from "./fpJoin.js";
import { resolveAndCompile } from "./queryLangResolve.js";
import { agentTagsMatching, parseQuery } from "./queryLang.js";
import { composeFilters, ensureQueryJoins, applyQueryToSpec, applyQueryToCollapseRoot } from "./queryLangCompile.js";
import { SESSION_MATCH_CAP, buildSessionMatchSpec, clauseGrain, readSessionMatches, sessionClauses } from "./sessionMatch.js";
import { getDimAdapter, getDimAdapterByPlural, pairFlowCounts } from "./dimAdapters.js";
import { hasMemorySignal } from "./memories.js";
import { hasFsSignal } from "./fsProvenance.js";

const PROMPT_ANSWER_STATUS_DIMENSION = "DIMENSION_FIELD_PROMPT_ANSWER_STATUS";
let promptAnswerStatusAvailable = true;

function isUnsupportedPromptAnswerStatus(error) {
  if (error?.status !== 400) return false;
  const details = `${error?.responseText || ""}\n${error?.message || ""}`;
  if (details.includes(PROMPT_ANSWER_STATUS_DIMENSION)) return true;
  // A backend compiled before wire field 203 existed decodes that value as
  // Unspecified before validation. Its stable validation error therefore
  // cannot name PROMPT_ANSWER_STATUS; it identifies the PROMPT alias instead.
  return details.includes("field Unspecified does not belong to entity")
    && details.includes("(alias: p)");
}

export function _resetPromptAnswerStatusCapabilityForTests() {
  promptAnswerStatusAvailable = true;
}

// Optional joins whose entity a scoped caller turned out not to reach. Recorded
// only after dropping one is seen to work, so a refusal raised by something
// else never costs a later query its decoration.
const unreachableOptionalEntities = new Set();

function isRowScopeUnenforceable(error) {
  return parseDenial(error).reason === DENIAL_REASON.ROW_SCOPE_UNENFORCEABLE;
}

function optionalEntitiesIn(step, into = new Set()) {
  for (const child of step?.children || []) {
    if (child.target?.optional && child.target?.entity) into.add(child.target.entity);
    optionalEntitiesIn(child.target, into);
  }
  return into;
}

function collectAliases(step, into) {
  if (!step) return into;
  if (step.alias) into.add(step.alias);
  for (const child of step.children || []) collectAliases(child.target, into);
  return into;
}

/**
 * Rebuild the step tree without the optional joins on `entities`, and without
 * the `optional` marker itself — it is a local annotation and the analytics
 * API is given only fields it declares.
 */
function rebuildStep(step, entities, dropped) {
  if (!step) return step;
  const { optional: _optional, children, ...rest } = step;
  const kept = (children || [])
    .filter((child) => {
      const drop = child.target?.optional && entities.has(child.target?.entity);
      if (drop) collectAliases(child.target, dropped);
      return !drop;
    })
    .map((child) => ({ ...child, target: rebuildStep(child.target, entities, dropped) }));
  return children ? { ...rest, children: kept } : rest;
}

/**
 * The spec with its optional joins on `entities` removed, along with every
 * output column those steps fed. Dropping by alias, not by field name, so a
 * step that projected more than one column cannot leave an orphan behind.
 */
function withoutOptional(spec, entities) {
  const dropped = new Set();
  const root = rebuildStep(spec.root, entities, dropped);
  const rides = (field) => dropped.has(field?.stepAlias);
  const metricRides = (m) => rides(m.computed?.field) || dropped.has(m.standard?.stepAlias);
  // An ordering on a column that left with its step would fail the whole
  // query (the planner refuses an order_by alias it cannot find).
  const droppedAliases = new Set([
    ...(spec.output?.dimensions || []).filter((d) => rides(d.field)).map((d) => d.outputAlias),
    ...(spec.output?.metrics || []).filter(metricRides).map((m) => m.outputAlias),
  ].filter(Boolean));
  return {
    ...spec,
    root,
    output: {
      ...spec.output,
      ...(spec.output?.dimensions
        ? { dimensions: spec.output.dimensions.filter((d) => !rides(d.field)) }
        : {}),
      ...(spec.output?.metrics
        ? { metrics: spec.output.metrics.filter((m) => !metricRides(m)) }
        : {}),
    },
    ...(spec.orderBy
      ? { orderBy: spec.orderBy.filter((o) => !droppedAliases.has(o.outputAlias)) }
      : {}),
  };
}

/**
 * Run a spec, dropping its `optional` joins and retrying once if the caller's
 * row scope cannot reach one of them.
 *
 * Mark a join optional only when the query is still ANSWERING THE SAME QUESTION
 * without it. A decorative join (a cluster's name beside its id) qualifies; a
 * join that narrows the result does not, because dropping that one silently
 * widens the answer instead of degrading it.
 *
 * The typed refusal names no table, so the latch records an entity only when
 * the spec had exactly one optional join to blame and dropping it worked.
 */
async function runDegradableQuery(spec) {
  const optional = optionalEntitiesIn(spec.root);
  if (!optional.size) return runAnalyticsQuery(spec);

  const known = new Set([...optional].filter((e) => unreachableOptionalEntities.has(e)));
  if (known.size) return runAnalyticsQuery(withoutOptional(spec, known));

  try {
    return await runAnalyticsQuery(withoutOptional(spec, new Set()));
  } catch (err) {
    if (!isRowScopeUnenforceable(err)) throw err;
    const rows = await runAnalyticsQuery(withoutOptional(spec, optional));
    if (optional.size === 1) unreachableOptionalEntities.add([...optional][0]);
    return rows;
  }
}

export function _resetIloClusterNameCapabilityForTests() {
  unreachableOptionalEntities.clear();
}
import { signalScopeFilter } from "./signalsDim.js";
import { namespaceForCommand, labelForCommand } from "./signalCatalog.js";
import { loadClusterTree, leafDescendantIds } from "./clusterTree.js";
import { leafToVisibleNodes, rollUpLeafCounts } from "./clusterLeafRollup.js";
import { getSessionLabels } from "./sessionLabels.js";
import { groupSessionRows, readLatestSessionRows, sessionPromptSpec } from "./sessionPreviewBatch.js";
import { getPinnedGenerationId } from "./generationPin.js";
import { toApiRange } from "./shell.js";
import { parseApiRequest } from "./parseApiRequest.js";
import { authedFetch, restPost } from "./restClient.js";
import { readConversationContent, conversationPartToBlock } from "./conversationContent.js";
import { readInvocationParameters, readConversationPage, countConversationMessages, fetchConversationRecord } from "./conversationRead.js";
import { applySessionPage } from "./sessionTabState.js";
import { parseTraceAddress } from "./traceAddress.js";
import {
  endpointIdToUuid,
  flattenInventoryGraphRow,
  mergeInventoryIdentity,
  uuidToEndpointId,
} from "./inventoryRows.js";
import { shapeScanVolume } from "./scanVolume.js";
import { withTraceReadSlot } from "./traceReadGate.js";
import { TOKEN_VOLUME_ALIASES, cacheTokenMetrics, inclusiveCacheMetric, reasoningTokenMetric, coverageMetrics, sumTokenBreakdown, tokenVolumeOf } from "./tokenVolume.js";
import { summarizeTokenCost, tokenCostEnabled } from "./tokenCost.js";
import { clientSurfaceIdentity, deriveAgentIdentity, modelLabel } from "./agentIdentity.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { foldFileMentions, foldContentMentions, mergeCounterpartyChannels, foldProbeMatches, partitionProbeResults, fileBasenameOf } from "./traceElsewhereChannels.js";
import { foldTraceNeedleMatches, looksInjected } from "./traceNeedleLens.js";
import { ENDPOINT_FIRING_ROW_LIMIT, findingGroupKey, parseFinding } from "./configShadowFindings.js";
import { shapeToolExecutions } from "./toolExecutions.js";
import { readTelemetryEvent, readTelemetryPage, TELEMETRY_EVENT_KINDS, TELEMETRY_PAGE_LIMIT, telemetryDimensions } from "./typedTelemetry.js";
import { isDiscoverTraceId, discoverTraceParts } from "./anatomyDiscoveryTrace.js";
import { getAuthContextEpoch, onAuthContextChange } from "./authTokenStore.js";
import { unansweredPrompt } from "./copilotAnswerGaps.js";

// Count each displayed subtree directly. A logical prompt can have source
// observations assigned to different leaves, so leaf counts are not additive.
function clusterPromptGroups(ids) {
  return [...new Set(ids.map(String))].map((id) => ({
    id,
    predicate: { pred: {
      field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_SUBTREE" },
      op: "OPERATOR_EQ", value: Number(id),
    } },
  }));
}

// Cluster↔cluster links are HIERARCHY CONTAINMENT, not co-occurrence (a
// prompt has one cluster assignment, so the event-join query is
// degenerate — both axes are ec.cluster_id — and would 400 on a double
// IN-list). Derive parent→child links from the tree, weighted by the
// child's distinct subtree prompt count within the caller's scope.
async function clusterContainmentLinks(aIds, bIds, apiRange, baseScope) {
  const tree = await loadClusterTree();
  if (!tree.isHierShape) return { links: [] };
  const childOf = (p) => new Set((tree.childrenOf.get(String(p)) || []).map(String));
  const aSet = new Set(aIds.map(String));
  const bSet = new Set(bIds.map(String));
  const pairs = []; // [aId, bId, deeperChildId]
  for (const aId of aSet) {
    const kids = childOf(aId);
    for (const bId of bSet) {
      if (kids.has(bId)) pairs.push([aId, bId, bId]);
      else if (childOf(bId).has(aId)) pairs.push([aId, bId, aId]);
    }
  }
  if (!pairs.length) return { links: [] };
  const childIds = [...new Set(pairs.map((p) => p[2]))];
  const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: baseScope, extraDims: ["cluster"] });
  if (unsupported?.length) return { links: [] };
  const timeRange = rangeToTimeRange(apiRange);
  const act = await rolledUpNodeCounts({ tree, nodeIds: childIds, baseScope, timeRange })
    ?? new Map((await countPromptGroups({
      root, filter, timeRange,
      groups: clusterPromptGroups(childIds), runQuery: runAnalyticsQuery, rowsToObjects,
    })).map((row) => [String(row.id), row.value]));
  return {
    links: pairs.map(([aId, bId, childId]) => ({ aId, bId, count: act.get(childId) || 0 })).filter((l) => l.count > 0),
  };
}

// Distinct prompts under each node from one leaf-grouped read, or null when
// leaf sums can't be exact (see rollUpLeafCounts) and each node must be counted.
async function rolledUpNodeCounts({ tree, nodeIds, baseScope, timeRange }) {
  const { root, filter, unsupported } = buildPromptRootAndFilter({
    scopePath: [...baseScope, { dim: "cluster", ids: nodeIds }], extraDims: ["cluster"],
  });
  if (unsupported?.length) return null;
  try {
    const rows = await rollUpLeafCounts({
      root, filter, timeRange, leafField: dimFieldRef("cluster"),
      leafToVisible: leafToVisibleNodes(tree, nodeIds),
      metrics: [{ alias: "n" }], runQuery: runAnalyticsQuery, rowsToObjects,
    });
    return rows && new Map(rows.map((row) => [row.node, row.n]));
  } catch (err) {
    console.warn("[flow] leaf rollup failed; counting each topic:", err);
    return null;
  }
}

// Per-id prompt sets for a pair of tag columns, read a few at a time rather
// than one after another.
const PROMPT_SET_CONCURRENCY = 4;

async function runAnalyticsFlowPair(aDim, aIds, bDim, bIds, apiRange, baseScope = []) {
  // Cluster-to-cluster links show containment. Other cluster axes count
  // each requested subtree directly before grouping by the opposite axis.
  if (aDim === "cluster" && bDim === "cluster") {
    return clusterContainmentLinks(aIds, bIds, apiRange, baseScope);
  }

  const aAdapter = getDimAdapter(aDim);
  const bAdapter = getDimAdapter(bDim);

  // Both axes adapter-backed — no real proto field to group by on
  // either side. Pull matching prompt-id sets per id (via each
  // adapter's fetchPromptIds, default impl runs one query per id
  // capped at 10K rows + memoized) and intersect in JS. Cost is N+M
  // queries, not N×M.
  if (aAdapter && bAdapter) {
    // Either adapter declined to bind a fetchPromptIds method (i.e.
    // composesWithPromptGraph: false and didn't supply an override) —
    // the dim can't participate in flow links via id-set intersection.
    // Degrade to empty rather than crash.
    if (!aAdapter.fetchPromptIds || !bAdapter.fetchPromptIds) return { links: [] };
    try {
      const paired = await pairFlowCounts(aAdapter, aIds, bAdapter, bIds, { range: apiRange, baseScope });
      if (paired) return { links: paired };
    } catch (err) {
      console.warn(`[flow] batched ${aAdapter.id} x ${bAdapter.id} links failed; intersecting per id:`, err);
    }
    const loadSets = async (adapter, ids) => {
      const sets = new Array(ids.length);
      let next = 0;
      const worker = async () => {
        while (next < ids.length) {
          const index = next++;
          sets[index] = [ids[index], await adapter.fetchPromptIds({ id: ids[index], range: apiRange, baseScope })];
        }
      };
      await Promise.all(Array.from({ length: Math.min(PROMPT_SET_CONCURRENCY, ids.length) }, worker));
      return sets;
    };
    const [aSets, bSets] = await Promise.all([loadSets(aAdapter, aIds), loadSets(bAdapter, bIds)]);
    const links = [];
    for (const [aId, aSet] of aSets) {
      if (!aSet.size) continue;
      for (const [bId, bSet] of bSets) {
        if (!bSet.size) continue;
        const [small, large] = aSet.size < bSet.size ? [aSet, bSet] : [bSet, aSet];
        let n = 0;
        for (const eid of small) if (large.has(eid)) n++;
        if (n > 0) links.push({ aId: String(aId), bId: String(bId), count: n });
      }
    }
    return { links };
  }

  // One axis adapter-backed — delegate fan-out to the adapter. The
  // default fanOutForFlow runs one query per synthetic id filtering
  // by predicate ∧ otherRef IN otherIds, grouped by otherRef.
  if (aAdapter || bAdapter) {
    const synthAdapter = aAdapter ?? bAdapter;
    const synthIds    = aAdapter ? aIds : bIds;
    const realDim     = aAdapter ? bDim : aDim;
    const realIds     = aAdapter ? bIds : aIds;
    // promptRootDimRef (not dimFieldRef): the adapter's fanOutForFlow groups by
    // this ref on a PROMPT root, where `model` must use PROMPT.model — the
    // pu-anchored dimFieldRef would 400 ("unknown step alias: pu") post-O-1805.
    const realRef     = promptRootDimRef(realDim);
    if (!realRef) return { links: [] };
    // Same opt-out semantics as the both-adapter branch — an adapter
    // without fanOutForFlow can't participate in flow links here.
    if (!synthAdapter.fanOutForFlow) return { links: [] };
    const toLink = (synthId, otherId, count) => (aAdapter
      ? { aId: String(synthId), bId: String(otherId), count }
      : { aId: String(otherId), bId: String(synthId), count });
    let batched = null;
    try {
      batched = await synthAdapter.flowCounts?.({
        ids: synthIds, otherRef: realRef, otherIds: realIds, otherDimKey: realDim, baseScope, range: apiRange,
      });
    } catch (err) {
      console.warn(`[flow] batched ${synthAdapter.id} links failed; reading each id:`, err);
    }
    if (batched) return { links: batched.map((row) => toLink(row.synthId, row.otherId, row.count)) };
    const perIdResults = [];
    for (const id of synthIds) {
      const rows = await synthAdapter.fanOutForFlow({
        id,
        otherRef:      realRef,
        otherIds:      realIds,
        otherDimArray: dimIsArray(realDim),
        otherDimKey:   realDim,
        otherGroups: realDim === "cluster" ? clusterPromptGroups(realIds) : undefined,
        baseScope,
        range:         apiRange,
      });
      perIdResults.push({ synthId: id, rows });
    }
    const links = [];
    for (const { synthId, rows } of perIdResults) {
      for (const r of rows) {
        if (!r.count) continue;
        links.push(aAdapter
          ? { aId: String(synthId), bId: String(r.otherId), count: r.count }
          : { aId: String(r.otherId), bId: String(synthId), count: r.count });
      }
    }
    return { links };
  }

  // A cluster axis can't filter by a top-level `cluster_id IN [...]`: the column
  // hands us hierarchy NODE ids (internal nodes), which never equal a leaf
  // ec.cluster_id, so the IN matched nothing and cluster↔X links blanked. Pass the
  // cluster ids as a SCOPE instead — buildPromptRootAndFilter puts a cluster_subtree
  // predicate in the ec stepFilter (the same server-side, generation-pinned walk
  // scoping uses everywhere; only parent ids on the wire, so no 1024-value IN cap).
  // Each requested subtree is counted before grouping by the opposite axis.
  // clusterContainmentLinks handles the case where both axes are clusters.
  const clusterIds = aDim === "cluster" ? aIds : bDim === "cluster" ? bIds : null;
  const clusterScope = clusterIds ? [{ dim: "cluster", ids: clusterIds }] : [];
  const extraDims = [aDim, bDim].filter((d) => dimNeedsClusterJoin(d) || dimNeedsUsageJoin(d) || dimNeedsAssignmentJoin(d));
  const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: [...baseScope, ...clusterScope], extraDims });
  if (unsupported.length) return { links: [] };
  // promptRootDimRef (not dimFieldRef): this query roots at PROMPT, so a `model`
  // axis must group by PROMPT.model (DIMENSION_FIELD_PROMPT_MODEL). dimFieldRef
  // returns the pu-anchored TOKEN_MODEL, which references a `pu` step the
  // PROMPT root no longer declares (O-1805) → 400 "unknown step alias: pu".
  const aRef = promptRootDimRef(aDim);
  const bRef = promptRootDimRef(bDim);
  const cast = (dim, v) => (dim === "cluster" ? Number(v) : String(v));
  // Only the non-cluster axis takes a top-level IN; the cluster axis is bounded by
  // its subtree in the ec stepFilter (see above).
  const filterParts = [];
  // Base scope (the Anatomy scope / a Position level's prefix) folds in as a
  // WHERE clause so the pair is counted WITHIN scope, not tenant-wide. Empty at
  // root (scopeFilter null), so the top-level page is unchanged.
  if (scopeFilter) filterParts.push(scopeFilter);
  if (aDim !== "cluster") filterParts.push({ pred: { field: aRef, op: "OPERATOR_IN", value: aIds.map((v) => cast(aDim, v)) } });
  if (bDim !== "cluster") filterParts.push({ pred: { field: bRef, op: "OPERATOR_IN", value: bIds.map((v) => cast(bDim, v)) } });
  const filter = filterParts.length === 1 ? filterParts[0] : { andExpr: { children: filterParts } };
  if (clusterIds) {
    const clusterIsA = aDim === "cluster";
    const otherAlias = clusterIsA ? "b_id" : "a_id";
    const timeRange = rangeToTimeRange(apiRange);
    let rolled = null;
    try {
      rolled = await rollUpLeafCounts({
        root, filter, timeRange, leafField: dimFieldRef("cluster"),
        leafToVisible: leafToVisibleNodes(await loadClusterTree(), clusterIds),
        groupDims: [{ field: clusterIsA ? bRef : aRef, outputAlias: otherAlias }],
        metrics: [{ alias: "n" }], runQuery: runAnalyticsQuery, rowsToObjects,
      });
    } catch (err) {
      console.warn("[flow] leaf rollup failed; counting each topic:", err);
    }
    if (rolled) {
      return { links: rolled.filter((row) => row.n > 0).map((row) => ({
        aId: String(clusterIsA ? row.node : row.a_id),
        bId: String(clusterIsA ? row.b_id : row.node), count: row.n,
      })) };
    }
    const rows = await countPromptGroups({
      root, filter, timeRange,
      groups: clusterPromptGroups(clusterIds),
      dimensions: [{ field: clusterIsA ? bRef : aRef, outputAlias: otherAlias }],
      runQuery: runAnalyticsQuery, rowsToObjects, valueAlias: "n",
    });
    return { links: rows.filter((row) => row.n > 0).map((row) => ({
      aId: String(clusterIsA ? row.id : row.a_id),
      bId: String(clusterIsA ? row.b_id : row.id), count: row.n,
    })) };
  }
  const spec = {
    root,
    timeRange: rangeToTimeRange(apiRange),
    filter,
    output: {
      dimensions: [
        { field: aRef, outputAlias: "a_id" },
        { field: bRef, outputAlias: "b_id" },
      ],
      metrics: [promptCount("n")],
    },
  };
  const rows = rowsToObjects(await runAnalyticsQuery(spec));
  return { links: rows.map((row) => ({ aId: String(row.a_id), bId: String(row.b_id), count: Number(row.n) || 0 })) };
}

function swimlaneEmpty({ bucketCount, fromMs, toMs, coherenceMode }) {
  return {
    rankedClusters: [],
    matrix: [], breadthMatrix: [], coherenceMatrix: [],
    coOccurrence: [], siblingScopes: [],
    breadthDim: "endpoints", selfBreadthValue: null,
    globalMax: 0, breadthMax: 0,
    minTime: fromMs, maxTime: toMs,
    bucketCount, subRowsByCluster: {}, subDim: "endpoints",
    coherenceMode, siblingsOf: null,
  };
}

// Dim plural names rendered as picker items in the column browser.
// Derived from DIMENSION_KEYS so the order matches DIMENSION_LIST
// (used by the sidebar-seed fallback in ScopeColumns). If the two
// diverge, the picker chip order flips between the loading state
// (seed) and the loaded state (server response). DIMENSION_KEYS is
// already filtered to the analytics-supported subset.
const PICKER_DIM_PLURALS = [...DIMENSION_KEYS];

function buildPickerCol({ depth, items, selectedDimKey, scopePath }) {
  const shapedItems = (items || []).map((it) => ({
    id: it.id,
    label: DIMENSIONS[it.id]?.label || it.id,
    count: Number(it.count) || 0,
    selected: it.id === selectedDimKey,
  }));
  const label = depth === 0 ? "Overview" : scopeStepLabel(scopePath[depth - 1]);
  return { type: "picker", label, items: shapedItems, depth };
}

function buildEntriesCol({ depth, dimPlural, entries, offset, limit, step }) {
  const dim = DIMENSIONS[dimPlural] || {};
  const list = entries?.entries ?? [];
  return {
    type: "entries",
    label: dim.label || dimPlural,
    entries: list,
    total:  Number(entries?.total) || list.length,
    offset, limit,
    selectedIds: step
      ? (Array.isArray(step.ids) && step.ids.length
          ? step.ids.map(String)
          : step.id != null ? [String(step.id)] : [])
      : [],
    selectedId: step?.id ?? null,
    dimKey: dimPlural,
    depth,
  };
}

// ── Dimension entries (ranked sparkline) — shared executor ──────

// Per-dim label-join metadata. When sort=name or search is set on a
// dim whose label lives on a separate entity (endpoint hostname,
// cluster name), we attach a label join via PROMPT's edge graph and
// reference the joined step's name field in the search predicate /
// order_by.
//
// Sessions don't have a human-readable name field on AI_SESSION today
// (the proto's `latest_summary` standard metric was dropped per the
// proto's reserved comment), so search/sort=name on session dim
// degrade gracefully — search ignores the predicate, sort=name falls
// back to id ordering.
const LABEL_JOIN_FOR_DIM = {
  endpoint: {
    edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT",
    targetEntity: "ENTITY_TYPE_ENDPOINT",
    labelAlias: "ep_lbl",
    labelField: "DIMENSION_FIELD_ENDPOINT_HOSTNAME",
  },
  cluster: {
    // ILO_CLUSTER hangs off the cluster join the helper already adds
    // when extraDims=["cluster"]. Reuse the alias `c` from
    // buildPromptRootAndFilter's cluster chain.
    edge: null,  // attach via buildPromptRootAndFilter's cluster chain
    labelAlias: "c",
    labelField: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME",
  },
  identity: {
    // display_name is a sibling field on the DIRECTORY_USER (`du`) step that
    // buildPromptRootAndFilter already adds for the identity dim (viaAssignment,
    // the PROMPT → assignment → directory_user chain) — reuse that alias, no
    // extra join (O-2400).
    edge: null,
    labelAlias: "du",
    labelField: "DIMENSION_FIELD_DIRECTORY_USER_DISPLAY_NAME",
  },
  // agent: value IS the label; search/sort=name handled inline
  // session: no name field; degrade silently
};

// Endpoint sidebar de-duplication needs the same two metadata fields on both
// entry pipelines: the normal PROMPT-ranked path and the TOKEN-ranked "Most
// tokens" path. Keep their output aliases identical so ScopeSidebar can apply
// one endpointDedupe contract regardless of sort mode.
function endpointEntryMetadataDimensions(stepAlias) {
  return [
    {
      field: { stepAlias, field: "DIMENSION_FIELD_ENDPOINT_SERIAL_NUMBER" },
      outputAlias: "serial",
    },
    {
      field: { stepAlias, field: "DIMENSION_FIELD_ENDPOINT_LAST_UPDATED" },
      outputAlias: "snapshot_time",
    },
  ];
}

function buildEntriesRoot({ apiScope, dim, needsLabelJoin, steer = null }) {
  // For cluster dim, buildPromptRootAndFilter already adds the
  // PROMPT_HAS_EVENT_CLUSTER → ILO_EVENT_CLUSTER chain. We need to
  // additionally attach EVENT_CLUSTER_IN_CLUSTER → ILO_CLUSTER for
  // names. For endpoint dim, attach PROMPT_ON_ENDPOINT → ENDPOINT.
  const { root, filter, unsupported } = buildPromptRootAndFilter({
    scopePath: apiScope, extraDims: [dim], steer,
  });
  if (!needsLabelJoin) return { root, filter, unsupported };

  if (dim === "cluster") {
    // root.children[0] is the cluster join; tack ILO_CLUSTER under it.
    const clusterJoin = root.children?.[0];
    if (clusterJoin?.target) {
      clusterJoin.target.children = [
        ...(clusterJoin.target.children ?? []),
        {
          edge: "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER",
          mode: "JOIN_MODE_LEFT",
          target: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER", optional: true },
        },
      ];
    }
    return { root, filter, unsupported };
  }
  if (dim === "endpoint") {
    root.children = [
      ...(root.children ?? []),
      {
        edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT",
        mode: "JOIN_MODE_LEFT",
        target: { alias: "ep_lbl", entity: "ENTITY_TYPE_ENDPOINT" },
      },
    ];
    return { root, filter, unsupported };
  }
  return { root, filter, unsupported };
}


// ── Hierarchical cluster entries (leiden_hier) ───────────────────
//
// When the active rebuild's cluster set is hierarchical (internal
// nodes at level 0 — see clusterTree.js), the clusters dim lists the
// TREE level for the current scope instead of the flat leaf ranking:
// roots at the top level; under a cluster scope step, that node's
// direct children. `clusters` is `hierarchical: true` in
// dimensionDefs, so the dim pane reappears at deeper scope levels —
// the same recursive-columns treatment files/projects use; this
// supplies the "children of the scoped node" data contract that
// treatment expects.
//
// Activity comes from ONE leaf-level aggregate (runQuery's parent
// expansion restricts it to the scoped subtree) rolled up client-side
// onto the visible nodes. Flat shapes (hdbscan default) return null
// and fall through to the existing flat listing unchanged.

// Visible tree level for a scope: children of the deepest cluster
// step (union across multi-selected ids), or roots when none.
function clusterLevelIds(tree, apiScope) {
  const clusterSteps = apiScope.filter((s) => s.dim === "cluster");
  const lastStep = clusterSteps[clusterSteps.length - 1] ?? null;
  if (!lastStep) return tree.roots;
  const parentIds = (lastStep.ids?.length ? lastStep.ids : [lastStep.id]).map(String);
  const seen = new Set();
  const out = [];
  for (const pid of parentIds) {
    for (const cid of tree.childrenOf.get(pid) ?? []) {
      if (!seen.has(cid)) { seen.add(cid); out.push(cid); }
    }
  }
  return out;
}

// Count each visible subtree under the surrounding scope. Source observations
// of the same prompt may occupy different leaves. A failed query keeps the
// structural tree visible rather than treating missing counts as zero activity.
async function rollupClusterActivity({ leafToVisible, apiScope, apiRange, steer = null }) {
  const nonClusterScope = apiScope.filter((s) => s.dim !== "cluster");
  const { root, filter, unsupported } = buildEntriesRoot({ apiScope: nonClusterScope, dim: "cluster", needsLabelJoin: false, steer });
  const activityByVisible = new Map();
  if (unsupported.length) return { activityByVisible, rollupOk: false, root, filter };
  try {
    const rows = await countPromptGroups({
      root, filter, timeRange: rangeToTimeRange(apiRange),
      groups: clusterPromptGroups([...leafToVisible.values()]),
      runQuery: runAnalyticsQuery, rowsToObjects,
    });
    for (const row of rows) activityByVisible.set(String(row.id), row.value);
    return { activityByVisible, rollupOk: true, root, filter };
  } catch (err) {
    console.warn("[rollupClusterActivity] activity rollup failed:", err);
    return { activityByVisible, rollupOk: false, root, filter };
  }
}

const CLUSTER_LEAF_BUCKET_LIMIT = 20000;

// Activity (and, with `buckets`, sparklines) for every visible node from one
// leaf-grouped read. Each row counts distinct prompts per leaf (and bucket);
// they add up to the scope's distinct total exactly when no prompt sits in two
// rows, and then a node's count is the sum over its leaves. Returns null when
// they don't, when the read fills its limit, or when it fails, so the caller
// counts each node directly.
async function leafRolledClusterActivity({ leafToVisible, apiScope, apiRange, steer = null, buckets = null }) {
  const nonClusterScope = apiScope.filter((s) => s.dim !== "cluster");
  const { root, filter, unsupported } = buildEntriesRoot({ apiScope: nonClusterScope, dim: "cluster", needsLabelJoin: false, steer });
  if (unsupported.length) return null;
  const timeRange = rangeToTimeRange(apiRange);
  const dimensions = [{ field: dimFieldRef("cluster"), outputAlias: "id" }];
  if (buckets) {
    dimensions.push({ field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds: buckets.bucketSeconds });
  }
  const leafSpec = { root, timeRange, output: { dimensions, metrics: [promptCount("value")] }, limit: CLUSTER_LEAF_BUCKET_LIMIT };
  const totalSpec = { root, timeRange, output: { metrics: [promptCount("total")] } };
  if (filter) {
    leafSpec.filter = filter;
    totalSpec.filter = filter;
  }
  let rows;
  let total;
  try {
    const [leafRows, totalRows] = await Promise.all([runAnalyticsQuery(leafSpec), runAnalyticsQuery(totalSpec)]);
    rows = rowsToObjects(leafRows);
    total = Number(rowsToObjects(totalRows)[0]?.total);
  } catch (err) {
    console.warn("[leafRolledClusterActivity] leaf rollup failed; counting each node:", err);
    return null;
  }
  if (rows.length >= CLUSTER_LEAF_BUCKET_LIMIT) return null;
  if (rows.reduce((sum, row) => sum + (Number(row.value) || 0), 0) !== total) return null;
  const activityByVisible = new Map();
  const visibleRows = [];
  for (const row of rows) {
    const visible = leafToVisible.get(String(row.id));
    if (!visible) continue;
    const value = Number(row.value) || 0;
    activityByVisible.set(visible, (activityByVisible.get(visible) || 0) + value);
    visibleRows.push({ id: visible, bucket: row.bucket, value });
  }
  const seriesByVisible = buckets ? densifyBuckets(visibleRows, buckets) : new Map();
  return { activityByVisible, seriesByVisible };
}

async function clusterHierarchyEntries({ apiScope, range, search = "", sort = "activity", offset = 0, limit = 100, steer = null, sparkline = true }) {
  const tree = await loadClusterTree();
  if (!tree.isHierShape) return null;

  const visibleIds = clusterLevelIds(tree, apiScope);
  if (visibleIds.length === 0) return { entries: [], count: 0, total: 0, maxValue: 0 };

  // At the tenant root the TREE is authoritative for what's listed: every
  // structural child shows (drillable) with activity as best-effort decoration —
  // an internal node can hold a large subtree even when no single row keys to its
  // own id, so the leaf → visible-ancestor map drives the rollup. UNDER A SCOPE we
  // additionally gate visibility on rolled-up activity (the filter below), so the
  // pane shows only the clusters the scope actually touches — and the badge
  // (clusterHierarchyCount) counts that same set.
  const leafToVisible = new Map();
  const visible = visibleIds.map((id) => {
    const leaves = leafDescendantIds(tree, id);
    for (const leaf of leaves) leafToVisible.set(String(leaf), id);
    return { id, node: tree.byId.get(id), leaves };
  });

  const apiRange = toApiRange(range);
  const { fromMs, toMs } = rangeBoundsMs(apiRange);
  const SPARKLINE_BUCKETS = 24;
  const bucketSeconds = bucketSecondsForRange(apiRange, SPARKLINE_BUCKETS);

  // Leaf rollup first; per-visible-node distinct counts only when it can't be
  // exact. rollupOk=false (failed query / scope unexpressible) → activity empty
  // and the full tree still lists. See rollupClusterActivity.
  const rolled = await leafRolledClusterActivity({
    leafToVisible, apiScope, apiRange, steer,
    buckets: sparkline ? { fromMs, toMs, bucketCount: SPARKLINE_BUCKETS, bucketSeconds } : null,
  });
  let activityByVisible, rollupOk, root, filter;
  let seriesByVisible = new Map();
  if (rolled) {
    ({ activityByVisible, seriesByVisible } = rolled);
    rollupOk = true;
  } else {
    ({ activityByVisible, rollupOk, root, filter } = await rollupClusterActivity({ leafToVisible, apiScope, apiRange, steer }));
  }

  // Tree levels are small: filter/sort/paginate client-side. Rank by
  // activity (most-first, or fewest-first for SORT_FEWEST), then by id so
  // order is stable.
  let nodes = visible;
  if (search) {
    const q = search.toLowerCase();
    nodes = nodes.filter((v) => (v.node?.name ?? v.id).toLowerCase().includes(q));
  }
  // Under an active scope, hide clusters with no rolled-up activity — the full
  // tree of mostly-0 rows under a scope reads as broken. Safe: activityByVisible
  // counts each node's whole subtree, so 0 == no scoped prompts anywhere under it
  // (nothing drillable lost). Only when the rollup actually ran (rollupOk) and a
  // non-cluster scope is active — never blank the pane on a failed rollup, and
  // never at the tenant root, where every cluster has activity and the full tree
  // should show.
  const scoped = Boolean(steer?.filter) || apiScope.some((s) => s.dim !== "cluster");
  if (scoped && rollupOk) {
    nodes = nodes.filter((v) => (activityByVisible.get(v.id) || 0) > 0);
  }
  if (sort === "name") {
    nodes = [...nodes].sort((a, b) => (a.node?.name ?? a.id).localeCompare(b.node?.name ?? b.id));
  } else {
    const dir = sort === SORT_FEWEST ? -1 : 1;
    nodes = [...nodes].sort((a, b) =>
      dir * ((activityByVisible.get(b.id) || 0) - (activityByVisible.get(a.id) || 0))
      || String(a.id).localeCompare(String(b.id)));
  }
  const total = nodes.length;
  const paged = nodes.slice(offset, offset + limit);

  // Count each visible subtree per bucket; adding leaf counts would duplicate
  // logical prompts observed in more than one leaf.
  if (!rolled && sparkline && paged.length > 0) {
    try {
      const sparkRows = await countPromptGroups({
        root, filter, timeRange: rangeToTimeRange(apiRange),
        groups: clusterPromptGroups(paged.map((node) => node.id)),
        dimensions: [{ field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds }],
        runQuery: runAnalyticsQuery, rowsToObjects,
      });
      seriesByVisible = densifyBuckets(sparkRows, { fromMs, toMs, bucketCount: SPARKLINE_BUCKETS, bucketSeconds });
    } catch (err) {
      console.warn("[clusterHierarchyEntries] sparkline rollup failed, leaving entries un-sparked:", err);
    }
  }

  const max = paged.reduce((m, v) => Math.max(m, activityByVisible.get(v.id) || 0), 0) || 1;
  const entries = paged.map((v) => {
    const activity = activityByVisible.get(v.id) || 0;
    const rawShare = Math.round((activity / max) * 100);
    return {
      id: v.id,
      label: v.node?.name || v.id,
      activity,
      share: activity > 0 ? Math.max(1, rawShare) : 0,
      // Same treatment as every other tree node: no note line (it
      // forced a taller two-line row that broke the sparkline layout).
      note: "",
      sparkline: {
        series: seriesByVisible.get(v.id) ?? new Array(SPARKLINE_BUCKETS).fill(0),
        stroke: "#7fa16c",
        fill: "rgba(127,161,108,0.12)",
      },
    };
  });
  return { entries, count: entries.length, total, maxValue: max };
}

// Pane-badge / show-hide count for the hierarchical shape: children
// at this scope position (structural — no activity filter), so "0"
// correctly hides the clusters pane under a leaf. Returns null for
// flat shapes → caller falls through to the COUNT_DISTINCT path.
async function clusterHierarchyCount(apiScope, range, steer = null) {
  const tree = await loadClusterTree();
  if (!tree.isHierShape) return null;
  const visibleIds = clusterLevelIds(tree, apiScope);
  // Tenant root (no non-cluster scope AND no steer) → structural child count,
  // which also drives pane show/hide (0 children hides the pane under a leaf).
  // Under a scope OR a steer, count only clusters with rolled-up activity —
  // matching the filtered list and the ENDPOINTS/IDENTITIES scoped counts.
  // Rollup failure → fall back to structural rather than under-report to 0.
  if (!steer?.filter && !apiScope.some((s) => s.dim !== "cluster")) return visibleIds.length;
  const leafToVisible = new Map();
  for (const id of visibleIds) for (const leaf of leafDescendantIds(tree, id)) leafToVisible.set(String(leaf), id);
  const apiRange = toApiRange(range);
  const active = await activeVisibleClusters({ leafToVisible, apiScope, apiRange, steer });
  if (active === ACTIVE_CLUSTERS_FAILED) return visibleIds.length;
  if (active !== ACTIVE_CLUSTERS_TRUNCATED) return active.size;
  const { activityByVisible, rollupOk } = await rollupClusterActivity({ leafToVisible, apiScope, apiRange, steer });
  if (!rollupOk) return visibleIds.length;
  return visibleIds.filter((id) => (activityByVisible.get(id) || 0) > 0).length;
}

const CLUSTER_LEAF_ACTIVITY_LIMIT = 5000;
const ACTIVE_CLUSTERS_FAILED = Symbol("activeClustersFailed");
const ACTIVE_CLUSTERS_TRUNCATED = Symbol("activeClustersTruncated");

// Visible nodes with any activity under the scope, from one leaf-grouped read:
// a node is active exactly when one of its leaves is. Presence, unlike a
// distinct count, is unaffected by a prompt observed in several leaves, so the
// badge needs no per-node count query. A read that fills its limit may have
// dropped leaves, so the caller falls back to the per-node rollup.
async function activeVisibleClusters({ leafToVisible, apiScope, apiRange, steer = null }) {
  const nonClusterScope = apiScope.filter((s) => s.dim !== "cluster");
  const { root, filter, unsupported } = buildEntriesRoot({ apiScope: nonClusterScope, dim: "cluster", needsLabelJoin: false, steer });
  if (unsupported.length) return ACTIVE_CLUSTERS_FAILED;
  const spec = {
    root,
    timeRange: rangeToTimeRange(apiRange),
    output: {
      dimensions: [{ field: dimFieldRef("cluster"), outputAlias: "id" }],
      metrics: [promptCount("value")],
    },
    limit: CLUSTER_LEAF_ACTIVITY_LIMIT,
  };
  if (filter) spec.filter = filter;
  let rows;
  try {
    rows = rowsToObjects(await runAnalyticsQuery(spec));
  } catch (err) {
    console.warn("[activeVisibleClusters] leaf activity read failed:", err);
    return ACTIVE_CLUSTERS_FAILED;
  }
  if (rows.length >= CLUSTER_LEAF_ACTIVITY_LIMIT) return ACTIVE_CLUSTERS_TRUNCATED;
  const active = new Set();
  for (const row of rows) {
    const visible = leafToVisible.get(String(row.id));
    if (visible && Number(row.value) > 0) active.add(visible);
  }
  return active;
}

// Resolve a TOKEN-rooted spec root for the model/token surfaces that don't
// hang off a PROMPT root (the per-prompt usage edge was removed in O-1805;
// the token store is ENTITY_TYPE_TOKEN since O-2021). Scopes TOKEN expresses
// directly (endpoint/session/agent/model) root at TOKEN; prompt-analysis
// scopes (cluster/tool/file/mcp/skill) fall back to the
// PROMPT_HAS_TOKENS collapse (PROMPT scope → semi-join on the token
// scan). Output rides on `pu` either way. Returns { root, scopeFilter }, or
// null when the scope can't be expressed against TOKEN at all (caller fails
// closed).
// Agent (provider) search: values are stored as canonical lower-kebab tags but
// rendered as friendly identity names (openai → ChatGPT), so a plain column
// ILIKE can't see what users type. Match EITHER form: an exact IN over the
// tags whose rendered labels contain the term (queryLang's label→tag
// inversion — the same table `agent:` filter values normalize through), OR'd
// with the raw-tag ILIKE so unknown/custom tags with no identity stay
// searchable by their stored value.
function agentSearchPred(fieldRef, search) {
  const ilike = { pred: { field: fieldRef, op: "OPERATOR_ILIKE", value: `%${search}%` } };
  const tags = agentTagsMatching(search);
  if (!tags.length) return ilike;
  return { orExpr: { children: [{ pred: { field: fieldRef, op: "OPERATOR_IN", value: tags } }, ilike] } };
}

function usageScopeRoot(apiScope) {
  const direct = buildUsageRootAndFilter({ scopePath: apiScope });
  if (!direct.unsupported.length) return { root: direct.root, scopeFilter: direct.filter };
  const collapsed = buildUsageCollapse({ scopePath: apiScope });
  if (collapsed.unsupported.length) return null;
  // PROMPT-rooted collapse; scope rides PROMPT's stepFilter, not a top-level filter.
  return { root: collapsed.root, scopeFilter: null };
}

// TOKEN-native dimension entries, rooted at ENTITY_TYPE_TOKEN (the multi-grain
// token_observation store, O-2021). A dim whose values live on a TOKEN column
// (model / session / endpoint / agent — see usageScopeFieldFor) ranks directly
// off the token entity: activity = per-observation COUNT(unit_id); "Most
// tokens" re-ranks by THROUGHPUT (tokenVolumeOf); name sort/search apply to the
// dim's TOKEN column. The `model` dim uses this for ALL sorts (it lives natively
// on TOKEN); the others use it ONLY for the explicit token sort (their
// activity/name ranking + label joins stay on the PROMPT root in
// executeDimensionEntries). Scope is re-anchored onto TOKEN's own columns; a
// scope it can't express (cluster/…) fails closed to empty. Labels: model
// + agent are value-is-label; session/endpoint carry the id and the sidebar's
// DimensionLabel resolves the friendly name from the label cache.
async function executeUsageDimEntries({ dim = "model", apiScope, offset, limit, search, sort, range }) {
  const groupField = usageScopeFieldFor(dim);
  if (!groupField) return { entries: [], count: 0, total: 0, maxValue: 0 };
  const apiRange = toApiRange(range);
  const { fromMs, toMs } = rangeBoundsMs(apiRange);
  const SPARKLINE_BUCKETS = 24;
  const bucketSeconds = bucketSecondsForRange(apiRange, SPARKLINE_BUCKETS);

  const resolved = usageScopeRoot(apiScope);
  if (!resolved) return { entries: [], count: 0, total: 0, maxValue: 0 };
  let { root, scopeFilter } = resolved;

  let groupRef = { stepAlias: "pu", field: groupField };
  const wantsTokenSort = sort === "tokens";
  const wantsNameSort   = sort === "name";
  const wantsRecentSort = sort === "recent";

  let searchPred = null;
  // Endpoint token entries need endpoint metadata for hardware-serial
  // de-duplication. The native endpoint rollup roots at ENDPOINT and follows
  // ENDPOINT_HAS_TOKENS, preserving every token grain while making serial and
  // snapshot fields available in the same grouped query.
  if (dim === "endpoint" && root.entity === "ENTITY_TYPE_TOKEN" && !search) {
    root = {
      alias: "ep_usage",
      entity: "ENTITY_TYPE_ENDPOINT",
      children: [{
        edge: "EDGE_TYPE_ENDPOINT_HAS_TOKENS",
        mode: "JOIN_MODE_INNER",
        target: root,
      }],
    };
    groupRef = { stepAlias: "ep_usage", field: "DIMENSION_FIELD_ENDPOINT_ENDPOINT_ID" };
  } else if (dim === "endpoint" && root.entity === "ENTITY_TYPE_TOKEN") {
    // Endpoint hostname search still keeps PROMPT as the time-bounded driver
    // and collapses its matching endpoint ids into TOKEN; rooting the search at
    // ENDPOINT would scan the tenant's whole endpoint set before the requested
    // time range can narrow the work. A model predicate remains on `pu`.
    // buildUsageCollapse cannot carry model scope on PROMPT because model is a
    // TOKEN-native field. Leave that predicate in scopeFilter on `pu`.
    const promptScope = apiScope.filter((step) => step.dim !== "model");
    const collapsed = buildUsageCollapse({ scopePath: promptScope });
    if (collapsed.unsupported.length) return { entries: [], count: 0, total: 0, maxValue: 0 };
    root = collapsed.root;
  }
  if (dim === "endpoint" && root.entity !== "ENTITY_TYPE_ENDPOINT") {
    root.children = [
      ...(root.children ?? []),
      {
        edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT",
        mode: search ? "JOIN_MODE_INNER" : "JOIN_MODE_LEFT",
        target: {
          alias: "ep_usage",
          entity: "ENTITY_TYPE_ENDPOINT",
          ...(search ? { stepFilter: [{
            pred: {
              field: { stepAlias: "ep_usage", field: "DIMENSION_FIELD_ENDPOINT_HOSTNAME" },
              op: "OPERATOR_ILIKE",
              value: `%${search}%`,
            },
          }] } : {}),
        },
      },
    ];
  } else if (search && dim === "agent") {
    searchPred = agentSearchPred(groupRef, search);
  } else if (search) {
    searchPred = { pred: { field: groupRef, op: "OPERATOR_ILIKE", value: `%${search}%` } };
  }
  const rankFilter = combineAnd(scopeFilter, searchPred);

  const outputMetrics = [{
    outputAlias: "value",
    computed: { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_UNIT_ID" }, op: "AGGREGATE_OP_COUNT" },
  }];
  if (wantsRecentSort) {
    outputMetrics.push({
      outputAlias: "last_event",
      computed: { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_OBSERVED_AT" }, op: "AGGREGATE_OP_MAX" },
    });
  }
  if (wantsTokenSort) {
    outputMetrics.push(
      { outputAlias: "input_tokens",  standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_INPUT_TOKENS" } },
      { outputAlias: "output_tokens", standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" } },
      ...cacheTokenMetrics("pu"),
    );
  }
  // Token sort orders server-side by input_tokens DESC (orderBy takes one
  // alias; input is a rough proxy) and re-ranks client-side below by the
  // full provider-true volume (TOKEN_VOLUME_ALIASES).
  const orderBy = wantsRecentSort
    ? [{ outputAlias: "last_event", direction: "DIRECTION_DESC" }]
    : wantsNameSort
    ? [{ outputAlias: "id", direction: "DIRECTION_ASC" }]
    : wantsTokenSort
    ? [{ outputAlias: "input_tokens", direction: "DIRECTION_DESC" }]
    : [{ outputAlias: "value", direction: "DIRECTION_DESC" }];

  // Token sort re-ranks client-side by total volume, so over-fetch 3× to give
  // the re-rank headroom near the page cut-off (mirrors the prior behaviour).
  const clientPaginate = wantsTokenSort;
  const rankServerLimit = wantsTokenSort ? limit * 3 : limit;

  const rankDimensions = [{ field: groupRef, outputAlias: "id" }];
  if (dim === "endpoint") {
    rankDimensions.push(...endpointEntryMetadataDimensions("ep_usage"));
  }
  const rankSpec = {
    root,
    timeRange: rangeToTimeRange(apiRange),
    output: { dimensions: rankDimensions, metrics: outputMetrics },
    orderBy,
    limit: rankServerLimit, offset: clientPaginate ? 0 : offset,
  };
  if (rankFilter) rankSpec.filter = rankFilter;

  const totalSpec = {
    root,
    timeRange: rangeToTimeRange(apiRange),
    output: { metrics: [{ outputAlias: "total", computed: { field: groupRef, op: "AGGREGATE_OP_COUNT_DISTINCT" } }] },
  };
  if (rankFilter) totalSpec.filter = rankFilter;

  const [rawRankRows, totalRows] = await Promise.all([
    runAnalyticsQuery(rankSpec).then(rowsToObjects),
    runAnalyticsQuery(totalSpec).then(rowsToObjects).catch(() => null),
  ]);

  let rankRows = rawRankRows;
  if (wantsTokenSort) {
    rankRows = rankRows
      .map((r) => ({ ...r, value: tokenVolumeOf(r) }))
      .sort((a, b) => Number(b.value) - Number(a.value));
  }
  const pagedRankRows = clientPaginate
    ? rankRows.slice(offset, offset + limit)
    : rankRows.slice(0, limit);

  // Sparkline: per-model activity bucketed on observed_at, for the visible ids.
  const ids = pagedRankRows.map((r) => r.id).filter((v) => v !== null && v !== undefined && v !== "");
  let densified = new Map();
  if (ids.length > 0) {
    const inPred = { pred: { field: groupRef, op: "OPERATOR_IN", value: ids.map(String) } };
    const sparkSpec = {
      root,
      timeRange: rangeToTimeRange(apiRange),
      output: {
        dimensions: [
          { field: groupRef, outputAlias: "id" },
          { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_OBSERVED_AT" }, outputAlias: "bucket", bucketSeconds },
        ],
        metrics: [{
          outputAlias: "value",
          computed: { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_UNIT_ID" }, op: "AGGREGATE_OP_COUNT" },
        }],
      },
    };
    sparkSpec.filter = combineAnd(rankFilter, inPred);
    try {
      const rawSparkRows = rowsToObjects(await runAnalyticsQuery(sparkSpec));
      densified = densifyBuckets(rawSparkRows, { fromMs, toMs, bucketCount: SPARKLINE_BUCKETS, bucketSeconds });
    } catch (err) {
      console.warn("[executeUsageDimEntries] sparkline failed, leaving entries un-sparked:", err);
    }
  }

  // Empty-id bucket = "unattributed" (token observations with no value resolved
  // for this column — no model, no provider, a null endpoint). For value-is-label
  // dims (model/agent) the id IS the label, so give the empty row a readable name;
  // for id-keyed dims (session/endpoint) the sidebar's DimensionLabel resolves the
  // friendly name from the cache, so the raw id is the right fallback.
  const emptyLabel = dim === "model" ? "(no model)" : dim === "agent" ? "(unattributed)" : "";
  const max = pagedRankRows.reduce((m, e) => Math.max(m, Number(e.value) || 0), 0) || 1;
  const shaped = pagedRankRows.map((e) => {
    const activity = Number(e.value) || 0;
    const rawShare = Math.round((activity / max) * 100);
    const share = activity > 0 ? Math.max(1, rawShare) : 0;
    const id = String(e.id ?? "");
    return {
      id, label: id || emptyLabel, activity, share, note: "",
      ...(dim === "endpoint" ? {
        serial: String(e.serial ?? ""),
        snapshotTime: e.snapshot_time ?? null,
      } : {}),
      sparkline: {
        series: densified.get(id) ?? new Array(SPARKLINE_BUCKETS).fill(0),
        stroke: "#7fa16c",
        fill: "rgba(127,161,108,0.12)",
      },
    };
  });
  const total = Number(totalRows?.[0]?.total) || shaped.length;
  return { entries: shaped, count: shaped.length, total, maxValue: max };
}

export async function executeDimensionEntries(params = {}) {
  // If a dim/scope combo cannot be expressed by analytics, return no entries.
  // `labels: false` skips the session-label overlay. Id-resolution callers
  // (the scope/steer session sets: topic patterns, the wiki steer) page up to
  // 2000 sessions and never display them, so they do not need the extra label
  // queries. Display surfaces keep the default.
  const { dimKey, parentScopePath = [], offset = 0, limit = 100, search = "", sort = "activity", range, minimal = false, sparkline = true, labels = true, query } = params;
  const dim = DIM_SINGULAR[dimKey] ?? dimKey;
  const apiScope = toApiScope(parentScopePath, range);
  // The sidebar scope steer (same as the counts). The entries list MUST honor it
  // too, or a steered count (e.g. ENDPOINTS 4) sits above an unfiltered listing.
  const { compiled: steerCompiled, active: steerActive, carryable } = await compileSteer(query);
  const EMPTY_ENTRIES = { entries: [], count: 0, total: 0, maxValue: 0 };
  // A steer this entries path can't carry → empty, matching the "—" count.
  if (steerActive && !carryable) return EMPTY_ENTRIES;
  // When a signal is in scope, this breakdown must count only the prompts that
  // fired it. The signal has no server predicate (it's dropped from apiScope),
  // so we layer an `event_id IN (fired ids)` filter on. Computed once and reused
  // below; also forces TOKEN-rooted (model) and hierarchical-cluster dims down
  // the PROMPT-rooted path so the filter actually applies to them.
  const sigFilter = signalScopeFilter(parentScopePath);
  // Dim adapters (mcp_server, skill, …) own their own entries pipeline.
  // See src/lib/dimAdapters.js for the registry.
  const adapter = getDimAdapter(dim);
  if (adapter) {
    // Adapters build off buildPromptRootAndFilter, so the steer rides through.
    return adapter.listEntries({ apiScope, offset, limit, search, sort, range, signalFilter: sigFilter, steer: steerCompiled });
  }
  const fallback =
    !isAnalyticsSupportedDim(dim) ||
    unsupportedScopeDims(apiScope).length > 0;
  if (fallback) {
    return EMPTY_ENTRIES;
  }

  // Hierarchical cluster sets list tree levels instead of the flat
  // leaf ranking; flat sets return null and continue below.
  // Hierarchical cluster sets list tree levels; under a signal scope take the
  // flat PROMPT-rooted ranking instead so the event_id IN filter applies.
  // Hierarchical cluster entries now carry the steer (via rollupClusterActivity
  // → buildEntriesRoot), so the listing stays tree-shaped and matches the steered
  // count — same as drilling.
  if (dim === "cluster" && !sigFilter) {
    const hier = await clusterHierarchyEntries({ apiScope, range, search, sort, offset, limit, steer: steerCompiled, sparkline: sparkline && !minimal });
    if (hier) return hier;
  }

  // TOKEN-native dims (model / session / endpoint / agent) divert to the TOKEN
  // root ONLY for the explicit "Most tokens" sort. On "Most traces"/name they
  // rank by PROMPT count off the PROMPT root below — model included, via
  // promptRootDimRef (DIMENSION_FIELD_PROMPT_MODEL on the `p` step; dimFieldRef's
  // pu-anchored TOKEN_MODEL would 400 on a PROMPT root). model used to divert for
  // ALL sorts, silently showing token-observation counts on its default "Most
  // traces" sort (O-2204). Under a signal scope even "Most tokens" can't be
  // event-scoped (tokens carry no event_id), so it falls through too (O-2198) —
  // and likewise under a steer, so the steered filter actually applies.
  if (sort === "tokens" && usageScopeFieldFor(dim) && !sigFilter && !steerActive) {
    const usage = await executeUsageDimEntries({ dim, apiScope, offset, limit, search, sort, range });
    return dim === "session" && labels ? overlaySessionLabels(usage, toApiRange(range)) : usage;
  }

  const apiRange = toApiRange(range);
  const { fromMs, toMs } = rangeBoundsMs(apiRange);
  const SPARKLINE_BUCKETS = 24;
  const bucketSeconds = bucketSecondsForRange(apiRange, SPARKLINE_BUCKETS);

  const labelJoin = LABEL_JOIN_FOR_DIM[dim];
  const isArrayDim = dimIsArray(dim);
  const valueIsLabel = dimValueIsLabel(dim);
  // Search/sort by name semantics:
  //   endpoint / cluster — needs a join to the label entity, ILIKE on
  //                        hostname / cluster_name
  //   agent / file / tool_call — value IS the label, ILIKE / ORDER BY
  //                              applies directly to the dim field
  const wantsAgentSearch = Boolean(search) && valueIsLabel;
  const wantsNameSearch  = Boolean(search) && Boolean(labelJoin);
  const wantsNameSort    = sort === "name" && (Boolean(labelJoin) || valueIsLabel);
  const wantsRecentSort  = sort === "recent";
  // Anything reaching here has NO TOKEN column (cluster / tool_call /
  // mcp_server / skill / file / host / …): the TOKEN-native dims (model +
  // session/endpoint/agent on the token sort) already diverted to
  // executeUsageDimEntries above. Without a TOKEN column there's no way to rank
  // these by tokens (and the per-prompt usage edge is gone, O-1805), so "Most
  // tokens" falls through to the default value-DESC (activity) order below —
  // entries still render, ranked by prompt count — rather than emit a dead edge.
  // Always pull the label entity when the dim has one — sidebar /
  // column-browser entries render `entry.label` statically (they
  // don't go through scopeStepLabel's lazy cache), so an unjoined
  // query leaves UUIDs / cluster ints visible until something else
  // requests the label.
  const needsLabelJoin = Boolean(labelJoin);

  const { root: rankRoot, filter: rankFilter, unsupported: rankUnsupported } = buildEntriesRoot({ apiScope, dim, needsLabelJoin, steer: steerCompiled });
  // Fail closed on a scope this PROMPT root can't carry (today: model) instead
  // of silently widening the breakdown to all models (O-1805).
  if (rankUnsupported.length) return { entries: [], count: 0, total: 0, maxValue: 0 };
  // promptRootDimRef (not dimFieldRef): this path roots at PROMPT, so `model`
  // must group by DIMENSION_FIELD_PROMPT_MODEL on the `p` step — dimFieldRef's
  // pu-anchored TOKEN_MODEL 400s here ("unknown step alias: pu"). Identical to
  // dimFieldRef for every non-model dim. (O-2204.)
  const dimRef = promptRootDimRef(dim);
  const labelRef = needsLabelJoin && labelJoin
    ? { stepAlias: labelJoin.labelAlias, field: labelJoin.labelField }
    : null;

  // Search predicate.
  let searchPred = null;
  if (wantsAgentSearch) {
    // Array(String) dims (file, tool_call) accept LIKE/ILIKE
    // element-wise per the proto, so this works uniformly for both
    // scalar string fields (provider) and array fields.
    searchPred = dim === "agent"
      ? agentSearchPred(dimRef, search)
      : { pred: { field: dimRef, op: "OPERATOR_ILIKE", value: `%${search}%` } };
  } else if (wantsNameSearch && labelRef) {
    searchPred = { pred: { field: labelRef, op: "OPERATOR_ILIKE", value: `%${search}%` } };
  }
  // When a signal is in scope, restrict this breakdown to the prompts that
  // fired it (event_id IN fired set) — so every dim, clusters/skills included,
  // counts only signal-firing prompts. Layered on top of the base scope; the
  // signal step itself was dropped from apiScope (see signalScopeFilter).
  // The steer is already folded into rankRoot (joins) + rankFilter (filter) by
  // buildEntriesRoot → buildPromptRootAndFilter, so the rank / total / sparkline
  // specs below inherit it just like a scope predicate.
  const rankFilterFinal = combineAnd(combineAnd(rankFilter, searchPred), sigFilter);

  // Output dims. When name sort is requested OR we joined for search,
  // include the label so order_by alias resolves and the FE can
  // optionally use it.
  const outputDimensions = [{ field: dimRef, outputAlias: "id" }];
  if (needsLabelJoin && labelRef) {
    outputDimensions.push({ field: labelRef, outputAlias: "name" });
  }
  // Endpoint sidebar entries need the same re-registration identity inputs as
  // the inventory surfaces: hardware serial groups one physical machine and
  // last_updated selects its newest metadata snapshot. Keep these on the
  // endpoint label join so every loaded sidebar page can collapse duplicate
  // endpoint_ids without another metadata request.
  if (dim === "endpoint") {
    outputDimensions.push(...endpointEntryMetadataDimensions("ep_lbl"));
  }

  // User-facing activity counts logical prompts once despite source overlap
  // or a join producing several rows for the same prompt.
  const outputMetrics = [promptCount("value")];
  if (wantsRecentSort) {
    outputMetrics.push({
      outputAlias: "last_event",
      computed: {
        field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" },
        op: "AGGREGATE_OP_MAX",
      },
    });
  }
  // Order: activity (default), fewest-first, recent, name. Anything else falls
  // through to value DESC. Name sort: by joined-label alias when we joined a
  // label entity, otherwise by the dim id itself (value-is-label dims like file
  // paths, tool names). "Most tokens" can't reach here (wantsTokenSort is always
  // false for these no-TOKEN-column dims), so it falls through to value DESC =
  // activity. Both count orders add the name as a second key: counts tie often
  // (a fewest-first list is mostly 1-trace rows), and without it offset paging
  // can repeat or skip rows at page boundaries.
  const nameSortAlias = needsLabelJoin ? "name" : "id";
  const nameTiebreak = { outputAlias: nameSortAlias, direction: "DIRECTION_ASC" };
  const orderBy = wantsRecentSort
    ? [{ outputAlias: "last_event", direction: "DIRECTION_DESC" }]
    : wantsNameSort
    ? [nameTiebreak]
    : sort === SORT_FEWEST
    ? [{ outputAlias: "value", direction: "DIRECTION_ASC" }, nameTiebreak]
    : [{ outputAlias: "value", direction: "DIRECTION_DESC" }, nameTiebreak];

  // Server-side limit override for array dims with the buggy descriptor
  // path. The planner currently emits `GROUP BY <array_col>` for those
  // (each unique path-set is one group), so popular individual paths
  // get diluted across many small array groups. Pulling 5000 array
  // groups covers the long tail before we explode + re-aggregate
  // client-side. No-op overhead when the descriptor is correct (one
  // string per row anyway). Cluster scope is a normal server-paginated query
  // now (cluster_subtree, O-1624). Only array dims paginate client-side.
  const clientPaginate = isArrayDim;
  const rankServerLimit = isArrayDim
    ? Math.max(limit * 50, 5000)
    : limit;

  const rankSpec = {
    root: rankRoot,
    timeRange: rangeToTimeRange(apiRange),
    output: { dimensions: outputDimensions, metrics: outputMetrics },
    orderBy,
    limit: rankServerLimit, offset: clientPaginate ? 0 : offset,
  };
  if (rankFilterFinal) rankSpec.filter = rankFilterFinal;

  const totalSpec = {
    root: rankRoot,
    timeRange: rangeToTimeRange(apiRange),
    output: {
      metrics: [{
        outputAlias: "total",
        computed: { field: dimRef, op: "AGGREGATE_OP_COUNT_DISTINCT" },
      }],
    },
  };
  if (rankFilterFinal) totalSpec.filter = rankFilterFinal;

  // The total (scalar COUNT_DISTINCT) runs alongside the rank query; a
  // failure degrades to null so the entries still render (total falls back
  // to the page size below). `minimal` callers (the query-bar value
  // autocomplete) skip it — and the sparkline fan-out below — so a dropdown is
  // ONE round trip instead of three. `sparkline: false` is the narrower opt-out
  // for surfaces that DON'T draw per-entry sparklines (the Anatomy position
  // stack, descent, distinctiveness, baseline, and flow reads): it drops only
  // the heavy sparkline fan-out while KEEPING the cheap distinct-count so their
  // "distinct" stats stay accurate.
  // Both ride the degradable runner: the cluster label join is marked
  // `optional` (buildEntriesRoot), and only that runner takes the marker off
  // the wire and drops the join when the viewer's row scope cannot reach
  // ILO_CLUSTER. Sent raw, a flat-generation tenant's whole Topics listing
  // failed at the gateway with `unknown field "optional"` (OR-333).
  const [rawRankRows, totalRows] = await Promise.all([
    runDegradableQuery(rankSpec).then(rowsToObjects),
    minimal ? Promise.resolve(null) : runDegradableQuery(totalSpec).then(rowsToObjects).catch(() => null),
  ]);

  // Detect the descriptor-bug case: planner returned the array column
  // as the group-by value rather than the individual elements.
  // Explode + sum per element. If the descriptor is correct (or the
  // dim isn't array-typed), this is a no-op pass-through.
  let rankRows = isArrayDim ? explodeAndSumArrayIds(rawRankRows, sort) : rawRankRows;
  // Slice to the actually-requested limit + offset after the explode.
  const pagedRankRows = clientPaginate
    ? rankRows.slice(offset, offset + limit)
    : rankRows.slice(0, limit);

  // If the planner returned arrays for `id` (i.e. array-equality
  // grouping due to the descriptor bug), the sparkline IN-predicate
  // would be a scalar list compared to an Array column — ClickHouse
  // rejects that, the query throws, and the whole entries fetch
  // would propagate the error. Detect by inspecting whether ANY raw
  // row had an array id; if so, skip the sparkline entirely (each
  // entry gets a flat-zero series). Forward-compatible: when the
  // descriptor is fixed, ids are scalar strings and the sparkline
  // path runs as before.
  const sawArrayIds = isArrayDim && rawRankRows.some((r) => Array.isArray(r.id));

  const ids = pagedRankRows.map((r) => r.id).filter((v) => v !== null && v !== undefined && v !== "");
  let densified = new Map();
  if (!minimal && sparkline && ids.length > 0 && !sawArrayIds) {
    const inPred = {
      pred: { field: dimRef, op: "OPERATOR_IN", value: ids.map((v) => dim === "cluster" ? Number(v) : String(v)) },
    };
    const sparkSpec = {
      root: rankRoot,
      timeRange: rangeToTimeRange(apiRange),
      output: {
        dimensions: [
          { field: dimRef, outputAlias: "id" },
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds },
        ],
        metrics: [promptCount("value")],
      },
    };
    sparkSpec.filter = combineAnd(rankFilterFinal, inPred);
    // Wrap in try/catch even on the happy path so a single
    // sparkline-query failure (e.g., a planner-side validation
    // error we haven't seen yet) never takes down the whole
    // entries fetch.
    try {
      // Same root as the rank read, so the same degradable runner: the name
      // join rides along unused here and must not cost a scoped viewer the
      // sparklines.
      const rawSparkRows = rowsToObjects(await runDegradableQuery(sparkSpec));
      densified = densifyBuckets(rawSparkRows, { fromMs, toMs, bucketCount: SPARKLINE_BUCKETS, bucketSeconds });
    } catch (err) {
      console.warn(`[executeDimensionEntries] sparkline failed for dim=${dim}, leaving entries un-sparked:`, err);
    }
  }

  const max = pagedRankRows.reduce((m, e) => Math.max(m, Number(e.value) || 0), 0) || 1;
  // Value-is-label dims with an empty bucket (no model / no provider resolved)
  // get a readable empty label so the row is legible + still clickable — mirrors
  // executeUsageDimEntries (the TOKEN path model used to take, O-2204).
  const emptyLabel = dim === "model" ? "(no model)" : dim === "agent" ? "(unattributed)" : "";
  const shaped = pagedRankRows.map((e) => {
    const activity = Number(e.value) || 0;
    const rawShare = Math.round((activity / max) * 100);
    const share = activity > 0 ? Math.max(1, rawShare) : 0;
    const id = String(e.id ?? "");
    // Use the joined label when present; otherwise fall back to id (lazy label
    // cache resolves it client-side), or the readable empty-bucket label.
    const label = e.name ? String(e.name) : (id || emptyLabel);
    return {
      id, label, activity, share, note: "",
      ...(dim === "endpoint" ? {
        serial: String(e.serial ?? ""),
        snapshotTime: e.snapshot_time ?? null,
      } : {}),
      sparkline: {
        series: densified.get(id) ?? new Array(SPARKLINE_BUCKETS).fill(0),
        stroke: "#7fa16c",
        fill: "rgba(127,161,108,0.12)",
      },
    };
  });
  // Distinct count for the dim+scope+range; falls back to the page size
  // only if the count query returned nothing.
  const total = Number(totalRows?.[0]?.total) || shaped.length;
  const result = { entries: shaped, count: shaped.length, total, maxValue: max };
  // Sessions carry no human-readable name, so the generic path labels their
  // entries with the raw notification_id — overlay the most-recent prompt
  // summary instead (see overlaySessionLabels / sessionLabels.js), unless the
  // caller opted out of labels (id resolution).
  return dim === "session" && labels ? overlaySessionLabels(result, apiRange) : result;
}

// The welcome page's groups, read as a dimension's entries are (getDimensionEntries
// with dimKey "groups"): the departments of the directory people who ran the
// work, the most requests first, each with its line over the range. Read off
// the PROMPT -> assignment -> directory user join the identity dimension rides,
// INNER, so only requests the directory attributes count; people with no
// department share the empty value. No sidebar, scope or flow reads a group,
// so it stays out of the dimension registries.
export const GROUPS_DIM = "groups";
const DEPARTMENT = { stepAlias: "du", field: "DIMENSION_FIELD_DIRECTORY_USER_DEPARTMENT" };
export async function executeGroupEntries(params = {}) {
  const { parentScopePath = [], offset = 0, limit = 100, search = "", range, minimal = false, sparkline = true, query } = params;
  const EMPTY = { entries: [], count: 0, total: 0, maxValue: 0 };
  const { compiled: steer, active, carryable } = await compileSteer(query);
  if (active && !carryable) return EMPTY;
  const { root, filter: scoped, unsupported } = buildPromptRootAndFilter({ scopePath: toApiScope(parentScopePath, range), extraDims: ["identity"], steer });
  if (unsupported.length) return EMPTY;
  const searchPred = search ? { pred: { field: DEPARTMENT, op: "OPERATOR_ILIKE", value: `%${search}%` } } : null;
  const filter = combineAnd(combineAnd(scoped, searchPred), signalScopeFilter(parentScopePath));
  const apiRange = toApiRange(range);
  const spec = (output, rest = {}) => ({ root, timeRange: rangeToTimeRange(apiRange), output, ...(filter ? { filter } : {}), ...rest });
  const [rows, totals] = await Promise.all([
    runAnalyticsQuery(spec(
      { dimensions: [{ field: DEPARTMENT, outputAlias: "id" }], metrics: [promptCount("value")] },
      { orderBy: [{ outputAlias: "value", direction: "DIRECTION_DESC" }, { outputAlias: "id", direction: "DIRECTION_ASC" }], limit, offset },
    )).then(rowsToObjects),
    minimal ? null : runAnalyticsQuery(spec({ metrics: [{ outputAlias: "total", computed: { field: DEPARTMENT, op: "AGGREGATE_OP_COUNT_DISTINCT" } }] })).then(rowsToObjects).catch(() => null),
  ]);
  const BUCKETS = 24;
  const ids = rows.map((r) => String(r.id ?? ""));
  let lines = new Map();
  if (!minimal && sparkline && ids.length) {
    const { fromMs, toMs } = rangeBoundsMs(apiRange);
    const bucketSeconds = bucketSecondsForRange(apiRange, BUCKETS);
    try {
      lines = densifyBuckets(rowsToObjects(await runAnalyticsQuery(spec(
        {
          dimensions: [{ field: DEPARTMENT, outputAlias: "id" }, { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds }],
          metrics: [promptCount("value")],
        },
        // The empty value too: no department is a row of its own.
        { filter: combineAnd(filter, { pred: { field: DEPARTMENT, op: "OPERATOR_IN", value: ids } }) },
      ))), { fromMs, toMs, bucketCount: BUCKETS, bucketSeconds });
    } catch (err) {
      console.warn("[executeGroupEntries] sparkline failed, leaving entries un-sparked:", err);
    }
  }
  const max = rows.reduce((m, r) => Math.max(m, Number(r.value) || 0), 0) || 1;
  const entries = rows.map((r, i) => {
    const activity = Number(r.value) || 0;
    return {
      id: ids[i], label: ids[i], activity, share: activity > 0 ? Math.max(1, Math.round((activity / max) * 100)) : 0, note: "",
      sparkline: { series: lines.get(ids[i]) ?? new Array(BUCKETS).fill(0), stroke: "#7fa16c", fill: "rgba(127,161,108,0.12)" },
    };
  });
  return { entries, count: entries.length, total: Number(totals?.[0]?.total) || entries.length, maxValue: max };
}

// Sessions have no name field on AI_SESSION, so every session-label surface
// otherwise falls back to a meaningless id (full UUID in entries, an 8-char
// prefix in chips). Replace an entries result's labels with each session's
// most-recent prompt summary (sessionLabels.js), keeping the short-id prefix
// wherever no summary is available so the fallback matches the chip resolver.
// Mutates + returns the result; getSessionLabels never throws. The label is
// the session's most-recent prompt WITHIN the viewed window, so the current
// timeline (apiRange) is passed through — a session's label then matches the
// data the user is looking at rather than a fixed trailing window.
async function overlaySessionLabels(result, apiRange) {
  const entries = result?.entries;
  if (!entries?.length) return result;
  const labels = await getSessionLabels(entries.map((e) => e.id), rangeToTimeRange(apiRange));
  for (const e of entries) {
    e.label = labels.get(String(e.id)) || String(e.id).slice(0, 8);
  }
  return result;
}

// Explode + re-aggregate workaround for array-typed dim responses
// where the planner's descriptor was scalar-shaped (so it grouped by
// array equality instead of arrayJoin'd elements). Each input row's
// `id` may be:
//   - a string (descriptor was correct, e.g. tool_call_names) — passed
//     through unchanged
//   - an array of strings (descriptor bug, e.g. resource_paths) —
//     explode each element and add the row's value to that element's
//     running total
// Empty arrays drop out (they represent prompts with no paths). Sorted in
// the list's requested order, not by value alone: the server already ordered
// the rows, and re-ranking here would undo an A-Z or fewest-first sort.
function explodeAndSumArrayIds(rows, sort) {
  const totals = new Map();
  for (const r of rows) {
    const v = Number(r.value) || 0;
    const id = r.id;
    if (Array.isArray(id)) {
      for (const elem of id) {
        if (elem == null || elem === "") continue;
        const k = String(elem);
        totals.set(k, (totals.get(k) || 0) + v);
      }
    } else if (id != null && id !== "") {
      const k = String(id);
      totals.set(k, (totals.get(k) || 0) + v);
    }
  }
  return [...totals.entries()]
    .map(([id, value]) => ({ id, value }))
    .sort(compareDimEntries(sort));
}

// Joins the PROMPT-rooted count specs know how to absorb (via ensureQueryJoins
// inside applyQueryToSpec). A steer whose compiled joins fall outside this set
// can't be carried onto a count root, so the caller surfaces "—".
const STEER_CARRYABLE_JOINS = new Set(["cluster", "endpoint_host", "identity"]);

// Compile the sidebar scope STEER (a query string) for the facet-count path.
// Returns { compiled: {filter, joins}, active, carryable }. The compiled query
// is applied to each count spec via applyQueryToSpec — the SAME path the trace
// list / overview use — so a join steer (endpoint by hostname, cluster,
// identity) carries onto the PROMPT-rooted COUNT_DISTINCT, not just no-join ones.
async function compileSteer(query) {
  const q = String(query || "").trim();
  if (!q) return { compiled: null, active: false, carryable: true };
  try {
    const compiled = await resolveAndCompile(q);
    if (!compiled?.filter) return { compiled: null, active: false, carryable: true };
    const carryable = (compiled.joins || []).every((j) => STEER_CARRYABLE_JOINS.has(j));
    return { compiled, active: true, carryable };
  } catch {
    // A transient typo shouldn't blank the sidebar — treat as inactive so counts
    // stay scope-only until the steer parses.
    return { compiled: null, active: false, carryable: true };
  }
}

export async function executeDimensionCount(params = {}) {
  const { dimKey, parentScopePath = [], range, query } = params;
  const dim = DIM_SINGULAR[dimKey] ?? dimKey;
  const sigFilter = signalScopeFilter(parentScopePath);
  const { compiled: steerCompiled, active: steerActive, carryable } = await compileSteer(query);
  // A steer this count path can't carry (a join outside the known set) → return
  // null so the header shows "—" instead of a count that ignores the steer.
  if (steerActive && !carryable) return null;
  // Dim adapters own their own count semantics, but build off the shared
  // buildPromptRootAndFilter, so the steer rides through them like any scope.
  const adapter = getDimAdapter(dim);
  if (adapter) {
    return adapter.count({ apiScope: toApiScope(parentScopePath, range), range, signalFilter: sigFilter, steer: steerCompiled });
  }
  const fallback =
    !isAnalyticsSupportedDim(dim) ||
    unsupportedScopeDims(toApiScope(parentScopePath, range)).length > 0;
  if (fallback) {
    return null;
  }
  const apiScope = toApiScope(parentScopePath, range);
  // model counts distinct DIMENSION_FIELD_PROMPT_MODEL off the PROMPT root below
  // (promptRootDimRef), matching its PROMPT-rooted entries — no TOKEN-root count
  // detour (O-2204). model was the only `viaUsage` dim, so nothing needs one.
  // Hierarchical cluster sets badge the children at this position; under a signal
  // scope, count distinct clusters off the PROMPT root below instead (O-2198).
  // Hierarchical cluster count comes from the tree rollup, which now carries the
  // steer (rollupClusterActivity → buildEntriesRoot → buildPromptRootAndFilter),
  // so a steered CLUSTERS count stays the top-level-children semantics — same as
  // drilling — instead of switching to a flat distinct-cluster count.
  if (dim === "cluster" && !sigFilter) {
    const hierCount = await clusterHierarchyCount(apiScope, range, steerCompiled);
    if (hierCount != null) return hierCount;
  }
  const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: [dim], steer: steerCompiled });
  // Fail closed on a scope this PROMPT root can't carry (today: model) instead of
  // counting across all models while a model filter is active (O-1805).
  if (unsupported.length) return 0;
  // promptRootDimRef (not dimFieldRef): PROMPT root → model counts distinct
  // DIMENSION_FIELD_PROMPT_MODEL on `p`. Identical to dimFieldRef for non-model.
  const fieldRef = promptRootDimRef(dim);
  const spec = {
    root,
    timeRange: rangeToTimeRange(toApiRange(range)),
    output: {
      metrics: [{
        outputAlias: "total",
        computed: { field: fieldRef, op: "AGGREGATE_OP_COUNT_DISTINCT" },
      }],
    },
  };
  // Scope the count to the signal's fired prompts when a signal is in scope.
  // (The steer is already folded into root+filter by buildPromptRootAndFilter.)
  const countFilter = combineAnd(filter, sigFilter);
  if (countFilter) spec.filter = countFilter;
  const [row = {}] = rowsToObjects(await runAnalyticsQuery(spec));
  return Number(row.total) || 0;
}

// Batches multiple COUNT_DISTINCT metrics into a single query.
//
// SAFETY INVARIANT: every item in `items` must already need the same
// join shape (same `extraDims`). The caller (executeDimensionCounts)
// enforces this by partitioning dims into direct/joined/usage/arrays
// groups before calling here — DO NOT collapse those groups into one
// "smarter" mega-query. The fan-out is multiplicative: a PROMPT ×
// TOKEN × ILO_EVENT_CLUSTER cartesian scans 3–10× more bytes
// than three parallel queries with the joins each one actually needs,
// and the wall-clock win is zero because the parallel queries are
// already bounded by max(slowest). Cross-join consolidation looks
// good in a "fewer requests" audit but makes things worse in
// practice.
async function executeDimensionCountGroup({ items, apiScope, range, extraDims = [], steerCompiled = null }) {
  if (!items.length) return {};
  const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims, steer: steerCompiled });
  // Fail closed on a scope this PROMPT root can't carry (today: model): return a
  // zero for every requested dim rather than counts widened across all models (O-1805).
  if (unsupported.length) return Object.fromEntries(items.map(({ key }) => [key, 0]));
  const spec = {
    root,
    timeRange: rangeToTimeRange(toApiRange(range)),
    output: {
      metrics: items.map(({ dim }, i) => ({
        outputAlias: `c${i}`,
        computed: {
          field: dimFieldRef(dim),
          op: "AGGREGATE_OP_COUNT_DISTINCT",
        },
      })),
    },
  };
  // The steer is already folded into root+filter by buildPromptRootAndFilter.
  if (filter) spec.filter = filter;
  const [row = {}] = rowsToObjects(await runAnalyticsQuery(spec));
  return Object.fromEntries(items.map(({ key }, i) => [key, Number(row[`c${i}`]) || 0]));
}

// Per-dimension picker counts depend only on scope + range (+ the pinned
// cluster generation) — never on the trailing dim. But buildColumnsWithFlowLinks
// is cache-keyed including trailDim, so toggling the trail (e.g. clicking back
// to the dimension picker) re-executes these counts even though they were just
// on screen, and the cluster/usage joins make that slow. This short read-through
// cache dedupes it: the toggle reuses the just-computed counts. Keyed to
// invalidate on range / scope / generation change, TTL ≈ the data-client's
// stale window so it never serves counts staler than a normal refetch would.
const _dimCountsCache = new Map(); // key -> { t, p }
const DIM_COUNTS_TTL_MS = 30_000;

onAuthContextChange(() => _dimCountsCache.clear());

function executeDimensionCounts(args = {}) {
  const authContextEpoch = getAuthContextEpoch();
  const { dimKeys = [], parentScopePath = [], range, query } = args;
  const key = JSON.stringify([authContextEpoch, [...dimKeys].sort(), parentScopePath, range, getPinnedGenerationId() ?? null, query || ""]);
  const now = Date.now();
  const hit = _dimCountsCache.get(key);
  if (hit && now - hit.t < DIM_COUNTS_TTL_MS) return hit.p;
  // Bound the map: drop expired entries before inserting a fresh one, and if a
  // burst of distinct keys is still over cap within one TTL window, evict
  // oldest-first (Map preserves insertion order) so it can't grow unbounded.
  if (_dimCountsCache.size > 64) {
    for (const [k, v] of _dimCountsCache) if (now - v.t >= DIM_COUNTS_TTL_MS) _dimCountsCache.delete(k);
    while (_dimCountsCache.size > 64) _dimCountsCache.delete(_dimCountsCache.keys().next().value);
  }
  const p = executeDimensionCountsUncached(args).then((counts) => {
    if (getAuthContextEpoch() !== authContextEpoch) {
      const error = new Error("dimension-count auth context changed during request");
      error.name = "AuthContextChangedError";
      throw error;
    }
    return counts;
  });
  _dimCountsCache.set(key, { t: now, p });
  p.catch(() => { if (_dimCountsCache.get(key)?.p === p) _dimCountsCache.delete(key); }); // don't cache failures
  return p;
}

async function executeDimensionCountsUncached({ dimKeys = [], parentScopePath = [], range, query } = {}) {
  const apiScope = toApiScope(parentScopePath, range);
  const forceFallback = unsupportedScopeDims(apiScope).length > 0;
  const items = dimKeys.map((key) => ({ key, dim: DIM_SINGULAR[key] ?? key }));
  const out = {};
  const { compiled: steerCompiled, active: steerActive, carryable } = await compileSteer(query);
  // A steer the PROMPT-rooted count path can't carry → no dim can honor it;
  // show "—" everywhere rather than a mix of filtered + silently-unfiltered counts.
  if (steerActive && !carryable) {
    for (const { key } of items) out[key] = null;
    return out;
  }

  const countIndividually = (group) => Promise.all(group.map(async ({ key }) => {
    try {
      out[key] = await executeDimensionCount({ dimKey: key, parentScopePath, range, query });
    } catch {
      // A refused or failed read is not an observed empty set. `null` is the
      // established unavailable-count sentinel: the sidebar renders it as an
      // unavailable state and the column picker treats it as unknown. Returning
      // zero here made a row-scope or object-policy denial indistinguishable
      // from a tenant that genuinely had no clusters, skills, or signals.
      out[key] = null;
    }
  }));

  if (forceFallback) {
    await countIndividually(items);
    return out;
  }


  // Hierarchical cluster sets count children-at-position from the
  // tree (see clusterHierarchyCount) — the batched COUNT_DISTINCT
  // would report leaves. The tree fetch is session-cached, so this
  // await is a one-time cost on the first sidebar mount.
  const clusterHier = items.some((i) => i.dim === "cluster") &&
    (await loadClusterTree()).isHierShape;

  const fallback = [];
  const direct = [];
  const joined = [];
  const usage = [];
  const arrays = [];
  const assignment = [];
  for (const item of items) {
    const adapter = getDimAdapter(item.dim);
    if (!isAnalyticsSupportedDim(item.dim)) fallback.push(item);
    // Adapter-backed dims with countBatchable: false (today: every
    // adapter) own their count pipeline; routing them through the
    // batched COUNT_DISTINCT spec would emit `field: Unspecified`
    // and the planner would reject. countIndividually invokes the
    // adapter's own count() instead. Adapters that DO support
    // batching (e.g. a future predicate-but-on-a-real-field adapter)
    // can opt in by declaring countBatchable: true.
    else if (adapter && !adapter.countBatchable) fallback.push(item);
    // Array(String) dims (tool_call, file) require arrayJoin at query
    // time, which expands rows and silently drops prompts with empty
    // arrays. Mixed into a group with scalar metrics, this causes
    // the scalar COUNT_DISTINCT counts to under-report (endpoints
    // that never made a tool call vanish from the row set). Count
    // each array dim in its own query so its arrayJoin context can't
    // contaminate the others.
    else if (dimIsArray(item.dim)) arrays.push(item);
    // Hierarchical cluster count comes from the tree, not the batch —
    // countIndividually routes through executeDimensionCount, which
    // hits the clusterHierarchyCount override.
    else if (item.dim === "cluster" && clusterHier) fallback.push(item);
    else if (dimNeedsClusterJoin(item.dim)) joined.push(item);
    // Identity (viaAssignment) gets its own join group — mixing it into the
    // cluster `joined` group would cartesian PROMPT × ec × u (O-2400).
    else if (dimNeedsAssignmentJoin(item.dim)) assignment.push(item);
    // `viaUsage` dims (today: model) are counted one-by-one via
    // executeDimensionCount rather than mixed into the multi-dim batch spec.
    // model's distinct count roots at the PROMPT root there (PROMPT_MODEL via
    // promptRootDimRef, O-2204), so it no longer needs a TOKEN-root count.
    else if (dimNeedsUsageJoin(item.dim)) usage.push(item);
    else direct.push(item);
  }

  await Promise.all([
    (async () => {
      if (!direct.length) return;
      try {
        Object.assign(out, await executeDimensionCountGroup({ items: direct, apiScope, range, steerCompiled }));
      } catch {
        await countIndividually(direct);
      }
    })(),
    (async () => {
      if (!joined.length) return;
      try {
        Object.assign(out, await executeDimensionCountGroup({
          items: joined,
          apiScope,
          range,
          extraDims: [...new Set(joined.map((item) => item.dim))],
          steerCompiled,
        }));
      } catch {
        await countIndividually(joined);
      }
    })(),
    (async () => {
      if (!assignment.length) return;
      try {
        Object.assign(out, await executeDimensionCountGroup({
          items: assignment,
          apiScope,
          range,
          extraDims: [...new Set(assignment.map((item) => item.dim))],
          steerCompiled,
        }));
      } catch {
        await countIndividually(assignment);
      }
    })(),
    // `viaUsage` dims (today: model) are counted individually via
    // executeDimensionCount — model's distinct count roots at the PROMPT root
    // there (PROMPT_MODEL, O-2204), kept out of the multi-dim batch spec.
    countIndividually(usage),
    countIndividually(arrays),
    countIndividually(fallback),
  ]);

  return out;
}

// ── Contrast (FE-side TV scoring) ───────────────────────────────

const CONTRAST_RANK_K = 60;  // top-K rows pulled before scoring
const CONTRAST_RANK_M = 30;  // top-M cols pulled before scoring

async function contrastViaAnalytics({
  apiScope, rowDimS, colDimS, rowLimit, colLimit, selfId, selfColId, apiRange,
}) {
  const tree = await loadClusterTree();
  // Contrast a scoped cluster against its SIBLINGS — the clusters at its own
  // drill level — with the scoped cluster highlighted. NOT its children (one
  // level too deep) and NOT the roots (too shallow once you've drilled in).
  // ContrastPanel strips the cluster step and passes it as selfId/selfColId;
  // for a hierarchical cluster axis we re-inject its PARENT as the scope step,
  // so the query scopes to the parent's subtree and clusterLevelIds returns
  // the parent's children (the scoped cluster's siblings). A root has no
  // parent → fall back to the top level (roots, unscoped). Flat tenants keep
  // the strip-and-pin behavior.
  const clusterSelf = colDimS === "cluster" ? selfColId : rowDimS === "cluster" ? selfId : null;
  const selfParent = (clusterSelf != null && tree?.isHierShape)
    ? (tree.byId.get(String(clusterSelf))?.parentId ?? null)
    : null;
  const effectiveScope = selfParent != null
    ? [...apiScope, { dim: "cluster", id: selfParent }]
    : apiScope;

  const extraDims = [rowDimS, colDimS].filter(
    (d) => dimNeedsClusterJoin(d) || dimNeedsUsageJoin(d) || dimNeedsAssignmentJoin(d),
  );
  const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({
    scopePath: effectiveScope, extraDims,
  });
  // Fail closed on a scope this PROMPT root can't carry (today: model) instead of
  // contrasting across all models while a model filter is active (O-1805).
  if (unsupported.length) return contrastEmpty(rowDimS, colDimS);
  const rowRef = dimFieldRef(rowDimS);
  const colRef = dimFieldRef(colDimS);
  const cast = (dim) => (dim === "cluster" ? Number : String);

  // Q1+Q2: top RANK_K rows + top RANK_M cols by activity.
  const rankSpec = (axisRef, limit, alias) => {
    const spec = {
      root,
      timeRange: rangeToTimeRange(apiRange),
      output: {
        dimensions: [{ field: axisRef, outputAlias: "id" }],
        metrics: [promptCount("activity")],
      },
      orderBy: [{ outputAlias: "activity", direction: "DIRECTION_DESC" }],
      limit,
    };
    if (scopeFilter) spec.filter = scopeFilter;
    return spec;
  };

  // Cluster axis hierarchy rollup (O-1624). A cluster axis shows the
  // hierarchy nodes at the current drill level (clusterLevelIds — the scoped
  // cluster's children, else the roots), counting each node's whole subtree.
  // Without this hierarchy projection the axis listed raw leaves plus an
  // always-empty self column for the (internal) scoped node. `null` for flat
  // tenants or an empty level → contrast keeps its leaf behavior there, so
  // non-hierarchical clusters are unchanged.
  const axisNodes = (dimS) => {
    if (dimS !== "cluster" || !tree?.isHierShape) return null;
    const visibleIds = clusterLevelIds(tree, effectiveScope).map(String);
    if (!visibleIds.length) return null;
    const leafToNode = new Map();
    for (const id of visibleIds) for (const lf of leafDescendantIds(tree, id)) leafToNode.set(String(lf), id);
    return { ids: visibleIds, nodeFor: (leaf) => leafToNode.get(String(leaf)) ?? null };
  };
  const rowNodes = axisNodes(rowDimS);
  const colNodes = axisNodes(colDimS);

  // Every node at the level from one leaf-grouped read and its distinct total,
  // or null when those can't give exact node counts (see rollUpLeafCounts).
  const rollUpLevel = async (nodes, filter, alias, groupDims = []) => {
    try {
      return await rollUpLeafCounts({
        root, filter, timeRange: rangeToTimeRange(apiRange), leafField: dimFieldRef("cluster"),
        leafToVisible: leafToVisibleNodes(tree, nodes.ids), groupDims,
        metrics: [{ alias }], runQuery: runAnalyticsQuery, rowsToObjects,
      });
    } catch (err) {
      console.warn("[contrast] leaf rollup failed; counting each topic:", err);
      return null;
    }
  };

  // Parent activity must count the union of source observations in its subtree.
  const rankAxis = async (axisRef, nodes, limit) => {
    if (!nodes) return runAnalyticsQuery(rankSpec(axisRef, limit)).then(rowsToObjects);
    const rolled = await rollUpLevel(nodes, scopeFilter, "activity");
    let rows;
    if (rolled) {
      const activity = new Map(rolled.map((row) => [row.node, row.activity]));
      rows = nodes.ids.map((id) => ({ id, activity: activity.get(id) || 0 }));
    } else {
      rows = await countPromptGroups({
        root, filter: scopeFilter, timeRange: rangeToTimeRange(apiRange),
        groups: clusterPromptGroups(nodes.ids), valueAlias: "activity",
        runQuery: runAnalyticsQuery, rowsToObjects,
      });
    }
    return rows.sort((a, b) => b.activity - a.activity).slice(0, limit);
  };

  const [topRowRows, topColRows] = await Promise.all([
    rankAxis(rowRef, rowNodes, CONTRAST_RANK_K),
    rankAxis(colRef, colNodes, CONTRAST_RANK_M),
  ]);
  // Pin the scoped id as a row/col even if outside top-K, so it's always
  // shown and highlighted (selfRow/ColIndex). For a rolled cluster axis the
  // scoped cluster is one of the displayed siblings, so its column is
  // non-empty (its leaves fold onto it) — exactly the self column we want.
  if (selfId != null && !topRowRows.some((r) => String(r.id) === String(selfId))) {
    topRowRows.push({ id: selfId, activity: 0 });
  }
  if (selfColId != null && !topColRows.some((c) => String(c.id) === String(selfColId))) {
    topColRows.push({ id: selfColId, activity: 0 });
  }
  const topRows = topRowRows.filter((r) => r.id !== null && r.id !== undefined);
  const topCols = topColRows.filter((c) => c.id !== null && c.id !== undefined);
  if (!topRows.length || !topCols.length) return contrastEmpty(rowDimS, colDimS);

  const restrictions = [];
  if (!rowNodes) restrictions.push({ pred: { field: rowRef, op: "OPERATOR_IN", value: topRows.map((row) => cast(rowDimS)(row.id)) } });
  if (!colNodes) restrictions.push({ pred: { field: colRef, op: "OPERATOR_IN", value: topCols.map((col) => cast(colDimS)(col.id)) } });
  const matrixFilter = combineAnd(scopeFilter, restrictions.length ? { andExpr: { children: restrictions } } : null);
  let matrixRows;
  if (rowNodes && colNodes) {
    const groups = [];
    for (const row of topRows) {
      const rowLeaves = new Set(leafDescendantIds(tree, row.id).map(String));
      for (const col of topCols) {
        if (!leafDescendantIds(tree, col.id).some((id) => rowLeaves.has(String(id)))) continue;
        groups.push({ id: `${row.id}|${col.id}`, predicate: combineAnd(
          clusterPromptGroups([row.id])[0].predicate, clusterPromptGroups([col.id])[0].predicate,
        ) });
      }
    }
    const rows = await countPromptGroups({ root, filter: matrixFilter, timeRange: rangeToTimeRange(apiRange), groups, runQuery: runAnalyticsQuery, rowsToObjects, valueAlias: "n" });
    matrixRows = rows.map((row) => ({ row_id: row.id.split("|")[0], col_id: row.id.split("|")[1], n: row.n }));
  } else if (rowNodes || colNodes) {
    const nodes = rowNodes ?? colNodes;
    const nodeAlias = rowNodes ? "row_id" : "col_id";
    const otherDim = { field: rowNodes ? colRef : rowRef, outputAlias: rowNodes ? "col_id" : "row_id" };
    const topIds = new Set((rowNodes ? topRows : topCols).map((node) => String(node.id)));
    const levelIds = new Set(nodes.ids);
    const rolled = [...topIds].every((id) => levelIds.has(id))
      ? await rollUpLevel(nodes, matrixFilter, "n", [otherDim])
      : null;
    if (rolled) {
      matrixRows = rolled
        .filter((row) => topIds.has(row.node))
        .map((row) => ({ [nodeAlias]: row.node, [otherDim.outputAlias]: row[otherDim.outputAlias], n: row.n }));
    } else {
      const rows = await countPromptGroups({
        root, filter: matrixFilter, timeRange: rangeToTimeRange(apiRange),
        groups: clusterPromptGroups([...topIds]),
        dimensions: [otherDim],
        runQuery: runAnalyticsQuery, rowsToObjects, valueAlias: "n",
      });
      matrixRows = rows.map((row) => ({ ...row, [nodeAlias]: row.id }));
    }
  } else {
    matrixRows = rowsToObjects(await runAnalyticsQuery({
      root, timeRange: rangeToTimeRange(apiRange), filter: matrixFilter,
      output: {
        dimensions: [{ field: rowRef, outputAlias: "row_id" }, { field: colRef, outputAlias: "col_id" }],
        metrics: [promptCount("n")],
      },
    }));
  }
  const cells = new Map(matrixRows.map((row) => [`${row.row_id}|${row.col_id}`, Number(row.n) || 0]));

  // Compute per-row TV-divergence vs col marginal, weight by sqrt(activity)
  // for the score (matches legacy "tiny-but-divergent vs huge-but-boring"
  // balance). Self-pin via score boost.
  const rowAct = topRows.map((r) => Number(r.activity) || 0);
  const colAct = topCols.map((c) => Number(c.activity) || 0);
  const colTotal = colAct.reduce((s, n) => s + n, 0) || 1;
  const rowTotal = rowAct.reduce((s, n) => s + n, 0) || 1;

  const rowScored = topRows.map((r, ri) => {
    const aTotal = Math.max(rowAct[ri], 1);
    let tv = 0;
    for (let ci = 0; ci < topCols.length; ci++) {
      const observed = cells.get(`${r.id}|${topCols[ci].id}`) || 0;
      const pCondGivenRow = observed / aTotal;
      const pColMarginal  = colAct[ci] / colTotal;
      tv += Math.abs(pCondGivenRow - pColMarginal);
    }
    const divergence = 0.5 * tv;
    const boost = (selfId != null && String(r.id) === String(selfId)) ? Infinity : 0;
    return { ...r, divergence, score: boost || Math.sqrt(aTotal) * divergence };
  });
  const colScored = topCols.map((c, ci) => {
    const aTotal = Math.max(colAct[ci], 1);
    let tv = 0;
    for (let ri = 0; ri < topRows.length; ri++) {
      const observed = cells.get(`${topRows[ri].id}|${c.id}`) || 0;
      const pCondGivenCol = observed / aTotal;
      const pRowMarginal  = rowAct[ri] / rowTotal;
      tv += Math.abs(pCondGivenCol - pRowMarginal);
    }
    const divergence = 0.5 * tv;
    const boost = (selfColId != null && String(c.id) === String(selfColId)) ? Infinity : 0;
    return { ...c, divergence, score: boost || Math.sqrt(aTotal) * divergence };
  });

  // Take top rowLimit/colLimit by score, then re-sort by score-without-boost
  // so self doesn't pin to index 0.
  const finalRows = rowScored.sort((a, b) => b.score - a.score).slice(0, rowLimit);
  const finalCols = colScored.sort((a, b) => b.score - a.score).slice(0, colLimit);
  finalRows.sort((a, b) => Number.isFinite(b.score) - Number.isFinite(a.score) || (Number.isFinite(b.score) ? b.score - a.score : 0));

  const grandTotal = rowTotal;
  const matrix         = finalRows.map(() => new Array(finalCols.length).fill(0));
  const liftMatrix     = finalRows.map(() => new Array(finalCols.length).fill(0));
  const expectedMatrix = finalRows.map(() => new Array(finalCols.length).fill(0));
  const surpriseMatrix = finalRows.map(() => new Array(finalCols.length).fill(0));
  let maxCell = 0, maxSurprise = 0, maxExpected = 0, minExpected = Infinity;
  for (let ri = 0; ri < finalRows.length; ri++) {
    const rTotal = Number(finalRows[ri].activity) || 0;
    for (let ci = 0; ci < finalCols.length; ci++) {
      const cTotal = Number(finalCols[ci].activity) || 0;
      const observed = cells.get(`${finalRows[ri].id}|${finalCols[ci].id}`) || 0;
      const expected = grandTotal > 0 ? (rTotal * cTotal) / grandTotal : 0;
      matrix[ri][ci] = observed;
      expectedMatrix[ri][ci] = expected;
      liftMatrix[ri][ci] = expected > 0 ? observed / expected : 0;
      const surprise = observed - expected;
      surpriseMatrix[ri][ci] = surprise;
      if (observed > maxCell) maxCell = observed;
      if (surprise > maxSurprise) maxSurprise = surprise;
      if (expected > maxExpected) maxExpected = expected;
      if (expected > 0 && expected < minExpected) minExpected = expected;
    }
  }
  if (!isFinite(minExpected)) minExpected = 0;

  const dimToPlural = {
    endpoint: "endpoints", agent: "agents", session: "sessions", cluster: "clusters",
  };

  // Resolve human labels for the kept rows/cols. The contrast panel
  // renders `row.label` / `col.label` statically — without this
  // lookup the axis chips show UUIDs / cluster ints. Two parallel
  // queries (one per axis) hitting the dim's label entity.
  const [rowNames, colNames] = await Promise.all([
    fetchAxisNames(rowDimS, finalRows.map((r) => r.id), apiRange),
    fetchAxisNames(colDimS, finalCols.map((c) => c.id), apiRange),
  ]);

  return {
    rows: finalRows.map((r) => {
      const idStr = String(r.id);
      const label = rowNames.get(idStr) ?? idStr;
      return {
        id: idStr, label, name: label,
        activity: Number(r.activity) || 0,
        divergence: Number(r.divergence) || 0,
      };
    }),
    cols: finalCols.map((c) => {
      const idStr = String(c.id);
      const label = colNames.get(idStr) ?? idStr;
      return {
        id: idStr, label, name: label,
        activity: Number(c.activity) || 0,
        divergence: Number(c.divergence) || 0,
      };
    }),
    matrix, liftMatrix, expectedMatrix, surpriseMatrix,
    maxCell, maxSurprise, maxExpected, minExpected,
    rowDim: dimToPlural[rowDimS] ?? rowDimS,
    colDim: dimToPlural[colDimS] ?? colDimS,
    selfRowIndex: selfId    != null ? finalRows.findIndex((r) => String(r.id) === String(selfId))    : -1,
    selfColIndex: selfColId != null ? finalCols.findIndex((c) => String(c.id) === String(selfColId)) : -1,
  };
}

// Look up display names for a top-K axis. Returns Map<idStr, label>.
// Delegates to the shared label loader so endpoint/cluster lookups
// hit the same cache as the sidebar + scope-step renderer. Without
// this, the contrast/swimlane axes used to issue their own
// ENTITY_TYPE_ENDPOINT query alongside the sidebar's, fetching the
// same hostnames twice with different time ranges.
//
// `apiRange` is no longer threaded into the query: labels are
// time-invariant by domain (an endpoint's hostname doesn't change
// with the selected date range), and the shared cache key intentionally
// drops range so a wider-window fetch satisfies a narrower one.
const AXIS_SINGULAR_TO_PLURAL = {
  endpoint: "endpoints",
  cluster:  "clusters",
  agent:    "agents",
  session:  "sessions",
  file:     "files",
  tool_call: "tools",
};
// eslint-disable-next-line no-unused-vars
async function fetchAxisNames(dimS, ids, _apiRange) {
  if (!ids?.length) return new Map();
  const plural = AXIS_SINGULAR_TO_PLURAL[dimS];
  if (!plural) return new Map();
  return loadLabels(plural, ids);
}

function contrastEmpty(rowDimS, colDimS) {
  const dimToPlural = { endpoint: "endpoints", agent: "agents", session: "sessions", cluster: "clusters" };
  return {
    rows: [], cols: [], matrix: [],
    liftMatrix: [], expectedMatrix: [], surpriseMatrix: [],
    maxCell: 0, maxSurprise: 0, maxExpected: 0, minExpected: 0,
    rowDim: dimToPlural[rowDimS] ?? rowDimS,
    colDim: dimToPlural[colDimS] ?? colDimS,
    selfRowIndex: -1, selfColIndex: -1,
  };
}

// ── Fuzzy-join helpers ──────────────────────────────────────────

function inPred(stepAlias, field, values) {
  return { pred: { field: { stepAlias, field }, op: "OPERATOR_IN", value: values } };
}

// Group anchor rows by a string-valued dim, mapping value → array of
// per-anchor event_time seconds. Used by the FE's per-(dim, value)
// time-decay scoring.
function mapAnchorsByValue(anchorRows, dimKey) {
  const out = new Map();
  for (const r of anchorRows) {
    const k = r[dimKey] == null ? null : String(r[dimKey]);
    if (!k) continue;
    const tSec = candidateEventTimeSec(r);
    if (!Number.isFinite(tSec)) continue;
    let arr = out.get(k);
    if (!arr) { arr = []; out.set(k, arr); }
    arr.push(tSec);
  }
  return out;
}

function candidateEventTimeSec(r) {
  if (typeof r.event_time === "number") return r.event_time / 1000;
  return parseServerTimestamp(r.event_time) / 1000;
}

function minAbsDelta(anchorTimesSec, tSec) {
  let min = Infinity;
  for (const a of anchorTimesSec) {
    const d = Math.abs(tSec - a);
    if (d < min) min = d;
  }
  return min;
}

const detailRangeAround = (value) => {
        const eventMs = parseServerTimestamp(value);
        if (!Number.isFinite(eventMs) || eventMs <= 0) return null;
        const radiusMs = 5 * 60 * 1000;
        return {
          absolute: {
            start: new Date(eventMs - radiusMs).toISOString(),
            end: new Date(eventMs + radiusMs).toISOString(),
          },
        };
      };



function commandBasename(p) {
  if (!p) return "";
  const norm = String(p).replace(/\\/g, "/");
  const i = norm.lastIndexOf("/");
  return i >= 0 ? norm.slice(i + 1) : norm;
}

// Typed activity rows carry the fields needed by the execution-log display.
function shapeCommandRow(r) {
  const ts = typeof r.ts === "number" ? r.ts : parseServerTimestamp(r.ts);
  const data = r.data ?? {};
  const eventType = String(r.eventType || "");
  let command;
  let tool;
  switch (eventType) {
    case "ProcessStart":
      command = data.command_line || data.image_name || "ProcessStart";
      tool = commandBasename(data.image_name) || "process";
      break;
    case "FileState":
      command = `File: ${data.filepath || "(unknown)"}`;
      tool = "file";
      break;
    case "FileDelete":
      command = `Delete: ${data.filepath || "(unknown)"}`;
      tool = "file";
      break;
    case "FileRename":
      command = `Rename: ${data.original_filepath || "?"} → ${data.new_filepath || "?"}`;
      tool = "file";
      break;
    case "DnsQuery":
      command = `DNS: ${data.query_name || "(unknown)"}`;
      tool = "dns";
      break;
    case "NetworkConnection":
      command = `Net: ${data.remote_address || "?"}:${data.remote_port || "?"}`;
      tool = "net";
      break;
    default:
      command = eventType;
      tool = eventType.toLowerCase();
  }
  const endpointId = String(r.endpointId || "");
  const sessionId  = String(r.sessionId  || "");
  return {
    id: String(r.id || ""),
    timestamp: ts,
    command,
    tool,
    exitCode: 0,
    durationMs: 0,
    endpointId,
    endpointName: endpointId ? endpointId.slice(0, 8) : "",
    sessionId,
    sessionTitle: sessionId ? sessionId.slice(0, 8) : "",
    model: "",
    stdout: "",
    eventType,
  };
}

// Analytics spec for the session's ORIGINATING agent process (claude/cursor/
// codex/… + its launch command line) — the trace-sidebar tooltip. Roots on the
// ENTITY_TYPE_AI_AGENT_EXECUTION graph entity (endpoint_notifications pinned to
// notification_type='AIAgentExecution') and filters by notification_id — the
// dashboard's session id — directly. Same runAnalyticsQuery path getTrace uses
// for sessionClusters/prevTrace/toolExec, so no bespoke transport, no id bridge,
// no endpoint scan / time-window. A session can have multiple launch rows
// (resumes); take the earliest (ORDER BY timestamp ASC). shapeSessionOrigin
// parses the `details` JSON blob client-side.
export function sessionOriginSpec(sessionId, timeRange = { relative: { durationSeconds: String(90 * 86400) } }) {
  if (!sessionId) return null;
  return {
    root: { alias: "n", entity: "ENTITY_TYPE_AI_AGENT_EXECUTION" },
    timeRange,
    filter: { pred: {
      field: { stepAlias: "n", field: "DIMENSION_FIELD_AI_AGENT_EXECUTION_NOTIFICATION_ID" },
      op: "OPERATOR_EQ", value: sessionId,
    }},
    output: { dimensions: [
      { field: { stepAlias: "n", field: "DIMENSION_FIELD_AI_AGENT_EXECUTION_IMAGE_NAME" }, outputAlias: "image_name" },
      { field: { stepAlias: "n", field: "DIMENSION_FIELD_AI_AGENT_EXECUTION_PROVIDER" },   outputAlias: "provider" },
      { field: { stepAlias: "n", field: "DIMENSION_FIELD_AI_AGENT_EXECUTION_COMMAND_LINE" }, outputAlias: "command_line" },
      { field: { stepAlias: "n", field: "DIMENSION_FIELD_AI_AGENT_EXECUTION_ACCOUNT_NAME" }, outputAlias: "account_name" },
      { field: { stepAlias: "n", field: "DIMENSION_FIELD_AI_AGENT_EXECUTION_DOMAIN_NAME" }, outputAlias: "domain_name" },
      { field: { stepAlias: "n", field: "DIMENSION_FIELD_AI_AGENT_EXECUTION_TIMESTAMP" },  outputAlias: "ts" },
    ]},
    orderBy: [{ outputAlias: "ts", direction: "DIRECTION_ASC" }],
    limit: 1,
  };
}

// Visible rows of a batching dim (today: mcp_server) each ask for a sparkline as
// they scroll in. The requests that arrive in the same tick share one bucketed
// read with a filtered count per entry. Each count's predicate is the one the
// per-entry read adds as a scope step, so every series is unchanged; the OR of
// them only narrows the rows scanned.
const SPARKLINE_BATCH_MAX = 126; // plus the bucket column: the output-column cap
const pendingSparklineBatches = new Map();

function batchedEntrySparkline(req) {
  const key = JSON.stringify([req.dimKey, req.parentScopePath, req.range, req.bucketCount, req.query || ""]);
  let batch = pendingSparklineBatches.get(key);
  if (!batch) {
    batch = { req, waiters: new Map() };
    pendingSparklineBatches.set(key, batch);
    setTimeout(() => {
      pendingSparklineBatches.delete(key);
      flushSparklineBatch(batch);
    }, 0);
  }
  return new Promise((resolve) => {
    const id = String(req.id);
    batch.waiters.set(id, [...(batch.waiters.get(id) ?? []), resolve]);
  });
}

async function flushSparklineBatch({ req, waiters }) {
  const { adapter, dimKey, parentScopePath, range, bucketCount, query } = req;
  const seriesById = new Map();
  try {
    const apiScope = toApiScope(parentScopePath, range);
    const apiRange = toApiRange(range);
    const { fromMs, toMs } = rangeBoundsMs(apiRange);
    const bucketSeconds = bucketSecondsForRange(apiRange, bucketCount);
    // The steer, as the entries the rows came from honour it; one this PROMPT
    // root can't carry draws no line, as those entries are none.
    const { compiled: steer, active: steerActive, carryable } = await compileSteer(query);
    const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, steer });
    // Fail closed on a scope this PROMPT root can't carry, as the per-entry read does.
    if (!unsupported.length && !(steerActive && !carryable)) {
      const scopeFilter = combineAnd(filter, signalScopeFilter(parentScopePath));
      const ids = [...waiters.keys()];
      const chunks = [];
      for (let start = 0; start < ids.length; start += SPARKLINE_BATCH_MAX) {
        chunks.push(ids.slice(start, start + SPARKLINE_BATCH_MAX));
      }
      await Promise.all(chunks.map(async (chunk) => {
        const spec = {
          root,
          timeRange: rangeToTimeRange(apiRange),
          output: {
            dimensions: [
              { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds },
            ],
            metrics: chunk.map((id, index) => promptCount(`entry_${index}`, "p", adapter.predicate([id], "p"))),
          },
          filter: combineAnd(scopeFilter, adapter.predicate(chunk, "p")),
        };
        try {
          const rows = rowsToObjects(await runAnalyticsQuery(spec));
          const tagged = rows.flatMap((row) => chunk.map((id, index) => ({ id, bucket: row.bucket, value: row[`entry_${index}`] })));
          const densified = densifyBuckets(tagged, { fromMs, toMs, bucketCount, bucketSeconds });
          for (const id of chunk) seriesById.set(id, densified.get(id) ?? new Array(bucketCount).fill(0));
        } catch (err) {
          console.warn(`[getDimEntrySparkline] batched read failed for ${chunk.length} ${dimKey} entries:`, err);
        }
      }));
    }
  } catch (err) {
    console.warn(`[getDimEntrySparkline] batched read failed for ${dimKey}:`, err);
  }
  for (const [id, resolvers] of waiters) {
    const series = seriesById.get(id) ?? [];
    for (const resolve of resolvers) resolve({ series });
  }
}

function combineAnd(a, b) {
  if (!a) return b ?? null;
  if (!b) return a;
  const flatten = (x) => (x.andExpr ? x.andExpr.children : [x]);
  return { andExpr: { children: [...flatten(a), ...flatten(b)] } };
}

function chunkedInPredicate(field, values, { chunkSize = 1000, cast = String } = {}) {
  const normalized = [...new Set((values || []).map(cast).filter((v) => v !== "" && v != null))];
  if (normalized.length === 0) return null;
  if (normalized.length <= chunkSize) {
    return { pred: { field, op: "OPERATOR_IN", value: normalized } };
  }
  const children = [];
  for (let i = 0; i < normalized.length; i += chunkSize) {
    children.push({
      pred: { field, op: "OPERATOR_IN", value: normalized.slice(i, i + chunkSize) },
    });
  }
  return { orExpr: { children } };
}


// Empty activity lens for a scope the analytics API cannot express.
function emptyActivityLens() {
  return { layers: [], total: [] };
}

function overviewActivityFromRows(rows, {
  valueAlias = "value",
  idAlias = "agent",
  bucketCount,
  bucketSeconds,
  fromMs,
  toMs,
  topN,
} = {}) {
  // valueAlias accepts an array so a single row can carry a derived
  // metric (e.g. tokens = input_tokens + output_tokens) without an
  // extra query pass.
  const aliases = Array.isArray(valueAlias) ? valueAlias : [valueAlias];
  const byKey = new Map();
  for (const r of rows) {
    const raw = r[idAlias];
    // Skip rows where the group key is null/empty — happens on the
    // model lens for token observations with no model resolved yet
    // (parser failed / late-arriving usage). Counting them under ""
    // would produce a useless empty-string layer.
    if (raw == null || raw === "") continue;
    const id = String(raw);
    let entry = byKey.get(id);
    if (!entry) {
      entry = { id, total: 0, points: [] };
      byKey.set(id, entry);
    }
    let v = 0;
    for (const a of aliases) v += Number(r[a]) || 0;
    entry.total += v;
    entry.points.push({ id, bucket: r.bucket, value: v });
  }
  const ranked = [...byKey.values()].sort((a, b) => b.total - a.total).slice(0, topN);
  const dense = densifyBuckets(
    ranked.flatMap((e) => e.points),
    { fromMs, toMs, bucketCount, bucketSeconds },
  );
  // The agent lens groups by PROMPT_PROVIDER (canonical lower-kebab
  // tags like `anthropic`, `openai`); run them through
  // deriveAgentIdentity so the legend chip reads "Claude" / "ChatGPT"
  // instead of the raw provider tag. Non-agent lenses (e.g. tokens
  // by model) pass the id through unchanged.
  const labelForAgentId = idAlias === "agent"
    ? (id) => deriveAgentIdentity(id).name || String(id)
    : (id) => String(id);
  const layers = ranked.map((e, i) => ({
    id:     e.id,
    label:  labelForAgentId(e.id),
    color:  paletteFor(e.id, i),
    series: dense.get(e.id) ?? new Array(bucketCount).fill(0),
  }));
  const length = layers[0]?.series.length ?? 0;
  const total = new Array(length).fill(0);
  for (const l of layers) {
    for (let i = 0; i < Math.min(length, l.series.length); i++) total[i] += l.series[i];
  }
  return { layers, total };
}

// Per-model token-type breakdown for the Token-volume legend hover. The chart
// total stays by-model and unchanged; this hangs a "where did THIS model's
// tokens go" decomposition off each legend chip — one mini-series per token
// bucket (cache read/write · input · output) over the same window, plus the
// reasoning + coverage totals. reasoning rides along as a figure (a subset of
// output, never a band/total) and coverage is the measured-vs-total signal.
// Keyed by model so it attaches to the matching by-model layer (layer.id).
const TOKEN_TYPE_BANDS = [
  { id: "cache_read",  label: "Cache read",  alias: "cache_read_tokens",     color: layerPalette.network },
  { id: "cache_write", label: "Cache write", alias: "cache_creation_tokens", color: layerPalette.command },
  { id: "input",       label: "Input",       alias: "input_tokens",          color: layerPalette.files },
  { id: "output",      label: "Output",      alias: "output_tokens",         color: layerPalette.inference },
];
function modelTypeBreakdown(rows, { bucketCount, bucketSeconds, fromMs, toMs } = {}) {
  const pointsByModel = new Map();
  const aggByModel = new Map();
  for (const r of rows || []) {
    // String-key to match overviewActivityFromRows' layer.id (= String(raw)),
    // so the perModel.get(layer.id) attach can't miss on a non-string model id.
    const model = r.model == null ? "" : String(r.model);
    if (model === "") continue;
    let pts = pointsByModel.get(model);
    if (!pts) { pts = []; pointsByModel.set(model, pts); }
    for (const band of TOKEN_TYPE_BANDS) {
      const v = Number(r[band.alias]) || 0;
      if (v) pts.push({ id: band.id, bucket: r.bucket, value: v });
    }
    let agg = aggByModel.get(model);
    if (!agg) { agg = { reasoning: 0, callsTotal: 0, callsWithTokens: 0 }; aggByModel.set(model, agg); }
    agg.reasoning += Number(r.reasoning_tokens) || 0;
    agg.callsTotal += Number(r.calls_total) || 0;
    agg.callsWithTokens += Number(r.calls_with_tokens) || 0;
  }
  const out = new Map();
  for (const [model, pts] of pointsByModel) {
    const dense = densifyBuckets(pts, { fromMs, toMs, bucketCount, bucketSeconds });
    const bands = TOKEN_TYPE_BANDS.map((band) => {
      const series = dense.get(band.id) ?? new Array(bucketCount).fill(0);
      const total = series.reduce((a, b) => a + b, 0);
      return { id: band.id, label: band.label, color: band.color, series, total };
    });
    out.set(model, { bands, ...(aggByModel.get(model) || { reasoning: 0, callsTotal: 0, callsWithTokens: 0 }) });
  }
  return out;
}

const LAYER_RANK_PALETTE = [
  layerPalette.inference, // top — brand green
  layerPalette.code,      // purple/blue
  layerPalette.network,   // cyan
  layerPalette.files,     // amber
  layerPalette.command,   // orange
  layerPalette.memory,    // violet
];

function paletteFor(_label, idx) {
  return LAYER_RANK_PALETTE[idx % LAYER_RANK_PALETTE.length];
}

export const COMMAND_EVENT_TYPES = [
  "ProcessStart",
  "FileState",
  "FileDelete",
  "FileRename",
  "DnsQuery",
  "NetworkConnection",
];

// Scope path → server-canonical form. Each step carries `ids` (the
// canonical multi-select shape) plus a back-compat `id` field so server
// code that hasn't been taught about ids[] still resolves the primary
// value. Hierarchical dims (project / file / host / skill) collapse to
// a single id by definition; multi-select is for simple dims only.
function toApiScope(scopePath = [], range = null) {
  const out = [];
  for (const step of scopePath || []) {
    const ids = Array.isArray(step.ids) && step.ids.length
      ? step.ids
      : (step.id != null ? [step.id] : []);

    // Adapter lookup is plural-aware: adapters declare their own
    // `plural` so callers don't need a DIM_SINGULAR entry for every
    // dim. Try plural first (sidebar emits plural keys), then
    // singular via DIM_SINGULAR, then the bare key.
    const adapterByPlural = getDimAdapterByPlural(step.dim);
    const dim = adapterByPlural?.id ?? (DIM_SINGULAR[step.dim] ?? step.dim);
    const adapter = adapterByPlural ?? getDimAdapter(dim);
    const apiStep = { dim, id: ids[0], ids };

    // Adapter-backed dims can rewrite themselves into one or more
    // standard scope steps before dispatch sees them. Used by
    // derived-attribution dims (Linear ticket → endpoint + time
    // window, project → endpoint set, etc.). The original step is
    // replaced by the rewrite output; if the adapter returns []
    // the step is dropped entirely (typical for "not yet hydrated").
    if (adapter && typeof adapter.rewriteScopeStep === "function") {
      const rewritten = adapter.rewriteScopeStep(apiStep, { range });
      if (Array.isArray(rewritten)) {
        for (const r of rewritten) out.push(r);
        continue;
      }
      // null/undefined → fall through to pass-through behavior.
    }
    out.push(apiStep);
  }
  return out;
}


// A token column with no observation comes back null/undefined (the backend
// preserves NULLs). Keep absence as null — never coerce to 0 — so the UI can
// distinguish "unmeasured" from "measured zero" (design: null = unmeasured).
function tokenOrNull(v) {
  return v == null ? null : Number(v);
}

// Fine capture sources that token_observation's MV collapses into the coarse
// 'proxy' attribution bucket (agent-host, turn-grain, prompt-linked). Used to
// suppress the redundant coarse 'proxy' token source beside one of these.
const AGENT_HOST_CAPTURE_SOURCES = new Set(["proxy", "hooks", "transcript", "local_tap"]);

// Merge a prompt's fine capture source with the capture sources seen on its
// token-usage rows into the set of distinct capture mechanisms.
//
// token_observation.source is a COARSE attribution bucket, not the capture
// mechanism: its MV collapses every agent-host capture (proxy, hooks,
// transcript) to 'proxy'. So a token source of 'proxy' beside a fine agent-host
// prompt source (e.g. 'transcript') is the coarse echo of the same capture, not
// a second mechanism — including it renders a spurious 'proxy' pill. Drop it in
// exactly that case; a genuinely different rail (e.g. 'otlp' tokens) still
// shows through.
export function mergeCaptureSources(promptCaptureSource, tokenCaptureSources = []) {
  const tokenSources = promptCaptureSource && AGENT_HOST_CAPTURE_SOURCES.has(promptCaptureSource)
    ? tokenCaptureSources.filter((s) => s !== "proxy")
    : tokenCaptureSources;
  return [...new Set([promptCaptureSource, ...tokenSources].filter(Boolean))].sort();
}

// Mirrors `CaptureSource` in shared-protos. Nothing here has a compiler
// backstop — unlike endpoint-server's exhaustive match, an unmapped variant
// falls through to "" (numeric) or the raw string, and the trace renders with a
// blank or unrecognised capture source. Add a variant here, in
// `captureSourceLabel` (ContextPanel.jsx), and to the test below in the same
// change.
export function promptCaptureSourceFromEventData(evd) {
  const given = evd?.capture_source ?? evd?.captureSource;
  // Newer sources (11 and up) reach the events table as their enum number in
  // text, for example "12" for the browser extension.
  const raw = typeof given === "string" && /^\s*\d+\s*$/.test(given) ? Number(given) : given;
  if (raw == null || raw === "" || raw === 0) return "";
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (s === "proxy" || s === "capture_source_proxy") return "proxy";
    if (s === "otlp" || s === "otlp_logs" || s === "capture_source_otlp_logs") return "otlp";
    if (s === "office" || s === "office_traces" || s === "capture_source_office_traces") return "office";
    if (s === "compliance" || s === "compliance_sync" || s === "capture_source_compliance_sync") return "compliance_sync";
    if (s === "hooks" || s === "capture_source_hooks") return "hooks";
    if (s === "transcript" || s === "capture_source_transcript") return "transcript";
    if (s === "copilot_sync" || s === "capture_source_copilot_sync") return "copilot_sync";
    if (s === "openai_sync" || s === "capture_source_openai_sync") return "openai_sync";
    if (s === "dashboard_chat" || s === "capture_source_dashboard_chat") return "dashboard_chat";
    if (s === "local_tap" || s === "capture_source_local_tap") return "local_tap";
    if (s === "perplexity_audit_log" || s === "capture_source_perplexity_audit_log") return "perplexity_audit_log";
    if (s === "browser_extension" || s === "capture_source_browser_extension") return "browser_extension";
    if (s === "langsmith_sync" || s === "capture_source_langsmith_sync") return "langsmith_sync";
    if (s === "otlp_traces" || s === "capture_source_otlp_traces") return "otlp_traces";
    if (s === "vertex_sync" || s === "capture_source_vertex_sync") return "vertex_sync";
    return s;
  }
  switch (Number(raw)) {
    case 1: return "proxy";
    case 2: return "otlp";
    case 3: return "office";
    case 4: return "compliance_sync";
    case 5: return "hooks";
    case 6: return "transcript";
    case 7: return "copilot_sync";
    case 8: return "openai_sync";
    case 9: return "dashboard_chat";
    case 10: return "local_tap";
    case 11: return "perplexity_audit_log";
    case 12: return "browser_extension";
    case 13: return "langsmith_sync";
    case 14: return "otlp_traces";
    case 15: return "vertex_sync";
    default: return "";
  }
}

function sourceObservationBoolean(value) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return null;
}

export async function fetchTraceSourceObservations(sourceEventId, timeRange) {
  const sourceId = String(sourceEventId || "").trim();
  if (!sourceId) return [];
  const field = (name) => ({ stepAlias: "so", field: name });
  const sourceEventField = field("DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_SOURCE_EVENT_ID");
  const traceField = field("DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_TRACE_ID");
  const [anchor] = rowsToObjects(await runAnalyticsQuery({
    root: { alias: "so", entity: "ENTITY_TYPE_CONVERSATION_SOURCE_OBSERVATION" },
    timeRange,
    filter: { pred: { field: sourceEventField, op: "OPERATOR_EQ", value: sourceId } },
    output: { dimensions: [{ field: traceField, outputAlias: "trace_id" }] },
    limit: 1,
  }));
  const traceId = String(anchor?.trace_id || "").trim();
  if (!traceId) return [];

  const rows = rowsToObjects(await runAnalyticsQuery({
    root: { alias: "so", entity: "ENTITY_TYPE_CONVERSATION_SOURCE_OBSERVATION" },
    timeRange,
    filter: { pred: { field: traceField, op: "OPERATOR_EQ", value: traceId } },
    output: {
      dimensions: [
        { field: field("DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_SOURCE_CAPTURE"), outputAlias: "source_capture" },
        { field: field("DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_ENDPOINT_ID"), outputAlias: "endpoint_id" },
        { field: field("DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_BROWSER_FAMILY"), outputAlias: "browser_family" },
        { field: field("DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_BROWSER_EXTENSION_VERSION"), outputAlias: "browser_extension_version" },
        { field: field("DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_BROWSER_PRIVATE_BROWSING"), outputAlias: "browser_private_browsing" },
        { field: field("DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_BROWSER_OBSERVATION_KIND"), outputAlias: "browser_observation_kind" },
      ],
      metrics: [{
        outputAlias: "observation_count",
        computed: { field: sourceEventField, op: "AGGREGATE_OP_COUNT_DISTINCT" },
      }],
    },
    limit: 100,
  }));
  return rows.map((row) => ({
    traceId,
    captureSource: String(row.source_capture || "").trim(),
    endpointId: String(row.endpoint_id || "").trim(),
    browserFamily: String(row.browser_family || "").trim(),
    extensionVersion: String(row.browser_extension_version || "")
      .replace(/[\x00-\x1f\x7f]/g, "")
      .slice(0, 64),
    privateBrowsing: sourceObservationBoolean(row.browser_private_browsing),
    observationKind: String(row.browser_observation_kind || "").trim(),
    observationCount: Math.max(0, Math.floor(Number(row.observation_count) || 0)),
  }));
}

// The client surfaces on one prompt's usage rows. PROMPT_HAS_TOKENS is
// planner-collapsed, so the predicate lives in the PROMPT step filter and the
// output comes from TOKEN.
export function promptClientSurfaceSpecFor(requestId, timeRange) {
  return {
    root: {
      alias: "p", entity: "ENTITY_TYPE_PROMPT",
      stepFilter: [{
        pred: {
          field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
          op: "OPERATOR_EQ", value: requestId,
        },
      }],
      children: [{
        edge: "EDGE_TYPE_PROMPT_HAS_TOKENS",
        mode: "JOIN_MODE_INNER",
        target: { alias: "tk", entity: "ENTITY_TYPE_TOKEN" },
      }],
    },
    timeRange,
    output: {
      dimensions: [
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_CLIENT_SURFACE" }, outputAlias: "client_surface" },
      ],
      metrics: [{
        outputAlias: "rows",
        computed: { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_UNIT_ID" }, op: "AGGREGATE_OP_COUNT" },
      }],
    },
    orderBy: [{ outputAlias: "rows", direction: "DIRECTION_DESC" }],
    limit: 5,
  };
}

// The most-observed non-empty client surface, or "" when none was stamped.
export function firstClientSurface(rows) {
  for (const row of rows || []) {
    const surface = String(row?.client_surface || "").trim();
    if (surface) return surface;
  }
  return "";
}

// The surface stored on a conversation trace: a browser capture's parser
// reports the app it ran in (for example "gemini-docs"), which skwach keeps
// on the trace. A browser event's session is its conversation's trace.
export function traceClientSurfaceSpecFor(traceId, timeRange) {
  const T = (name) => ({ stepAlias: "t", field: `DIMENSION_FIELD_CONVERSATION_TRACE_${name}` });
  return {
    root: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE" },
    timeRange,
    filter: { pred: { field: T("TRACE_ID"), op: "OPERATOR_EQ", value: traceId } },
    output: { dimensions: [{ field: T("CLIENT_SURFACE"), outputAlias: "client_surface" }] },
    limit: 5,
  };
}

// A trace's rows can mix a reported surface with the capture-mechanism
// fallback ("browser_extension") stored before one was reported.
export function reportedClientSurface(rows) {
  const surfaces = (rows || []).map((row) => String(row?.client_surface || "").trim());
  return surfaces.find((surface) => clientSurfaceIdentity(surface)) || "";
}

// Adapt typed conversation details and scalar metadata for the trace view.
export function adaptTrace(t, detail) {
  const ts = t.event_time ? parseServerTimestamp(t.event_time) : Date.now();
  const sessionShort = t.session_id ? t.session_id.slice(0, 8) : "";
  const endpointShort = t.endpoint_id ? t.endpoint_id.slice(0, 8) : "";
  const endpointName = (t.endpoint_name && t.endpoint_name.trim()) || endpointShort;
  const clusterIdStr = t.cluster_id != null && t.cluster_id > 0 ? String(t.cluster_id) : null;
  // skwach wrote a NON_PROMPT tombstone — the captured frame decoded to no
  // user prompt (Robin metadata/end-marker). Drives the "Non-prompt frame"
  // label so the row isn't shown as a never-resolving "Pending" (O-1312).
  const filtered = Boolean(t.filtered);
  // Infrastructure prompts (skwach message_type="SCAFFOLDING": tool loops,
  // harness XML, warmup, permission checks) get no embedding → never sent to
  // clustering → no cluster_id, so they'd read "Pending" forever. They never
  // WILL cluster, so mark them terminal. Distinct from `filtered` (NON_PROMPT =
  // no user prompt decoded at all); like it, scaffolding wins over the
  // cluster_id-derived state since there's nothing to cluster.
  const scaffolding = t.message_type === "SCAFFOLDING";
  // cluster_status / cluster_ancestors: only the trace-detail endpoint
  // populates these. Listing rows fall back to deriving from cluster_id.
  const clusterStatus = filtered
    ? "filtered"
    : scaffolding
    ? "scaffolding"
    : typeof t.cluster_status === "string" && t.cluster_status
    ? t.cluster_status
    : (t.cluster_id == null || t.cluster_id <= -2 ? "pending"
       : t.cluster_id === -1 ? "outlier"
       : t.cluster_id > 0 ? "clustered"
       : "pending");
  const clusterAncestors = Array.isArray(t.cluster_ancestors)
    ? t.cluster_ancestors.map((a) => ({ id: String(a.id), name: String(a.name || a.id) }))
    : [];
  const sessionClusters = Array.isArray(t.session_clusters)
    ? t.session_clusters.map((c) => ({
        id: String(c.id),
        name: String(c.name || c.id),
        traceCount: Number(c.traceCount) || 0,
      }))
    : [];
  const turns = detail?.turns ?? [];
  const tools = detail?.tools ?? [];
  // Full definitions (name + description + schema) of the tools the model was
  // OFFERED — distinct from the name-only `tools` list. Rendered under the
  // system prompt in the trace detail (the model's "setup").
  const toolDefs = detail?.toolDefs ?? [];
  // Office-agent turn tool executions (resolved via the turn-key join in
  // getTrace); empty for proxy/log-path traces and for list rows.
  const toolExecutions = detail?.toolExecutions ?? [];
  // The session's originating agent process (image_name + launch command_line +
  // user), resolved from the AI endpoint-stats RPC. null when not resolvable;
  // only the trace-detail path populates it (list rows leave it null).
  const sessionOrigin = detail?.sessionOrigin ?? null;
  const prevTraceInSession = detail?.prevTraceInSession ?? null;
  const settings = detail?.settings
    ?? { temperature: 0, maxOutputTokens: 0, stream: false, reasoningEffort: "" };
  // Raw, unparsed api_request JSON string — used by the trace-detail
  // "raw" view + Download JSON button so they show what was actually
  // sent, not a reconstruction from parsed turns.
  const apiRequestRaw = detail?.api_request_raw ?? "";
  // Display-only generation controls read straight off the raw request body
  // (see requestControls in parseApiRequest.js — kept out of the parity-pinned
  // `settings`). Empty strings for list rows (no raw body) collapse the rows.
  const requestEffort = detail?.requestEffort || "";
  const requestSpeed = detail?.requestSpeed || "";
  // True when this is a suffix-split row whose full-body reconstruction failed
  // or succeeded with known-missing context. Drives the trace-detail banner so
  // an analyst does not treat incomplete evidence as the whole conversation.
  const partialBody = Boolean(detail?.partialBody);
  const partialBodyReason = detail?.partialBodyReason || "";
  const sourceObservations = Array.isArray(detail?.sourceObservations) ? detail.sourceObservations : [];
  const captureSources = [...new Set([
    ...(Array.isArray(t.capture_sources) ? t.capture_sources : []),
    ...sourceObservations.map((observation) => observation.captureSource),
  ].map((s) => String(s || "").trim()).filter(Boolean))].sort();
  const moduleCapture = Boolean(t.module_capture);
  const moduleCaptureProjected = Boolean(t.module_capture_projected);
  const promptCaptureSource = String(t.prompt_capture_source || "").trim();
  // The producer-stamped product surface ("copilot-vscode"), detail-only; ""
  // when the capture path attributes none.
  const clientSurface = String(t.client_surface || "").trim();
  const rawCapture = Boolean(t.raw_capture);
  // Per-turn facets (files / hosts / skills) live on `turn.facets` for
  // each entry in `turns` and render inline next to that turn — see
  // aggregateTurnChips() in ContextPanel.jsx.
  return {
    id: t.trace_id,
    traceId: t.trace_id,
    timestamp: ts,
    eventTime: t.event_time || "",
    // Empty when the prompt hasn't been enriched yet (or never will).
    // TraceRow decides whether to render a "Summarising…" pending
    // badge or a muted placeholder based on the trace's age.
    summary: t.summary || "",
    operation: "ai.prompt",
    model: modelLabel(t.model, t.provider),
    provider: t.provider || "unknown",
    providerApi: "messages.create",
    // Absence is preserved as null (design: "null = unmeasured, never zero").
    // A NULL/absent token column stays null so the UI can show "—" instead of
    // a misleading "0"; a present value maps through as its number. totalTokens
    // is null only when BOTH sides are absent — when one side is present it
    // counts the absent side as 0 (so "100 in / —" totals 100).
    inputTokens: tokenOrNull(t.input_tokens),
    outputTokens: tokenOrNull(t.output_tokens),
    // Rest of the token breakdown (detail panel only; list rows leave these
    // null). Absence stays null so the chip can tell "unmeasured" from a
    // measured 0 — cache is null/0 off Anthropic, reasoning null/0 off
    // OpenAI/DeepSeek, and the chip gates display on provider accordingly.
    cacheReadTokens: tokenOrNull(t.cache_read_tokens),
    cacheCreationTokens: tokenOrNull(t.cache_creation_tokens),
    inclusiveCacheReadTokens: tokenOrNull(t.inclusive_cache_read_tokens),
    reasoningTokens: tokenOrNull(t.reasoning_tokens),
    callsTotal: tokenOrNull(t.calls_total),
    callsWithTokens: tokenOrNull(t.calls_with_tokens),
    // totalTokens stays WORK (input + output) — cache/reasoning are never added
    // in (reasoning is already inside output; cache is a separate axis).
    totalTokens: (t.input_tokens == null && t.output_tokens == null)
      ? null
      : (Number(t.input_tokens) || 0) + (Number(t.output_tokens) || 0),
    // Which grain answered the per-prompt token query (O-2021): "turn" (this
    // prompt's own, summable) | "submission" | "session" | "user_day" (shared
    // rollups). Drives the token MetaRow's label + tooltip. Empty for list rows
    // and any path that doesn't carry a grain.
    tokenGrain: t.token_grain || "",
    // Session-wide token spend rows (per provider x model x run), detail-only:
    // the Lanes strip's session-level cost basis. null for list rows and when
    // the session query answered nothing — absence, not a measured zero.
    sessionTokens: Array.isArray(t.session_token_rows) && t.session_token_rows.length
      ? t.session_token_rows
      : null,
    endpointId: t.endpoint_id,
    endpointName: endpointName,
    sessionId: t.session_id,
    // The agentic-turn key (empty for list rows and older captures). Every
    // API call in the turn shares it; the Lanes thread panel joins on it.
    // The zero UUID is the pre-feature / non-Claude sentinel, not a key —
    // joining on it would list every sentinel row as a "sibling".
    submissionId: t.submission_id && t.submission_id !== "00000000-0000-0000-0000-000000000000"
      ? t.submission_id
      : "",
    sessionTitle: sessionShort || "trace",
    sessionStatus: "Live",
    clusterIds: clusterIdStr ? [clusterIdStr] : [],
    primaryClusterId: clusterIdStr,
    primaryClusterName: t.cluster_name || "",
    clusterStatus,
    filtered,
    clusterAncestors,
    sessionClusters,
    excerpt: t.summary || "",
    settings,
    tools,
    toolDefs,
    toolExecutions,
    sessionOrigin,
    spans: [],
    turns,
    prevTraceInSession,
    apiRequestRaw,
    // Trace-detail display: effort control + fast-mode flag from the request
    // body. Distinct from the parity-pinned settings.reasoningEffort.
    conversationThreadId: detail?.conversationThreadId || null,
    requestEffort,
    requestSpeed,
    partialBody,
    partialBodyReason,
    // The session's earlier messages relative to this capture: pending, loaded
    // (with how many, of how many), or failed. See conversationRead.js.
    record: detail?.record ?? null,
    captureSources,
    captureSource: captureSources.join(", "),
    sourceObservations,
    promptCaptureSource,
    clientSurface,
    rawCapture,
    answerStatus: t.answer_status || "",
    unansweredPrompts: Array.isArray(t.unanswered_prompts)
      ? t.unanswered_prompts
      : [unansweredPrompt({ eventId: t.trace_id ? String(t.trace_id) : "", eventTime: ts, status: t.answer_status })].filter(Boolean),
    moduleCapture,
    moduleCaptureProjected,
  };
}

// Resolve one adjacent-column flow pair to { links: [{aId,bId,count}] }.
// Analytics-supported axes run the in-engine query. Unsupported pairs
// fail closed so one unavailable pair does not reject the whole set.
// A single failing pair degrades to no links rather than rejecting the
// whole set — otherwise one bad pair leaves every column stuck on
// "LOADING LINKS". Shared by getFlowLinks (viewport-driven) and
// buildColumnsWithFlowLinks (initial-window seed).
async function resolveFlowPair(pair, scopePath, range, apiRange, scoped = false) {
  const aDim = DIM_SINGULAR[pair.aDim] ?? pair.aDim;
  const bDim = DIM_SINGULAR[pair.bDim] ?? pair.bDim;
  const aIds = pair.aIds || [];
  const bIds = pair.bIds || [];
  if (!aIds.length || !bIds.length) return { links: [] };
  if (!isAnalyticsSupportedDim(aDim) || !isAnalyticsSupportedDim(bDim)) {
    return { links: [] };
  }
  // Fold the base scope into the analytics co-occurrence ONLY when the caller
  // opts in. The Anatomy flow hooks pass scoped:true so a pair is counted within
  // the current scope. The scope-columns finder does NOT — its columns list the
  // UNSCOPED entry set, so scoping the co-occurrence would collapse the mesh to
  // just the active scope's row (e.g. one endpoint). Both real-field and
  // synthetic-axis queries preserve the chosen base scope.
  const baseScope = scoped ? toApiScope(scopePath, range) : [];
  return runAnalyticsFlowPair(aDim, aIds, bDim, bIds, apiRange, baseScope).catch((err) => {
    console.warn("[resolveFlowPair] pair failed; no links for it:", err);
    return { links: [] };
  });
}

// Seed-window sizing. The caller (ScopeColumns) estimates how many rows to
// seed from its viewport height + buffer and passes it in — no upper cap, so a
// tall finder (or a long column) seeds everything it shows + buffers, not a
// flat 128. The estimate is viewport-bounded by construction. FLOW_SEED_MIN_ROWS
// is just a floor for a degenerate/unmeasured canvas; FLOW_SEED_DEFAULT_ROWS is
// the fallback for callers that don't estimate.
const FLOW_SEED_DEFAULT_ROWS = 128;
const FLOW_SEED_MIN_ROWS = 8;

// Initial-window flow links for the ScopeColumns finder, resolved in the
// same round-trip as the columns. buildColumnsWithFlowLinks already holds
// every entry id, so it can link the adjacent entries columns for their
// top `seedRows` rows (the scroll=0 window) right here — sparing the client
// a second getFlowLinks trip + render cycle + the "LOADING LINKS" overlay it
// used to flash once columns landed (O-1888 follow-up). `seedRows` is the
// caller's viewport-derived estimate (visible rows + buffer), so a short
// finder seeds only what it shows rather than a flat 128. Mirrors
// computeEntriesWindows(scroll=0) + buildAdjacentPairs + reshapeFlowLinks in
// ScopeColumns.jsx; keep the three in sync. Returns the client's flow-link
// shape ({colA,rowA,colB,rowB,count}) with absolute row indices.
async function seedFlowLinksForCols(cols, scopePath, range, apiRange, seedRows = FLOW_SEED_DEFAULT_ROWS) {
  const windows = cols.map((col, ci) => {
    if (col.type !== "entries") return null;
    const items = col.entries || [];
    if (!items.length) return null;
    return { ci, dimKey: col.dimKey, ids: items.slice(0, seedRows).map((e) => e.id) };
  });
  // Adjacent (a,b) entries pairs — first entries-col neighbour only, the
  // same `break`-after-first-match the client and worker use.
  const pairs = [];
  for (let a = 0; a < cols.length; a++) {
    const wA = windows[a];
    if (!wA || !wA.ids.length) continue;
    for (let b = a + 1; b < cols.length; b++) {
      const wB = windows[b];
      if (!wB || !wB.ids.length) continue;
      pairs.push({ colA: a, colB: b, aDim: wA.dimKey, aIds: wA.ids, bDim: wB.dimKey, bIds: wB.ids });
      break;
    }
  }
  if (!pairs.length) return [];
  const results = await Promise.all(
    pairs.map((p) => resolveFlowPair(p, scopePath, range, apiRange)),
  );
  // Reshape (aId,bId) → absolute (rowA,rowB) for drawFlowLinks.
  const out = [];
  for (let i = 0; i < pairs.length; i++) {
    const lp = pairs[i];
    const aEntries = cols[lp.colA]?.entries || [];
    const bEntries = cols[lp.colB]?.entries || [];
    for (const link of results[i]?.links || []) {
      const rowA = aEntries.findIndex((e) => String(e.id) === String(link.aId));
      const rowB = bEntries.findIndex((e) => String(e.id) === String(link.bId));
      if (rowA >= 0 && rowB >= 0) {
        out.push({ colA: lp.colA, rowA, colB: lp.colB, rowB, count: link.count });
      }
    }
  }
  return out;
}

// ── Per-method routing table ─────────────────────────────────────
//
// Add entries here as more endpoints get wired. The dataClient routes any
// method present in this table to HTTP; everything else falls through to
// the worker.

// ── Inventory Surface: server-side fleet-composition distributions ──
//
// Both Inventory Surface charts ("AI agents" + "Operating systems") computed in
// ClickHouse instead of paging every endpoint_metadata_current row into the
// browser — so they scale to six-figure fleets, where the prior client-side
// rollup capped at a few thousand rows. Installed agents come from the
// AGENT_INVENTORY entity (the pre-exploded endpoint_agent_inventory table,
// joined from ENDPOINT on endpoint_id) whose agent_name carries the actual
// product ("Claude Code", "Ollama"). Results are raw groups and the endpoint
// total; segment/color shaping stays client-side in
// lib/inventoryDistribution.js.
const F_EP_ID = "DIMENSION_FIELD_ENDPOINT_ENDPOINT_ID";
const F_EP_IS_SYNTHETIC = "DIMENSION_FIELD_ENDPOINT_IS_SYNTHETIC";
const F_INV_AGENT_NAME = "DIMENSION_FIELD_AGENT_INVENTORY_AGENT_NAME";
const F_EP_OS_PRODUCT = "DIMENSION_FIELD_ENDPOINT_OS_PRODUCT_NAME";
const F_EP_OS_MAJOR = "DIMENSION_FIELD_ENDPOINT_OS_VERSION_MAJOR";
const F_MC_ENDPOINT_ID = "DIMENSION_FIELD_ENDPOINT_METADATA_CHANGE_ENDPOINT_ID";
const F_MC_CHANGE_TIME = "DIMENSION_FIELD_ENDPOINT_METADATA_CHANGE_CHANGE_TIME";

const EP_ROOT = { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" };
const EP_ID_REF = { stepAlias: "e", field: F_EP_ID };
const EP_COUNT_DISTINCT = {
  outputAlias: "n",
  computed: { field: EP_ID_REF, op: "AGGREGATE_OP_COUNT_DISTINCT" },
};
const REAL_ENDPOINTS_FILTER = {
  pred: { field: { stepAlias: "e", field: F_EP_IS_SYNTHETIC }, op: "OPERATOR_EQ", value: false },
};

// Build the { root, filter } for an endpoint-inventory aggregation that scales to
// ANY fleet/scope size WITHOUT materializing an endpoint-id list (so no
// SCOPE_IDS_LIMIT cap):
//   • Global scope (no steps) → root ENTITY_TYPE_ENDPOINT directly (whole fleet;
//     the planner injects the per-tenant filter). ENDPOINT has no time field, so
//     timeRange is ignored on this path.
//   • Any narrow scope → root PROMPT (buildPromptRootAndFilter carries every
//     scope-dim shape — direct preds, the cluster chain, model-on-PROMPT, …) and
//     INNER-join ENDPOINT via EDGE_TYPE_PROMPT_ON_ENDPOINT. The aggregation then
//     runs over the endpoints with prompt activity in scope+range — the same
//     activity-derived population the prior id-list path used, minus the 1000 cap.
//     timeRange filters the prompts (so the set is range-scoped, as before).
// In BOTH paths the ENDPOINT step is alias "e", so callers build identical
// output dimensions/metrics regardless of which path produced the root.
// Exclude connector-generated synthetic endpoints from charts and table alike,
// matching the admin inventory's real-device population (is_synthetic = 0).
function buildEndpointAggRoot(scopePath, range) {
  const apiScope = toApiScope(scopePath, range);
  if (!apiScope.length) return { root: EP_ROOT, filter: REAL_ENDPOINTS_FILTER };

  const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope });
  // Fail closed on a scope this PROMPT root can't carry (empty today) rather
  // than under-filtering. The caller displays a query error.
  if (unsupported.length) {
    throw new Error(`inventory aggregation: unsupported scope dims ${unsupported.join(", ")}`);
  }
  root.children = [
    ...(root.children ?? []),
    {
      edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT",
      mode: "JOIN_MODE_INNER",
      target: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" },
    },
  ];
  return { root, filter: combineAnd(filter, REAL_ENDPOINTS_FILTER) };
}

// Distinct endpoints that BOTH have activity in scope+range AND carry a metadata
// row — ALWAYS PROMPT-rooted + INNER-join ENDPOINT (unlike buildEndpointAggRoot,
// which roots at ENDPOINT for the whole fleet at the root scope). Powers the
// "No snapshot" header tile server-side: No snapshot = In scope (active) − this.
// Computing it on the server (no id-list) keeps the tile stable and scalable
// instead of deriving it from the paginating, capped client row set. Fails
// closed to null on an unsupported scope so the caller can fall back.
async function executeInventoryActiveWithMetadata({ scopePath = [], range } = {}) {
  const apiScope = toApiScope(scopePath, range);
  const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope });
  if (unsupported.length) return { count: null };
  root.children = [
    ...(root.children ?? []),
    { edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT", mode: "JOIN_MODE_INNER", target: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" } },
  ];
  const spec = {
    root,
    timeRange: rangeToTimeRange(toApiRange(range)),
    output: { metrics: [{ outputAlias: "n", computed: { field: EP_ID_REF, op: "AGGREGATE_OP_COUNT_DISTINCT" } }] },
  };
  if (filter) spec.filter = filter;
  const rows = rowsToObjects(await runAnalyticsQuery(spec));
  return { count: Number(rows?.[0]?.n) || 0 };
}

// Attach the AGENT_INVENTORY join (alias "inv") to the ENDPOINT step — the
// root itself at global scope, or the "e" join target when scoped.
// Copy-on-write: buildEndpointAggRoot returns the shared EP_ROOT object at
// global scope, so mutating it would leak the join into sibling specs.
// Rollout note: the INNER join sees only rekeyed (agent-name) inventory
// rows; endpoints that have not re-reported since the rekey deploy are
// invisible here until their next metadata change, so coverage ramps.
function withAgentInventoryJoin(root) {
  const join = {
    edge: "EDGE_TYPE_ENDPOINT_HAS_AGENT_INVENTORY",
    mode: "JOIN_MODE_INNER",
    target: { alias: "inv", entity: "ENTITY_TYPE_AGENT_INVENTORY" },
  };
  if (root.entity === "ENTITY_TYPE_ENDPOINT") {
    return { ...root, children: [...(root.children ?? []), join] };
  }
  const endpointChild = (root.children ?? []).find((c) => c.target?.alias === "e");
  if (!endpointChild) {
    // Fail loudly rather than run the distribution without its join.
    throw new Error("withAgentInventoryJoin: no ENDPOINT step with alias 'e' on the scoped root");
  }
  return {
    ...root,
    children: root.children.map((c) =>
      c === endpointChild
        ? { ...c, target: { ...c.target, children: [...(c.target.children ?? []), join] } }
        : c,
    ),
  };
}

async function executeInventoryAgentDistribution({ scopePath = [], range } = {}) {
  const timeRange = rangeToTimeRange(toApiRange(range));
  const { root, filter: scopeFilter } = buildEndpointAggRoot(scopePath, range);
  const agentNameRef = { stepAlias: "inv", field: F_INV_AGENT_NAME };
  const countDistinctEndpoints = EP_COUNT_DISTINCT;

  // A: distinct endpoints per installed agent (ENDPOINT ⋈ AGENT_INVENTORY,
  //    grouped by the inventory's agent name).
  // B: total distinct endpoints in scope, which
  //    includes endpoints reporting no installed agents (no inventory join;
  //    matching the prior client-side `endpointsTotal`). Independent, so run
  //    them together.
  const distSpec = {
    root: withAgentInventoryJoin(root),
    timeRange,
    output: {
      dimensions: [{ field: agentNameRef, outputAlias: "agent" }],
      metrics: [countDistinctEndpoints],
    },
  };
  const totalSpec = { root, timeRange, output: { metrics: [countDistinctEndpoints] } };
  if (scopeFilter) {
    distSpec.filter = scopeFilter;
    totalSpec.filter = scopeFilter;
  }
  const [distRows, totalRows] = await Promise.all([
    runAnalyticsQuery(distSpec).then(rowsToObjects),
    runAnalyticsQuery(totalSpec).then(rowsToObjects),
  ]);

  const groups = distRows
    .map((r) => ({ value: String(r.agent ?? ""), count: Number(r.n) || 0 }))
    .filter((g) => g.value !== "");
  const endpointsTotal = Number(totalRows?.[0]?.n) || 0;

  return { groups, endpointsTotal };
}

// Server-side OS distribution for the Inventory Surface "Operating systems" tab
// — the sibling of executeInventoryAgentDistribution, sharing the same
// scope-aware root (buildEndpointAggRoot: ENDPOINT-root globally, PROMPT→ENDPOINT
// edge-join when scoped). Uses only dimensions that already exist in the graph
// (no backend change): groups by OS_PRODUCT_NAME + OS_VERSION_MAJOR. Per the
// proto field contract these resolve the OS family naturally — product_name is
// Windows-only (so its presence ⇒ Windows, and it carries the build, e.g.
// "Windows 11 Pro"); os_version.major is present for macOS (kernel major) but
// absent for Linux. The client folds (product, major) → Windows build / macOS /
// Other. Both are scalar per endpoint, so the cells partition the population.
async function executeInventoryOsDistribution({ scopePath = [], range } = {}) {
  const { root, filter: scopeFilter } = buildEndpointAggRoot(scopePath, range);

  const spec = {
    root,
    timeRange: rangeToTimeRange(toApiRange(range)),
    output: {
      dimensions: [
        { field: { stepAlias: "e", field: F_EP_OS_PRODUCT }, outputAlias: "product" },
        { field: { stepAlias: "e", field: F_EP_OS_MAJOR }, outputAlias: "major" },
      ],
      metrics: [EP_COUNT_DISTINCT],
    },
  };
  if (scopeFilter) spec.filter = scopeFilter;

  const rows = rowsToObjects(await runAnalyticsQuery(spec));
  const groups = rows.map((r) => ({
    product: String(r.product ?? ""),
    major: String(r.major ?? ""),
    count: Number(r.n) || 0,
  }));
  return { groups };
}

// ── Endpoint Inventory listing (Explore lens table, server-side) ─────────────
// Per-endpoint rows for the Inventory lens table, served by the analytics graph
// instead of paging the whole tenant into the browser and
// filtering/scoping/sorting client-side. Reuses the scope-aware root
// (buildEndpointAggRoot: ENDPOINT globally, PROMPT→ENDPOINT edge-join when
// scoped) and dedupes the join fan-out with count_distinct + GROUP BY over the
// endpoint dimensions. Free-text search is a server-side ILIKE across the scalar
// columns; sort + limit/offset are server-side; a sibling count_distinct query
// supplies the total. Rows are reshaped to the shared table shape via
// flattenInventoryGraphRow consumes typed OS fields and installed-tool arrays.
const F_EP_HOSTNAME = "DIMENSION_FIELD_ENDPOINT_HOSTNAME";
const F_EP_SERIAL = "DIMENSION_FIELD_ENDPOINT_SERIAL_NUMBER";
const F_EP_REGISTERED_USER = "DIMENSION_FIELD_ENDPOINT_REGISTERED_USER";
const F_EP_OS = "DIMENSION_FIELD_ENDPOINT_OPERATING_SYSTEM";
const F_EP_AGENT_VERSION = "DIMENSION_FIELD_ENDPOINT_AGENT_VERSION";
const F_EP_WATCHDOG_VERSION = "DIMENSION_FIELD_ENDPOINT_WATCHDOG_VERSION";
const F_EP_AGENT_SHA = "DIMENSION_FIELD_ENDPOINT_AGENT_EXECUTABLE_SHA256";
const F_EP_WATCHDOG_SHA = "DIMENSION_FIELD_ENDPOINT_WATCHDOG_EXECUTABLE_SHA256";
const F_EP_LAST_UPDATED = "DIMENSION_FIELD_ENDPOINT_LAST_UPDATED";
const F_EP_MANUFACTURER = "DIMENSION_FIELD_ENDPOINT_MANUFACTURER";
const F_EP_MODEL = "DIMENSION_FIELD_ENDPOINT_MODEL";
const F_EP_PROCESSOR_MODEL = "DIMENSION_FIELD_ENDPOINT_PROCESSOR_MODEL";
const F_EP_CORE_COUNT = "DIMENSION_FIELD_ENDPOINT_CORE_COUNT";
const F_EP_SYSTEM_MEMORY = "DIMENSION_FIELD_ENDPOINT_SYSTEM_MEMORY_MB";
const F_EP_DRIVE_DISK = "DIMENSION_FIELD_ENDPOINT_PRIMARY_DRIVE_DISK_SIZE_GB";
const F_EP_DRIVE_FREE = "DIMENSION_FIELD_ENDPOINT_PRIMARY_DRIVE_FREE_SPACE_GB";
const F_EP_SEARCH_BLOB = "DIMENSION_FIELD_ENDPOINT_SEARCH_BLOB";
const F_EP_DEVICE_FINGERPRINT_V1_SHA256 = "DIMENSION_FIELD_ENDPOINT_DEVICE_FINGERPRINT_V1_SHA256";

// Output dimensions; each outputAlias is a key `flattenInventoryGraphRow` reads.
const INVENTORY_ROW_DIMS = [
  { field: EP_ID_REF, outputAlias: "id" },
  { field: { stepAlias: "e", field: F_EP_HOSTNAME }, outputAlias: "hostname" },
  { field: { stepAlias: "e", field: F_EP_REGISTERED_USER }, outputAlias: "registered_user" },
  { field: { stepAlias: "e", field: F_EP_SERIAL }, outputAlias: "serial" },
  { field: { stepAlias: "e", field: F_EP_DEVICE_FINGERPRINT_V1_SHA256 }, outputAlias: "device_fingerprint_v1_sha256" },
  { field: { stepAlias: "e", field: F_EP_OS }, outputAlias: "operating_system" },
  ...[
    ["OS_VERSION_MAJOR", "os_major"],
    ["OS_PRODUCT_NAME", "os_product_name"],
    ["OS_VERSION_MINOR", "os_minor"],
    ["OS_VERSION_CODENAME", "os_codename"],
    ["OS_VERSION_EDITION", "os_edition"],
    ["OS_VERSION_DISPLAY_VERSION", "os_display_version"],
  ].map(([name, outputAlias]) => ({
    field: { stepAlias: "e", field: `DIMENSION_FIELD_ENDPOINT_${name}` }, outputAlias,
  })),
  { field: { stepAlias: "e", field: F_EP_AGENT_VERSION }, outputAlias: "agent_version" },
  { field: { stepAlias: "e", field: F_EP_WATCHDOG_VERSION }, outputAlias: "watchdog_version" },
  { field: { stepAlias: "e", field: F_EP_AGENT_SHA }, outputAlias: "agent_sha" },
  { field: { stepAlias: "e", field: F_EP_WATCHDOG_SHA }, outputAlias: "watchdog_sha" },
  { field: { stepAlias: "e", field: F_EP_LAST_UPDATED }, outputAlias: "last_updated" },
  { field: { stepAlias: "e", field: F_EP_MANUFACTURER }, outputAlias: "manufacturer" },
  { field: { stepAlias: "e", field: F_EP_MODEL }, outputAlias: "model" },
  { field: { stepAlias: "e", field: F_EP_PROCESSOR_MODEL }, outputAlias: "processor_model" },
  { field: { stepAlias: "e", field: F_EP_CORE_COUNT }, outputAlias: "core_count" },
  { field: { stepAlias: "e", field: F_EP_SYSTEM_MEMORY }, outputAlias: "system_memory_mb" },
  { field: { stepAlias: "e", field: F_EP_DRIVE_DISK }, outputAlias: "drive_disk" },
  { field: { stepAlias: "e", field: F_EP_DRIVE_FREE }, outputAlias: "drive_free" },
];

// Table column id → output alias to ORDER BY. `platform` maps to the OS enum;
// osDisplayVersion / osEdition / installedAiAgents are computed client-side and
// are not server-sortable (absent here → default last-seen desc).
//
// KNOWN DIVERGENCE: the version columns sort lexically here ("2.1.10" before
// "2.1.9" — the graph's ORDER BY has no numeric-component expression), while
// the admin page's repository sort orders the same columns naturally by
// numeric component. A version-aware ordering in the analytics planner is the
// fix; until then the two surfaces disagree on version ordering.
const INVENTORY_SORT_ALIAS = {
  snapshotTime: "last_updated", hostname: "hostname", registeredUser: "registered_user",
  platform: "operating_system", agentVersion: "agent_version", watchdogVersion: "watchdog_version",
  serial: "serial", manufacturer: "manufacturer", model: "model", processorModel: "processor_model",
  coreCount: "core_count", systemMemoryMb: "system_memory_mb",
  primaryDriveDiskSizeGb: "drive_disk", primaryDriveFreeSpaceGb: "drive_free",
  agentExecutableSha256: "agent_sha", watchdogExecutableSha256: "watchdog_sha",
};

// Build a case-insensitive "contains" LIKE pattern for the endpoint `search_blob`
// column: lowercase the term (full Unicode, matching the column's lowerUTF8 fold)
// and escape the ClickHouse LIKE metacharacters `\` `%` `_` so they match
// literally, then wrap in %…%. Mirrors `like_contains_pattern` in
// endpoint_metadata.rs so both search surfaces treat a term identically.
// Backslash is escaped first so the escapes added for `%` / `_` aren't re-escaped.
// Case-PRESERVING variant for the conversation search view: its ngram
// prefilter is case-sensitive and the planner drops it for ILIKE, which
// turns a granule-pruned read into a full scan — over this entity's padded
// 30-day window that is a byte-guard rejection, not a slow query (found
// live). Original case is correct by construction for probes quoted from
// the same capture the index ingested; for basenames it accepts missing a
// case-variant spelling, the same trade the related-work search measured
// as costless.
export function likeContainsPatternCS(term) {
  const escaped = String(term)
    .replace(/\\/g, "\\\\")
    .replace(/%/g, "\\%")
    .replace(/_/g, "\\_");
  return `%${escaped}%`;
}

const MIN_CONVERSATION_TERM_BYTES = 3;

export function conversationTermTooShort(term) {
  const encoder = new TextEncoder();
  const longest = Math.max(
    0,
    ...likeContainsPatternCS(term).split(/[%_]/).map((run) => encoder.encode(run.trim()).length),
  );
  return longest < MIN_CONVERSATION_TERM_BYTES;
}

export function likeContainsPattern(term) {
  const escaped = String(term)
    .toLowerCase()
    .replace(/\\/g, "\\\\")
    .replace(/%/g, "\\%")
    .replace(/_/g, "\\_");
  return `%${escaped}%`;
}

async function executeInventoryRows({
  scopePath = [], range, search = "", sortBy = null, page = 0, pageSize = 100, endpointIds = null,
} = {}) {
  const timeRange = rangeToTimeRange(toApiRange(range));
  const { root, filter: scopeFilter } = buildEndpointAggRoot(scopePath, range);

  const q = String(search || "").trim();
  // One case-sensitive LIKE against the materialized, lowerUTF8 `search_blob`
  // (the same 13-column concat the admin page searches — endpoint_metadata.rs).
  // ClickHouse prunes granules via the ngrambf_v1 index for LIKE (not for ILIKE
  // or position), and the JSON columns are serialized once at write time, not
  // per row per keystroke. `likeContainsPattern` lowercases (matching the blob's
  // lowerUTF8 fold) and escapes the LIKE metacharacters so the term matches
  // literally — identical contract to the admin repo's like_contains_pattern.
  const searchPred = q
    ? { pred: { field: { stepAlias: "e", field: F_EP_SEARCH_BLOB }, op: "OPERATOR_LIKE", value: likeContainsPattern(q) } }
    : null;
  const endpointFilter = endpointIds ? { pred: { field: EP_ID_REF, op: "OPERATOR_IN", value: endpointIds.slice(0, 100) } } : null;
  const filter = combineAnd(combineAnd(scopeFilter, searchPred), endpointFilter);

  const mappedAlias = sortBy && INVENTORY_SORT_ALIAS[sortBy.id];
  const bySessions = sortBy?.id === "sessions";
  const byInstalled = sortBy?.id === "installedAiAgents" || sortBy?.id === "installedLocalRuntimes";
  const traceJoin = {
    edge: "EDGE_TYPE_ENDPOINT_HAS_CONVERSATION_TRACES",
    mode: "JOIN_MODE_LEFT",
    target: { alias: "fleetTrace", entity: "ENTITY_TYPE_CONVERSATION_TRACE" },
  };
  // Aggregate before paging, retaining endpoints without activity. The total
  // and the ordinary inventory sorts do not need the trace join.
  const joins = bySessions ? [traceJoin] : byInstalled ? [{ edge: "EDGE_TYPE_ENDPOINT_HAS_AGENT_INVENTORY", mode: "JOIN_MODE_LEFT", target: { alias: "fleetInstalled", entity: "ENTITY_TYPE_AGENT_INVENTORY" } }] : [];
  const listRoot = !joins.length ? root : root.entity === "ENTITY_TYPE_ENDPOINT"
    ? { ...root, children: [...(root.children ?? []), ...joins] }
    : { ...root, children: root.children.map((child) => child.target?.alias === "e"
      ? { ...child, target: { ...child.target, children: [...(child.target.children ?? []), ...joins] } }
      : child) };
  const orderBy = [
    {
      outputAlias: bySessions ? "sessions" : byInstalled ? "installed_count" : mappedAlias || "last_updated",
      direction: (mappedAlias || bySessions || byInstalled) && sortBy.dir === "asc" ? "DIRECTION_ASC" : "DIRECTION_DESC",
    },
    // endpoint_id tiebreaker (the "id" output dimension) so a tie on the sort
    // column resolves deterministically at the page/1000-row boundary — mirrors
    // the admin repository's `endpoint_id ASC` final key.
    { outputAlias: "id", direction: "DIRECTION_ASC" },
  ];

  const listSpec = {
    root: listRoot,
    timeRange,
    output: { dimensions: INVENTORY_ROW_DIMS, metrics: [EP_COUNT_DISTINCT, ...(bySessions ? [{
      outputAlias: "sessions",
      computed: { field: { stepAlias: "fleetTrace", field: "DIMENSION_FIELD_CONVERSATION_TRACE_TRACE_ID" }, op: "AGGREGATE_OP_COUNT_DISTINCT" },
    }] : []), ...(byInstalled ? [{
      outputAlias: "installed_count",
      computed: {
        field: { stepAlias: "fleetInstalled", field: "DIMENSION_FIELD_AGENT_INVENTORY_AGENT_NAME" },
        op: "AGGREGATE_OP_COUNT_DISTINCT",
        predicate: { pred: {
          field: { stepAlias: "fleetInstalled", field: "DIMENSION_FIELD_AGENT_INVENTORY_AGENT_NAME" },
          op: sortBy.id === "installedLocalRuntimes" ? "OPERATOR_IN" : "OPERATOR_NOT_IN", value: RUNTIME_PRODUCT_NAMES,
        } },
      },
    }] : [])] },
    orderBy,
    limit: pageSize,
    offset: page * pageSize,
  };
  const totalSpec = { root, timeRange, output: { metrics: [EP_COUNT_DISTINCT] } };
  if (filter) {
    listSpec.filter = filter;
    totalSpec.filter = filter;
  }

  const [listRows, totalRows] = await Promise.all([
    runAnalyticsQuery(listSpec).then(rowsToObjects),
    runAnalyticsQuery(totalSpec).then(rowsToObjects),
  ]);
  // Load arrays only for the selected page, without metrics. Grouping the
  // listing by several array dimensions would expand them independently and
  // pair tool names with the wrong versions.
  const [installedById, identities] = await Promise.all([
    fetchInventoryInstalledTools(listRows, timeRange),
    fetchInventoryIdentities(listRows.map((row) => flattenInventoryGraphRow(row))),
  ]);
  const graphRows = listRows.map((row) => ({
    ...flattenInventoryGraphRow({ ...row, ...installedById.get(String(row.id)) }),
    ...(bySessions ? { sessions: Number(row.sessions) || 0 } : {}),
  }));
  const rows = identities ? graphRows.map((row) => mergeInventoryIdentity(row, identities.get(row.id))) : graphRows;
  return { rows, total: Number(totalRows?.[0]?.n) || 0 };
}

async function executeInventoryTotal({ scopePath = [], range } = {}) {
  const { root, filter } = buildEndpointAggRoot(scopePath, range);
  const rows = rowsToObjects(await runAnalyticsQuery({
    root,
    timeRange: rangeToTimeRange(toApiRange(range)),
    output: { metrics: [EP_COUNT_DISTINCT] },
    ...(filter ? { filter } : {}),
  }));
  return Number(rows?.[0]?.n) || 0;
}

const ENDPOINT_IDENTITY_BATCH_SIZE = 500;

async function fetchInventoryInstalledTools(rows, timeRange) {
  const ids = [...new Set(rows.map((row) => String(row.id || "")).filter(Boolean))];
  const byId = new Map();
  for (let start = 0; start < ids.length; start += ENDPOINT_IDENTITY_BATCH_SIZE) {
    const batch = ids.slice(start, start + ENDPOINT_IDENTITY_BATCH_SIZE);
    const result = rowsToObjects(await runAnalyticsQuery({
      root: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" },
      timeRange,
      filter: { pred: { field: EP_ID_REF, op: "OPERATOR_IN", value: batch } },
      output: { dimensions: [
        { field: EP_ID_REF, outputAlias: "id" },
        ...["NAMES", "PROVIDERS", "TYPES", "VERSIONS", "PATHS"].map((name) => ({
          field: { stepAlias: "e", field: `DIMENSION_FIELD_ENDPOINT_INSTALLED_AI_AGENT_${name}` },
          outputAlias: `installed_agent_${name.toLowerCase()}`,
        })),
      ] },
      limit: batch.length,
    }));
    for (const row of result) byId.set(String(row.id), row);
  }
  return byId;
}

// Analytics owns the inventory digest and paging, while allocation provenance
// remains authoritative in Postgres. Load that small identity projection in
// bounded chunks. A transient REST failure returns the graph rows unchanged;
// endpointDedupe then treats their unknown provenance conservatively.
async function fetchInventoryIdentities(rows) {
  const endpointIds = rows
    .map((row) => uuidToEndpointId(row.idRaw || row.id))
    .filter(Boolean);
  if (!endpointIds.length) return null;

  try {
    const batches = [];
    for (let start = 0; start < endpointIds.length; start += ENDPOINT_IDENTITY_BATCH_SIZE) {
      batches.push(endpointIds.slice(start, start + ENDPOINT_IDENTITY_BATCH_SIZE));
    }
    const responses = await Promise.all(batches.map((batch) => restPost("/v1/endpoints/identities:batchGet", { endpointIds: batch })));

    const identityByEndpointId = new Map();
    for (const response of responses) {
      for (const entry of response?.identities ?? []) {
        const id = endpointIdToUuid(entry?.endpointId ?? entry?.endpoint_id ?? "");
        const identityInfo = entry?.identityInfo ?? entry?.identity_info;
        if (id && identityInfo) identityByEndpointId.set(id, identityInfo);
      }
    }
    return identityByEndpointId;
  } catch {
    return null;
  }
}

// First-seen-per-endpoint for the Inventory Surface "First seen" rollout chart.
// endpoint_metadata_current carries no enrollment timestamp (it's a latest-state
// ReplacingMergeTree), so we approximate first-seen with the EARLIEST observed
// metadata change per endpoint — MIN(change_time) grouped by endpoint over
// ENTITY_TYPE_ENDPOINT_METADATA_CHANGE. This is a KNOWN-LOSSY proxy: the change
// log is append-on-diff, so it only covers endpoints that have logged a field
// change, and that first logged change post-dates true enrollment. The surface
// captions the coverage honestly; a faithful rollout needs a backend enrollment
// timestamp (tracked separately).
//
// Two deliberate choices:
//   • Window stitching for full history. The analytics API caps ANY single time
//     range at 90 days (MAX_ABSOLUTE_RANGE_SECS in shared-analytics/validate.rs —
//     it REJECTS, not clamps, a wider range), but the change log retains 365d.
//     change_time IS this entity's time field, so a single narrow range would
//     clamp the MIN and miss endpoints first seen earlier. So we run a set of
//     contiguous ≤90-day windows spanning the full TTL in parallel and take the
//     MIN change_time per endpoint ACROSS windows = true earliest observed.
//   • Whole-fleet, no scope join. Scope is applied CLIENT-SIDE in the surface
//     (intersect endpoint_id with the loaded in-scope rows — the same id
//     encoding on both sides, see scopedFirstSeenTimestamps), avoiding a fragile
//     3-level scope join carrying a MIN metric over a fanned PROMPT→ENDPOINT edge.
const FIRST_SEEN_WINDOW_DAYS = 89; // < the 90-day range cap, with headroom
const FIRST_SEEN_WINDOW_COUNT = 5; // 5 × 89d = 445d ⊇ the 365-day change-log TTL

// Run the change-log windowed aggregate: per-endpoint change_time reduced by `op`
// (MIN → first-seen, MAX → last-seen) over each ≤90-day window spanning the TTL, in
// parallel. Returns the raw per-window row lists ([{ id, <alias> }][]) for a fold.
// Shared by executeFirstSeenByEndpoint / executeLastSeenByEndpoint so both seen
// tabs ride the SAME analytics:execute path (only the aggregate op differs).
function runChangeTimeWindows({ op, alias }) {
  const dayMs = 86400 * 1000;
  const nowMs = Math.ceil(Date.now() / 60_000) * 60_000;
  const mcEndpointId = { stepAlias: "mc", field: F_MC_ENDPOINT_ID };
  const mcChangeTime = { stepAlias: "mc", field: F_MC_CHANGE_TIME };

  return Promise.all(
    Array.from({ length: FIRST_SEEN_WINDOW_COUNT }, (_, i) => {
      const endMs = nowMs - i * FIRST_SEEN_WINDOW_DAYS * dayMs;
      const startMs = endMs - FIRST_SEEN_WINDOW_DAYS * dayMs;
      const spec = {
        root: { alias: "mc", entity: "ENTITY_TYPE_ENDPOINT_METADATA_CHANGE" },
        timeRange: { absolute: { start: new Date(startMs).toISOString(), end: new Date(endMs).toISOString() } },
        output: {
          dimensions: [{ field: mcEndpointId, outputAlias: "id" }],
          metrics: [{ outputAlias: alias, computed: { field: mcChangeTime, op } }],
        },
      };
      return runAnalyticsQuery(spec).then(rowsToObjects);
    }),
  );
}

// Fold per-window change_time rows into one reduced-per-endpoint list. `pick` keeps
// the extreme across windows (< for MIN/first-seen, > for MAX/last-seen). Pure
// (the two named folds below are exported for test): drops blank ids / unparseable
// timestamps.
function foldChangeTimeWindows(windowResults, alias, outKey, keepIf) {
  const best = new Map();
  for (const rows of windowResults) {
    for (const r of rows ?? []) {
      const id = String(r?.id ?? "");
      const ms = parseServerTimestamp(r?.[alias]);
      if (!id || !Number.isFinite(ms)) continue;
      const prev = best.get(id);
      if (prev === undefined || keepIf(ms, prev)) best.set(id, ms);
    }
  }
  return Array.from(best, ([id, ms]) => ({ id, [outKey]: ms }));
}

// Fold per-window MIN(change_time) rows ([{ id, first_seen }] per window) into
// one true earliest-per-endpoint [{ id, firstSeenMs }].
export function foldFirstSeenWindows(windowResults = []) {
  return foldChangeTimeWindows(windowResults, "first_seen", "firstSeenMs", (ms, prev) => ms < prev);
}

// Fold raw file-touch events into one row per session, with write/read polarity.
//
// Keep both read and write events, deriving the display counts from their
// typed first_read_time and first_write_time fields.
//
// An author with zero writes is normal and must not be read as "did not author":
// an Anthropic agent's Write tool performs no spawned-process file open, so its
// authorship leaves no FileState write at all. `writes` therefore means "provably
// authored"; it never means "is the only author".
// The spec for "who else touched this file". Pure and exported so the bounding
// rules are testable without a server (mirrors sessionOriginSpec). Returns null
// when the caller gave no window — this query must never run unbounded.
export function fileCounterpartiesSpec({
  path, endpointId = null, startTime = null, endTime = null,
  allEndpoints = false, limit = 400,
} = {}) {
  const base = String(path || "").replace(/\\/g, "/").split("/").filter(Boolean).pop();
  if (!base || !startTime || !endTime) return null;
  const field = (name) => ({ stepAlias: "e", field: `DIMENSION_FIELD_TELEMETRY_TIMELINE_${name}` });
  const preds = [{ pred: { field: field("EVENT_KIND"), op: "OPERATOR_IN", value: ["STATE", "PROCESS_START"] } }];
  if (endpointId && !allEndpoints) preds.push({ pred: { field: field("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } });
  return {
    root: { alias: "e", entity: "ENTITY_TYPE_TELEMETRY_TIMELINE" },
    timeRange: rangeToTimeRange({ from: startTime, to: endTime }),
    filter: { andExpr: { children: preds } },
    output: { dimensions: telemetryDimensions() },
    orderBy: [{ outputAlias: "ts", direction: "DIRECTION_ASC" }, { outputAlias: "id", direction: "DIRECTION_ASC" }],
    limit,
    searchText: base,
  };
}

// Rows are time-ASC, so hitting the cap drops the LATEST touches — the panel
// would read as complete while omitting whoever came last. Basename matching is
// deliberately loose, so a common filename under "search all endpoints" can
// plausibly reach it.
export function countedAtCap(rowCount, limit = 400) {
  return rowCount >= limit;
}

// Put an agent name on each counterparty row. A session id is not a reading —
// "Codex · gpt-5.6-sol" is. Best-effort: a session with no prompt rows in the
// window keeps its id and still shows its file activity, because the OS side
// sees agents the prompt side does not (that asymmetry is the whole point of
// this search) and a missing label must never drop a participant.
// A counterparty's session id is not always a conversation. An OpenAI/Codex
// host process (`codex.exe … app-server`) carries ONE notification_id while
// multiplexing many conversations — one measured session hosted nine — so naming
// it alone reports the process, not the agent you are looking for.
//
// It resolves in two hops, both scoped exactly as the file search is and both
// cheap (0.07s and 0.30s measured; the file search itself is 0.45s):
//   1. `turn-ended` ProcessStart markers under that session carry the Codex
//      thread id. A turn marker fires at the END of a turn, so the turn that
//      touched the file is the first marker at-or-after the touch.
//   2. The conversation is the prompt whose body OWNS that thread — matched on
//      `metadata.conversation_id`, never on a bare mention of the id. Measured:
//      a bare mention returns two sessions (one merely discussing the thread),
//      ownership returns exactly one.
// These three shapes are an EXTERNAL (Codex) wire convention with no in-repo
// producer, so nothing here can pin them — they were read off live payloads
// rather than inferred: the marker rides a `codex-computer-use turn-ended`
// ProcessStart command line as
// `{"type":"agent-turn-complete","thread-id":…,"turn-id":…,"cwd":…}`, and the
// owning prompt body carries `metadata.conversation_id` equal to that thread.
// Every hop degrades to the unresolved host label if the shape changes, so a
// drift shows up as a coarser reading, never as a wrong one.
const TURN_MARKER = "agent-turn-complete";
const THREAD_RE = /thread-id[^0-9a-f]{1,12}([0-9a-f]{8}-[0-9a-f-]{27})/;

// The marker's payload is a JSON object embedded in a process command line and
// escaped once by shell quoting. PARSE it rather than pattern-match it, so field
// order, added keys or whitespace cannot break extraction; the regex remains as
// a fallback for a payload too mangled to parse. Both paths are pinned against a
// command line captured verbatim from live telemetry (see the fixture test), so
// this is verified against a real payload rather than an assumed shape.
export function threadIdFromMarker(commandLine) {
  const text = String(commandLine || "");
  if (!text.includes(TURN_MARKER)) return null;
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start >= 0 && end > start) {
    const unescaped = text.slice(start, end + 1).replace(/\\"/g, '"').replace(/\\\\/g, "\\");
    try {
      const payload = JSON.parse(unescaped);
      const id = payload?.["thread-id"] ?? payload?.thread_id ?? null;
      if (typeof id === "string" && id) return id;
    } catch {
      // fall through to the pattern below
    }
  }
  return THREAD_RE.exec(text)?.[1] ?? null;
}
// The turn's INPUT TEXT out of the same marker payload. Newer hosted clients
// (observed: "Codex Desktop") stopped carrying the thread id in their request
// bodies, so ownership can no longer be proven there — but the marker still
// quotes what the user asked, and the conversation that OWNS the thread
// carries that text in the conversation model. A short distinctive prefix is
// enough for the search view, and anything shorter than 20 chars is refused:
// probing for "hi" proves nothing.
export function markerInputProbe(commandLine) {
  const text = String(commandLine || "");
  if (!text.includes(TURN_MARKER) || !text.includes("input-messages")) return null;
  // NOT parsed as JSON, deliberately: a real payload carries Windows paths
  // ("cwd":"C:\\Users\\...") whose unescaped form is invalid JSON, and a parse
  // throws on exactly the machines this fallback exists for. The regex finds
  // the first quoted element of input-messages in the raw escaped text; one
  // level of backslash escaping is then dropped so the probe carries the
  // apostrophes and quotes the conversation model indexed.
  // Any RUN of backslashes before the quotes: the payload rides two layers of
  // shell quoting and different segments of one line arrive at different
  // escaping depths. No closing-quote requirement, deliberately: the
  // message's terminator is itself an escaped quote, indistinguishable from
  // content while escaped — so the capture takes a bounded prefix, and the
  // cut happens AFTER unescaping, where the terminator finally looks like
  // the quote it is.
  const m = /input-messages\\*"\s*:\s*\[\s*\\*"((?:[^"\\]|\\.){20,300})/.exec(text);
  if (!m) return null;
  const unescaped = m[1].replace(/\\(.)/g, "$1");
  // A doubly-escaped terminator leaves one backslash behind after a single
  // unescape pass; it belongs to the quote, not the message.
  const cut = unescaped.split('"')[0].replace(/\\+$/, "").trim();
  return cut.length >= 20 ? cut.slice(0, 60) : null;
}

// Bounded so a many-participant result can't fan out into per-row queries.
const MAX_RESOLVED_THREADS = 6;

// The REVERSE of resolveHostedConversations: given a conversation's thread id,
// find the HOST process session its OS telemetry is stamped with. A hosted
// agent (a Codex app-server) multiplexes many conversations under one
// notification id, so a conversation-scoped telemetry read comes back empty —
// the events exist, filed under the host. The turn-ended markers carry the
// thread id on the host's own ProcessStart rows, which makes them the join.
//
// `threadId` must already be a well-formed UUID (the caller validates via
// conversationThreadId) — it is interpolated into a LIKE pattern, and an
// unvalidated string would widen the match instead of erroring.
// The trace-open readings below fire automatically for every trace a reader
// opens, so their legs take the same tab-wide trace-read slot as the
// turn-clock fetchers (traceTurnClockFetch.js) — an unbounded burst of trace
// opens must queue at the tab's ceiling, not stack on ClickHouse. The signal
// is the caller's withdrawal, and it goes to BOTH layers: the gate gives the
// slot back at the abort, and the wire call aborts its fetch and detaches
// from the in-flight dedupe rather than running to completion for nobody.
const gatedAnalyticsRead = (spec, signal = null) =>
  withTraceReadSlot(signal, () => runAnalyticsQuery(spec, { signal }));

export async function fetchHostSessionForThread({ threadId, endpointId = null, startTime = null, endTime = null, signal = null } = {}) {
  if (!threadId || !startTime || !endTime) return { ok: false, hostSessionId: null };
  // A session/notification predicate is impossible here BY CONSTRUCTION: the
  // host session id is this query's OUTPUT. A hosted conversation shares
  // nothing with its host process except the thread id inside the turn-ended
  // markers, so resolving the host means scanning for that id. Every
  // expressible bound is applied: the process family, both LIKE terms (marker
  // prefix + UUID-validated thread id), endpoint EQ when the caller knows it,
  // the caller's span window, and the row cap. Making this cheaper is a
  // backend index/materialization of the marker shape. The query searches the
  // typed command line and returns only the host session identity.
  const E = (field) => ({ stepAlias: "e", field: `DIMENSION_FIELD_PROCESS_EVENT_${field}` });
  const preds = [
    { pred: { field: E("COMMAND_LINE"), op: "OPERATOR_LIKE", value: likeContainsPatternCS(TURN_MARKER) } },
    { pred: { field: E("COMMAND_LINE"), op: "OPERATOR_LIKE", value: likeContainsPatternCS(threadId) } },
  ];
  if (endpointId) {
    preds.push({ pred: { field: E("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } });
  }
  try {
    const rows = rowsToObjects(await gatedAnalyticsRead({
      root: { alias: "e", entity: "ENTITY_TYPE_PROCESS_EVENT" },
      timeRange: rangeToTimeRange({ from: startTime, to: endTime }),
      filter: { andExpr: { children: preds } },
      output: { dimensions: [{ field: E("TRACE_ID"), outputAlias: "session_id" }] },
      limit: 50,
    }, signal));
    const tally = new Map();
    for (const r of rows) {
      if (r?.session_id) tally.set(r.session_id, (tally.get(r.session_id) || 0) + 1);
    }
    const best = [...tally.entries()].sort((a, b) => b[1] - a[1])[0];
    return { ok: true, hostSessionId: best?.[0] ?? null };
  } catch (err) {
    return { ok: false, hostSessionId: null, error: String(err?.message || err) };
  }
}

// The turn RESPONSIBLE for a file touch: the last one to start at or before it.
// A prompt is the request; the work happens after it. Exported so the rule is
// testable — getting this wrong linked to a turn 23 minutes before the file
// existed, which read as a dead link.
export function pickResponsibleTurn(turns = [], at = null) {
  const sorted = [...turns].filter((t) => t?.trace_id).sort((a, b) =>
    String(a.at ?? "").localeCompare(String(b.at ?? "")));
  if (!at) return sorted[0] ?? null;
  return [...sorted].reverse().find((t) => t.at && t.at <= at)
    || sorted.find((t) => t.at && t.at >= at)
    || sorted[0]
    || null;
}

export function pickThreadForRow(markers, row) {
  // Markers are [{ ts, thread }] for one session, ascending. A turn marker fires
  // at turn END: the turn that did the work is the first one to END at or after
  // the last observed touch. A long row can STRADDLE its markers — touches both
  // before the first and after the last (measured live: a half-day of file
  // reads around a burst of turns) — so the middle case picks the first marker
  // inside the span: the turn that ended while the touching was under way.
  // The latest earlier marker remains the last resort for a trailing touch.
  const after = markers.find((m) => !row.last_seen || m.ts >= row.last_seen);
  const within = markers.find((m) => !row.first_seen || m.ts >= row.first_seen);
  const before = [...markers].reverse().find((m) => !row.first_seen || m.ts <= row.first_seen);
  return (after || within || before || null)?.thread ?? null;
}

async function resolveHostedConversations(rows, { startTime, endTime, endpointId, allEndpoints }) {
  const ids = rows.map((r) => r.session_id).filter(Boolean);
  if (!ids.length) return rows;
  const field = (name) => ({ stepAlias: "e", field: `DIMENSION_FIELD_TELEMETRY_TIMELINE_${name}` });
  const timeRange = rangeToTimeRange({ from: startTime, to: endTime });
  try {
    const preds = [
      { pred: { field: field("EVENT_KIND"), op: "OPERATOR_EQ", value: "PROCESS_START" } },
      { pred: { field: field("SESSION_ID"), op: "OPERATOR_IN", value: ids.slice(0, 1000) } },
    ];
    if (endpointId && !allEndpoints) preds.push({ pred: { field: field("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } });
    const markerRows = await readTelemetryPage({
      root: { alias: "e", entity: "ENTITY_TYPE_TELEMETRY_TIMELINE" }, timeRange,
      filter: { andExpr: { children: preds } },
      orderBy: [{ outputAlias: "ts", direction: "DIRECTION_ASC" }, { outputAlias: "id", direction: "DIRECTION_ASC" }],
      limit: 60,
    }, { search: TURN_MARKER, execute: runAnalyticsQuery });

    const bySession = new Map();
    for (const marker of markerRows) {
      const line = marker.data?.command_line ?? "";
      const thread = threadIdFromMarker(line);
      if (!thread || !marker.sessionId) continue;
      if (!bySession.has(marker.sessionId)) bySession.set(marker.sessionId, []);
      bySession.get(marker.sessionId).push({ ts: marker.ts, thread, probe: markerInputProbe(line) });
    }
    if (!bySession.size) return rows;

    const wanted = new Map();       // thread -> [row, …]
    const probeOf = new Map();      // thread -> the input text its markers quoted
    for (const row of rows) {
      const markers = bySession.get(row.session_id);
      if (!markers?.length) continue;
      const thread = pickThreadForRow(markers, row);
      if (!thread) continue;
      if (!wanted.has(thread)) wanted.set(thread, []);
      wanted.get(thread).push(row);
      if (!probeOf.has(thread)) {
        const withProbe = markers.find((m) => m.thread === thread && m.probe);
        if (withProbe) probeOf.set(thread, withProbe.probe);
      }
    }
    const threads = [...wanted.keys()].slice(0, MAX_RESOLVED_THREADS);
    if (!threads.length) return rows;

    const ownerField = (name) => ({ stepAlias: "e", field: `DIMENSION_FIELD_EVENT_${name}` });
    const ownerPreds = [{ pred: { field: ownerField("CONVERSATION_THREAD_ID"), op: "OPERATOR_IN", value: threads } }];
    if (endpointId && !allEndpoints) ownerPreds.push({ pred: { field: ownerField("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } });
    const ownerRows = rowsToObjects(await runAnalyticsQuery({
      root: { alias: "e", entity: "ENTITY_TYPE_EVENT" }, timeRange,
      filter: { andExpr: { children: ownerPreds } },
      output: { dimensions: [["CONVERSATION_THREAD_ID", "thread_id"], ["NOTIFICATION_ID", "session_id"], ["EVENT_ID", "trace_id"], ["EVENT_TIMESTAMP", "event_time"]]
        .map(([name, outputAlias]) => ({ field: ownerField(name), outputAlias })) },
      orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }], limit: 1001,
    }));
    if (ownerRows.length > 1000) return rows;
    const resolved = await Promise.all(threads.map(async (thread) => {
      const found = ownerRows.filter(row => row.thread_id === thread);
      // The owner is the session that carries it repeatedly; a one-off match is
      // a mention that slipped past the predicate.
      const tally = new Map();
      for (const f of found) {
        if (!f?.session_id) continue;
        const cur = tally.get(f.session_id) ?? { n: 0, turns: [] };
        cur.n += 1;
        if (f.trace_id) cur.turns.push({ trace_id: f.trace_id, at: f.event_time ?? null });
        tally.set(f.session_id, cur);
      }
      let best = [...tally.entries()].sort((a, b) => b[1].n - a[1].n)[0];
      if (!best && probeOf.get(thread)) {
        // Newer hosted clients never write the thread id into a request body,
        // so the hop above finds nothing. The marker's quoted input text still
        // identifies the conversation — the model indexes what was SAID, and
        // the thread's owner is the conversation that carries it. Two bounded
        // queries, only on the miss path; a deployment without the
        // conversation model skips silently and the row stays a host.
        try {
          const S = (field) => ({ stepAlias: "s", field: `DIMENSION_FIELD_CONVERSATION_SEARCH_${field}` });
          const sPreds = [
            { pred: { field: S("TEXT"), op: "OPERATOR_LIKE", value: likeContainsPatternCS(probeOf.get(thread)) } },
          ];
          if (endpointId && !allEndpoints) {
            sPreds.push({ pred: { field: S("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } });
          }
          // The slack matters doubly here: search rows carry the
          // conversation's ORIGIN time, and a hosted conversation can be
          // days older than the file window it touched. Narrow-retried like
          // every read of this entity.
          const hits = rowsToObjects(await runPaddedSearchWithRetry((pad) => ({
            root: { alias: "s", entity: "ENTITY_TYPE_CONVERSATION_SEARCH_CONTENT" },
            timeRange: rangeToTimeRange(pad ? turnBoundConversationRange(startTime, endTime) : { from: startTime, to: endTime }),
            filter: { andExpr: { children: sPreds } },
            output: { dimensions: [{ field: S("TRACE_ID"), outputAlias: "session_id" }] },
            limit: 200,
          })));
          const sTally = new Map();
          for (const h of hits) {
            if (h?.session_id) sTally.set(h.session_id, (sTally.get(h.session_id) || 0) + 1);
          }
          const owner = [...sTally.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
          if (owner) {
            // The owner's turns come from the enriched prompt rows — the ids
            // the trace tab opens — scoped to the same window as everything
            // else in this search.
            const P = (field) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${field}` });
            const turnRows = rowsToObjects(await runAnalyticsQuery({
              root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
              timeRange,
              filter: { pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_EQ", value: owner } },
              output: {
                dimensions: [
                  { field: P("EVENT_ID"), outputAlias: "trace_id" },
                  { field: P("EVENT_TIMESTAMP"), outputAlias: "event_time" },
                ],
              },
              orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }],
              limit: 200,
            }));
            best = [owner, {
              n: sTally.get(owner) || 1,
              turns: turnRows.filter((t) => t?.trace_id).map((t) => ({ trace_id: t.trace_id, at: t.event_time ?? null })),
            }];
          }
        } catch {
          // The fallback is an upgrade; an unresolved host is still a finding.
        }
      }
      return [thread, best ? { session_id: best[0], turns: best[1].turns } : null];
    }));

    const byThread = new Map(resolved);
    return rows.map((row) => {
      const markers = bySession.get(row.session_id);
      if (!markers?.length) return row;
      const thread = pickThreadForRow(markers, row);
      const conv = thread ? byThread.get(thread) : null;
      // The turn RESPONSIBLE for a touch is the last one to start at or before
      // it — a prompt is the request; the work happens after it. Falling back to
      // the earliest later turn keeps a touch that precedes any captured prompt
      // attributable rather than linking somewhere arbitrary.
      const responsible = pickResponsibleTurn(conv?.turns ?? [], row.first_seen ?? null);
      // Keep the host id: it is what the file events are actually stamped with,
      // and losing it would make the reading unverifiable.
      return conv?.session_id
        ? { ...row, host_session_id: row.session_id, session_id: conv.session_id,
            thread_id: thread, trace_id: responsible?.trace_id ?? null,
            trace_at: responsible?.at ?? null }
        : row;
    });
  } catch {
    return rows; // resolution is an upgrade; the participants are the finding
  }
}

async function labelCounterpartySessions(rows, { startTime, endTime } = {}) {
  const ids = rows.map((r) => r.session_id).filter(Boolean);
  if (!ids.length) return rows;
  const P = (field) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${field}` });
  // A MUCH wider window than the file search, deliberately. This filters on an
  // IN-list of session ids, which is selective enough that breadth is cheap —
  // unlike the file search, whose cost is the payload LIKE. And it has to be
  // wider: a long-lived host process (an app-server that hosts many agent
  // conversations) can have its prompts days outside the window in which it
  // touched the file, which left real participants labelled by bare id.
  const LABEL_WINDOW_DAYS = 30;
  const pad = LABEL_WINDOW_DAYS * 86400 * 1000;
  const anchor = Date.parse(startTime ?? "");
  const labelRange = Number.isFinite(anchor)
    ? { from: new Date(anchor - pad).toISOString(), to: new Date(Date.parse(endTime) + pad).toISOString() }
    : { from: startTime, to: endTime };
  try {
    const labelRows = rowsToObjects(await runAnalyticsQuery({
      root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
      timeRange: rangeToTimeRange(labelRange),
      filter: { pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_IN", value: ids.slice(0, 1000) } },
      output: {
        dimensions: [
          { field: P("NOTIFICATION_ID"), outputAlias: "session_id" },
          { field: P("AGENT_TYPE"), outputAlias: "agent_type" },
          { field: P("PROVIDER"), outputAlias: "provider" },
          { field: P("MODEL"), outputAlias: "model" },
          // Any prompt row makes the session OPENABLE: rows whose only
          // evidence is the content channel arrive without a trace_id, which
          // rendered a participant the reader could see but not visit.
          { field: P("EVENT_ID"), outputAlias: "trace_id" },
          // The machine badge's other half: index carrier rows have no
          // endpoint column, so the label hop supplies it.
          { field: P("ENDPOINT_ID"), outputAlias: "endpoint_id" },
          { field: P("EVENT_TIMESTAMP"), outputAlias: "at" },
        ],
      },
      // Newest first, so the representative trace is the session's most
      // recent turn near the window — the one a reader lands closest to.
      orderBy: [{ outputAlias: "at", direction: "DIRECTION_DESC" }],
      limit: 2000,
    }));
    const byId = new Map();
    for (const r of labelRows) {
      if (r?.session_id && !byId.has(r.session_id)) byId.set(r.session_id, r);
    }
    // A session the index holds no prompt of near the window (one older than
    // the index) is labelled by its capture frames, kept a year. They name no
    // turn to open.
    const unlabeled = [...new Set(ids.filter((id) => !byId.has(id)))].slice(0, SEARCH_SESSION_CAP);
    if (unlabeled.length && await framesListed()) {
      const frameRows = await runAnalyticsQuery({
        root: FRAME_ROOT,
        timeRange: rangeToTimeRange(labelRange),
        filter: { pred: { field: FRAME("TRACE_ID"), op: "OPERATOR_IN", value: unlabeled } },
        output: {
          dimensions: [{ field: FRAME("TRACE_ID"), outputAlias: "session_id" }],
          metrics: [
            { outputAlias: "agent_type", computed: { field: FRAME("AGENT_TYPE"), op: "AGGREGATE_OP_MAX" } },
            { outputAlias: "provider", computed: { field: FRAME("PROVIDER"), op: "AGGREGATE_OP_MAX" } },
            { outputAlias: "model", computed: { field: FRAME("MODEL"), op: "AGGREGATE_OP_MAX" } },
            { outputAlias: "endpoint_id", computed: { field: FRAME("ENDPOINT_ID"), op: "AGGREGATE_OP_MAX" } },
          ],
        },
        limit: unlabeled.length,
      }).then(rowsToObjects).catch(() => []);
      for (const r of frameRows) {
        if (r?.session_id && !byId.has(r.session_id)) byId.set(r.session_id, { ...r, trace_id: null });
      }
    }
    return rows.map((r) => {
      const l = byId.get(r.session_id);
      if (!l) return r;
      return {
        ...r,
        agent_type: l.agent_type ?? null,
        provider: l.provider ?? null,
        model: l.model ?? null,
        // A channel that already picked a specific turn (a naming call, a
        // resolved conversation) keeps it; the label hop only fills the gap.
        trace_id: r.trace_id ?? l.trace_id ?? null,
        endpoint_id: r.endpoint_id ?? l.endpoint_id ?? null,
      };
    });
  } catch {
    return rows; // labels are a nicety; the participants are the finding
  }
}

// How many underlying events a row keeps so the counts can be opened up.
const COUNTERPARTY_EVENT_SAMPLE = 20;

export function foldFileCounterparties(rows = []) {
  const byId = new Map();
  for (const r of rows) {
    const id = r?.session_id;
    if (!id) continue;
    let it = byId.get(id);
    if (!it) {
      it = {
        session_id: id, endpoint_id: r.endpoint_id ?? null,
        writes: 0, reads: 0, commands: 0, first_seen: null, last_seen: null, events: [],
      };
      byId.set(id, it);
    }
    const data = r.data ?? {};
    let kind;
    if (r.event_type === "ProcessStart") { it.commands += 1; kind = "command"; }
    else if (data.first_write_time != null) { it.writes += 1; kind = "write"; }
    else { it.reads += 1; kind = "read"; }
    // Keep the events themselves, bounded. A bare "read 12x" is not a reading —
    // the reader has to be able to open it and see WHICH reads, and when.
    if (it.events.length < COUNTERPARTY_EVENT_SAMPLE) {
      const d = data;
      it.events.push({
        at: r.event_time ?? null,
        kind,
        detail: (kind === "command" ? d?.command_line : d?.filepath) || "",
      });
    }
    const t = r.event_time ?? null;
    if (t && (it.first_seen == null || t < it.first_seen)) it.first_seen = t;
    if (t && (it.last_seen == null || t > it.last_seen)) it.last_seen = t;
  }
  // Authors first, then by total involvement — the counterparty a reader most
  // wants is whoever wrote the file, and a pure reader is the weaker signal.
  return [...byId.values()].sort((a, b) =>
    (b.writes - a.writes)
    || ((b.reads + b.commands) - (a.reads + a.commands))
    || String(a.session_id).localeCompare(String(b.session_id)));
}

// --- The other two Elsewhere channels, and the response fetch --------------
//
// Field builders for the normalized conversation model (typed facts graph).
// These entities exist only where the deployment has the typed graph enabled;
// everywhere else a query against them errors and the caller must degrade to
// "channel unavailable", never to "channel empty".
const CONV_SEARCH = (field) => ({ stepAlias: "s", field: `DIMENSION_FIELD_CONVERSATION_SEARCH_${field}` });
const CONV_MSG = (field) => ({ stepAlias: "m", field: `DIMENSION_FIELD_CONVERSATION_MESSAGE_${field}` });
const CONV_PART = (field) => ({ stepAlias: "mp", field: `DIMENSION_FIELD_CONVERSATION_MESSAGE_PART_${field}` });
const INVOCATION = (field) => ({ stepAlias: "mi", field: `DIMENSION_FIELD_MODEL_INVOCATION_${field}` });

// The TOOL-CALL channel: enriched prompt rows whose `resource_paths` name the
// file. Same bounding contract as fileCounterpartiesSpec — null without a
// window, endpoint-scoped unless the caller widened, never a wider window.
// Each hit is a TURN (an openable trace id), which is evidence the OS channel
// has to reconstruct and this one gets for free.
export function fileMentionTurnsSpec({
  path, endpointId = null, startTime = null, endTime = null,
  allEndpoints = false, limit = 500,
} = {}) {
  const base = String(path || "").replace(/\\/g, "/").split("/").filter(Boolean).pop();
  if (!base || !startTime || !endTime) return null;
  const P = (field) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${field}` });
  const preds = [
    // Element-wise over the array column; ILIKE for the same reason the OS
    // channel uses it — Windows paths are case-insensitive.
    { pred: { field: P("RESOURCE_PATHS"), op: "OPERATOR_ILIKE", value: likeContainsPattern(base) } },
  ];
  if (endpointId && !allEndpoints) {
    preds.push({ pred: { field: P("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } });
  }
  return {
    root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
    timeRange: rangeToTimeRange({ from: startTime, to: endTime }),
    filter: { andExpr: { children: preds } },
    output: {
      dimensions: [
        { field: P("NOTIFICATION_ID"), outputAlias: "session_id" },
        { field: P("ENDPOINT_ID"), outputAlias: "endpoint_id" },
        { field: P("EVENT_ID"), outputAlias: "trace_id" },
        { field: P("EVENT_TIMESTAMP"), outputAlias: "at" },
        { field: P("AGENT_TYPE"), outputAlias: "agent_type" },
        { field: P("PROVIDER"), outputAlias: "provider" },
        { field: P("MODEL"), outputAlias: "model" },
      ],
    },
    orderBy: [{ outputAlias: "at", direction: "DIRECTION_ASC" }],
    limit,
  };
}

// The CONTENT channel: the conversation model's search view, which indexes
// message text, tool results, and — since responses are captured — assistant
// output. This is the only channel that can see a file's NAME in words the
// agent said, rather than in telemetry the agent emitted.
//
// The view is a parameterized text search: the planner REQUIRES exactly one
// conjunctive LIKE/ILIKE on SEARCH_TEXT and extracts it as the index term, so
// the text predicate here is load-bearing, not an optimization.
export function conversationFileSearchSpec({
  path, endpointId = null, startTime = null, endTime = null,
  // The gateway bounds this entity to 200 rows per query and rejects more —
  // the cap is the server's, not a choice made here.
  allEndpoints = false, limit = 200, pad = true,
} = {}) {
  const base = fileBasenameOf(path);
  if (!base || !startTime || !endTime) return null;
  return conversationTextSearchSpec({
    term: likeContainsPatternCS(base), endpointId, startTime, endTime, allEndpoints, limit, pad,
  });
}

// One probe of the content-overlap search: does any conversation in the window
// carry this literal line of the file's content? The LIKE escaping matters —
// real file lines contain `%`, `_` and `\`, and an unescaped probe silently
// widens into a pattern that proves nothing.
export function contentProbeSpec({
  probe, endpointId = null, startTime = null, endTime = null,
  allEndpoints = false, limit = 200, pad = true,
} = {}) {
  const term = String(probe || "").trim();
  if (!term || !startTime || !endTime) return null;
  return conversationTextSearchSpec({
    term: likeContainsPatternCS(term), endpointId, startTime, endTime, allEndpoints, limit, pad,
  });
}

// Fingerprint-index carriages are stamped at their first observation, so a
// tight window on the index drops counterparts that first carried a string
// earlier. Their reads pad to the full allowance; conversation search content
// is re-stamped by later captures and takes the turn bound below instead.
//
// The gateway bounds this entity's time range to 30 days TOTAL (rejects
// wider), so the pad fills that allowance around the window rather than
// adding to it: a conversation older than ~15 days relative to a short
// window stays out of reach, and that is the server's line, not ours.
const CONVERSATION_SPAN_MAX_MS = 30 * 86400 * 1000;

function paddedConversationRange(startTime, endTime) {
  const from = Date.parse(startTime ?? "");
  const to = Date.parse(endTime ?? "");
  if (!Number.isFinite(from) || !Number.isFinite(to)) return { from: startTime, to: endTime };
  const slack = Math.max(0, Math.floor((CONVERSATION_SPAN_MAX_MS - (to - from)) / 2));
  return {
    from: new Date(from - slack).toISOString(),
    to: new Date(to + slack).toISOString(),
  };
}

const TURN_REACH_BACK_MS = 7 * 86400 * 1000;
const TURN_REACH_FORWARD_MS = 86400 * 1000;

export function turnBoundConversationRange(startTime, endTime) {
  const from = Date.parse(startTime ?? "");
  const to = Date.parse(endTime ?? "");
  if (!Number.isFinite(from) || !Number.isFinite(to)) return { from: startTime, to: endTime };
  const room = Math.max(0, CONVERSATION_SPAN_MAX_MS - (to - from));
  const back = Math.min(TURN_REACH_BACK_MS, room);
  const forward = Math.min(TURN_REACH_FORWARD_MS, room - back);
  return {
    from: new Date(from - back).toISOString(),
    to: new Date(to + forward).toISOString(),
  };
}

// The spawn-lineage join's front door: a spawned conversation OPENS with the
// prompt its parent issued, and the conversation model indexes that opening
// as a role=user, first-part, first-chunk search row that names the child's
// conversation. One prefix search over the window the child must have opened
// in resolves it (traceThreadsWiring picks the hit and reads its record).
//
// The window IS the read's cost. Search rows are stamped with their
// conversation's origin time, the entity's time range is the dominant term
// of an indexed text search, and the gateway refuses a read past its byte
// ceiling - a three-day window was refused live on the dogfood tenant. So
// the caller passes the bounds it can prove (the record's capture dates
// around the spawn, or a reach around its estimate), never a padded
// allowance, and narrows on an over-scan rejection. The reader-side
// reconstruction in traceThreadsWiring stays as the fallback wherever the
// surface is refused, so a rejection here returns ok:false with a reason
// rather than throwing.
// How a Codex child conversation opens: a plaintext header naming its task,
// then the encrypted assignment. `Message Type: MESSAGE` headers carry later
// messages into the same thread, so only NEW_TASK is the opening.
const CODEX_TASK_HEADER = "Message Type: NEW_TASK\nTask name: ";

export function spawnOpeningSearchSpec({ prompt, taskName = null, endpointId = null, fromMs = null, toMs = null } = {}) {
  // First line of the prompt, original case (case-sensitive LIKE keeps the
  // ngram prefilter). The matcher floor mirrors matchesSpawnPrompt's: below
  // ~20 chars a prefix proves nothing. A Codex child's prompt is encrypted and
  // its first line is the same for every child, so a task name matches the
  // header through its task line instead - the path that names the child.
  const task = String(taskName || "").trim();
  const line = task ? `${CODEX_TASK_HEADER}${task}\n` : String(prompt || "").split("\n")[0].trim().slice(0, 160);
  if (line.length < 20 || !Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return null;
  const escaped = likeContainsPatternCS(line).slice(1, -1);
  // The entity's own 30-day allowance bounds the window; a wider ask is
  // clipped forward from its start rather than refused by the gateway.
  const end = Math.min(toMs, fromMs + CONVERSATION_SPAN_MAX_MS);
  const preds = [
    { pred: { field: CONV_SEARCH("TEXT"), op: "OPERATOR_LIKE", value: `${escaped}%` } },
  ];
  if (endpointId) {
    preds.push({ pred: { field: CONV_SEARCH("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } });
  }
  return {
    root: { alias: "s", entity: "ENTITY_TYPE_CONVERSATION_SEARCH_CONTENT" },
    timeRange: rangeToTimeRange({ from: new Date(fromMs).toISOString(), to: new Date(end).toISOString() }),
    filter: { andExpr: { children: preds } },
    output: {
      dimensions: [
        { field: CONV_SEARCH("SOURCE_EVENT_ID"), outputAlias: "source_event_id" },
        { field: CONV_SEARCH("TRACE_ID"), outputAlias: "session_id" },
        { field: CONV_SEARCH("ROLE"), outputAlias: "role" },
        { field: CONV_SEARCH("PART_ORDINAL"), outputAlias: "part_ordinal" },
        { field: CONV_SEARCH("CHUNK_ORDINAL"), outputAlias: "chunk_ordinal" },
        { field: CONV_SEARCH("EVENT_TIME"), outputAlias: "at" },
      ],
    },
    orderBy: [{ outputAlias: "at", direction: "DIRECTION_ASC" }],
    limit: 200,
  };
}

function conversationTextSearchSpec({ term, endpointId, startTime, endTime, allEndpoints, limit, pad = true }) {
  const preds = [
    { pred: { field: CONV_SEARCH("TEXT"), op: "OPERATOR_LIKE", value: term } },
  ];
  if (endpointId && !allEndpoints) {
    preds.push({ pred: { field: CONV_SEARCH("ENDPOINT_ID"), op: "OPERATOR_EQ", value: endpointId } });
  }
  return {
    root: { alias: "s", entity: "ENTITY_TYPE_CONVERSATION_SEARCH_CONTENT" },
    timeRange: rangeToTimeRange(pad ? turnBoundConversationRange(startTime, endTime) : { from: startTime, to: endTime }),
    filter: { andExpr: { children: preds } },
    output: {
      dimensions: [
        // The conversation trace id IS the session notification id — the model
        // derives both from the same identity, verified against live data.
        { field: CONV_SEARCH("TRACE_ID"), outputAlias: "session_id" },
        { field: CONV_SEARCH("ENDPOINT_ID"), outputAlias: "endpoint_id" },
        { field: CONV_SEARCH("ROLE"), outputAlias: "role" },
        { field: CONV_SEARCH("PART_KIND"), outputAlias: "part_kind" },
        { field: CONV_SEARCH("EVENT_TIME"), outputAlias: "at" },
        { field: CONV_SEARCH("SNIPPET"), outputAlias: "snippet" },
      ],
    },
    orderBy: [{ outputAlias: "at", direction: "DIRECTION_ASC" }],
    limit,
  };
}

// --- The captured response for one trace ------------------------------------

// How long after the request a response is still credited to it. Agent turns
// run long (a single streamed response measured at 90+ seconds; tool-using
// turns run far longer before the next request), but an unbounded search
// would credit the NEXT turn's response to a request whose own response was
// never observed. The next captured prompt in the session bounds it exactly;
// this cap only decides how far to look when there is no next prompt.
const RESPONSE_WINDOW_MS = 6 * 3600 * 1000;

// The assistant message that answers the focal request: the FIRST one at or
// after the request's capture, and before the session's next request if one is
// known. Rows arrive with ReplacingMergeTree duplicates, so dedupe first.
export function pickResponseMessage(rows = [], traceTsMs = null, nextPromptTsMs = null) {
  const byId = new Map();
  for (const r of rows || []) {
    if (r?.message_id) byId.set(r.message_id, r);
  }
  const SKEW_MS = 2000;
  const candidates = [...byId.values()]
    .map((r) => ({ ...r, atMs: parseServerTimestamp(r.at) }))
    .filter((r) => Number.isFinite(r.atMs))
    .sort((a, b) => a.atMs - b.atMs);
  for (const c of candidates) {
    if (Number.isFinite(traceTsMs) && c.atMs < traceTsMs - SKEW_MS) continue;
    if (Number.isFinite(nextPromptTsMs) && c.atMs > nextPromptTsMs + SKEW_MS) return null;
    return c;
  }
  return null;
}

// The listing carries scalar text; selected tool parts carry a typed detail.
export function responsePartsFromRows(rows = []) {
  const byKey = new Map();
  for (const r of rows || []) {
    const ord = Number(r?.ordinal ?? NaN);
    if (!Number.isFinite(ord)) continue;
    byKey.set(ord, r);
  }
  return [...byKey.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, r]) => {
      const body = conversationPartToBlock(r.body);
      const text = typeof r.text === "string" ? r.text
        : r.kind === "reasoning" && typeof body?.thinking === "string" ? body.thinking
        : typeof body?.text === "string" ? body.text : "";
      return { kind: String(r.kind || ""), text, body };
    });
}

// Assistant-message times for one conversation — the anchor source for the
// approximate turn clock (traceTurnTimes). Rows arrive with representation
// duplicates (the same reply captured from a request echo AND a transcript),
// so dedupe by message id; ascending, bounded to the focal exchange.
export function foldMessageTimes(rows = [], { maxMs = null } = {}) {
  const byId = new Map();
  for (const r of rows || []) {
    if (r?.message_id) byId.set(r.message_id, r);
  }
  return [...byId.values()]
    .map((r) => parseServerTimestamp(r.at))
    .filter((t) => Number.isFinite(t) && (maxMs == null || t <= maxMs))
    .sort((a, b) => a - b);
}

// How far back the message-time query reaches. Long-running conversations are
// real (weeks), but the band only needs anchors for the turns it can draw —
// and 30 days is already the labelling window elsewhere in this panel.
const TURN_TIME_LOOKBACK_MS = 30 * 86400 * 1000;
// The gateway bounds conversation entities to 200 rows; newest-first keeps the
// densest tail of a long conversation, which is where pairing starts.
const TURN_TIME_LIMIT = 200;

export async function fetchConversationTurnTimes({ sessionId, timestamp, signal = null } = {}) {
  const tsMs = typeof timestamp === "number" ? timestamp : parseServerTimestamp(timestamp);
  if (!sessionId || !Number.isFinite(tsMs)) return { ok: false, times: [] };
  const iso = (t) => new Date(t).toISOString();
  try {
    const rows = rowsToObjects(await gatedAnalyticsRead({
      root: { alias: "m", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE" },
      timeRange: rangeToTimeRange({ from: iso(tsMs - TURN_TIME_LOOKBACK_MS), to: iso(tsMs + 3600 * 1000) }),
      filter: { andExpr: { children: [
        { pred: { field: CONV_MSG("TRACE_ID"), op: "OPERATOR_EQ", value: sessionId } },
        { pred: { field: CONV_MSG("ROLE"), op: "OPERATOR_EQ", value: "assistant" } },
      ] } },
      output: {
        dimensions: [
          { field: CONV_MSG("MESSAGE_ID"), outputAlias: "message_id" },
          { field: CONV_MSG("SOURCE_EVENT_ID"), outputAlias: "source_id" },
          { field: CONV_MSG("MESSAGE_AT"), outputAlias: "at" },
        ],
      },
      orderBy: [{ outputAlias: "at", direction: "DIRECTION_DESC" }],
      limit: TURN_TIME_LIMIT,
    }, signal));
    // Anchors must not include replies from AFTER the focal exchange — the
    // session keeps running past this trace, and a later reply would stretch
    // the axis into work that is not on it.
    return { ok: true, times: foldMessageTimes(rows, { maxMs: tsMs + 3600 * 1000 }) };
  } catch (err) {
    return { ok: false, times: [], error: String(err?.message || err) };
  }
}

// When messages happened, by id: one read for one session's messages (the
// gateway takes conversation messages only by an exact session), at most what
// fills its 200 conversation rows with duplicate captures, on the messages'
// own clock (MESSAGE_AT). Conversation entities are windowed by their
// session's start, which the caller's window [fromMs, toMs] must hold; it
// runs an hour past toMs. A message captured twice keeps its earliest time.
// Throws on a refused read, so the caller can ask again.
export const MESSAGE_TIME_BATCH = 60;
export async function fetchMessageTimes({ session, messageIds = [], fromMs, toMs, signal = null } = {}) {
  const out = new Map();
  const ids = [...new Set(messageIds.filter(Boolean))].slice(0, MESSAGE_TIME_BATCH);
  if (!session || !ids.length || !Number.isFinite(fromMs) || !Number.isFinite(toMs)) return out;
  const iso = (t) => new Date(t).toISOString();
  const rows = rowsToObjects(await gatedAnalyticsRead({
    root: { alias: "m", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE" },
    timeRange: rangeToTimeRange({ from: iso(fromMs), to: iso(toMs + 3600 * 1000) }),
    filter: { andExpr: { children: [
      { pred: { field: CONV_MSG("TRACE_ID"), op: "OPERATOR_EQ", value: session } },
      { pred: { field: CONV_MSG("MESSAGE_ID"), op: "OPERATOR_IN", value: ids } },
    ] } },
    output: {
      dimensions: [
        { field: CONV_MSG("MESSAGE_ID"), outputAlias: "message_id" },
        { field: CONV_MSG("MESSAGE_AT"), outputAlias: "at" },
      ],
    },
    limit: TURN_TIME_LIMIT,
  }, signal));
  for (const r of rows) {
    const at = parseServerTimestamp(r.at);
    const id = String(r.message_id || "").toLowerCase();
    if (id && Number.isFinite(at) && !(out.get(id) <= at)) out.set(id, at);
  }
  return out;
}

// Whether the gateway refused a read for scanning too much, in ANY of the
// phrasings it has used. The executor owns this prose and has rewritten it
// once already: the byte guard used to say "too many bytes" and now says
// "query exceeded an execution limit (read X against a Y ceiling)". Matching
// the old wording alone silently disabled every narrower-window retry below
// the moment the message changed, so the caller failed hard where it was
// designed to degrade. Match the whole family, and treat a new phrasing as
// the expected outcome rather than an impossible one.
//
// The stable half of each phrasing is what is matched here; the amounts and
// the remediation list vary by entity and by which ceiling fired.
// How many files one getFileSpread read asks about: each is one more
// predicate aggregate over the range's requests. And how many of those reads
// run at once: each is a wide aggregate over the whole range, and the files a
// workspace's sessions share can run to hundreds.
const FILE_SPREAD_CHUNK = 32;
const FILE_SPREAD_PARALLEL = 2;

export function isOverScanRejection(err) {
  const message = String(err?.message || err).toLowerCase();
  // One message covers all three ceilings: the executor normalizes rows,
  // bytes and memory into the same rejection, so there is no per-ceiling
  // wording to match. The legacy phrasing stays for a gateway that has not
  // been redeployed past #1242.
  return message.includes("exceeded an execution limit")
    || message.includes("too many bytes");
}

// Run the padded form of a conversation search; when the gateway rejects it
// for scanning too much (index pruning does not always engage across the
// entity's full 30-day allowance), fall back to the caller's own window
// rather than failing — a narrower answer beats none, and the guard's cost
// model is the server's to change, not ours to predict.
// Sessions whose captured conversation text contains `term`, for the trace
// log's bare-word search. Distinct session ids only: the caller ORs them into a
// filter, it does not render these rows.
export async function conversationSessionsMatching({ term, startTime, endTime, limit = 200 } = {}) {
  // The trace log's range runs to local end-of-day, so in UTC it can exceed the
  // 30 days this entity accepts and the gateway rejects the whole read. Keep the
  // newest 30 days rather than losing the search.
  const from = Date.parse(startTime ?? "");
  const to = Date.parse(endTime ?? "");
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];
  const clampedFrom = Math.max(from, to - CONVERSATION_SPAN_MAX_MS);
  const spec = conversationTextSearchSpec({
    term: likeContainsPatternCS(term),
    startTime: new Date(clampedFrom).toISOString(),
    endTime: new Date(to).toISOString(),
    allEndpoints: true,
    limit,
  });
  if (!spec) return [];
  const rows = rowsToObjects(await runAnalyticsQuery(spec));
  return [...new Set(rows.map((row) => String(row.session_id || "")).filter(Boolean))];
}

async function runPaddedSearchWithRetry(buildSpec) {
  const padded = buildSpec(true);
  if (!padded) return null;
  try {
    return await runAnalyticsQuery(padded);
  } catch (err) {
    if (!isOverScanRejection(err)) throw err;
    return runAnalyticsQuery(buildSpec(false));
  }
}

// The whole-trace needle lens: which sessions carry this trace's distinctive
// content? The caller derives side-tagged needles from every loaded turn
// (traceNeedleLens.deriveTraceNeedles) — no focal turn, no file pivot — and
// this runs one bounded index search per needle, tenant-wide. Tenant breadth
// is deliberate: a hand-off's whole point is that the counterpart can live
// under another endpoint, and a needle is selective enough that breadth is
// cheap. The raw-bodies fallback is NOT run here — at this needle count it
// would multiply the expensive scan class; the file panel keeps it for the
// focused case.
//
// A DIRECT export, not a dataClient method, exactly like fetchTraceResponse:
// dataClient.query() de-duplicates in-flight calls by JSON-serialized params,
// and an AbortSignal serializes to nothing — two callers would share one
// execution bound to the FIRST caller's signal, so the first caller's
// cleanup cancelled everyone (found live: a fresh Lanes open rendered
// "cancelled" from a predecessor's abort).
//
// Returns { rows, focal, of, diffuse, ok } — candidates one per session with
// sent/received direction evidence, the focal session split out. The signal
// stops UNSTARTED probes: a reader switching traces abandons the batch, and
// the queue must not keep spending tenant-wide searches on a question nobody
// is asking.
export async function searchTraceHandoffCandidates({ needles = [], sessionId = null, startTime = null, endTime = null, focalSpan = null, focalEndpointId = null, laneStrings = [], handoffStrings = [], signal = null } = {}) {
  const asked = (Array.isArray(needles) ? needles : []).filter((n) => n && n.needle);
  if (!asked.length || !startTime || !endTime) return { rows: [], focal: null, ok: false };
  // Discovery prefers the content-fingerprint index: bounded anchored reads
  // instead of one tenant-wide ILIKE per needle. The fallback is whole-path
  // or nothing — entity absent on an older backend, an un-indexed trace, or
  // a transport failure probes the derived needles exactly as before the
  // index existed; an indexed answer is authoritative, and no leftover
  // needle buys its own probe (measured: the index's recall strictly
  // dominated the probes', and leftover derived needles matched nothing).
  // The index is a faster route to the same claim, never a new failure mode
  // for the dock.
  let fpOut = null;
  if (await fingerprintEntityAvailable()) {
    try {
      // The index stamps a (fingerprint, trace) pair at its FIRST
      // observation, the same origin-time semantics that make the probes pad
      // their window — so the fp reads must speak the padded window too, or
      // a counterpart that first carried the string before a short session
      // span silently vanishes from the index path while the probe path
      // still finds it (found live: 34 sessions dropped to 14).
      const { from, to } = paddedConversationRange(startTime, endTime);
      fpOut = await runFpNeedleJoin({ sessionId, startTime: from, endTime: to, laneStrings, handoffStrings, signal });
      if (signal?.aborted) return { rows: [], focal: null, ok: false, error: "cancelled" };
      // "Un-indexed" is dominated by history the backfill has not reached:
      // the index is written by an in-cluster materialized view at ingest.
      // Under sustained ingest backpressure the index lags with the rest of
      // the pipeline (docs/scale-systems.md measures multi-hour drains under
      // burst), so a fresh trace CAN reach this fallback in that state — at
      // exactly the probe cost the dock paid before the index existed. A
      // freshness gate here could not do better: the client has no index
      // watermark to compare against, and this fallback already is the
      // degradation path.
      if (!fpOut?.indexed) fpOut = null;
    } catch {
      if (signal?.aborted) return { rows: [], focal: null, ok: false, error: "cancelled" };
      fpOut = null;
    }
  }
  // The index IS the string list: its shared-fingerprint entries replace
  // the derived needles wholesale, so the client never reconciles two
  // implementations of the same extraction, and no string ever buys a
  // per-needle tenant-wide search. The derived needles survive only for
  // the WHOLE-PATH fallback (entity absent on an older backend, un-indexed
  // trace, transport failure), where probes are the only route to any
  // answer at all. An indexed trace with an empty list is the honest
  // "nothing is shared", never a fallback trigger.
  const folded = fpOut ? fpOut.needles : asked;
  const results = fpOut ? fpOut.results.slice() : new Array(asked.length);
  const pending = [];
  if (!fpOut) {
    for (let i = 0; i < asked.length; i++) pending.push(i);
  }
  let next = 0;
  const worker = async () => {
    while (next < pending.length) {
      const i = pending[next++];
      if (signal?.aborted) {
        results[i] = { ok: false, error: "cancelled" };
        continue;
      }
      results[i] = await runPaddedSearchWithRetry((pad) => contentProbeSpec({
        probe: asked[i].needle, allEndpoints: true, startTime, endTime, pad,
      }))
        .then((r) => (r == null ? { ok: false, error: "unbounded" } : { ok: true, rows: rowsToObjects(r) }))
        .catch((err) => ({ ok: false, error: String(err?.message || err) }));
    }
  };
  try {
    // Concurrency 4 is per BATCH, and at most one batch runs per open lanes
    // view (per-trace cache, refcounted shared flight, abort on switch), on
    // a surface behind the per-browser experimental opt-in — this is not an
    // ungated fleet-wide multiplier.
    await Promise.all(Array.from({ length: Math.min(4, pending.length) }, worker));
    if (signal?.aborted) {
      return { rows: [], focal: null, ok: false, error: "cancelled" };
    }
    if (!fpOut && results.every((r) => !r?.ok)) {
      return { rows: [], focal: null, ok: false, error: results[0]?.error || "search unavailable" };
    }
    const { rows, focal, of, diffuse } = foldTraceNeedleMatches(folded, results, {
      focalSessionId: sessionId, focalSpan, focalEndpointId,
      sessionInventory: fpOut?.sessions || null,
    });
    const labelled = await labelCounterpartySessions(focal ? [focal, ...rows] : rows, { startTime, endTime });
    for (const row of labelled) {
      if (row.sameEndpoint == null && row.endpoint_id != null && focalEndpointId != null) {
        row.sameEndpoint = String(row.endpoint_id) === String(focalEndpointId);
      }
    }
    const focalOut = focal ? labelled.shift() : null;
    const via = fpOut ? "fingerprints" : "content-probes";
    return {
      rows: labelled, focal: focalOut, of,
      diffuse: fpOut?.diffuseTotal ?? diffuse,
      ok: true, via, needles: folded,
    };
  } catch (err) {
    return { rows: [], focal: null, ok: false, error: String(err?.message || err) };
  }
}

// The full counterpart chunk behind one join-pin preview. The search rows
// deliberately carry only the stored head-of-chunk snippet (search_text is a
// wide column the list projection must not drag; the schema exports that
// choice and expects per-row follow-ups), so a match that sits deeper in the
// chunk cannot be shown from the list alone. This fetches ONE conversation's
// matching chunks, trace-bound, and returns the first that actually contains
// the needle — preferring one that is not harness framing. Sensitive-read
// gated like every raw text read: ok:false means show the snippet head, not
// an error.
export async function fetchJoinMirrorWindow({ sessionId, needle, startTime, endTime } = {}) {
  if (!sessionId || !needle || !startTime || !endTime) return { ok: false };
  try {
    const rows = rowsToObjects(await runPaddedSearchWithRetry((pad) => ({
      root: { alias: "s", entity: "ENTITY_TYPE_CONVERSATION_SEARCH_CONTENT" },
      timeRange: rangeToTimeRange(pad ? turnBoundConversationRange(startTime, endTime) : { from: startTime, to: endTime }),
      filter: { andExpr: { children: [
        { pred: { field: CONV_SEARCH("TEXT"), op: "OPERATOR_LIKE", value: likeContainsPatternCS(needle) } },
        { pred: { field: CONV_SEARCH("TRACE_ID"), op: "OPERATOR_EQ", value: sessionId } },
      ] } },
      output: {
        dimensions: [
          { field: CONV_SEARCH("TEXT"), outputAlias: "text" },
          { field: CONV_SEARCH("ROLE"), outputAlias: "role" },
          { field: CONV_SEARCH("PART_KIND"), outputAlias: "kind" },
          { field: CONV_SEARCH("EVENT_TIME"), outputAlias: "at" },
        ],
      },
      orderBy: [{ outputAlias: "at", direction: "DIRECTION_ASC" }],
      // Enough rows to COUNT the counterpart's footprint by side, not just
      // pick one excerpt — the index path has no per-occurrence rows of its
      // own, so this read is where "2 in responses - 3 in tool results"
      // comes from. Still one conversation, still bounded.
      limit: 48,
    })));
    const holding = rows.filter((r) => String(r.text || "").includes(needle));
    const best = holding.find((r) => !looksInjected(r.text)) || holding[0];
    if (!best) return { ok: false };
    const counts = { prompts: 0, responses: 0, tools: 0 };
    for (const r of holding) {
      const role = String(r.role || "");
      const kind = String(r.kind || "");
      if (role === "assistant") counts.responses += 1;
      else if (role === "tool" || kind === "tool_result") counts.tools += 1;
      else counts.prompts += 1;
    }
    return {
      ok: true, text: String(best.text), role: String(best.role || ""),
      kind: String(best.kind || ""), at: best.at ?? null, counts,
    };
  } catch {
    return { ok: false };
  }
}

export async function fetchTraceResponse({ traceId, sessionId, timestamp, signal = null } = {}) {
  const tsMs = typeof timestamp === "number" ? timestamp : parseServerTimestamp(timestamp);
  if (!traceId || !sessionId || !Number.isFinite(tsMs)) {
    return { status: "unavailable", reason: "missing trace identity" };
  }
  const iso = (t) => new Date(t).toISOString();
  const E = (field) => ({ stepAlias: "e", field: `DIMENSION_FIELD_EVENT_${field}` });

  // Whether a response was observed at all, from the invocation record. This
  // is also the availability probe: on a deployment without the conversation
  // model the query errors, and the whole reading reports unavailable rather
  // than "no response", which would be a claim about the agent.
  let observed = false;
  try {
    const invRows = rowsToObjects(await gatedAnalyticsRead({
      root: { alias: "mi", entity: "ENTITY_TYPE_MODEL_INVOCATION" },
      timeRange: rangeToTimeRange({ from: iso(tsMs - 3600 * 1000), to: iso(tsMs + RESPONSE_WINDOW_MS) }),
      // The invocation entity is a TRACE-BOUND view: the planner requires a
      // conjunctive EQ on its trace id (the conversation = the session) and
      // rejects the query without one. SOURCE_EVENT_ID then narrows to the
      // focal request within that conversation.
      filter: { andExpr: { children: [
        { pred: { field: INVOCATION("TRACE_ID"), op: "OPERATOR_EQ", value: sessionId } },
        { pred: { field: INVOCATION("SOURCE_EVENT_ID"), op: "OPERATOR_EQ", value: traceId } },
      ] } },
      output: {
        dimensions: [
          { field: INVOCATION("RESPONSE_OBSERVED"), outputAlias: "response_observed" },
          { field: INVOCATION("RESPONSE_ID"), outputAlias: "response_id" },
          { field: INVOCATION("STATUS"), outputAlias: "status" },
        ],
      },
      limit: 8,
    }, signal));
    observed = invRows.some((r) => r?.response_observed === true || r?.response_observed === "true" || r?.response_observed === 1);
  } catch (err) {
    return { status: "unavailable", reason: String(err?.message || err) };
  }

  // The next captured prompt bounds the response window exactly; without it a
  // later turn's reply could be credited to this request.
  let nextPromptTsMs = null;
  try {
    const nextRows = rowsToObjects(await gatedAnalyticsRead({
      root: { alias: "e", entity: "ENTITY_TYPE_EVENT" },
      timeRange: rangeToTimeRange({ from: iso(tsMs + 1), to: iso(tsMs + RESPONSE_WINDOW_MS) }),
      filter: { andExpr: { children: [
        { pred: { field: E("EVENT_TYPE"), op: "OPERATOR_EQ", value: "AiPrompt" } },
        { pred: { field: E("NOTIFICATION_ID"), op: "OPERATOR_EQ", value: sessionId } },
      ] } },
      output: { dimensions: [{ field: E("EVENT_TIMESTAMP"), outputAlias: "at" }] },
      orderBy: [{ outputAlias: "at", direction: "DIRECTION_ASC" }],
      limit: 1,
    }, signal));
    const t = parseServerTimestamp(nextRows[0]?.at);
    if (Number.isFinite(t)) nextPromptTsMs = t;
  } catch {
    // Bounding is an upgrade; the pick falls back to the fixed window.
  }

  try {
    return await traceResponseFromRecord({ sessionId, tsMs, nextPromptTsMs, observed, signal });
  } catch (err) {
    if (!err?.recordRouteMissing) return { status: "unavailable", reason: String(err?.message || err) };
  }

  try {
    const msgRows = rowsToObjects(await gatedAnalyticsRead({
      root: { alias: "m", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE" },
      timeRange: rangeToTimeRange({ from: iso(tsMs - 60 * 1000), to: iso(tsMs + RESPONSE_WINDOW_MS) }),
      filter: { andExpr: { children: [
        { pred: { field: CONV_MSG("TRACE_ID"), op: "OPERATOR_EQ", value: sessionId } },
        { pred: { field: CONV_MSG("ROLE"), op: "OPERATOR_EQ", value: "assistant" } },
      ] } },
      output: {
        dimensions: [
          { field: CONV_MSG("MESSAGE_ID"), outputAlias: "message_id" },
          { field: CONV_MSG("SOURCE_EVENT_ID"), outputAlias: "source_id" },
          { field: CONV_MSG("MESSAGE_AT"), outputAlias: "at" },
          { field: CONV_MSG("SOURCE_CAPTURE"), outputAlias: "capture" },
          { field: CONV_MSG("COMPLETENESS"), outputAlias: "completeness" },
        ],
      },
      orderBy: [{ outputAlias: "at", direction: "DIRECTION_ASC" }],
      limit: 12,
    }, signal));
    const msg = pickResponseMessage(msgRows, tsMs, nextPromptTsMs);
    if (!msg) return { status: observed ? "missing" : "none" };

    const partRows = rowsToObjects(await gatedAnalyticsRead({
      root: { alias: "mp", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE_PART" },
      timeRange: rangeToTimeRange({ from: iso(tsMs - 60 * 1000), to: iso(tsMs + RESPONSE_WINDOW_MS) }),
      // Trace-bound like the invocation view: the TRACE_ID predicate is the
      // planner's admission requirement, MESSAGE_ID the actual selection.
      filter: { andExpr: { children: [
        { pred: { field: CONV_PART("TRACE_ID"), op: "OPERATOR_EQ", value: sessionId } },
        { pred: { field: CONV_PART("MESSAGE_ID"), op: "OPERATOR_EQ", value: msg.message_id } },
      ] } },
      output: {
        dimensions: [
          { field: CONV_PART("ORDINAL"), outputAlias: "ordinal" },
          { field: CONV_PART("KIND"), outputAlias: "kind" },
          { field: CONV_PART("TEXT"), outputAlias: "text" },
          { field: CONV_PART("SOURCE_EVENT_ID"), outputAlias: "source_id" },
        ],
      },
      orderBy: [{ outputAlias: "ordinal", direction: "DIRECTION_ASC" }],
      limit: 60,
    }, signal));
    const toolParts = partRows.filter((part) => part.kind === "tool_use");
    if (toolParts.length) {
      if (toolParts.some((part) => !part.source_id)) throw new Error("Captured tool source is unavailable.");
      const contents = await withTraceReadSlot(signal, () => readConversationContent({
        traceId: uuidToEndpointId(sessionId),
        timeRange: rangeToTimeRange({ from: iso(tsMs - 60 * 1000), to: iso(tsMs + RESPONSE_WINDOW_MS) }),
        references: toolParts.map((part) => ({ messagePart: {
          sourceEventId: uuidToEndpointId(part.source_id), messageId: uuidToEndpointId(msg.message_id), ordinal: Number(part.ordinal),
        } })),
      }, { signal }));
      toolParts.forEach((part, index) => { part.body = contents[index].messagePart; });
    }
    const parts = responsePartsFromRows(partRows).filter((p) => p.text || (p.kind === "tool_use" && p.body));
    if (!parts.length) return { status: observed ? "missing" : "none" };
    return {
      status: "captured",
      messageId: msg.message_id, sourceEventId: msg.source_id,
      at: msg.at ?? null,
      capture: msg.capture ?? null,
      completeness: msg.completeness ?? null,
      parts,
    };
  } catch (err) {
    return { status: "unavailable", reason: String(err?.message || err) };
  }
}

const RESPONSE_RECORD_PAGES = 4;
const RESPONSE_RECORD_PAGE_MESSAGES = 256;

async function traceResponseFromRecord({ sessionId, tsMs, nextPromptTsMs, observed, signal }) {
  const iso = (t) => new Date(t).toISOString();
  const traceId = uuidToEndpointId(sessionId);
  const timeRange = rangeToTimeRange({
    from: iso(tsMs - 60 * 1000),
    to: iso(Number.isFinite(nextPromptTsMs) ? nextPromptTsMs - 1 : tsMs + RESPONSE_WINDOW_MS),
  });
  const assistant = [];
  let before = null;
  for (let page = 0; page < RESPONSE_RECORD_PAGES; page++) {
    const read = await withTraceReadSlot(signal, () => fetchConversationRecord({
      traceId, timeRange, limit: RESPONSE_RECORD_PAGE_MESSAGES, ...(before ? { before } : {}),
    }, signal));
    for (const message of read?.messages || []) {
      if (message.role !== "assistant") continue;
      assistant.push({
        message_id: endpointIdToUuid(message.messageId),
        source_id: endpointIdToUuid(message.sourceEventId),
        at: message.messageAt ?? null,
        capture: message.sourceCapture || null,
        completeness: message.completeness || null,
        parts: Array.isArray(message.parts) ? message.parts : [],
      });
    }
    before = read?.next?.observedAt ? read.next : null;
    if (!before) break;
  }
  const msg = pickResponseMessage(assistant, tsMs, nextPromptTsMs);
  if (!msg) return { status: observed ? "missing" : "none" };

  const elided = msg.parts.filter((part) => part.elided);
  if (elided.length) {
    const contents = await withTraceReadSlot(signal, () => readConversationContent({
      traceId,
      timeRange,
      references: elided.map((part) => ({ messagePart: {
        sourceEventId: uuidToEndpointId(msg.source_id), messageId: uuidToEndpointId(msg.message_id), ordinal: Number(part.ordinal),
      } })),
    }, { signal }));
    elided.forEach((part, index) => { part.body = contents[index].messagePart; });
  }
  const parts = responsePartsFromRows(msg.parts.map((part) => ({ ordinal: part.ordinal, kind: part.kind, body: part.body })))
    .filter((p) => p.text || (p.kind === "tool_use" && p.body));
  if (!parts.length) return { status: observed ? "missing" : "none" };
  return {
    status: "captured",
    messageId: msg.message_id, sourceEventId: msg.source_id,
    at: msg.at,
    capture: msg.capture,
    completeness: msg.completeness,
    parts,
  };
}

async function executeFirstSeenByEndpoint() {
  const windowResults = await runChangeTimeWindows({ op: "AGGREGATE_OP_MIN", alias: "first_seen" });
  return { byEndpoint: foldFirstSeenWindows(windowResults) };
}

const RECENT_ENDPOINT_MAX_PAGE_SIZE = 25;
const RECENT_ENDPOINT_WINDOW_SECONDS = 30 * 86400;
const RECENT_STATUS_ID_REF = {
  stepAlias: "es",
  field: "DIMENSION_FIELD_ENDPOINT_STATUS_ENDPOINT_ID",
};
const RECENT_STATUS_RECEIVED_AT_REF = {
  stepAlias: "es",
  field: "DIMENSION_FIELD_ENDPOINT_STATUS_RECEIVED_AT",
};

function recentEndpointCursorFilter(cursor) {
  const receivedAt = cursor?.receivedAt;
  const endpointId = cursor?.endpointId;
  if (!receivedAt || !endpointId) return null;

  return {
    orExpr: {
      children: [
        {
          pred: {
            field: RECENT_STATUS_RECEIVED_AT_REF,
            op: "OPERATOR_LT",
            value: receivedAt,
          },
        },
        {
          andExpr: {
            children: [
              {
                pred: {
                  field: RECENT_STATUS_RECEIVED_AT_REF,
                  op: "OPERATOR_EQ",
                  value: receivedAt,
                },
              },
              {
                pred: {
                  field: RECENT_STATUS_ID_REF,
                  op: "OPERATOR_GT",
                  value: endpointId,
                },
              },
            ],
          },
        },
      ],
    },
  };
}

const RECENT_ENDPOINT_DETAIL_DIMS = [
  { field: EP_ID_REF, outputAlias: "id" },
  { field: { stepAlias: "e", field: F_EP_HOSTNAME }, outputAlias: "hostname" },
  { field: { stepAlias: "e", field: F_EP_OS }, outputAlias: "operating_system" },
  { field: { stepAlias: "e", field: F_EP_OS_PRODUCT }, outputAlias: "os_product_name" },
  { field: { stepAlias: "e", field: F_EP_AGENT_VERSION }, outputAlias: "agent_version" },
];

/**
 * Return one cursor-paginated page of endpoints heard from in the last 30
 * days, plus the metadata needed to render the Connect-page strip.
 *
 * Heartbeat recency belongs to ENDPOINT_STATUS, not endpoint metadata:
 * endpoint_metadata_current is a current snapshot and can remain unchanged
 * while a healthy endpoint continues to beacon. The status query is bounded
 * by the entity's 30-day retention and the explicit relative time range; the
 * second query hydrates only the returned endpoint ids.
 */
export async function executeRecentEndpointPage({
  cursor = null,
  limit = RECENT_ENDPOINT_MAX_PAGE_SIZE,
  includeTotal = true,
} = {}) {
  const pageLimit = Math.max(1, Math.min(RECENT_ENDPOINT_MAX_PAGE_SIZE, Number(limit) || RECENT_ENDPOINT_MAX_PAGE_SIZE));
  const timeRange = { relative: { durationSeconds: String(RECENT_ENDPOINT_WINDOW_SECONDS) } };
  const cursorFilter = recentEndpointCursorFilter(cursor);

  const pageSpec = {
    root: { alias: "es", entity: "ENTITY_TYPE_ENDPOINT_STATUS" },
    timeRange,
    ...(cursorFilter ? { filter: cursorFilter } : {}),
    output: {
      dimensions: [{ field: RECENT_STATUS_ID_REF, outputAlias: "id" }],
      metrics: [{
        outputAlias: "last_seen",
        computed: { field: RECENT_STATUS_RECEIVED_AT_REF, op: "AGGREGATE_OP_MAX" },
      }],
    },
    orderBy: [
      { outputAlias: "last_seen", direction: "DIRECTION_DESC" },
      { outputAlias: "id", direction: "DIRECTION_ASC" },
    ],
    limit: pageLimit,
  };
  const totalSpec = {
    root: { alias: "es", entity: "ENTITY_TYPE_ENDPOINT_STATUS" },
    timeRange,
    output: {
      metrics: [{
        outputAlias: "total",
        computed: { field: RECENT_STATUS_ID_REF, op: "AGGREGATE_OP_COUNT_DISTINCT" },
      }],
    },
  };

  const [pageResult, totalResult] = await Promise.all([
    runAnalyticsQuery(pageSpec).then(rowsToObjects),
    includeTotal ? runAnalyticsQuery(totalSpec).then(rowsToObjects) : Promise.resolve(null),
  ]);
  const statusRows = pageResult
    .map((row) => {
      const lastSeenMs = parseServerTimestamp(row?.last_seen);
      return {
        id: String(row?.id ?? ""),
        lastSeen: Number.isFinite(lastSeenMs) ? new Date(lastSeenMs).toISOString() : null,
      };
    })
    .filter((row) => row.id && row.lastSeen);
  const total = includeTotal ? Number(totalResult?.[0]?.total ?? 0) || 0 : null;

  if (statusRows.length === 0) return { rows: [], total, nextCursor: null };

  const endpointIds = statusRows.map((row) => row.id);
  const detailRows = rowsToObjects(await runAnalyticsQuery({
    root: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" },
    timeRange,
    filter: { pred: { field: EP_ID_REF, op: "OPERATOR_IN", value: endpointIds } },
    output: { dimensions: RECENT_ENDPOINT_DETAIL_DIMS },
    limit: endpointIds.length,
  }));
  const detailsById = new Map(detailRows.map((row) => [String(row?.id ?? ""), row]));
  const rows = statusRows.map(({ id, lastSeen }) => flattenInventoryGraphRow({
    ...(detailsById.get(id) ?? {}),
    id,
    last_updated: lastSeen,
  }));
  const last = statusRows[statusRows.length - 1];

  return {
    rows,
    total,
    nextCursor: statusRows.length === pageLimit
      ? { receivedAt: last.lastSeen, endpointId: last.id }
      : null,
  };
}

// Last-seen-per-endpoint for the Inventory Surface "Last seen" chart — a TRUE
// last-contact: MAX(received_at) per endpoint over ENTITY_TYPE_ENDPOINT_STATUS
// (endpoint_status_current, a ReplacingMergeTree(received_at) read FINAL — the
// server-side receive time of each endpoint's most recent heartbeat). Requires
// backend PR #2162, which registers this entity; until it deploys the query
// 400s and the surface shows the last-seen tab's error state (first-seen, on the
// change log, is unaffected).
//
// This REPLACES the earlier MAX(change_time) proxy: received_at advances on
// every heartbeat, so a healthy-but-stable endpoint no longer reads stale, and a
// zero-AI-activity endpoint still appears as long as it's alive. One row per
// endpoint after FINAL, so no windowing (unlike the append-only change log).
//
// Horizon: endpoint_status_current has a 30-day TTL, so an endpoint not heard
// from in 30 days ages out and has no last-seen — correct for the metric, but it
// means the last-seen population is ≤ the no-TTL metadata_current fleet total.
// Whole-fleet only (the status entity is edge-less → root-only), so the surface
// scopes it client-side by endpoint id (see scopedLastSeenTimestamps); a
// server-side-scoped last-seen would need an edge on the status entity.
async function executeLastSeenByEndpoint() {
  const esEndpointId = { stepAlias: "es", field: "DIMENSION_FIELD_ENDPOINT_STATUS_ENDPOINT_ID" };
  const esReceivedAt = { stepAlias: "es", field: "DIMENSION_FIELD_ENDPOINT_STATUS_RECEIVED_AT" };
  // Full retention window (a touch over the 30-day TTL) — the current-liveness
  // snapshot, independent of the dashboard date range: "last seen" means when we
  // last heard from the endpoint, not activity within the selected range.
  const nowMs = Math.ceil(Date.now() / 60_000) * 60_000;
  const spec = {
    root: { alias: "es", entity: "ENTITY_TYPE_ENDPOINT_STATUS" },
    timeRange: { absolute: { start: new Date(nowMs - 31 * 86400 * 1000).toISOString(), end: new Date(nowMs).toISOString() } },
    output: {
      dimensions: [{ field: esEndpointId, outputAlias: "id" }],
      metrics: [{ outputAlias: "last_seen", computed: { field: esReceivedAt, op: "AGGREGATE_OP_MAX" } }],
    },
  };
  const rows = rowsToObjects(await runAnalyticsQuery(spec));
  const byEndpoint = [];
  for (const r of rows) {
    const id = String(r?.id ?? "");
    const ms = parseServerTimestamp(r?.last_seen);
    if (id && Number.isFinite(ms)) byEndpoint.push({ id, lastSeenMs: ms });
  }
  return { byEndpoint };
}

const BROWSER_CAPTURE_FAMILIES = new Set(["chrome", "firefox"]);
const BROWSER_CAPTURE_COVERAGE_STATES = new Set([
  "unspecified",
  "ready",
  "extension_not_installed",
  "native_host_not_registered",
  "normal_access_denied",
  "private_access_denied",
  "unsupported_browser",
  "degraded",
]);
const BROWSER_CAPTURE_FAILURE_REASONS = new Set([
  "page_injection",
  "validation",
  "redaction",
  "parser",
  "unsupported_route",
  "local_storage",
  "agent_ipc",
  "backend_upload",
]);
const BROWSER_CAPTURE_DROP_REASONS = new Set([
  "queue_full",
  "event_too_large",
  "retry_exhausted",
  "invalid_record",
  "redaction_failed",
]);

function browserCaptureCount(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number < 0) return 0;
  return Math.min(Math.floor(number), Number.MAX_SAFE_INTEGER);
}

function browserCaptureOptionalBoolean(value) {
  return typeof value === "boolean" ? value : null;
}

function shapeBrowserCaptureStatus(value) {
  if (!value || typeof value !== "object" || !BROWSER_CAPTURE_FAMILIES.has(value.browser_family)) return null;
  const counters = value.counters && typeof value.counters === "object" ? value.counters : {};
  const failures = Array.isArray(counters.failures)
    ? counters.failures.slice(0, BROWSER_CAPTURE_FAILURE_REASONS.size).flatMap((entry) =>
        entry && BROWSER_CAPTURE_FAILURE_REASONS.has(entry.reason)
          ? [{ reason: entry.reason, count: browserCaptureCount(entry.count) }]
          : [])
    : [];
  const drops = Array.isArray(counters.drops)
    ? counters.drops.slice(0, BROWSER_CAPTURE_DROP_REASONS.size).flatMap((entry) =>
        entry && BROWSER_CAPTURE_DROP_REASONS.has(entry.reason)
          ? [{
              reason: entry.reason,
              eventCount: browserCaptureCount(entry.event_count),
              byteCount: browserCaptureCount(entry.byte_count),
            }]
          : [])
    : [];
  const timestamp = (raw) => {
    const number = Number(raw);
    return Number.isFinite(number) && number >= 0 ? number : null;
  };
  const version = typeof value.extension_version === "string"
    ? value.extension_version.replace(/[\x00-\x1f\x7f]/g, "").slice(0, 64)
    : "";

  return {
    browserFamily: value.browser_family,
    extensionVersion: version,
    extensionInstalled: browserCaptureOptionalBoolean(value.extension_installed),
    nativeHostRegistered: browserCaptureOptionalBoolean(value.native_host_registered),
    normalAccessAllowed: browserCaptureOptionalBoolean(value.normal_access_allowed),
    privateAccessAllowed: browserCaptureOptionalBoolean(value.private_access_allowed),
    eligibleTabs: browserCaptureCount(value.eligible_tabs),
    hookedTabs: browserCaptureCount(value.hooked_tabs),
    lastLiveCaptureMs: timestamp(value.last_live_capture_time),
    lastReconciliationMs: timestamp(value.last_reconciliation_time),
    lastAgentAcceptMs: timestamp(value.last_agent_accept_time),
    pendingFailedCaptures: browserCaptureCount(value.pending_failed_captures),
    pendingFailedCaptureBytes: browserCaptureCount(value.pending_failed_capture_bytes),
    coverageState: BROWSER_CAPTURE_COVERAGE_STATES.has(value.coverage_state) ? value.coverage_state : "unspecified",
    counters: {
      liveRequestObservations: browserCaptureCount(counters.live_request_observations),
      liveResponseObservations: browserCaptureCount(counters.live_response_observations),
      websocketObservations: browserCaptureCount(counters.websocket_observations),
      reconciliationAttempts: browserCaptureCount(counters.reconciliation_attempts),
      reconciliationRecoveredMessages: browserCaptureCount(counters.reconciliation_recovered_messages),
      reconciliationUnresolvedGaps: browserCaptureCount(counters.reconciliation_unresolved_gaps),
      nativeHostDisconnects: browserCaptureCount(counters.native_host_disconnects),
      protocolVersionRejects: browserCaptureCount(counters.protocol_version_rejects),
      failures,
      drops,
    },
  };
}

async function executeBrowserCaptureHealth({ endpointId } = {}) {
  const id = String(endpointId ?? "").trim();
  if (!id) return { statuses: [], receivedAtMs: null };

  const endpointField = { stepAlias: "es", field: "DIMENSION_FIELD_ENDPOINT_STATUS_ENDPOINT_ID" };
  const receivedAtField = { stepAlias: "es", field: "DIMENSION_FIELD_ENDPOINT_STATUS_RECEIVED_AT" };
  const statusField = { stepAlias: "es", field: "DIMENSION_FIELD_ENDPOINT_STATUS_BROWSER_CAPTURE_STATUS_JSON" };
  const nowMs = Date.now();
  const spec = {
    root: {
      alias: "es",
      entity: "ENTITY_TYPE_ENDPOINT_STATUS",
      stepFilter: [{ pred: { field: endpointField, op: "OPERATOR_EQ", value: id } }],
    },
    timeRange: { absolute: { start: new Date(nowMs - 31 * 86400 * 1000).toISOString(), end: new Date(nowMs).toISOString() } },
    output: {
      dimensions: [
        { field: receivedAtField, outputAlias: "received_at" },
        { field: statusField, outputAlias: "browser_capture_status_json" },
      ],
    },
    orderBy: [{ outputAlias: "received_at", direction: "DIRECTION_DESC" }],
    limit: 1,
  };
  const [row] = rowsToObjects(await runAnalyticsQuery(spec));
  if (!row) return { statuses: [], receivedAtMs: null };

  let parsed;
  try {
    parsed = typeof row.browser_capture_status_json === "string"
      ? JSON.parse(row.browser_capture_status_json)
      : row.browser_capture_status_json;
  } catch {
    parsed = [];
  }
  const statuses = Array.isArray(parsed)
    ? parsed.slice(0, BROWSER_CAPTURE_FAMILIES.size).map(shapeBrowserCaptureStatus).filter(Boolean)
    : [];
  const receivedAtMs = parseServerTimestamp(row.received_at);
  return { statuses, receivedAtMs: Number.isFinite(receivedAtMs) ? receivedAtMs : null };
}

// ── ILO clustering generations (the generation picker) ──────────────
//
// Lists completed/running clustering generations + their algorithm facets for
// the generation picker. Both read ENTITY_TYPE_ILO_CLUSTER_GENERATION — the pin
// DISCOVERY surface, which runQuery's generation pin deliberately skips — and
// both must be FRESH (a just-completed rebuild has to show), so the executes
// pass { cache: false } to bypass the result cache. The entity has no time
// field (rows TTL 30d server-side), so the required timeRange is inert.
const GEN_ALIAS = "g";
const GEN_FIELD = {
  id: "DIMENSION_FIELD_ILO_CLUSTER_GENERATION_GENERATION_ID",
  algorithm: "DIMENSION_FIELD_ILO_CLUSTER_GENERATION_ALGORITHM",
  status: "DIMENSION_FIELD_ILO_CLUSTER_GENERATION_STATUS",
  clusters: "DIMENSION_FIELD_ILO_CLUSTER_GENERATION_CLUSTERS_CREATED",
  events: "DIMENSION_FIELD_ILO_CLUSTER_GENERATION_EVENTS_PROCESSED",
  completedAt: "DIMENSION_FIELD_ILO_CLUSTER_GENERATION_COMPLETED_AT",
};
const GEN_TIME_RANGE = { relative: { durationSeconds: String(30 * 86400) } };
// Failed generations are excluded everywhere: nothing to pin on a failure, and
// a run of failures shouldn't crowd out completed rows.
const genStatusPred = {
  pred: { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.status }, op: "OPERATOR_IN", value: ["completed", "running"] },
};

function generationAlgoFacetSpec() {
  return {
    root: { alias: GEN_ALIAS, entity: "ENTITY_TYPE_ILO_CLUSTER_GENERATION" },
    timeRange: GEN_TIME_RANGE,
    filter: genStatusPred,
    output: {
      dimensions: [{ field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.algorithm }, outputAlias: "algorithm" }],
      metrics: [{ outputAlias: "n", computed: { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.id }, op: "AGGREGATE_OP_COUNT" } }],
    },
    orderBy: [{ outputAlias: "n", direction: "DIRECTION_DESC" }],
  };
}

function generationListSpec(algorithm) {
  const filter = (!algorithm || algorithm === "all")
    ? genStatusPred
    : { andExpr: { children: [
        genStatusPred,
        { pred: { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.algorithm }, op: "OPERATOR_EQ", value: algorithm } },
      ] } };
  return {
    root: { alias: GEN_ALIAS, entity: "ENTITY_TYPE_ILO_CLUSTER_GENERATION" },
    timeRange: GEN_TIME_RANGE,
    filter,
    output: {
      dimensions: [
        { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.id },          outputAlias: "id" },
        { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.algorithm },   outputAlias: "algorithm" },
        { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.status },      outputAlias: "status" },
        { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.clusters },    outputAlias: "clusters" },
        { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.events },      outputAlias: "events" },
        { field: { stepAlias: GEN_ALIAS, field: GEN_FIELD.completedAt }, outputAlias: "completedAt" },
      ],
    },
    // Running rows carry epoch completed_at; order by status first (DESC puts
    // "running" ahead of "completed"), then recency, so in-progress passes lead.
    orderBy: [
      { outputAlias: "status", direction: "DIRECTION_DESC" },
      { outputAlias: "completedAt", direction: "DIRECTION_DESC" },
    ],
    limit: 25,
  };
}

function shapeGenerationRow(r) {
  return {
    generationId: String(r.id ?? ""),
    // Values are already short lowercase names ('hdbscan', 'leiden_hier').
    algorithm: r.algorithm ? String(r.algorithm) : "unknown",
    status: r.status ?? "",
    completedAt: r.completedAt ?? null,
    clustersCreated: Number(r.clusters ?? 0),
    eventsProcessed: Number(r.events ?? 0),
  };
}

// Number of buckets in the endpoint-firing sparkline/chart. Matches the
// prompt-side firing chart's densified series length so the two read alike.
const ENDPOINT_FIRING_BUCKETS = 48;

// The row ceiling lives beside the rollup it bounds (see configShadowFindings.js)
// rather than here: it is the only cap on that client-side work, and a test that
// pins the cost has to import it without dragging this module's DOM-dependent
// graph into a node-environment suite.

// Fold firing timestamps into a densified fixed-length series over `apiRange`.
// The endpoint firing chart derives its series from the same rows that build the
// table (see getSignalEndpointFirings), so there's no second aggregate query to
// bucket server-side. Returns zeros when the range has no resolvable bounds.
function bucketTimestamps(timesMs, apiRange) {
  const series = new Array(ENDPOINT_FIRING_BUCKETS).fill(0);
  const tr = rangeToTimeRange(apiRange);
  const startMs = Date.parse(tr?.absolute?.start ?? "");
  const endMs = Date.parse(tr?.absolute?.end ?? "");
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return series;
  const span = endMs - startMs;
  for (const ms of timesMs) {
    if (!Number.isFinite(ms) || ms < startMs || ms > endMs) continue;
    // Clamp the right edge so a firing exactly at `end` lands in the last bucket
    // rather than one past it.
    const i = Math.min(ENDPOINT_FIRING_BUCKETS - 1, Math.floor(((ms - startMs) / span) * ENDPOINT_FIRING_BUCKETS));
    series[i] += 1;
  }
  return series;
}

// Collapse one endpoint's raw finding rows into distinct problems.
//
// A signal_tags row is one OBSERVATION, not one problem. The same condition can
// be observed repeatedly — a same-content re-write of the source, a startup
// baseline, an agent durability re-send — and `managed_settings_shadowed` gates on
// PRESENCE rather than on a change, so each observation emits its own tag. Several
// rows on one endpoint routinely mean one finding seen several times, which is why
// a panel that listed rows verbatim read as several separate problems.
//
// The group key is (tag, layer, source_path): the tag value carries no
// config_target_id, but layer + path IS the target identity as a tag can see it
// (config_target_id is itself derived from agent + layer + scope).
function groupFindings(findings) {
  const groups = new Map();
  for (const f of findings) {
    const detail = parseFinding(f.value) || {};
    const key = findingGroupKey(f.tag, detail);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        tag: f.tag,
        layer: detail.layer ?? "",
        sourcePath: detail.source_path ?? "",
        exists: detail.exists,
        count: 0,
        firstSeenMs: null,
        lastSeenMs: null,
        writers: [],
        operations: [],
        otelKeys: [],
        // The newest observation's parsed detail — what "current" means for this
        // group, since `findings` arrives newest-first.
        detail,
        eventId: f.eventId,
      };
      groups.set(key, g);
    }
    g.count += 1;
    const ms = f.firedAtMs;
    if (Number.isFinite(ms)) {
      if (g.lastSeenMs === null || ms > g.lastSeenMs) g.lastSeenMs = ms;
      if (g.firstSeenMs === null || ms < g.firstSeenMs) g.firstSeenMs = ms;
    }
    // Distinct writers/operations across the group: several different processes
    // writing the same file is a materially different story from one process
    // writing it repeatedly, so don't flatten that away.
    const writer = detail.writer;
    if (writer && !g.writers.includes(writer)) g.writers.push(writer);
    const op = detail.operation;
    if (op && !g.operations.includes(op)) g.operations.push(op);
    for (const k of Array.isArray(detail.otel_keys) ? detail.otel_keys : []) {
      if (!g.otelKeys.includes(k)) g.otelKeys.push(k);
    }
  }
  return Array.from(groups.values()).sort((a, b) => (b.lastSeenMs ?? 0) - (a.lastSeenMs ?? 0));
}

// Fill in `hostname` on endpoint-firing rows, in place. signal_tags carries no
// hostname and there is no ENDPOINT→SignalTags edge, so this is a second root
// read keyed on the ids we just found. Both sides speak hyphenated UUIDs (the
// analytics id form), so no id conversion happens here. Best-effort: on failure
// the rows keep their empty hostname and the table falls back to the id.
//
// `timeRange` is REQUIRED even though this read is purely id-anchored — the
// validator rejects a query without one, and the failure is quiet (the catch
// below turns it into id-only rows), so omitting it looks like "these endpoints
// have no hostname" rather than a broken query. Endpoint rows are current-state,
// so the window only has to be wide enough to contain them: reuse the caller's.
async function attachEndpointHostnames(endpoints, apiRange) {
  const ids = endpoints.map((e) => e.endpointId).filter(Boolean);
  if (!ids.length) return;
  // The analytics validator rejects an IN list longer than 1024.
  const IN_LIST_MAX = 1000;
  const batches = Array.from({ length: Math.ceil(ids.length / IN_LIST_MAX) }, (_, i) =>
    ids.slice(i * IN_LIST_MAX, (i + 1) * IN_LIST_MAX),
  );
  try {
    const results = await Promise.all(
      batches.map((batch) =>
        runAnalyticsQuery({
          root: EP_ROOT,
          timeRange: rangeToTimeRange(apiRange),
          filter: { pred: { field: EP_ID_REF, op: "OPERATOR_IN", value: batch } },
          output: {
            dimensions: [
              { field: EP_ID_REF, outputAlias: "id" },
              { field: { stepAlias: "e", field: F_EP_HOSTNAME }, outputAlias: "hostname" },
            ],
          },
          limit: batch.length,
        }).then(rowsToObjects),
      ),
    );
    const nameById = new Map();
    for (const r of results.flat()) {
      const id = String(r?.id ?? "");
      if (id) nameById.set(id, String(r?.hostname ?? ""));
    }
    for (const ep of endpoints) ep.hostname = nameById.get(ep.endpointId) || "";
  } catch (err) {
    console.warn("[attachEndpointHostnames] failed; falling back to ids:", err);
  }
}

// The request-grain scope, compiled once for every list that reads prompts
// under it. getTraces (one row per request) and getSessionLog (one row per
// conversation) must agree exactly on what a scope, a search and a query-bar
// expression mean, so the predicate assembly lives here and both call it:
// endpoint / session / agent / model / cluster / identity / signal / skill and
// adapter scopes, the search box's ILIKE, and the compiled query-language
// filter with its joins. See getTraces for the handler-level notes on each.
//
// Returns { apiScope, apiRange, trimmed, filter, root, dropEnriched,
// unexpressibleScopeDim }; a dim with no analytics expression sets
// `unexpressibleScopeDim` and the caller fails closed rather than running an
// unscoped query.
const EMPTY_BUCKET_SCOPE_DIMS = new Set(["agent", "model"]);

async function compileTraceScope({ scopePath = [], range, search = "", query = "", canReadSensitivePrompts = true } = {}) {
  const apiScope = toApiScope(scopePath, range);

  const apiRange = toApiRange(range);

  // Experimental query-language refinement (QueryBar). `query` is a
  // human-typed expression (OR / NOT / comparisons / substring) that
  // compiles to a PROMPT-aliased FilterExpr ANDed onto the scope filter
  // below, plus any cross-entity joins (identity; cluster reuses `ec`).
  // Empty/uncompilable → no-op (the bar surfaces errors inline).
  const { filter: queryFilter, joins: queryJoins, warnings: queryWarnings, searchedConversations } = await resolveAndCompile(query, {
    range: { startTime: apiRange?.from, endTime: apiRange?.to },
  });
  // Per-trace tokens aren't joined here. O-1805 removed the per-prompt
  // usage edge and this list kept session-level token semantics; O-2021's
  // generic prompt→tokens edge (EDGE_TYPE_PROMPT_HAS_TOKENS, a grain-aware
  // collapse attributing each session at its finest grain — turn→per-prompt
  // via unit_id=event_id, submission→submission_id, session→session_id) is
  // deliberately NOT
  // wired in — doing so would change what the list displays. "Most tokens"
  // degrades to newest-first — the list
  // still renders, just not ranked by tokens. Per-trace token columns
  // stay out of the projection below for the same reason.

  // Top-level endpoint/session/agent/model/identity predicates all target
  // fields carried by PROMPT or its optional identity join. Cluster scope
  // rides in the ec step's stepFilter so it co-locates with the generation
  // pin; signal scopes follow the same filter-only join pattern.
  const filterParts = [];
  // Synthetic-dim predicates (mcp_server, skill, …) collect
  // separately and land in the PROMPT root's stepFilter. They
  // differ on the "is this row's body too large to ship in the list
  // projection" question: the bodyFitsInListProjection adapter
  // capability flag drives the dropEnriched decision generically.
  const syntheticStepFilterPreds = [{
    pred: {
      field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ANALYSIS_MODE" },
      op: "OPERATOR_NE",
      value: "NON_PROMPT",
    },
  }];
  // Cluster scope lowers to the server-side cluster_subtree walk (O-1624),
  // placed in the ec step's stepFilter (below) — not the top-level filter —
  // so it co-locates with the generation pin (the planner then resolves the
  // subtree in the pinned generation). Populated in the scope loop.
  const clusterScopePreds = [];
  // Signal scope → PROMPT → SignalTags join + a namespace predicate in the
  // `st` step's stepFilter (O-2437), mirroring the cluster subtree path.
  const signalScopePreds = [];
  // Identity scope → PROMPT→ENDPOINT_USER_ASSIGNMENT→DIRECTORY_USER join
  // (added to the root below) + a du.source_id predicate, mirroring the
  // viaAssignment path every other identity-scoped surface uses.
  let hasIdentityScope = false;
  // Omit PROMPT_ENRICHED_PROMPT (prompt/response text — tagged
  // Sensitivity::Sensitive, owned by AiSessions, so gated on
  // tenant.ai_sessions.read.sensitive) when the caller's role lacks that
  // read. The backend column policy denies the WHOLE query if any
  // requested column is sensitive and the read is missing — so a role
  // like sec-ops/viewer (analytics execute.raw but no sensitive read)
  // would otherwise 403 here, emptying not just the Trace log but every
  // surface derived from `traces` (the cluster swimlane / contrast matrix
  // on the Overview). Dropping the one sensitive column means the query
  // carries only readable columns and returns the non-sensitive rows;
  // those rows render with summaryUnavailable (TraceRow shows the redacted
  // affordance), the same path the dropped-enriched projection uses below. Defaults
  // true so a pre-RBAC tenant or an unresolved/loading caps posture still
  // requests the full projection — the caller only passes false when caps
  // are RESOLVED and the permission is confirmed absent (see ScopedPage).
  let dropEnriched = canReadSensitivePrompts === false;
  let unexpressibleScopeDim = null;
  for (const step of apiScope) {
    const adapter = getDimAdapter(step.dim);
    // Drop empty/blank ids: a step with no real value can't scope anything, and
    // an empty-string id would otherwise inject a `= ""` predicate (e.g. an
    // empty `memories:` step ANDing `tag = ""` onto the real tag → zero rows).
    // The `if (!ids.length) continue` guards below then skip such a step.
    const stepValues = (step.ids?.length ? step.ids : step.id != null ? [step.id] : []).filter((x) => x != null);
    const ids = stepValues.filter((x) => x !== "");
    // Signal-tags-backed scopes are edge-joined (PROMPT → SignalTags), so
    // handle them before the adapter branch: the predicate targets the joined
    // `st` step that only the join added below provides.
    //   • signal        — value IS the tag namespace (commands → backend namespaces).
    //   • skill / memory — value is st.tag within a fixed namespace (dimSignalNamespace).
    if (step.dim === "signal") {
      const ns = ids.map(namespaceForCommand).filter(Boolean);
      if (!ns.length) continue;
      signalScopePreds.push({
        pred: {
          field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" },
          op: ns.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
          value: ns.length > 1 ? ns : ns[0],
        },
      });
      continue;
    }
    // Tag-valued signal dims (skill, memory): gate the join to the dim's fixed
    // namespace and match the dim ids against st.tag. Generic over
    // dimSignalNamespace so a new such dim needs no branch here — and so it is
    // handled BEFORE the adapter branch below (both carry an adapter, but their
    // predicate lives on the `st` join, not the PROMPT root).
    const stNamespace = dimSignalNamespace(step.dim);
    if (stNamespace) {
      if (!ids.length) continue;
      signalScopePreds.push({ pred: { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" }, op: "OPERATOR_EQ", value: stNamespace } });
      signalScopePreds.push({
        pred: {
          field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG" },
          op: ids.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
          value: ids.length > 1 ? ids : ids[0],
        },
      });
      continue;
    }
    if (adapter) {
      if (!ids.length) continue;
      const combined = adapter.predicate(ids, "p");
      if (combined) syntheticStepFilterPreds.push(combined);
      // Adapter declares whether its prompts fit in the inline
      // list projection. False → drop PROMPT_ENRICHED_PROMPT and
      // mark rows summaryUnavailable.
      if (adapter.bodyFitsInListProjection === false) dropEnriched = true;
      continue;
    }
    if (step.dim === "tool_call") {
      // PROMPT_TOOL_CALL_NAMES is Array(String). Filter it element-
      // wise (IN → hasAny(tool_calls, [...])) in the PROMPT root's
      // stepFilter, exactly how the mcp_server adapter filters this same
      // column. Array fields reject scalar EQ, so the op is always IN
      // with a list.
      if (!ids.length) continue;
      syntheticStepFilterPreds.push({
        pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES" }, op: "OPERATOR_IN", value: ids.map(String) },
      });
      continue;
    }
    if (step.dim === "cluster") {
      // Cluster scope → server-side subtree (O-1623/O-1624). The
      // cluster_subtree predicate rides in the ec step's stepFilter (built
      // below), co-located with the generation pin withGenerationPin injects
      // on that step, so the planner resolves the subtree in the pinned
      // generation. Filter-only + EQ/IN; numeric node id(s). No client-side
      // leaf expansion.
      if (!ids.length) continue;
      clusterScopePreds.push({
        pred: {
          field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_SUBTREE" },
          op: ids.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
          value: ids.length > 1 ? ids.map(Number) : Number(ids[0]),
        },
      });
      continue;
    }
    let fieldRef;
    let cast = String;
    if (step.dim === "endpoint")      fieldRef = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" };
    else if (step.dim === "session")  fieldRef = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" };
    else if (step.dim === "agent")    fieldRef = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" };
    // Model scope rides PROMPT's own request-body model column
    // (DIMENSION_FIELD_PROMPT_MODEL) — the same field this list already
    // projects and sorts on. O-1805 removed the per-prompt usage join,
    // but the request-body model equals the response-resolved
    // TOKEN_MODEL the Models tab ranks on for every Claude row
    // (verified on staging), so a model scope is a direct PROMPT.model
    // predicate — the same resolution every sibling PROMPT-rooted breakdown
    // uses (buildPromptRootAndFilter, O-1805 follow-up).
    else if (step.dim === "model")    fieldRef = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MODEL" };
    // Identity rides the joined DIRECTORY_USER (du) source_id; the
    // PROMPT→u→du join is added to the root below when hasIdentityScope.
    else if (step.dim === "identity") {
      // Guard before setting the flag (like the cluster/signal/skill branches):
      // an empty id must skip the step, not leave hasIdentityScope set — that
      // would add the u→du INNER join with no du.source_id predicate and match
      // every attributed prompt (widen the query) instead of matching none.
      if (!ids.length) continue;
      fieldRef = { stepAlias: "du", field: "DIMENSION_FIELD_DIRECTORY_USER_SOURCE_ID" };
      hasIdentityScope = true;
    }
    else {
      // No analytics expression for this dim (see handler header).
      // Fail closed: record it and stop; the post-loop guard returns
      // empty rather than running an unscoped query.
      unexpressibleScopeDim = step.dim;
      break;
    }
    const values = EMPTY_BUCKET_SCOPE_DIMS.has(step.dim) ? stepValues : ids;
    if (!values.length) continue;
    filterParts.push({
      pred: {
        field: fieldRef,
        op: values.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
        value: values.length > 1 ? values.map(cast) : cast(values[0]),
      },
    });
  }
  if (unexpressibleScopeDim) return { unexpressibleScopeDim };
  const trimmed = String(search || "").trim();
  if (trimmed) {
    // Search ILIKEs enriched_prompt OR provider (PROMPT fields), OR the
    // sessions whose captured conversation text carries the term — the
    // summary is AI-written, so a phrase the user typed is not in it.
    const sessionIds = await conversationSessionsMatching({
      term: trimmed,
      startTime: apiRange?.from,
      endTime: apiRange?.to,
    }).catch(() => []);
    filterParts.push({
      orExpr: { children: [
        { pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT" }, op: "OPERATOR_ILIKE", value: `%${trimmed}%` } },
        { pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" },        op: "OPERATOR_ILIKE", value: `%${trimmed}%` } },
        ...(sessionIds.length
          ? [{ pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, op: "OPERATOR_IN", value: sessionIds } }]
          : []),
      ]},
    });
  }
  const scopeFilter = filterParts.length === 0
    ? null
    : filterParts.length === 1
    ? filterParts[0]
    : { andExpr: { children: filterParts } };
  // AND the experimental query-language filter onto scope + search (no-op
  // when the query is empty / uncompilable).
  const filter = composeFilters(scopeFilter, queryFilter);

  // Select the page from the narrowest graph that can express the active
  // filters. Optional cluster-name enrichment happens only after this
  // distinct-id page has been chosen, so join fan-out cannot consume the
  // LIMIT/OFFSET or shift page boundaries.
  const candidateRoot = {
    alias: "p", entity: "ENTITY_TYPE_PROMPT",
    stepFilter: syntheticStepFilterPreds,
    children: [
      ...(clusterScopePreds.length ? [{
        edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
        mode: "JOIN_MODE_INNER",
        target: {
          alias: "ec", entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
          stepFilter: clusterScopePreds,
        },
      }] : []),
      // Signal scope (O-2437): INNER join to the firing prompts when a
      // signal namespace is in scope; absent otherwise.
      ...(signalScopePreds.length ? [{
        edge: "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS",
        mode: "JOIN_MODE_INNER",
        target: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS", stepFilter: signalScopePreds },
      }] : []),
      // Identity scope: PROMPT → ENDPOINT_USER_ASSIGNMENT (u) →
      // DIRECTORY_USER (du), INNER (attributed prompts only). The
      // du.source_id predicate rides the top-level scope filter above;
      // the assignment entity reads the resolved view, so
      // anthropic-synthetic endpoints resolve here too.
      ...(hasIdentityScope ? [{
        edge: "EDGE_TYPE_PROMPT_HAS_USER_ASSIGNMENTS",
        mode: "JOIN_MODE_INNER",
        target: {
          alias: "u", entity: "ENTITY_TYPE_ENDPOINT_USER_ASSIGNMENT",
          children: [{
            edge: "EDGE_TYPE_USER_ASSIGNMENT_HAS_DIRECTORY_USER",
            mode: "JOIN_MODE_INNER",
            target: { alias: "du", entity: "ENTITY_TYPE_DIRECTORY_USER" },
          }],
        },
      }] : []),
    ],
  };
  // Attach any cross-entity joins the query needs (identity → u→du under
  // PROMPT; cluster reuses the existing `ec` and is skipped). No-op when
  // the query has no such clause.
  const candidateRootWithJoins = queryJoins.length
    ? ensureQueryJoins(candidateRoot, queryJoins)
    : candidateRoot;
  return { apiScope, apiRange, trimmed, filter, root: candidateRootWithJoins, dropEnriched, queryWarnings, searchedConversations, unexpressibleScopeDim: null };
}

// Session lists read each session's own capture frames
// (CONVERSATION_SOURCE_OBSERVATION): every accepted capture of a conversation,
// kept a year and stored in time order. Every frame read is bounded by the
// selected range (the planner refuses more than 90 days) and scans only that
// range, so a session is listed for any activity in it; the prompt index lists
// a session only while it holds a prompt of it, 30 days. Frames run about five
// times the index's rows per endpoint, so a long range scans more than the
// index read did: one the planner refuses for scanning too much, or any other
// failed read, lists from the index as before. A session
// here is a trace: a sub-agent's trace is listed beside its parent's, and a
// trace with activity but no analyzed prompt is listed too. Frames carry a
// session's machine, provider and model, so those scopes read them, as does a
// search for words; any other scope, a query clause on a prompt's own fields,
// or a stack whose graph has no frames reads the prompt index as before. Each
// listed session's request count and unanswered prompts still come from the
// prompt index, so a session older than it has none of them; its title does
// too, except that a session begun before the index's horizon that the index
// holds nothing of takes its prompt analysis's (fetchLatestSessionPrompts).
const FRAME_ROOT = { alias: "o", entity: "ENTITY_TYPE_CONVERSATION_SOURCE_OBSERVATION" };
const FRAME = (name) => ({ stepAlias: "o", field: `DIMENSION_FIELD_CONVERSATION_SOURCE_OBSERVATION_${name}` });
const FRAME_SCOPE_FIELDS = { endpoint: "ENDPOINT_ID", session: "TRACE_ID", agent: "PROVIDER", model: "MODEL" };
// The full-text index takes 30 days a read; the planner, 1024 values a list.
const SEARCH_SLICE_MS = 30 * 86400000;
const SEARCH_SESSION_CAP = 1024;
const NO_SESSION = "00000000-0000-0000-0000-000000000000";

async function framesListed() {
  try {
    const schema = await describeSchema();
    return (schema?.entities || []).some((entity) => entity?.entity === FRAME_ROOT.entity);
  } catch {
    return false;
  }
}

// The words a query asks for when it is only words, each of which a session
// must mention; null when it has a field clause, an OR or a NOT.
function queryWords(query) {
  const { ast } = parseQuery(String(query || ""));
  if (!ast) return [];
  const nodes = ast.type === "and" ? ast.children : [ast];
  return nodes.every((node) => node.type === "text") ? nodes.map((node) => node.value) : null;
}

// The sessions whose conversation text carries `term` (the full-text index, a
// 30-day read at a time) or, while the prompt index holds them, whose prompt
// summary does; at most SEARCH_SESSION_CAP of them.
async function sessionsMentioning(term, apiRange) {
  const from = Date.parse(apiRange?.from ?? "");
  const to = Date.parse(apiRange?.to ?? "");
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];
  const slices = [];
  for (let end = to; end > from; end -= SEARCH_SLICE_MS) {
    slices.push({ startTime: new Date(Math.max(from, end - SEARCH_SLICE_MS)).toISOString(), endTime: new Date(end).toISOString() });
  }
  const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
  const lists = await Promise.all([
    ...slices.map((slice) => conversationSessionsMatching({ term, ...slice }).catch(() => [])),
    // Grouped by session: a dimension alone reads prompts, and a busy
    // session's would crowd the rest out of the limit.
    runAnalyticsQuery({
      root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
      timeRange: rangeToTimeRange(apiRange),
      filter: { pred: { field: P("ENRICHED_PROMPT"), op: "OPERATOR_ILIKE", value: `%${term}%` } },
      output: {
        dimensions: [{ field: P("NOTIFICATION_ID"), outputAlias: "session_id" }],
        metrics: [{ outputAlias: "prompts", computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
      },
      limit: SEARCH_SESSION_CAP,
    }).then((rows) => rowsToObjects(rows).map((row) => row.session_id)).catch(() => []),
  ]);
  const found = new Set();
  for (const list of lists) {
    for (const id of list) {
      if (found.size >= SEARCH_SESSION_CAP) break;
      if (id) found.add(String(id).toLowerCase());
    }
  }
  return [...found];
}

// How many sessions and machines the frames hold for a scope and query over a
// range, as the session lists count them: null where the lists read the
// prompt index instead (see compileFrameScope) or the read fails.
async function frameTotals({ scopePath, range, query }) {
  try {
    if (!(await framesListed())) return null;
    const compiled = await compileFrameScope({ scopePath, range, query });
    if (!compiled) return null;
    const [row] = rowsToObjects(await runAnalyticsQuery({
      root: FRAME_ROOT,
      timeRange: rangeToTimeRange(compiled.apiRange),
      ...(compiled.filter ? { filter: compiled.filter } : {}),
      output: { metrics: [
        { outputAlias: "sessions", computed: { field: FRAME("TRACE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
        { outputAlias: "endpoints", computed: { field: FRAME("ENDPOINT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
      ] },
      limit: 1,
    }));
    return row ? { sessions: Number(row.sessions) || 0, endpoints: Number(row.endpoints) || 0 } : null;
  } catch (err) {
    console.warn("[overview totals] frame read failed:", err);
    return null;
  }
}

// The machines of `sessionIds` as their capture frames name them over
// `range`, each with the latest activity of those sessions on it (machine ->
// ms); null where the frames are not listed or the read fails.
async function sessionMachines(sessionIds, range) {
  if (!(await framesListed())) return null;
  try {
    const batches = [];
    for (let i = 0; i < sessionIds.length; i += SEARCH_SESSION_CAP) batches.push(sessionIds.slice(i, i + SEARCH_SESSION_CAP));
    // Grouped by machine (a dimension alone reads frames, not machines).
    const reads = await Promise.all(batches.map((batch) => runAnalyticsQuery({
      root: FRAME_ROOT,
      timeRange: rangeToTimeRange(toApiRange(range)),
      filter: { pred: { field: FRAME("TRACE_ID"), op: "OPERATOR_IN", value: batch } },
      output: {
        dimensions: [{ field: FRAME("ENDPOINT_ID"), outputAlias: "endpoint_id" }],
        metrics: [{ outputAlias: "last_time", computed: { field: FRAME("EVENT_TIME"), op: "AGGREGATE_OP_MAX" } }],
      },
      limit: batch.length,
    }).then(rowsToObjects)));
    const machines = new Map();
    for (const row of reads.flat()) {
      const machine = String(row?.endpoint_id || "");
      const lastMs = parseServerTimestamp(row?.last_time);
      if (machine && Number.isFinite(lastMs)) machines.set(machine, Math.max(machines.get(machine) ?? -Infinity, lastMs));
    }
    return machines;
  } catch (err) {
    console.warn("[machine people] frame read failed:", err);
    return null;
  }
}

// The scope, the search box and a query of words as a filter on frames; null
// when a scope step or a query clause needs a prompt's own fields.
async function compileFrameScope({ scopePath = [], range, search = "", query = "" } = {}) {
  const words = queryWords(query);
  if (words === null) return null;
  const apiScope = toApiScope(scopePath, range);
  const apiRange = toApiRange(range);
  const parts = [];
  for (const step of apiScope) {
    const field = FRAME_SCOPE_FIELDS[step.dim];
    if (!field) return null;
    const stepValues = (step.ids?.length ? step.ids : step.id != null ? [step.id] : []).filter((x) => x != null);
    const values = EMPTY_BUCKET_SCOPE_DIMS.has(step.dim) ? stepValues : stepValues.filter((x) => x !== "");
    if (!values.length) continue;
    parts.push({ pred: { field: FRAME(field), op: values.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ", value: values.length > 1 ? values.map(String) : String(values[0]) } });
  }
  const trimmed = String(search || "").trim();
  const [searched, ...worded] = await Promise.all([
    trimmed ? sessionsMentioning(trimmed, apiRange) : null,
    ...words.map((word) => sessionsMentioning(word, apiRange)),
  ]);
  const among = (ids) => ({ pred: { field: FRAME("TRACE_ID"), op: "OPERATOR_IN", value: ids.length ? ids : [NO_SESSION] } });
  // The search box also matches the provider, as the prompt-index search does.
  if (searched) parts.push({ orExpr: { children: [{ pred: { field: FRAME("PROVIDER"), op: "OPERATOR_ILIKE", value: `%${trimmed}%` } }, among(searched)] } });
  for (const ids of worded) parts.push(among(ids));
  return {
    apiRange,
    filter: parts.length === 0 ? null : parts.length === 1 ? parts[0] : { andExpr: { children: parts } },
    searchedConversations: Boolean(trimmed || words.length),
  };
}

// Each session's request count as the prompt index has it, and the machine its
// prompts ran on, by session id: { requests, endpointId }.
async function fetchSessionRequestCounts(sessionIds, timeRange) {
  const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
  const counts = new Map();
  const batches = [];
  for (let i = 0; i < sessionIds.length; i += SEARCH_SESSION_CAP) batches.push(sessionIds.slice(i, i + SEARCH_SESSION_CAP));
  await Promise.all(batches.map(async (batch) => {
    try {
      const rows = rowsToObjects(await runAnalyticsQuery({
        root: { alias: "p", entity: "ENTITY_TYPE_PROMPT", stepFilter: [{ pred: { field: P("ANALYSIS_MODE"), op: "OPERATOR_NE", value: "NON_PROMPT" } }] },
        timeRange,
        filter: { pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_IN", value: batch } },
        output: {
          dimensions: [{ field: P("NOTIFICATION_ID"), outputAlias: "session_id" }],
          metrics: [
            { outputAlias: "requests", computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
            { outputAlias: "endpoint_id", computed: { field: P("ENDPOINT_ID"), op: "AGGREGATE_OP_MAX" } },
          ],
        },
        limit: batch.length,
      }));
      for (const row of rows) {
        counts.set(String(row.session_id || "").toLowerCase(), { requests: Number(row.requests) || 0, endpointId: String(row.endpoint_id || "") });
      }
    } catch (err) {
      console.warn("[session list] request counts failed:", err);
    }
  }));
  return counts;
}

// The connector each session came in through, from its frames.
async function fetchFrameSourceChips(sessionIds, timeRange) {
  const chips = new Map();
  const batches = [];
  for (let i = 0; i < sessionIds.length; i += SESSION_HYDRATION_BATCH) batches.push(sessionIds.slice(i, i + SESSION_HYDRATION_BATCH));
  await Promise.all(batches.map(async (batch) => {
    try {
      const rows = rowsToObjects(await runAnalyticsQuery({
        root: FRAME_ROOT,
        timeRange,
        filter: { andExpr: { children: [
          { pred: { field: FRAME("TRACE_ID"), op: "OPERATOR_IN", value: batch } },
          { pred: { field: FRAME("SOURCE_CAPTURE"), op: "OPERATOR_IN", value: Object.keys(SOURCE_CHIPS) } },
        ] } },
        output: {
          dimensions: [{ field: FRAME("TRACE_ID"), outputAlias: "session_id" }, { field: FRAME("SOURCE_CAPTURE"), outputAlias: "capture_source" }],
          metrics: [{ outputAlias: "frames", computed: { field: FRAME("SOURCE_EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
        limit: batch.length * Object.keys(SOURCE_CHIPS).length,
      }));
      for (const row of rows) {
        const chip = chipForCaptureSource(row.capture_source);
        if (row.session_id && chip) chips.set(String(row.session_id).toLowerCase(), chip);
      }
    } catch (err) {
      console.warn("[session list] connector source read failed:", err);
    }
  }));
  return chips;
}

// One session per row, from its frames: its activity in the range, its machine,
// provider and model, newest (or oldest) first. Null when the session list must
// read the prompt index instead (see compileFrameScope); sorting by request
// count does too, since frames do not count requests the way the index does.
async function traceSessionLog({ scopePath, sort, limit, offset, range, search, query, canReadSensitivePrompts }) {
  if (sort === "requests" || !(await framesListed())) return null;
  const compiled = await compileFrameScope({ scopePath, range, search, query });
  if (!compiled) return null;
  const { apiRange, filter, searchedConversations } = compiled;
  const timeRange = rangeToTimeRange(apiRange);
  const dropEnriched = canReadSensitivePrompts === false;
  const pageLimit = Math.max(0, Math.floor(Number(limit) || 0));
  const pageOffset = Math.max(0, Math.floor(Number(offset) || 0));
  const where = filter ? { filter } : {};
  const sessionSpec = {
    root: FRAME_ROOT,
    timeRange,
    ...where,
    output: {
      dimensions: [{ field: FRAME("TRACE_ID"), outputAlias: "session_id" }],
      metrics: [
        { outputAlias: "last_time", computed: { field: FRAME("EVENT_TIME"), op: "AGGREGATE_OP_MAX" } },
        { outputAlias: "first_time", computed: { field: FRAME("EVENT_TIME"), op: "AGGREGATE_OP_MIN" } },
        { outputAlias: "endpoint_id", computed: { field: FRAME("ENDPOINT_ID"), op: "AGGREGATE_OP_MAX" } },
        { outputAlias: "provider", computed: { field: FRAME("PROVIDER"), op: "AGGREGATE_OP_MAX" } },
        { outputAlias: "model", computed: { field: FRAME("MODEL"), op: "AGGREGATE_OP_MAX" } },
      ],
    },
    orderBy: sort === "oldest"
      ? [{ outputAlias: "first_time", direction: "DIRECTION_ASC" }]
      : [{ outputAlias: "last_time", direction: "DIRECTION_DESC" }],
    limit: pageLimit,
    ...(pageOffset ? { offset: pageOffset } : {}),
  };
  const countSpec = {
    root: FRAME_ROOT,
    timeRange,
    ...where,
    output: { metrics: [{ outputAlias: "total", computed: { field: FRAME("TRACE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }] },
    limit: 1,
  };
  let sessionRows;
  let countRows;
  try {
    [sessionRows, countRows] = await Promise.all([
      runAnalyticsQuery(sessionSpec).then(rowsToObjects),
      runAnalyticsQuery(countSpec).then(rowsToObjects).catch(() => null),
    ]);
  } catch (err) {
    // A list the frames cannot serve (on a large fleet a long range can pass
    // the planner's scan limit) is read from the prompt index, as before.
    if (err?.name === "AbortError") throw err;
    console.warn("[session list] frame read failed; reading the prompt index:", err);
    return null;
  }
  const sessions = [];
  const seen = new Set();
  for (const row of sessionRows) {
    const sessionId = String(row?.session_id || "").toLowerCase();
    if (!sessionId || seen.has(sessionId)) continue;
    seen.add(sessionId);
    sessions.push({ ...row, session_id: sessionId });
  }
  const sessionIds = sessions.map((s) => s.session_id);
  const spans = new Map(sessions.map((s) => [s.session_id, {
    firstMs: s.first_time ? parseServerTimestamp(s.first_time) : null,
    lastMs: s.last_time ? parseServerTimestamp(s.last_time) : null,
  }]));
  const [latestBySession, unansweredBySession, requestCounts, sourceChips] = await Promise.all([
    fetchLatestSessionPrompts(sessionIds, timeRange, { dropEnriched, spans }),
    fetchUnansweredPrompts(sessionIds, timeRange),
    fetchSessionRequestCounts(sessionIds, timeRange),
    fetchFrameSourceChips(sessionIds, timeRange),
  ]);
  const adapted = sessions.map((s) => {
    const latest = latestBySession.get(s.session_id) || null;
    const trace = adaptTrace({
      trace_id:      latest?.trace_id ? String(latest.trace_id) : "",
      endpoint_id:   s.endpoint_id || latest?.endpoint_id || "",
      endpoint_name: "",
      session_id:    s.session_id,
      event_time:    s.last_time,
      provider:      s.provider || latest?.provider || "",
      model:         latest?.model || s.model || "",
      cluster_id:    0,
      cluster_name:  "",
      summary:       latest?.enriched || "",
      filtered:      false,
      message_type:  latest?.message_type || "",
      answer_status: latest?.answer_status || "",
      unanswered_prompts: unansweredBySession.get(s.session_id) || [],
    });
    trace.latestTraceId = trace.id || "";
    trace.id = s.session_id;
    trace.requestCount = requestCounts.get(s.session_id)?.requests || 0;
    trace.firstTimestamp = s.first_time ? parseServerTimestamp(s.first_time) : null;
    trace.lastTimestamp = s.last_time ? parseServerTimestamp(s.last_time) : null;
    // A session the prompt index holds no prompt of has no summary to load:
    // say so rather than claim the body is hidden or still being analyzed.
    if (!latest) trace.summaryMissing = true;
    else if (dropEnriched) trace.summaryUnavailable = true;
    trace.viaSource = sourceChipFor(sourceChips, s.session_id);
    return trace;
  });
  const countedTotal = Number(countRows?.[0]?.total);
  const countIsExact = Number.isFinite(countedTotal) && countedTotal >= 0;
  adapted.total = Math.max(adapted.length + pageOffset, countIsExact ? countedTotal : 0);
  adapted.totalIsExact = countIsExact;
  adapted.queryWarnings = [];
  adapted.searchedConversations = searchedConversations;
  return adapted;
}

// Every session with activity in the range, from its frames, as a match with
// no clauses: newest first, at most SESSION_MATCH_CAP. Null where the session
// match must read the prompt index (see compileFrameScope).
async function traceSessionMatches({ scopePath, range }) {
  if (!(await framesListed())) return null;
  const compiled = await compileFrameScope({ scopePath, range });
  if (!compiled) return null;
  const timeRange = rangeToTimeRange(compiled.apiRange);
  let rows;
  try {
    rows = rowsToObjects(await runAnalyticsQuery({
      root: FRAME_ROOT,
      timeRange,
      ...(compiled.filter ? { filter: compiled.filter } : {}),
      output: {
        dimensions: [{ field: FRAME("TRACE_ID"), outputAlias: "session_id" }],
        metrics: [
          { outputAlias: "last_time", computed: { field: FRAME("EVENT_TIME"), op: "AGGREGATE_OP_MAX" } },
          { outputAlias: "first_time", computed: { field: FRAME("EVENT_TIME"), op: "AGGREGATE_OP_MIN" } },
          { outputAlias: "agent", computed: { field: FRAME("PROVIDER"), op: "AGGREGATE_OP_MAX" } },
          { outputAlias: "endpoint_id", computed: { field: FRAME("ENDPOINT_ID"), op: "AGGREGATE_OP_MAX" } },
          { outputAlias: "model", computed: { field: FRAME("MODEL"), op: "AGGREGATE_OP_MAX" } },
        ],
      },
      orderBy: [{ outputAlias: "last_time", direction: "DIRECTION_DESC" }],
      limit: SESSION_MATCH_CAP + 1,
    }));
  } catch (err) {
    // As the session list: a match the frames cannot serve reads the index.
    if (err?.name === "AbortError") throw err;
    console.warn("[session match] frame read failed; reading the prompt index:", err);
    return null;
  }
  const { sessions, capped } = readSessionMatches(rows, [], { parseTime: parseServerTimestamp });
  const counts = await fetchSessionRequestCounts(sessions.map((s) => s.id), timeRange);
  for (const s of sessions) {
    const counted = counts.get(s.id);
    s.requests = counted?.requests || 0;
    // A connector's capture (Copilot, Vertex AI, Perplexity, compliance sync)
    // names no machine in its frames; the index's prompts may.
    if (!s.endpointId && counted?.endpointId) s.endpointId = counted.endpointId;
  }
  return { sessions, clauses: [], capped, prefiltered: false, warnings: [], searchedConversations: false };
}

// The conversation model keeps each prompt's analysis (its summary, when it was
// sent) for a year; the prompt index keeps it for a month. A session the index
// cannot hold all of, because it began before the index's first retained
// instant, is read from the prompt analysis instead: its newest summary where
// the index has none of it, and how many prompts it sent. (Its topics cannot
// be: the clustering keeps only its latest run, over about the same month.)
// The analysis view rebuilds the current messages of the whole window it
// reads, and the planner refuses about 90 days of that even for one session,
// so a read keeps within ANALYSIS_WINDOW_MS of the sessions it asks about. The
// analysis refuses a reader scoped to some machines, who keeps what the index
// holds, as does a deployment without it.
const ANALYSIS_ROOT = { alias: "p", entity: "ENTITY_TYPE_PROMPT_ANALYSIS" };
const ANALYSIS = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_ANALYSIS_${name}` });
const ANALYSIS_WINDOW_MS = 30 * 86400000;

// When an entity's retained rows begin, as the schema states it, in ms: null
// where the schema does not list the entity, 0 where it states no horizon.
async function entityHorizonMs(entity) {
  try {
    const schema = await describeSchema();
    const found = (schema?.entities || []).find((e) => e?.entity === entity);
    if (!found) return null;
    const ms = Date.parse(String(found.timeHorizon?.earliest || ""));
    return Number.isFinite(ms) ? ms : 0;
  } catch {
    return null;
  }
}

// Whether a session that began at `firstMs` began before the prompt index's
// first retained instant, so the index holds only its later part, if any. An
// index the schema does not list holds none of it.
async function beyondPromptIndex(firstMs) {
  if (!Number.isFinite(firstMs)) return false;
  const horizon = await entityHorizonMs("ENTITY_TYPE_PROMPT");
  return horizon == null || firstMs < horizon;
}

// Of `spans` (session id -> { firstMs, lastMs }, as a record or the frames date
// a session), the sessions that began before the prompt index's horizon, where
// the conversation model's entities are listed to read them from.
async function spansBeyondIndex(spans) {
  const out = new Map();
  if (!spans?.size || (await entityHorizonMs(ANALYSIS_ROOT.entity)) == null) return out;
  for (const [id, span] of spans) {
    if (await beyondPromptIndex(span?.firstMs)) out.set(String(id).toLowerCase(), span);
  }
  return out;
}

// Sessions in reads whose windows stay within ANALYSIS_WINDOW_MS: newest
// first, a session joining a read while the read's window still fits, at most
// SESSION_HYDRATION_BATCH a read. A session longer than the window is read
// over its last ANALYSIS_WINDOW_MS, where its newest prompts are; nothing
// before `horizonMs`, where the analysis keeps none.
function analysisReads(spans, horizonMs) {
  const items = [];
  for (const [id, span] of spans) {
    const last = Number(span?.lastMs);
    if (!Number.isFinite(last)) continue;
    const begun = Number.isFinite(Number(span?.firstMs)) ? Number(span.firstMs) : last;
    const first = Math.max(begun, last - ANALYSIS_WINDOW_MS, horizonMs || 0);
    if (first <= last) items.push({ id, first, last });
  }
  items.sort((a, b) => b.last - a.last);
  const reads = [];
  let cur = null;
  for (const item of items) {
    if (cur && cur.ids.length < SESSION_HYDRATION_BATCH && cur.last - Math.min(cur.first, item.first) <= ANALYSIS_WINDOW_MS) {
      cur.ids.push(item.id);
      cur.first = Math.min(cur.first, item.first);
    } else {
      cur = { ids: [item.id], first: item.first, last: item.last };
      reads.push(cur);
    }
  }
  const pad = 60000;
  return reads.map((r) => ({
    ids: r.ids,
    timeRange: rangeToTimeRange({ from: new Date(r.first - pad).toISOString(), to: new Date(r.last + pad).toISOString() }),
  }));
}

// A session's newest prompt with a summary, else its newest prompt.
function pickLatestPrompt(group) {
  group.sort((a, b) => parseServerTimestamp(b.event_time) - parseServerTimestamp(a.event_time));
  return group.find((r) => String(r.enriched || "").trim()) || group[0];
}

// What the prompt analysis says of each session in `spans`, by id: with
// `latest`, its newest summarized prompt shaped as fetchLatestSessionPrompts'
// rows (no request id, which nothing older than the index can open, and no
// answer status, which the analysis does not keep); with `counts`, how many
// prompts it sent and its first and last, as the prompt index counts them
// (non-prompt frames left out). A read that fails or is refused leaves its
// sessions out.
async function fetchAnalysisSessions(spans, { latest = true, counts = false, dropEnriched = false } = {}) {
  const out = new Map();
  const horizon = spans?.size ? await entityHorizonMs(ANALYSIS_ROOT.entity) : null;
  if (horizon == null) return out;
  const dimensions = [
    { field: ANALYSIS("TRACE_ID"), outputAlias: "session_id" },
    { field: ANALYSIS("EVENT_TIME"), outputAlias: "event_time" },
    { field: ANALYSIS("ENDPOINT_ID"), outputAlias: "endpoint_id" },
    { field: ANALYSIS("PROVIDER"), outputAlias: "provider" },
    { field: ANALYSIS("MODEL"), outputAlias: "model" },
    { field: ANALYSIS("MESSAGE_TYPE"), outputAlias: "message_type" },
    ...(dropEnriched ? [] : [{ field: ANALYSIS("SUMMARY"), outputAlias: "enriched" }]),
  ];
  const runRows = async (spec) => rowsToObjects(await runAnalyticsQuery(spec));
  const entry = (id) => out.get(id) || { latest: null, requests: null, firstMs: null, lastMs: null };
  await Promise.all(analysisReads(spans, horizon).map(async ({ ids, timeRange }) => {
    try {
      const [latestRead, countRows] = await Promise.all([
        latest
          ? readLatestSessionRows({
            runQuery: runRows,
            root: ANALYSIS_ROOT,
            ids,
            timeRange,
            notificationField: ANALYSIS("TRACE_ID").field,
            timestampField: ANALYSIS("EVENT_TIME").field,
            summaryField: dropEnriched ? null : ANALYSIS("SUMMARY").field,
            dimensions,
            rowsPerSession: SESSION_HYDRATION_LATEST_ROWS,
          })
          : null,
        counts
          ? runRows({
            root: ANALYSIS_ROOT,
            timeRange,
            filter: { andExpr: { children: [
              { pred: { field: ANALYSIS("TRACE_ID"), op: "OPERATOR_IN", value: ids } },
              { pred: { field: ANALYSIS("PIPELINE_MODE"), op: "OPERATOR_NE", value: "NON_PROMPT" } },
            ] } },
            output: {
              dimensions: [{ field: ANALYSIS("TRACE_ID"), outputAlias: "session_id" }],
              metrics: [
                { outputAlias: "requests", computed: { field: ANALYSIS("SOURCE_EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
                { outputAlias: "first_time", computed: { field: ANALYSIS("EVENT_TIME"), op: "AGGREGATE_OP_MIN" } },
                { outputAlias: "last_time", computed: { field: ANALYSIS("EVENT_TIME"), op: "AGGREGATE_OP_MAX" } },
              ],
            },
            limit: ids.length,
          })
          : [],
      ]);
      for (const [id, group] of latestRead?.rowsById || []) {
        if (group.length) out.set(String(id).toLowerCase(), { ...entry(String(id).toLowerCase()), latest: { ...pickLatestPrompt(group), trace_id: "", answer_status: "" } });
      }
      for (const row of countRows) {
        const id = String(row?.session_id || "").toLowerCase();
        if (!ids.includes(id)) continue;
        out.set(id, {
          ...entry(id),
          requests: Number(row.requests) || 0,
          firstMs: row.first_time ? parseServerTimestamp(row.first_time) : null,
          lastMs: row.last_time ? parseServerTimestamp(row.last_time) : null,
        });
      }
    } catch (err) {
      if (err?.status !== 403) console.warn("[session analysis] read failed:", err);
    }
  }));
  return out;
}

// The newest prompt of each session: its newest prompt with a summary when it
// has one (a run of scaffolding or warmup frames carries none), otherwise its
// newest prompt, which still supplies model, endpoint and the request id. Two
// reads per batch of 100 (see readLatestSessionRows), whatever the sessions'
// sizes. A session the second read cut off falls back to the newest-first
// read. A batch that fails leaves its sessions unhydrated rather than failing
// the page.
const SESSION_HYDRATION_BATCH = 100;
const SESSION_HYDRATION_ROWS = 6;
const SESSION_HYDRATION_LATEST_ROWS = 8;
const UNANSWERED_PROMPT_ROWS = 2000;

async function fetchUnansweredPrompts(sessionIds, timeRange) {
  const out = new Map();
  const ids = [...new Set((sessionIds || []).map(String).filter(Boolean))];
  const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
  const batches = [];
  for (let i = 0; i < ids.length; i += SESSION_HYDRATION_BATCH) batches.push(ids.slice(i, i + SESSION_HYDRATION_BATCH));
  await Promise.all(batches.map(async (batch) => {
    let rows;
    try {
      rows = rowsToObjects(await runAnalyticsQuery({
        root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
        timeRange,
        filter: { andExpr: { children: [
          { pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_IN", value: batch } },
          { pred: { field: P("ANSWER_STATUS"), op: "OPERATOR_IN", value: ["not_yet_retrieved", "no_answer_returned"] } },
        ] } },
        output: { dimensions: [
          { field: P("NOTIFICATION_ID"), outputAlias: "session_id" },
          { field: P("EVENT_ID"), outputAlias: "trace_id" },
          { field: P("EVENT_TIMESTAMP"), outputAlias: "event_time" },
          { field: P("ANSWER_STATUS"), outputAlias: "answer_status" },
        ] },
        limit: UNANSWERED_PROMPT_ROWS,
      }));
    } catch (err) {
      console.warn("[getSessionLog] unanswered-prompt read failed:", err);
      return;
    }
    for (const id of batch) out.set(id, []);
    for (const row of rows) {
      const prompt = unansweredPrompt({
        eventId: row.trace_id ? String(row.trace_id) : "",
        eventTime: row.event_time ? parseServerTimestamp(row.event_time) : null,
        status: row.answer_status,
      });
      const sessionId = String(row.session_id || "");
      if (prompt && out.has(sessionId)) out.get(sessionId).push(prompt);
    }
  }));
  return out;
}

const SOURCE_CHIPS = {
  langsmith_sync: "LangSmith",
  vertex_sync: "Vertex AI",
};
const SOURCE_CHIP_CAPTURE_SOURCES = ["langsmith_sync", "13", "vertex_sync", "15"];

function chipForCaptureSource(value) {
  const raw = String(value ?? "").trim();
  const name = promptCaptureSourceFromEventData({ capture_source: /^\d+$/.test(raw) ? Number(raw) : raw });
  return SOURCE_CHIPS[name] || null;
}

export function sourceChipSessionsSpec(sessionIds, timeRange) {
  const E = (name) => ({ stepAlias: "e", field: `DIMENSION_FIELD_EVENT_${name}` });
  return {
    root: { alias: "e", entity: "ENTITY_TYPE_EVENT" },
    timeRange,
    filter: { andExpr: { children: [
      { pred: { field: E("NOTIFICATION_ID"), op: "OPERATOR_IN", value: sessionIds } },
      { pred: { field: E("CAPTURE_SOURCE"), op: "OPERATOR_IN", value: SOURCE_CHIP_CAPTURE_SOURCES } },
    ] } },
    output: {
      dimensions: [
        { field: E("NOTIFICATION_ID"), outputAlias: "session_id" },
        { field: E("CAPTURE_SOURCE"), outputAlias: "capture_source" },
      ],
      metrics: [{ outputAlias: "events", computed: { field: E("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
    },
    limit: sessionIds.length * Object.keys(SOURCE_CHIPS).length,
  };
}

async function fetchSourceChips(sessionIds, timeRange) {
  const ids = [...new Set((sessionIds || []).map(String).filter(Boolean))];
  const batches = [];
  for (let i = 0; i < ids.length; i += SESSION_HYDRATION_BATCH) batches.push(ids.slice(i, i + SESSION_HYDRATION_BATCH));
  const chips = new Map();
  await Promise.all(batches.map(async (batch) => {
    try {
      for (const row of rowsToObjects(await runAnalyticsQuery(sourceChipSessionsSpec(batch, timeRange)))) {
        const chip = chipForCaptureSource(row.capture_source);
        if (row.session_id && chip) chips.set(String(row.session_id).toLowerCase(), chip);
      }
    } catch (err) {
      console.warn("[trace list] connector source read failed:", err);
    }
  }));
  return chips;
}

const sourceChipFor = (chips, sessionId) => chips.get(String(sessionId || "").toLowerCase()) || null;

async function markSourceChips(traces, timeRange) {
  const chips = await fetchSourceChips(traces.map((trace) => trace.sessionId), timeRange);
  for (const trace of traces) trace.viaSource = sourceChipFor(chips, trace.sessionId);
}

// What each session's conversation record says of it, by session id: its
// trace's machine and provider (CONVERSATION_TRACE, read for many traces at
// once), and the span its record was observed over with how many messages it
// holds (the record summaries read). The record is kept far longer than the
// prompt index (on staging the index holds about 30 days, the record months),
// so a session the index no longer holds still has its machine and span. With
// `model`, also the model of its latest model call: a session's model calls,
// like its messages, are read one trace at a time, so only for one session.
export async function fetchConversationHeads(sessionIds, { model = false } = {}) {
  const out = new Map();
  const ids = [...new Set((sessionIds || []).map((id) => String(id || "").toLowerCase()).filter(Boolean))];
  if (!ids.length) return out;
  // Conversation entities are windowed by when a session began, so the
  // record's own 90 days, whatever range the caller shows.
  const timeRange = indexRetentionRange();
  const CT = (field) => ({ stepAlias: "t", field: `DIMENSION_FIELD_CONVERSATION_TRACE_${field}` });
  const head = (id) => out.get(id) || { endpointId: "", provider: "", model: "", firstMs: null, lastMs: null, messages: 0 };
  const batches = [];
  for (let i = 0; i < ids.length; i += SESSION_HYDRATION_BATCH) batches.push(ids.slice(i, i + SESSION_HYDRATION_BATCH));
  await Promise.all(batches.map(async (batch) => {
    const [traces, summaries] = await Promise.all([
      runAnalyticsQuery({
        root: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE" },
        timeRange,
        filter: { pred: { field: CT("TRACE_ID"), op: "OPERATOR_IN", value: batch } },
        output: { dimensions: [
          { field: CT("TRACE_ID"), outputAlias: "trace_id" },
          { field: CT("ENDPOINT_ID"), outputAlias: "endpoint_id" },
          { field: CT("PROVIDER"), outputAlias: "provider" },
        ] },
        limit: batch.length * 4,
      }).then(rowsToObjects).catch(() => []),
      readRecordSummaries(batch),
    ]);
    for (const row of traces) {
      const id = String(row?.trace_id || "").toLowerCase();
      if (!batch.includes(id)) continue;
      const cur = head(id);
      out.set(id, { ...cur, endpointId: cur.endpointId || String(row.endpoint_id || ""), provider: cur.provider || String(row.provider || "") });
    }
    for (const summary of summaries) {
      if (!batch.includes(summary.id)) continue;
      out.set(summary.id, { ...head(summary.id), firstMs: summary.firstMs, lastMs: summary.lastMs, messages: summary.messages });
    }
  }));
  if (model && ids.length === 1) {
    const [id] = ids;
    const MI = (field) => ({ stepAlias: "mi", field: `DIMENSION_FIELD_MODEL_INVOCATION_${field}` });
    const calls = await runAnalyticsQuery({
      root: { alias: "mi", entity: "ENTITY_TYPE_MODEL_INVOCATION" },
      timeRange,
      filter: { pred: { field: MI("TRACE_ID"), op: "OPERATOR_EQ", value: id } },
      output: {
        dimensions: [{ field: MI("PROVIDER"), outputAlias: "provider" }, { field: MI("MODEL"), outputAlias: "model" }],
        metrics: [{ outputAlias: "last_at", computed: { field: MI("EVENT_TIME"), op: "AGGREGATE_OP_MAX" } }],
      },
      limit: 20,
    }).then(rowsToObjects).catch(() => []);
    const when = (row) => {
      const ms = parseServerTimestamp(row?.last_at);
      return Number.isFinite(ms) ? ms : -Infinity;
    };
    const latest = calls.filter((row) => String(row?.model || "")).sort((a, b) => when(b) - when(a))[0];
    if (latest) {
      const cur = head(id);
      out.set(id, { ...cur, model: String(latest.model), provider: cur.provider || String(latest.provider || "") });
    }
  }
  return out;
}

// When each session's record was first and last observed and how many
// messages it holds, for up to 256 sessions in one read. Nothing for a
// session with no messages in the record's window, or where the read fails.
async function readRecordSummaries(ids) {
  try {
    const response = await authedFetch("/v1/conversations:readRecordSummaries", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ traceIds: ids.map(uuidToEndpointId), timeRange: sessionRecordRange(null) }),
    });
    if (!response.ok) return [];
    const json = await response.json();
    const ms = (value) => {
      const t = Date.parse(String(value || ""));
      return Number.isFinite(t) ? t : null;
    };
    return (Array.isArray(json?.summaries) ? json.summaries : [])
      .map((summary) => ({
        id: endpointIdToUuid(String(summary?.traceId || "")).toLowerCase(),
        messages: Number(summary?.messageCount) || 0,
        firstMs: ms(summary?.firstObservedAt),
        lastMs: ms(summary?.lastObservedAt),
      }))
      .filter((summary) => summary.id && summary.messages > 0);
  } catch {
    return [];
  }
}

// `spans` (session id -> { firstMs, lastMs }, or a promise of it) dates the
// sessions where the caller knows them; the rest that the index holds nothing
// of are dated by their conversation records. Without `analysis` the caller
// reads the analysis itself. A session the index holds nothing of that began
// before its horizon takes its newest summary from the prompt analysis.
async function fetchLatestSessionPrompts(sessionIds, timeRange, { dropEnriched = false, spans = null, analysis = true } = {}) {
  const out = new Map();
  const ids = [...new Set((sessionIds || []).map(String).filter(Boolean))];
  const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
  const dimensions = [
    { field: P("NOTIFICATION_ID"), outputAlias: "session_id" },
    { field: P("EVENT_ID"), outputAlias: "trace_id" },
    { field: P("EVENT_TIMESTAMP"), outputAlias: "event_time" },
    { field: P("ENDPOINT_ID"), outputAlias: "endpoint_id" },
    { field: P("PROVIDER"), outputAlias: "provider" },
    { field: P("MODEL"), outputAlias: "model" },
    { field: P("MESSAGE_TYPE"), outputAlias: "message_type" },
    { field: P("ANSWER_STATUS"), outputAlias: "answer_status" },
    ...(dropEnriched ? [] : [{ field: P("ENRICHED_PROMPT"), outputAlias: "enriched" }]),
  ];
  const runRows = async (spec) => rowsToObjects(await runAnalyticsQuery(spec));
  const pick = pickLatestPrompt;
  const batches = [];
  for (let i = 0; i < ids.length; i += SESSION_HYDRATION_BATCH) batches.push(ids.slice(i, i + SESSION_HYDRATION_BATCH));
  await Promise.all(batches.map(async (batch) => {
    try {
      const { rowsById, incomplete } = await readLatestSessionRows({
        runQuery: runRows,
        ids: batch,
        timeRange,
        notificationField: P("NOTIFICATION_ID").field,
        timestampField: P("EVENT_TIMESTAMP").field,
        summaryField: dropEnriched ? null : P("ENRICHED_PROMPT").field,
        dimensions,
        rowsPerSession: SESSION_HYDRATION_LATEST_ROWS,
      });
      for (const [id, group] of rowsById) if (group.length) out.set(id, pick(group));
      if (!incomplete.length) return;
      const grouped = await groupSessionRows(
        runRows,
        incomplete,
        (forIds) => sessionPromptSpec({
          ids: forIds, timeRange, notificationField: P("NOTIFICATION_ID").field, dimensions, perSessionLimit: SESSION_HYDRATION_ROWS,
        }),
        { hasAnswer: dropEnriched ? () => true : (row) => Boolean(String(row.enriched || "").trim()) },
      );
      for (const [id, group] of grouped) if (group.length) out.set(id, pick(group));
    } catch (err) {
      console.warn("[getSessionLog] session hydration failed:", err);
    }
  }));
  const missing = ids.filter((id) => !out.has(id));
  if (!analysis || !missing.length || (await entityHorizonMs(ANALYSIS_ROOT.entity)) == null) return out;
  const given = spans ? await Promise.resolve(spans).catch(() => null) : null;
  const known = new Map();
  const unknown = [];
  for (const id of missing) {
    const span = given?.get(String(id).toLowerCase());
    if (span) known.set(String(id).toLowerCase(), span);
    else unknown.push(id);
  }
  if (unknown.length) for (const [id, rec] of await fetchConversationHeads(unknown)) known.set(id, rec);
  const analysed = await fetchAnalysisSessions(await spansBeyondIndex(known), { dropEnriched });
  for (const id of missing) {
    const latest = analysed.get(String(id).toLowerCase())?.latest;
    if (latest) out.set(id, latest);
  }
  return out;
}

// The session's token spend rows, per provider x model x run, newest run
// first: the Lanes density strip's cost basis for a trace and for a session
// alike. One spec so the two subjects cannot drift on what the strip is priced
// against.
// The session's requests as the prompt index counts them: how many, and the
// first and last of them. The whole-conversation view opens on this; a request
// view carries it too, so both know the conversation's span.
function sessionSpanSpecFor(sessionId, timeRange) {
  const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
  return {
    root: {
      alias: "p", entity: "ENTITY_TYPE_PROMPT",
      stepFilter: [{ pred: { field: P("ANALYSIS_MODE"), op: "OPERATOR_NE", value: "NON_PROMPT" } }],
    },
    timeRange,
    filter: { pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_EQ", value: sessionId } },
    output: { metrics: [
      { outputAlias: "requests", computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
      { outputAlias: "first_time", computed: { field: P("EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MIN" } },
      { outputAlias: "last_time", computed: { field: P("EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MAX" } },
    ] },
    limit: 1,
  };
}
// The request a moment in a session is focused on: the latest at or before
// it, as the prompt index dates them. Every captured request counts here,
// NON_PROMPT frames included: a request row of any kind opens under its own
// time and must resolve to itself, not to the real prompt before it (the
// session span excludes those frames because it counts conversation turns).
// The bound is sent in the store's own timestamp form (UTC, millisecond
// precision).
function serverTimestampOf(ms) {
  return new Date(ms).toISOString().replace("T", " ").replace("Z", "");
}
function focalRequestSpecFor(sessionId, untilMs, timeRange) {
  const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
  const children = [{ pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_EQ", value: sessionId } }];
  if (Number.isFinite(untilMs)) children.push({ pred: { field: P("EVENT_TIMESTAMP"), op: "OPERATOR_LTE", value: serverTimestampOf(untilMs) } });
  return {
    root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
    timeRange,
    filter: { andExpr: { children } },
    output: { dimensions: [{ field: P("EVENT_ID"), outputAlias: "request_id" }, { field: P("EVENT_TIMESTAMP"), outputAlias: "event_time" }] },
    // Sibling calls can share a millisecond; the id breaks the tie the same
    // way every time, so a moment always resolves to the same request.
    orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_DESC" }, { outputAlias: "request_id", direction: "DIRECTION_DESC" }],
    limit: 1,
  };
}
function sessionSpanOf(rows) {
  const span = rows?.[0] || {};
  return {
    requestCount: Number(span.requests) || 0,
    firstTimestamp: span.first_time ? parseServerTimestamp(span.first_time) : null,
    lastTimestamp: span.last_time ? parseServerTimestamp(span.last_time) : null,
    lastTime: span.last_time || "",
  };
}
// A session's span as the prompt index counts its requests, else as its
// conversation record dates its messages (fetchConversationHeads): a session
// whose requests aged out of the index is still the whole conversation its
// record keeps. Null when neither knows it. The record's heads ride along
// (`heads`), so the whole conversation does not read them again.
async function sessionSpanOrRecord(sessionId, span) {
  if (span?.requestCount > 0) return span;
  const heads = await fetchConversationHeads([sessionId], { model: true });
  const rec = heads.get(String(sessionId || "").toLowerCase());
  if (!rec || !Number.isFinite(rec.firstMs)) return null;
  const last = Number.isFinite(rec.lastMs) ? rec.lastMs : rec.firstMs;
  return { requestCount: 0, firstTimestamp: rec.firstMs, lastTimestamp: last, lastTime: serverTimestampOf(last), heads };
}
function sessionClustersSpecFor(sessionId, timeRange) {
  return {
    root: {
      alias: "p", entity: "ENTITY_TYPE_PROMPT",
      // No skwach_major step_filter: the production version is scoped
      // server-side now.
      stepFilter: [],
      children: [{
        edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
        mode: "JOIN_MODE_INNER",
        target: {
          alias: "ec", entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
          children: [{
            edge: "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER",
            mode: "JOIN_MODE_LEFT",
            target: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER", optional: true },
          }],
        },
      }],
    },
    timeRange,
    filter: { pred: {
      field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" },
      op: "OPERATOR_EQ", value: sessionId,
    }},
    output: {
      dimensions: [
        { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, outputAlias: "cluster_id" },
        { field: { stepAlias: "c",  field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" },     outputAlias: "name" },
      ],
      metrics: [promptCount("trace_count")],
    },
    orderBy: [{ outputAlias: "trace_count", direction: "DIRECTION_DESC" }],
    limit: 10,
  };
}
const shapeSessionClusters = (rows) => (rows || []).map((r) => ({
  id:         Number(r.cluster_id),
  name:       String(r.name || r.cluster_id),
  traceCount: Number(r.trace_count) || 0,
}));

// The whole conversation as the trace: getTrace's answer when its id names a
// session rather than a request. Same shape as a request's, with no request
// facts (no tokens of its own, no instructions, no run) and the record read
// unbounded from the session's first request. The summary, model and endpoint
// are the newest summarized request's, as the session log shows them.
// `withoutRecord`: the caller reads the record itself (over its own window),
// so the newest page is not read here as well; the trace comes back with no
// turns and an account of nothing read yet.
async function wholeConversationTrace(sessionId, span, wideRange, untilMs = null, { withoutRecord = false } = {}) {
  const bound = Number.isFinite(untilMs) ? untilMs : null;
  const headsRead = span.heads ? Promise.resolve(span.heads) : fetchConversationHeads([sessionId], { model: true });
  // Begun before the prompt index's horizon, it has only its later prompts
  // there, if any: how many it sent, and its title where the index has none,
  // are the prompt analysis's, read as soon as its record dates it, beside
  // the rest.
  const key = String(sessionId).toLowerCase();
  const olderRead = headsRead
    .then(async (heads) => fetchAnalysisSessions(await spansBeyondIndex(new Map([[key, heads.get(key)]].filter(([, rec]) => rec))), { counts: true }))
    .then((analysed) => analysed.get(key) || null, () => null);
  const [latestBySession, clusterRows, sessionOrigin, tokenRowsRaw, recordRead, unansweredBySession, sessionCaptureSources, recordHeads, older] = await Promise.all([
    fetchLatestSessionPrompts([sessionId], wideRange, { analysis: false }),
    runDegradableQuery(sessionClustersSpecFor(sessionId, wideRange)).then(rowsToObjects).catch(() => []),
    runAnalyticsQuery(sessionOriginSpec(sessionId, wideRange)).then((r) => shapeSessionOrigin(rowsToObjects(r)[0])).catch(() => null),
    runAnalyticsQuery(sessionTokensSpecFor(sessionId, wideRange)).then(rowsToObjects).catch(() => []),
    withoutRecord ? Promise.resolve({ skipped: true }) : readConversationPage({ traceId: sessionId, timeRange: sessionRecordRange(span.firstTimestamp, bound) })
      .then((page) => ({ page }), (error) => ({ error })),
    fetchUnansweredPrompts([sessionId], wideRange),
    runAnalyticsQuery(sessionCaptureSourcesSpec(sessionId, sessionRecordRange(span.firstTimestamp, bound)))
      .then((r) => [...new Set(rowsToObjects(r).map((row) => promptCaptureSourceFromEventData({ capture_source: row.capture })).filter(Boolean))])
      .catch(() => []),
    headsRead,
    olderRead,
  ]);
  const latest = latestBySession.get(sessionId) || older?.latest || null;
  // Its machine, provider and model as its conversation record says, else as
  // its newest prompt did.
  const rec = recordHeads.get(key) || null;
  const empty = { session: true, pending: false, loading: false, rejected: false, loaded: 0, total: null, nextCursor: null, exhausted: false, pages: 0, ...(bound != null ? { untilMs: bound } : {}) };
  let turns = [];
  let record = recordRead.page
    ? null
    : recordRead.skipped ? { ...empty } : { ...empty, rejected: true, reason: String(recordRead.error?.message || recordRead.error) };
  if (recordRead.page) ({ turns, record } = applySessionPage({ data: { turns, record: empty } }, recordRead.page, { cursor: null }).data);
  const trace = adaptTrace({
    trace_id:      latest?.trace_id ? String(latest.trace_id) : "",
    endpoint_id:   rec?.endpointId || latest?.endpoint_id || "",
    endpoint_name: "",
    session_id:    sessionId,
    event_time:    span.lastTime || latest?.event_time || "",
    provider:      rec?.provider || latest?.provider || "",
    model:         rec?.model || latest?.model || "",
    cluster_id:    0,
    cluster_name:  "",
    summary:       latest?.enriched || "",
    filtered:      false,
    message_type:  "",
    answer_status: latest?.answer_status || "",
    unanswered_prompts: unansweredBySession.get(sessionId) || [],
    prompt_capture_source: sessionCaptureSources.length === 1 ? sessionCaptureSources[0] : "",
    session_token_rows: normalizeSessionTokenRows(tokenRowsRaw),
    session_clusters: shapeSessionClusters(clusterRows),
  }, {
    turns, record, tools: [], toolDefs: [], settings: null, sessionOrigin,
  });
  trace.id = sessionId;
  trace.traceId = "";
  trace.requestId = null;
  trace.untilMs = bound;
  trace.latestTraceId = latest?.trace_id ? String(latest.trace_id) : "";
  trace.requestCount = older?.requests ?? span.requestCount;
  trace.firstTimestamp = span.firstTimestamp;
  trace.lastTimestamp = span.lastTimestamp;
  return trace;
}

function sessionTokensSpecFor(sessionId, timeRange) {
  return {
    root: {
      alias: "tk", entity: "ENTITY_TYPE_TOKEN",
      stepFilter: [{
        pred: {
          field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_SESSION_ID" },
          op: "OPERATOR_EQ", value: sessionId,
        },
      }],
    },
    timeRange,
    output: {
      dimensions: [
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_PROVIDER" },      outputAlias: "provider" },
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_MODEL" },         outputAlias: "model" },
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_SUBMISSION_ID" }, outputAlias: "submission_id" },
      ],
      metrics: [
        { outputAlias: "input_tokens",  standard: { stepAlias: "tk", metric: "METRIC_TYPE_TOKEN_SUM_INPUT_TOKENS" } },
        { outputAlias: "output_tokens", standard: { stepAlias: "tk", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" } },
        ...cacheTokenMetrics("tk"),
        ...inclusiveCacheMetric("tk"),
        ...coverageMetrics("tk"),
        { outputAlias: "last_observed_at", computed: { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_OBSERVED_AT" }, op: "AGGREGATE_OP_MAX" } },
      ],
    },
    orderBy: [{ outputAlias: "last_observed_at", direction: "DIRECTION_DESC" }],
    limit: 1000,
  };
}

// The spend rows as the detail carries them. Absence stays null (unmeasured,
// never zero); the zero UUID is the pre-feature submission sentinel, not a run
// key (see adaptTrace), so it maps to "" and no cut keys on it.
function normalizeSessionTokenRows(rows) {
  return (rows || []).map((r) => ({
    provider: String(r.provider || ""),
    model: String(r.model || ""),
    submissionId: r.submission_id && String(r.submission_id) !== "00000000-0000-0000-0000-000000000000"
      ? String(r.submission_id)
      : "",
    lastObservedAt: r.last_observed_at || "",
    inputTokens: tokenOrNull(r.input_tokens),
    outputTokens: tokenOrNull(r.output_tokens),
    cacheReadTokens: tokenOrNull(r.cache_read_tokens),
    cacheCreationTokens: tokenOrNull(r.cache_creation_tokens),
    inclusiveCacheReadTokens: tokenOrNull(r.inclusive_cache_read_tokens),
    callsTotal: Number(r.calls_total) || 0,
  }));
}

// Every captured API call under one key -- an agentic turn's submission id, or
// a whole session -- with the per-call token bands joined by nearest time. The
// Lanes thread panel and the sub-agent join read runs through this; a trace
// opened on a session reads its whole conversation's calls through the same
// shape, so a spawn from any of the session's turns can be matched to its child.
// Limits are sized to the biggest runs seen (420 calls) with headroom; a set
// past them is truncated oldest-first and the result says so.
const TURN_TRACES_LIMIT = 1000;
async function tracesUnderKey(id, promptField, tokenField) {
  const wideRange = { relative: { durationSeconds: String(90 * 86400) } };
  const promptSpec = {
    root: {
      alias: "p", entity: "ENTITY_TYPE_PROMPT",
      stepFilter: [{ pred: { field: { stepAlias: "p", field: promptField }, op: "OPERATOR_EQ", value: id } }],
    },
    timeRange: wideRange,
    output: {
      dimensions: [
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },        outputAlias: "trace_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT" }, outputAlias: "summary" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MODEL" },           outputAlias: "model" },
      ],
    },
    orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }],
    limit: TURN_TRACES_LIMIT,
  };
  const tokenSpec = {
    root: {
      alias: "tk", entity: "ENTITY_TYPE_TOKEN",
      stepFilter: [{ pred: { field: { stepAlias: "tk", field: tokenField }, op: "OPERATOR_EQ", value: id } }],
    },
    timeRange: wideRange,
    output: {
      dimensions: [
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_OBSERVED_AT" },                  outputAlias: "observed_at" },
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_INPUT_TOKENS" },                 outputAlias: "input_tokens" },
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_OUTPUT_TOKENS" },                outputAlias: "output_tokens" },
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_CACHED_INPUT_TOKENS" },          outputAlias: "cache_read_tokens" },
        { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_CACHE_CREATION_INPUT_TOKENS" },  outputAlias: "cache_creation_tokens" },
      ],
    },
    orderBy: [{ outputAlias: "observed_at", direction: "DIRECTION_ASC" }],
    limit: TURN_TRACES_LIMIT,
  };
  // Bands are an enrichment: the trace listing must survive the token
  // store having nothing for this turn (older captures, other providers).
  const [promptRows, tokenRows] = await Promise.all([
    runAnalyticsQuery(promptSpec).then(rowsToObjects),
    runAnalyticsQuery(tokenSpec).then(rowsToObjects).catch(() => []),
  ]);
  const traces = promptRows
    .map((r) => ({
      traceId: String(r.trace_id || ""),
      time: r.event_time ? parseServerTimestamp(r.event_time) : null,
      summary: String(r.summary || ""),
      model: String(r.model || ""),
      input: null, output: null, cacheRead: null, cacheWrite: null,
    }))
    .filter((r) => r.traceId && r.time != null)
    .sort((a, b) => a.time - b.time);
  const calls = tokenRows
    .map((r) => ({
      time: r.observed_at ? parseServerTimestamp(r.observed_at) : null,
      input: Number(r.input_tokens) || 0,
      output: Number(r.output_tokens) || 0,
      cacheRead: Number(r.cache_read_tokens) || 0,
      cacheWrite: Number(r.cache_creation_tokens) || 0,
    }))
    .filter((r) => r.time != null)
    .sort((a, b) => a.time - b.time);
  // Monotonic two-pointer nearest-time match, one call per trace.
  const JOIN_TOLERANCE_MS = 5_000;
  let ci = 0;
  for (const tr of traces) {
    while (ci < calls.length - 1 && Math.abs(calls[ci + 1].time - tr.time) <= Math.abs(calls[ci].time - tr.time)) ci++;
    const c = calls[ci];
    if (c && Math.abs(c.time - tr.time) <= JOIN_TOLERANCE_MS) {
      tr.input = c.input;
      tr.output = c.output;
      tr.cacheRead = c.cacheRead;
      tr.cacheWrite = c.cacheWrite;
      ci = Math.min(ci + 1, calls.length - 1);
    }
  }
  // Either projection filling its limit means the run extends past what
  // was fetched (ascending order drops the newest calls) - say so.
  const truncated = promptRows.length >= TURN_TRACES_LIMIT || tokenRows.length >= TURN_TRACES_LIMIT;
  return { rows: traces, truncated };
}

// The prompt index's retention as a window: 90 days, ending an hour past now
// for clock skew. Lookups keyed by a session or a request run over it when
// nothing narrower is known; anything older has aged out. Exactly 90 days:
// the analytics service refuses an absolute window wider than that.
function indexRetentionRange() {
  const end = Math.ceil(Date.now() / 60_000) * 60_000 + 3600000;
  return { absolute: { start: new Date(end - 90 * 86400000).toISOString(), end: new Date(end).toISOString() } };
}

// The window a session's record is read in, always as two instants: from the
// session's first request when that is known (a day of slack for clock skew),
// else 90 days before the end, the record's retention; up to `untilMs` when
// the axis is bounded at a moment, else an hour past now for clock skew.
export function sessionCaptureSourcesSpec(sessionId, timeRange) {
  return {
    root: { alias: "m", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE" },
    timeRange,
    filter: { pred: { field: CONV_MSG("TRACE_ID"), op: "OPERATOR_EQ", value: sessionId } },
    output: {
      dimensions: [{ field: CONV_MSG("SOURCE_CAPTURE"), outputAlias: "capture" }],
      metrics: [{ outputAlias: "messages", computed: { field: CONV_MSG("MESSAGE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
    },
    limit: 20,
  };
}

function sessionRecordRange(sinceMs, untilMs = null) {
  const end = Number.isFinite(untilMs) ? untilMs : Math.ceil(Date.now() / 60_000) * 60_000 + 3600000;
  const start = Number.isFinite(sinceMs) ? sinceMs - 86400000 : end - 90 * 86400000;
  return { absolute: { start: new Date(start).toISOString(), end: new Date(end).toISOString() } };
}

export const HTTP_METHODS = {
  getEndpointScopeStatus: {
    execute: async ({ endpointIds = [] } = {}) => {
      const ids = [...new Set(endpointIds.map((id) => String(id).toLowerCase()))];
      if (!ids.length || ids.length > 1000) throw new Error("Choose between 1 and 1000 endpoints.");
      const field = { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_ENDPOINT_ID" };
      const rows = rowsToObjects(await runAnalyticsQuery({
        root: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" },
        // ENDPOINT has no time field: existence is independent of activity
        // and of the date range on the link.
        timeRange: { relative: { durationSeconds: "86400" } },
        filter: { pred: { field, op: "OPERATOR_IN", value: ids } },
        output: { metrics: [{
          outputAlias: "count", computed: { field, op: "AGGREGATE_OP_COUNT_DISTINCT" },
        }] },
        limit: 1,
      }, { cache: false }));
      const count = Number(rows[0]?.count);
      if (!Number.isSafeInteger(count) || count < 0 || count > ids.length) {
        throw new Error("Endpoint lookup returned an invalid count.");
      }
      return { found: count === ids.length };
    },
  },
  // ILO clustering generations for the generation picker (see the builders
  // above). Fresh reads — the pin discovery surface, cache:false.
  getGenerationAlgorithms: {
    execute: async () => {
      const rows = rowsToObjects(await runAnalyticsQuery(generationAlgoFacetSpec(), { cache: false }));
      return rows
        .map((r) => ({ algorithm: r.algorithm ? String(r.algorithm) : "unknown", count: Number(r.n ?? 0) }))
        .filter((a) => a.algorithm);
    },
  },
  getGenerations: {
    execute: async ({ algorithm } = {}) => {
      const rows = rowsToObjects(await runAnalyticsQuery(generationListSpec(algorithm), { cache: false }));
      return rows.map(shapeGenerationRow).filter((g) => g.generationId);
    },
  },
  init: {
    // Readiness signal for the data client. Hits the analytics API's
    // DescribeSchema (memoized at the module level after first call,
    // so this is a single round trip per page load and zero on
    // subsequent navigations). The schema response itself isn't used
    // here: useWorkerReady() only reports whether the backend answered.
    //
    // A 403 here is NOT a readiness failure: it means the backend is
    // reachable and the session is authenticated, but this role lacks
    // `tenant.analytics.execute` (e.g. IT / Fleet Admin). Treating it as a
    // failure would replace the dashboard with the startup error for any
    // role without analytics. Analytics surfaces gate/handle their own
    // denial downstream. Any other failure (network, 5xx, 401) still propagates.
    execute: async () => {
      try {
        await describeSchema();
      } catch (err) {
        if (err?.status !== 403) throw err;
      }
      return { ok: true };
    },
  },

  getTraces: {
    // PROMPT-rooted query: ai_prompt_analysis carries the trace identity,
    // endpoint/session ids, timestamp, and prompt metadata needed by this
    // list. Rooting here avoids scanning the much larger raw events table
    // before optional cluster/signal/identity enrichment. Analysis ingestion
    // is asynchronous, so the default newest page also merges a tightly
    // bounded raw-event fallback for prompts not indexed here yet. The planner
    // scopes PROMPT to the production analysis version and FINALs it server-side.
    //
    // Model comes from PROMPT's own request-body column
    // (DIMENSION_FIELD_PROMPT_MODEL); per-row token counts aren't
    // projected (no per-prompt token join here). sort=model orders on the projected `model` alias
    // directly; sort=tokens degrades to newest-first.
    //
    // Scope handling fails closed. The loop below builds an analytics
    // predicate for every dim it can express (endpoint / session / agent /
    // model / cluster, tool_call via array membership, plus the mcp_server /
    // skill adapters; identity rides the PROMPT → assignment → directory_user
    // join (viaAssignment), matching every other identity-scoped surface;
    // signal / skill ride the SignalTags join via their own branches below).
    // Any other dim — project / host / url / command
    // (no DIM_INFO field) or file (PROMPT_RESOURCE_PATHS is a filterable
    // Array(String) server-side now, but this traces-scope path isn't yet
    // wired to emit the membership predicate; un-parking it is gated on the
    // backend carrying the array retype — api-server in shared envs — being
    // deployed; see featureFlags.js ANALYTICS_SUPPORTED_DIM_PLURALS) — returns an empty,
    // flagged result. We never silently drop the predicate because that
    // would show unscoped traces as if they were scoped.
    execute: async ({ scopePath = [], sort = "newest", limit = 1000, offset = 0, range, search = "", query = "", canReadSensitivePrompts = true } = {}) => {
      const compiled = await compileTraceScope({ scopePath, range, search, query, canReadSensitivePrompts });
      if (compiled.unexpressibleScopeDim) {
        const empty = [];
        empty.total = 0;
        empty.totalIsExact = true;
        empty.unsupportedScope = [compiled.unexpressibleScopeDim];
        return empty;
      }
      const { apiScope, apiRange, trimmed, filter, root: candidateRootWithJoins, dropEnriched } = compiled;

      const orderBy = sort === "oldest"
        ? [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }]
        : sort === "session"
        ? [{ outputAlias: "session_id", direction: "DIRECTION_ASC" }, { outputAlias: "event_time", direction: "DIRECTION_ASC" }]
        : sort === "model"
        // Model A-Z, newest first within each model. Orders on the projected
        // `model` alias, sourced from the prompt-level request-body model
        // (DIMENSION_FIELD_PROMPT_MODEL) — the response-resolved TOKEN_MODEL
        // isn't joined per-prompt here.
        ? [{ outputAlias: "model", direction: "DIRECTION_ASC" }, { outputAlias: "event_time", direction: "DIRECTION_DESC" }]
        // sort === "tokens" degrades to newest (handled by the default below):
        // per-trace tokens aren't joined here.
        : [{ outputAlias: "event_time", direction: "DIRECTION_DESC" }];

      // When enriched bodies are dropped (dropEnriched — set when the
      // caller lacks the sensitive-prompt read, or an adapter declares
      // its bodies too large for the list projection), omit
      // PROMPT_ENRICHED_PROMPT from the projection. Rows still render —
      // the summary cell falls back to a redacted placeholder. Detail
      // view fetches the body separately on click.
      const listDimensions = [
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },        outputAlias: "trace_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },     outputAlias: "endpoint_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, outputAlias: "session_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" },       outputAlias: "provider" },
        { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, outputAlias: "cluster_id" },
        { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" },       outputAlias: "cluster_name" },
        // Model comes from the prompt-level request-body model
        // (DIMENSION_FIELD_PROMPT_MODEL) — the response-resolved TOKEN_MODEL
        // isn't joined per-prompt here. This is the request-body model
        // written on the PROMPT row itself; the adapter's `model` coalesce
        // (below) reads this directly.
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MODEL" },                    outputAlias: "model" },
        // Hydration sees NON_PROMPT only for bounded raw-event candidates. The
        // list drops those rows before adapting them; detail reads can still
        // explain a directly opened non-prompt frame.
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ANALYSIS_MODE" }, outputAlias: "analysisMode" },
        // skwach writes message_type="SCAFFOLDING" for infrastructure prompts
        // (tool loops, harness XML, warmup) — these get no embedding, so they're
        // never sent to clustering and would otherwise read "cluster Pending"
        // forever. Projected so the detail chip can mark them terminal. Same
        // already-joined PROMPT step.
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MESSAGE_TYPE" },    outputAlias: "message_type" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ANSWER_STATUS" },   outputAlias: "answer_status" },
        // Per-trace token columns stay out of this projection (no per-prompt
        // token join), so per-row tokens
        // are 0; the summary bar's "N tokens" total comes from the scope-level TOKEN
        // sums instead (TraceLogPanel's totalTokens prop).
      ];
      if (!dropEnriched) {
        listDimensions.splice(5, 0,
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT" }, outputAlias: "enriched" },
        );
      }
      const pageLimit = Math.max(0, Math.floor(Number(limit) || 0));
      const pageOffset = Math.max(0, Math.floor(Number(offset) || 0));
      const candidateDimensions = [
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, outputAlias: "trace_id" },
      ];
      const candidateMetric = (field, op, outputAlias) => ({
        outputAlias,
        computed: { field: { stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${field}` }, op: `AGGREGATE_OP_${op}` },
      });
      // Filter and authorize source rows before grouping. The representative
      // remains a real source event for hydration and drill-down; its endpoint
      // metadata comes from that exact event, not from another observation.
      const candidateMetrics = [
        candidateMetric("NOTIFICATION_ID", "MIN", "session_id"),
        candidateMetric("EVENT_TIMESTAMP", sort === "oldest" ? "MIN" : "MAX", "event_time"),
        candidateMetric("EVENT_ID", "COUNT_DISTINCT", "candidate_rows"),
      ];
      if (sort === "model") {
        candidateMetrics.push(candidateMetric("MODEL", "MIN", "model"));
      }
      const candidateSpec = {
        root: candidateRootWithJoins,
        timeRange: rangeToTimeRange(apiRange),
        filter,
        output: {
          dimensions: candidateDimensions,
          metrics: candidateMetrics,
        },
        orderBy: [...orderBy, { outputAlias: "trace_id", direction: "DIRECTION_ASC" }],
        limit: pageLimit,
        ...(pageOffset ? { offset: pageOffset } : {}),
      };
      const loadCandidates = async () => rowsToObjects(await runAnalyticsQuery(candidateSpec));

      // Count uses the same root/filter so scope/search match the list,
      // and counts distinct visible source events —
      // protecting the total from optional-join fan-out.
      const countSpec = {
        root: candidateRootWithJoins,
        timeRange: rangeToTimeRange(apiRange),
        filter,
        output: {
          metrics: [promptCount("total")],
        },
      };

      const loadCount = async () => rowsToObjects(await runAnalyticsQuery(countSpec));

      // Analysis ingestion can trail raw event ingestion. For the homepage's
      // default newest page, cover that gap with a small EVENT query restricted
      // to AiPrompt and the only scopes EVENT can reproduce exactly. Other sorts,
      // searches, and analysis-only scopes remain PROMPT-only so their semantics
      // do not change.
      const loadRecentRawPrompts = async () => {
        const eligible = pageLimit > 0
          && pageOffset === 0
          && (sort === "newest" || sort === "tokens")
          && !trimmed
          && !String(query || "").trim()
          && apiScope.every((step) => step.dim === "endpoint" || step.dim === "session");
        if (!eligible) return { enabled: false, failed: false, saturated: false, rows: [] };

        const { fromMs, toMs } = rangeBoundsMs(apiRange);
        const nowMs = Date.now();
        const endMs = Math.min(nowMs, toMs);
        if (nowMs - endMs >= 3 * 60 * 60 * 1000) {
          return { enabled: false, failed: false, saturated: false, rows: [] };
        }
        const startMs = Math.max(fromMs, endMs - 3 * 60 * 60 * 1000);
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs >= endMs) {
          return { enabled: false, failed: false, saturated: false, rows: [] };
        }

        const rawPreds = [{
          pred: {
            field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TYPE" },
            op: "OPERATOR_EQ",
            value: "AiPrompt",
          },
        }];
        for (const step of apiScope) {
          const ids = (step.ids?.length ? step.ids : step.id != null ? [step.id] : [])
            .map(String)
            .filter(Boolean);
          if (!ids.length) continue;
          rawPreds.push({
            pred: {
              field: {
                stepAlias: "e",
                field: step.dim === "endpoint"
                  ? "DIMENSION_FIELD_EVENT_ENDPOINT_ID"
                  : "DIMENSION_FIELD_EVENT_NOTIFICATION_ID",
              },
              op: ids.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
              value: ids.length > 1 ? ids : ids[0],
            },
          });
        }
        const rawSpec = {
          root: { alias: "e", entity: "ENTITY_TYPE_EVENT" },
          timeRange: {
            absolute: {
              start: new Date(startMs).toISOString(),
              end: new Date(endMs).toISOString(),
            },
          },
          filter: rawPreds.length === 1 ? rawPreds[0] : { andExpr: { children: rawPreds } },
          output: {
            dimensions: [
              { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },        outputAlias: "trace_id" },
              { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_ENDPOINT_ID" },     outputAlias: "endpoint_id" },
              { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_NOTIFICATION_ID" }, outputAlias: "session_id" },
              { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
            ],
          },
          orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_DESC" }],
          limit: pageLimit + 1,
        };
        try {
          const rows = rowsToObjects(await runAnalyticsQuery(rawSpec));
          return {
            enabled: true,
            failed: false,
            saturated: rows.length > pageLimit,
            rows: rows.slice(0, pageLimit),
          };
        } catch (err) {
          console.warn("[getTraces] recent raw-prompt fallback failed:", err);
          return { enabled: true, failed: true, saturated: false, rows: [] };
        }
      };

      const [candidateRows, countRows, recentRaw] = await Promise.all([
        loadCandidates(),
        loadCount().catch(() => null),
        loadRecentRawPrompts(),
      ]);

      const candidatesById = new Map();
      const orderedCandidates = [];
      const addCandidate = (row, source) => {
        const traceId = String(row?.trace_id || "");
        if (!traceId || candidatesById.has(traceId)) return;
        const candidate = { ...row, trace_id: traceId, candidateSource: source };
        candidatesById.set(traceId, candidate);
        orderedCandidates.push(candidate);
      };
      for (const row of candidateRows) addCandidate(row, "prompt");
      for (const row of recentRaw.rows) addCandidate(row, "event");
      if (recentRaw.enabled) {
        orderedCandidates.sort((a, b) =>
          parseServerTimestamp(b.event_time) - parseServerTimestamp(a.event_time));
      }
      // Hydrate the bounded candidate union before selecting the page. Prompt
      // and raw candidates are each capped at pageLimit, so this remains bounded
      // to 2 * pageLimit ids and can refill a page after non-prompt rows drop.
      const idsToHydrate = orderedCandidates.map((row) => row.trace_id);
      const hydrationRoot = {
        alias: "p",
        entity: "ENTITY_TYPE_PROMPT",
        children: [{
          edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
          mode: "JOIN_MODE_LEFT",
          target: {
            alias: "ec",
            entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
            children: [{
              edge: "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER",
              mode: "JOIN_MODE_LEFT",
              target: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER", optional: true },
            }],
          },
        }],
      };
      const hydrateBatch = async (eventIds) => {
        const buildHydrationSpec = (dimensions) => ({
          root: hydrationRoot,
          timeRange: rangeToTimeRange(apiRange),
          filter: {
            pred: {
              field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
              op: "OPERATOR_IN",
              value: eventIds,
            },
          },
          output: { dimensions },
        });
        const compatibleDimensions = listDimensions.filter((dimension) =>
          dimension.field?.field !== PROMPT_ANSWER_STATUS_DIMENSION);
        const dimensions = promptAnswerStatusAvailable ? listDimensions : compatibleDimensions;
        try {
          return rowsToObjects(await runDegradableQuery(buildHydrationSpec(dimensions)));
        } catch (err) {
          // Dashboard assets can reach an environment before the matching
          // analytics binary during a staged rollout. Keep the trace list
          // usable until PROMPT_ANSWER_STATUS is available; a later refresh
          // automatically restores the answer-state projection.
          if (!promptAnswerStatusAvailable || !isUnsupportedPromptAnswerStatus(err)) throw err;
          promptAnswerStatusAvailable = false;
          console.warn("[getTraces] answer status is not available yet; retrying compatible hydration");
          return rowsToObjects(
            await runDegradableQuery(buildHydrationSpec(compatibleDimensions)),
          );
        }
      };
      const hydrationBatches = Array.from(
        { length: Math.ceil(idsToHydrate.length / 1000) },
        (_, index) => idsToHydrate.slice(index * 1000, (index + 1) * 1000),
      );
      const rawEventIds = recentRaw.rows
        .map((row) => String(row.trace_id || ""))
        .filter(Boolean);
      const rawSessionIds = new Set(recentRaw.rows
        .map((row) => String(row.session_id || ""))
        .filter(Boolean));
      const classificationTraceId = rawSessionIds.size === 1
        ? rawSessionIds.values().next().value
        : "";
      const classifyNormalizedBatch = async (eventIds) => {
        const spec = {
          root: { alias: "mi", entity: "ENTITY_TYPE_MODEL_INVOCATION" },
          timeRange: rangeToTimeRange(apiRange),
          filter: {
            andExpr: { children: [
              {
                pred: {
                  field: { stepAlias: "mi", field: "DIMENSION_FIELD_MODEL_INVOCATION_TRACE_ID" },
                  op: "OPERATOR_EQ",
                  value: classificationTraceId,
                },
              },
              {
                pred: {
                  field: { stepAlias: "mi", field: "DIMENSION_FIELD_MODEL_INVOCATION_SOURCE_EVENT_ID" },
                  op: "OPERATOR_IN",
                  value: eventIds,
                },
              },
            ] },
          },
          output: {
            dimensions: [
              { field: { stepAlias: "mi", field: "DIMENSION_FIELD_MODEL_INVOCATION_SOURCE_EVENT_ID" }, outputAlias: "source_event_id" },
            ],
            metrics: [{
              outputAlias: "user_message_count",
              computed: {
                field: { stepAlias: "mi", field: "DIMENSION_FIELD_MODEL_INVOCATION_PRIMARY_MESSAGE_ID" },
                op: "AGGREGATE_OP_COUNT",
              },
            }],
          },
          limit: eventIds.length,
        };
        return rowsToObjects(await runAnalyticsQuery(spec));
      };
      const invocationBatches = Array.from(
        { length: Math.ceil(rawEventIds.length / 1000) },
        (_, index) => rawEventIds.slice(index * 1000, (index + 1) * 1000),
      );
      // ModelInvocation is a parameterized trace view and cannot classify
      // events from multiple sessions in one legal query. Do not turn a page
      // load into one query per session: keep analyzed rows and fail closed on
      // raw-only candidates until the page is narrowed to one session.
      let invocationClassificationFailed = rawEventIds.length > 0 && !classificationTraceId;
      const [hydratedRows, invocationRows] = await Promise.all([
        hydrationBatches.length
          ? Promise.all(hydrationBatches.map(hydrateBatch)).then((rows) => rows.flat())
          : [],
        invocationBatches.length && classificationTraceId
          ? Promise.all(invocationBatches.map(classifyNormalizedBatch))
            .then((rows) => rows.flat())
            .catch((err) => {
              invocationClassificationFailed = true;
              console.warn("[getTraces] normalized raw-event classification failed:", err);
              return [];
            })
          : [],
      ]);
      const hydratedById = new Map();
      for (const row of hydratedRows) {
        const traceId = String(row.trace_id || "");
        if (traceId && !hydratedById.has(traceId)) hydratedById.set(traceId, row);
      }
      const nonUserEventIds = new Set(invocationRows
        .filter((row) => Number(row.user_message_count) === 0)
        .map((row) => String(row.source_event_id || ""))
        .filter(Boolean));
      const rawIds = new Set(rawEventIds);
      const selectedCandidates = orderedCandidates
        .filter((candidate) => !nonUserEventIds.has(candidate.trace_id))
        // If normalized classification is unavailable, keep the analyzed
        // prompt list useful but fail closed on unclassified raw-only rows.
        .filter((candidate) => !invocationClassificationFailed
          || !rawIds.has(candidate.trace_id)
          || hydratedById.has(candidate.trace_id))
        .filter((candidate) => hydratedById.get(candidate.trace_id)?.analysisMode !== "NON_PROMPT")
        .slice(0, pageLimit);
      // A prompt-sourced candidate that did not hydrate is one whose body this
      // caller may not read here: the candidate query asks non-sensitive columns
      // and so reaches rows the hydration's scope does not. When none of them
      // hydrated, the caller reads no bodies in this scope at all and the panel
      // shows the same policy notice it shows without the permission.
      const promptCandidates = orderedCandidates
        .filter((candidate) => candidate.candidateSource === "prompt");
      const contentWithheld = !dropEnriched
        && promptCandidates.length > 0
        && promptCandidates.every((candidate) => !hydratedById.has(candidate.trace_id));

      const adapted = [];
      for (const candidate of selectedCandidates) {
        const tid = candidate.trace_id;
        const hydrated = hydratedById.get(tid);
        const r = hydrated ?? candidate;
        const adaptedTrace = adaptTrace({
          trace_id:    tid,
          endpoint_id: r.endpoint_id,
          endpoint_name: "",
          session_id:  r.session_id,
          event_time:  r.event_time,
          provider:    r.provider || "",
          // Prompt-level request-body model (the response-resolved TOKEN_MODEL
          // isn't joined per-prompt). No token columns are passed here, so
          // adaptTrace leaves the per-row tokens null (unmeasured) — the list
          // doesn't surface per-prompt tokens.
          model:       r.model || "",
          cluster_id:   r.cluster_id != null ? Number(r.cluster_id) : 0,
          cluster_name: r.cluster_name || "",
          summary:      r.enriched || "",
          filtered:     false,
          // SCAFFOLDING → terminal "Infrastructure" cluster badge (never clusters).
          message_type: r.message_type || "",
          answer_status: r.answer_status || "",
        });
        // Two ways a body is absent rather than late. dropEnriched: the
        // projection skipped PROMPT_ENRICHED_PROMPT. No hydration row at all:
        // the scope refused this prompt's body. Either way mark it, so TraceRow
        // renders the redacted placeholder instead of the "Pending" badge,
        // which means "skwach hasn't analyzed this yet" and never resolves here.
        if ((dropEnriched && (hydrated || candidate.candidateSource === "prompt"))
          || (candidate.candidateSource === "prompt" && !hydrated)) {
          adaptedTrace.summaryUnavailable = true;
        }
        adapted.push(adaptedTrace);
      }

      const countedTotal = Number(countRows?.[0]?.total);
      const countIsExact = Number.isFinite(countedTotal) && countedTotal >= 0;
      const promptTotal = countIsExact ? countedTotal : candidateRows.length;
      const knownPending = [...rawIds]
        .filter((id) => !invocationClassificationFailed
          && !nonUserEventIds.has(id)
          && !hydratedById.has(id))
        .length;
      adapted.total = Math.max(adapted.length, promptTotal + knownPending);
      adapted.totalIsExact = countIsExact
        && !recentRaw.failed
        && !recentRaw.saturated
        && !invocationClassificationFailed;
      adapted.contentWithheld = contentWithheld;
      adapted.queryWarnings = compiled.queryWarnings;
      adapted.searchedConversations = Boolean(compiled.searchedConversations);
      await markSourceChips(adapted, rangeToTimeRange(apiRange));
      return adapted;
    },
  },

  getSessionLog: {
    // One row per SESSION (conversation), not per request. An agent session is
    // a stream of captures, and every request re-sends the conversation so
    // far, so a request-grain log repeats each conversation once per call and
    // buries the sessions under their own churn. This is the same PROMPT-rooted
    // scope as getTraces (compileTraceScope: every scope dim, the search box
    // and the query bar apply unchanged), collapsed to the session by grouping
    // on the notification id and ordered by the newest request in each. Each
    // session then carries its newest prompt (summary, model, endpoint) through
    // the AI_SESSION -> PROMPT sub-limited join, the read sessionLabels.js
    // already uses. Rows are trace-shaped so TraceLogPanel renders them through
    // the same table; `id` is the newest request's event id, which is what
    // opening the row shows until the session-first detail lands.
    // `byTrace` lists sessions as the workspace does, from their capture
    // frames (traceSessionLog). Explore's list and the Reviews lens leave it
    // off and read the prompt index as before, until the rest of Explore (its
    // breakdowns and request list) can move with them.
    execute: async ({ scopePath = [], sort = "newest", limit = 200, offset = 0, range, search = "", query = "", canReadSensitivePrompts = true, byTrace = false } = {}) => {
      const traced = byTrace ? await traceSessionLog({ scopePath, sort, limit, offset, range, search, query, canReadSensitivePrompts }) : null;
      if (traced) return traced;
      const compiled = await compileTraceScope({ scopePath, range, search, query, canReadSensitivePrompts });
      if (compiled.unexpressibleScopeDim) {
        const empty = [];
        empty.total = 0;
        empty.totalIsExact = true;
        empty.unsupportedScope = [compiled.unexpressibleScopeDim];
        return empty;
      }
      const { apiRange, filter, root, dropEnriched } = compiled;
      const timeRange = rangeToTimeRange(apiRange);
      const pageLimit = Math.max(0, Math.floor(Number(limit) || 0));
      const pageOffset = Math.max(0, Math.floor(Number(offset) || 0));
      const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
      // Newest first is the session whose newest request is latest; oldest is
      // the earliest-started session; requests ranks by volume, recency
      // breaking ties.
      const orderBy = sort === "oldest"
        ? [{ outputAlias: "first_time", direction: "DIRECTION_ASC" }]
        : sort === "requests"
        ? [{ outputAlias: "requests", direction: "DIRECTION_DESC" }, { outputAlias: "last_time", direction: "DIRECTION_DESC" }]
        : [{ outputAlias: "last_time", direction: "DIRECTION_DESC" }];
      const sessionSpec = {
        root,
        timeRange,
        filter,
        output: {
          dimensions: [{ field: P("NOTIFICATION_ID"), outputAlias: "session_id" }],
          metrics: [
            { outputAlias: "requests", computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
            { outputAlias: "last_time", computed: { field: P("EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MAX" } },
            { outputAlias: "first_time", computed: { field: P("EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MIN" } },
          ],
        },
        orderBy,
        limit: pageLimit,
        ...(pageOffset ? { offset: pageOffset } : {}),
      };
      // The same root and filter, counting sessions rather than requests.
      const countSpec = {
        root,
        timeRange,
        filter,
        output: {
          metrics: [{ outputAlias: "total", computed: { field: P("NOTIFICATION_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
      };
      const [sessionRows, countRows] = await Promise.all([
        runAnalyticsQuery(sessionSpec).then(rowsToObjects),
        runAnalyticsQuery(countSpec).then(rowsToObjects).catch(() => null),
      ]);
      const sessions = [];
      const seen = new Set();
      for (const row of sessionRows) {
        const sessionId = String(row?.session_id || "");
        if (!sessionId || seen.has(sessionId)) continue;
        seen.add(sessionId);
        sessions.push({ ...row, session_id: sessionId });
      }
      const sessionIds = sessions.map((s) => s.session_id);
      const [latestBySession, unansweredBySession, sourceChips] = await Promise.all([
        fetchLatestSessionPrompts(sessionIds, timeRange, { dropEnriched, analysis: false }),
        fetchUnansweredPrompts(sessionIds, timeRange),
        fetchSourceChips(sessionIds, timeRange),
      ]);
      const adapted = sessions.map((s) => {
        const latest = latestBySession.get(s.session_id) || null;
        const trace = adaptTrace({
          trace_id:      latest?.trace_id ? String(latest.trace_id) : "",
          endpoint_id:   latest?.endpoint_id || "",
          endpoint_name: "",
          session_id:    s.session_id,
          // The row's time is the session's newest request in the window,
          // whether or not the hydration found a summarized request.
          event_time:    s.last_time,
          provider:      latest?.provider || "",
          model:         latest?.model || "",
          cluster_id:    0,
          cluster_name:  "",
          summary:       latest?.enriched || "",
          filtered:      false,
          message_type:  latest?.message_type || "",
          answer_status: latest?.answer_status || "",
          unanswered_prompts: unansweredBySession.get(s.session_id) || [],
        });
        // The row is the session's: its id opens the whole conversation
        // through getTrace, the same tab a request opens bounded at itself.
        trace.latestTraceId = trace.id || "";
        trace.id = s.session_id;
        trace.requestCount = Number(s.requests) || 0;
        trace.firstTimestamp = s.first_time ? parseServerTimestamp(s.first_time) : null;
        trace.lastTimestamp = s.last_time ? parseServerTimestamp(s.last_time) : null;
        // The session list groups non-sensitive columns and so reaches sessions
        // whose prompts the hydration's scope does not. Either way the body is
        // absent rather than late, and "Pending" would claim the session is
        // still being analyzed.
        if (dropEnriched || !latest) trace.summaryUnavailable = true;
        trace.viaSource = sourceChipFor(sourceChips, s.session_id);
        return trace;
      });
      const countedTotal = Number(countRows?.[0]?.total);
      const countIsExact = Number.isFinite(countedTotal) && countedTotal >= 0;
      adapted.total = Math.max(adapted.length + pageOffset, countIsExact ? countedTotal : 0);
      adapted.totalIsExact = countIsExact;
      adapted.queryWarnings = compiled.queryWarnings;
      adapted.searchedConversations = Boolean(compiled.searchedConversations);
      return adapted;
    },
  },


  getSessionMatches: {
    // The sessions a query matches as sessions (lib/sessionMatch.js): each
    // top-level clause holds somewhere in the session, a negated one nowhere.
    // At most SESSION_MATCH_CAP of them, newest activity first, each with how
    // many of its requests every counted clause matched; `capped` says there
    // are more. The scope narrows the requests read as it narrows the session
    // log. `prefiltered` says the rows' spans are the matching requests' only,
    // so getSessionHeads reads the sessions' own.
    execute: async ({ scopePath = [], range, query = "" } = {}) => {
      if (!sessionClauses(query).clauses.length) {
        const byTrace = await traceSessionMatches({ scopePath, range });
        if (byTrace) return byTrace;
      }
      const scope = await compileTraceScope({ scopePath, range });
      if (scope.unexpressibleScopeDim) {
        return { sessions: [], clauses: [], capped: false, prefiltered: false, warnings: [], unsupportedScope: [scope.unexpressibleScopeDim] };
      }
      const { apiRange, filter: scopeFilter, root } = scope;
      const range0 = { startTime: apiRange?.from, endTime: apiRange?.to };
      const warnings = [];
      let searchedConversations = false;
      const clauses = [];
      const resolved = await Promise.all(sessionClauses(query).clauses.map(async (clause) => ({ clause, out: await resolveAndCompile(clause.text, { range: range0 }) })));
      for (const { clause, out } of resolved) {
        warnings.push(...(out.warnings || []));
        if (out.searchedConversations) searchedConversations = true;
        if (!out.filter) {
          warnings.push(...(out.errors || []));
          continue;
        }
        const grain = clauseGrain(out.filter);
        if (grain === "request") warnings.push({ message: `${clause.text} is matched within single requests, not across the trace` });
        clauses.push({ ...clause, filter: out.filter, joins: out.joins || [], grain });
      }
      const joins = [...new Set(clauses.flatMap((c) => c.joins))];
      const spec = buildSessionMatchSpec({
        root: joins.length ? ensureQueryJoins(root, joins) : root,
        timeRange: rangeToTimeRange(apiRange),
        scopeFilter,
        clauses,
      });
      const rows = rowsToObjects(await runAnalyticsQuery(spec));
      const { sessions, capped } = readSessionMatches(rows, clauses, { parseTime: parseServerTimestamp });
      return {
        sessions,
        clauses: clauses.map(({ text, negated, label, grain }) => ({ text, negated, label, grain })),
        capped,
        prefiltered: clauses.some((c) => c.grain === "count" && !c.negated),
        warnings,
        searchedConversations,
      };
    },
  },

  getFileSpread: {
    // How widely files were touched across the range: of the range's sessions
    // (`total`), how many touched each file (`df`), a file given by its folder
    // and name and matched at the end of any path. With `endpoints`, only the
    // sessions those machines ran count. Aggregates only, one row per read;
    // the files are read FILE_SPREAD_CHUNK at a time so one read's predicates
    // stay bounded however many files are asked about, and FILE_SPREAD_PARALLEL
    // reads at a time so they stay bounded however many chunks that makes.
    execute: async ({ range, keys = [], endpoints = [] } = {}) => {
      const list = [...new Set((Array.isArray(keys) ? keys : []).map((k) => String(k || "").trim()).filter(Boolean))];
      if (!list.length) return { total: 0, df: {} };
      const machines = [...new Set((Array.isArray(endpoints) ? endpoints : []).map((e) => String(e || "").trim()).filter(Boolean))];
      const scope = await compileTraceScope({ scopePath: [], range });
      const { apiRange, root } = scope;
      const onMachines = machines.length
        ? { pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" }, op: "OPERATOR_IN", value: machines } }
        : null;
      const scopeFilter = scope.filter && onMachines ? { andExpr: { children: [scope.filter, onMachines] } } : scope.filter || onMachines;
      const range0 = { startTime: apiRange?.from, endTime: apiRange?.to };
      const SESSION = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" };
      const compiled = await Promise.all(list.map((k) => resolveAndCompile(`file:*${k}`, { range: range0 })));
      const chunks = [];
      for (let i = 0; i < list.length; i += FILE_SPREAD_CHUNK) chunks.push(list.map((k, j) => j).slice(i, i + FILE_SPREAD_CHUNK));
      const readChunk = async (idx) => {
        const parts = idx.filter((j) => compiled[j]?.filter);
        const joins = [...new Set(parts.flatMap((j) => compiled[j].joins || []))];
        const spec = {
          root: joins.length ? ensureQueryJoins(root, joins) : root,
          timeRange: rangeToTimeRange(apiRange),
          ...(scopeFilter ? { filter: scopeFilter } : {}),
          output: {
            metrics: [
              { outputAlias: "total", computed: { field: SESSION, op: "AGGREGATE_OP_COUNT_DISTINCT" } },
              ...parts.map((j) => ({ outputAlias: `df_${j}`, computed: { field: SESSION, op: "AGGREGATE_OP_COUNT_DISTINCT", predicate: compiled[j].filter } })),
            ],
          },
        };
        return rowsToObjects(await runAnalyticsQuery(spec))[0] || {};
      };
      const rows = new Array(chunks.length);
      let next = 0;
      const worker = async () => {
        while (next < chunks.length) {
          const at = next++;
          rows[at] = await readChunk(chunks[at]);
        }
      };
      await Promise.all(Array.from({ length: Math.min(FILE_SPREAD_PARALLEL, chunks.length) }, worker));
      const df = {};
      let total = 0;
      for (const row of rows) {
        total = Math.max(total, Number(row.total) || 0);
        for (const [alias, v] of Object.entries(row)) {
          const m = /^df_(\d+)$/.exec(alias);
          if (m) df[list[Number(m[1])]] = Number(v) || 0;
        }
      }
      return { total, df };
    },
  },

  getDirectoryGroups: {
    // Whether the directory gives anyone a department ({ any }): the welcome
    // page shows groups only for a tenant whose directory has some. At most one
    // row, read off the directory's people themselves (the entity keeps no
    // time, so the window is the label reads' widest), not the range's requests.
    execute: async () => {
      const department = { stepAlias: "du", field: "DIMENSION_FIELD_DIRECTORY_USER_DEPARTMENT" };
      const rows = rowsToObjects(await runAnalyticsQuery({
        root: { alias: "du", entity: "ENTITY_TYPE_DIRECTORY_USER" },
        timeRange: { relative: { durationSeconds: String(90 * 86400) } },
        filter: { pred: { field: department, op: "OPERATOR_NE", value: "" } },
        output: { dimensions: [{ field: department, outputAlias: "department" }] },
        limit: 1,
      }));
      return { any: rows.some((r) => String(r?.department || "").trim() !== "") };
    },
  },

  getMachinePeople: {
    // Whose each machine of a list of sessions is, as the directory assigns
    // machines to people (keyed by machine id): the person's name and
    // department ("" where the directory gives none); where a machine is
    // assigned to more than one person, the one with most of those sessions.
    // A machine no one is assigned to is left out. Bounded by the sessions (a
    // session match's, at most SESSION_MATCH_CAP) and their machines.
    execute: async ({ ids = [], range } = {}) => {
      const sessionIds = [...new Set((Array.isArray(ids) ? ids : []).map((id) => String(id || "").toLowerCase()).filter(Boolean))];
      if (!sessionIds.length) return {};
      const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
      // The directory is reached only through a prompt's machine: by session,
      // over the range, as the index holds the sessions. Where the range
      // reaches back past the index, a machine whose sessions here all fell
      // before the index's horizon could not be found that way, so it is also
      // asked about over the index's whole retention: a session older than the
      // index finds the person of a machine the index has seen since. A machine
      // left unresolved with a session since is assigned to no one.
      const apiRange = toApiRange(range);
      const read = (filter, timeRange, limit) => runAnalyticsQuery({
        root: {
          alias: "p", entity: "ENTITY_TYPE_PROMPT",
          children: [{
            edge: "EDGE_TYPE_PROMPT_HAS_USER_ASSIGNMENTS", mode: "JOIN_MODE_INNER",
            target: {
              alias: "u", entity: "ENTITY_TYPE_ENDPOINT_USER_ASSIGNMENT",
              children: [{ edge: "EDGE_TYPE_USER_ASSIGNMENT_HAS_DIRECTORY_USER", mode: "JOIN_MODE_INNER", target: { alias: "du", entity: "ENTITY_TYPE_DIRECTORY_USER" } }],
            },
          }],
        },
        timeRange,
        filter,
        output: {
          dimensions: [
            { field: P("ENDPOINT_ID"), outputAlias: "endpoint_id" },
            { field: { stepAlias: "du", field: "DIMENSION_FIELD_DIRECTORY_USER_DISPLAY_NAME" }, outputAlias: "name" },
            { field: { stepAlias: "du", field: "DIMENSION_FIELD_DIRECTORY_USER_DEPARTMENT" }, outputAlias: "department" },
          ],
          metrics: [{ outputAlias: "n", computed: { field: P("NOTIFICATION_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
        limit,
      }).then(rowsToObjects);
      const horizon = await entityHorizonMs("ENTITY_TYPE_PROMPT");
      const pastIndex = await beyondPromptIndex(Date.parse(apiRange?.from ?? ""));
      const [bySession, machines] = await Promise.all([
        read({ pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_IN", value: sessionIds } }, rangeToTimeRange(apiRange), Math.min(5000, sessionIds.length * 2)),
        pastIndex ? sessionMachines(sessionIds, range) : null,
      ]);
      const resolved = new Set(bySession.map((row) => String(row?.endpoint_id || "")));
      const unresolved = [...(machines || [])]
        .filter(([machine, lastMs]) => !resolved.has(machine) && !(horizon != null && lastMs >= horizon))
        .map(([machine]) => machine)
        .slice(0, SEARCH_SESSION_CAP);
      const byMachine = unresolved.length
        ? await read({ pred: { field: P("ENDPOINT_ID"), op: "OPERATOR_IN", value: unresolved } }, indexRetentionRange(), Math.min(5000, unresolved.length * 2)).catch(() => [])
        : [];
      const rows = [...bySession, ...byMachine];
      const best = new Map();
      for (const row of rows) {
        const ep = String(row?.endpoint_id || "");
        const name = String(row?.name || "").trim();
        const department = String(row?.department || "").trim();
        const n = Number(row?.n) || 0;
        if (!ep || !name) continue;
        if (!best.has(ep) || n > best.get(ep).n) best.set(ep, { name, department, n });
      }
      return Object.fromEntries([...best].map(([ep, v]) => [ep, { name: v.name, department: v.department }]));
    },
  },

  getSessionTopics: {
    // What work each of a list of sessions was, keyed by session id: its
    // requests' clusters, each named as the topics list names it (in a
    // hierarchy, under its top node; in a flat listing, as itself), most of
    // the session's requests first. One read for the lot: the sessions are a
    // session match's (at most SESSION_MATCH_CAP, under the IN-list cap),
    // each in a handful of clusters.
    execute: async ({ ids = [], range } = {}) => {
      const sessionIds = [...new Set((Array.isArray(ids) ? ids : []).map((id) => String(id || "").toLowerCase()).filter(Boolean))];
      if (!sessionIds.length) return {};
      const timeRange = rangeToTimeRange(toApiRange(range));
      const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
      const EC = { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" };
      const [rows, tree] = await Promise.all([
        runAnalyticsQuery({
          root: {
            alias: "p", entity: "ENTITY_TYPE_PROMPT", stepFilter: [],
            children: [{ edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER", mode: "JOIN_MODE_INNER", target: { alias: "ec", entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER" } }],
          },
          timeRange,
          filter: { andExpr: { children: [
            { pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_IN", value: sessionIds } },
            { pred: { field: EC, op: "OPERATOR_GT", value: 0 } },
          ] } },
          output: {
            dimensions: [{ field: P("NOTIFICATION_ID"), outputAlias: "session_id" }, { field: EC, outputAlias: "cluster_id" }],
            metrics: [promptCount("n")],
          },
          limit: Math.min(50000, sessionIds.length * 40),
        }).then(rowsToObjects),
        loadClusterTree(),
      ]);
      const named = (id) => {
        let node = tree.byId.get(String(id));
        if (!tree.isHierShape) return node?.name || "";
        const seen = new Set();
        while (node?.parentId && tree.byId.has(node.parentId) && !seen.has(node.id)) {
          seen.add(node.id);
          node = tree.byId.get(node.parentId);
        }
        return node?.name || "";
      };
      const by = new Map();
      for (const row of rows) {
        const sid = String(row?.session_id || "").toLowerCase();
        const name = named(row?.cluster_id);
        if (!sid || !name) continue;
        if (!by.has(sid)) by.set(sid, new Map());
        const m = by.get(sid);
        m.set(name, (m.get(name) || 0) + (Number(row.n) || 0));
      }
      const out = {};
      for (const [sid, m] of by) out[sid] = [...m.entries()].sort((a, b) => b[1] - a[1]).map(([name, n]) => ({ name, n }));
      return out;
    },
  },

  getSessionHeads: {
    // What a list of sessions shows of each, keyed by session id: its newest
    // prompt's summary, model, provider and machine, and with `whole` the
    // session's own span and size. With `whole` its conversation record
    // (fetchConversationHeads) gives its machine and provider (and, asked for
    // one session, its model) and the span its record was observed over, the
    // prompt's standing where the record has none: a session the prompt index
    // no longer holds keeps its head. Without it, a match's spans are its own
    // (it reads only the requests its clauses speak of).
    execute: async ({ ids = [], range, whole = false } = {}) => {
      const sessionIds = [...new Set((Array.isArray(ids) ? ids : []).map((id) => String(id || "").toLowerCase()).filter(Boolean))];
      if (!sessionIds.length) return {};
      const timeRange = rangeToTimeRange(toApiRange(range));
      const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
      const recordsRead = whole ? fetchConversationHeads(sessionIds, { model: sessionIds.length === 1 }) : null;
      // A session begun before the prompt index's horizon has only its later
      // prompts there, if any: its count, and its title where the index has
      // none, are the prompt analysis's, read as soon as its record dates it.
      const olderRead = recordsRead
        ? recordsRead.then(async (records) => fetchAnalysisSessions(await spansBeyondIndex(records), { counts: true })).catch(() => new Map())
        : null;
      const [latest, spans, records, older] = await Promise.all([
        fetchLatestSessionPrompts(sessionIds, timeRange, whole ? { analysis: false } : {}),
        whole
          ? runAnalyticsQuery({
            root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
            timeRange,
            filter: { pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_IN", value: sessionIds } },
            output: {
              dimensions: [{ field: P("NOTIFICATION_ID"), outputAlias: "session_id" }],
              metrics: [
                { outputAlias: "last_time", computed: { field: P("EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MAX" } },
                { outputAlias: "first_time", computed: { field: P("EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MIN" } },
                { outputAlias: "requests", computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
              ],
            },
            limit: sessionIds.length,
          }).then(rowsToObjects).catch(() => [])
          : [],
        recordsRead || new Map(),
        olderRead || new Map(),
      ]);
      for (const [id, analysed] of older) if (!latest.has(id) && analysed.latest) latest.set(id, analysed.latest);
      const out = {};
      for (const [id, row] of latest) {
        out[String(id).toLowerCase()] = {
          summary: String(row?.enriched || "").replace(/\s+/g, " ").trim(),
          model: String(row?.model || ""),
          provider: String(row?.provider || ""),
          endpointId: String(row?.endpoint_id || ""),
        };
      }
      for (const row of spans) {
        const id = String(row?.session_id || "").toLowerCase();
        if (!id) continue;
        out[id] = {
          ...(out[id] || {}),
          firstMs: row.first_time ? parseServerTimestamp(row.first_time) : null,
          lastMs: row.last_time ? parseServerTimestamp(row.last_time) : null,
          requests: Number(row.requests) || 0,
        };
      }
      for (const [id, rec] of records) {
        const prompt = out[id] || {};
        const spanned = whole && Number.isFinite(rec.firstMs);
        const counted = older.get(id)?.requests;
        out[id] = {
          ...prompt,
          summary: prompt.summary || "",
          model: rec.model || prompt.model || "",
          provider: rec.provider || prompt.provider || "",
          endpointId: rec.endpointId || prompt.endpointId || "",
          ...(spanned ? { firstMs: rec.firstMs, lastMs: Number.isFinite(rec.lastMs) ? rec.lastMs : rec.firstMs, requests: counted ?? prompt.requests ?? 0, messages: rec.messages } : {}),
        };
      }
      return out;
    },
  },

  getSessionPage: {
    // One page of a session's conversation record, for a trace tab's axis:
    // `cursor` is the keyset the previous page handed back (null for the newest
    // page) and `exclude` every message id already on the axis. See
    // readConversationPage for why exact paging needs both. `sinceMs`, the
    // session's first request when known, widens the read to the whole
    // session; without it the same 90-day window the capture-anchored record
    // read uses applies.
    // A request tab reads the same record up to its own time (`untilMs`) and
    // names itself (`focalEventId`) so the messages it shipped are marked as
    // its own.
    execute: async ({ id, cursor = null, exclude = [], sinceMs = null, untilMs = null, focalEventId = null, maxMessages = null } = {}) => {
      const sessionId = String(id || "").trim();
      if (!sessionId) throw new Error("A trace id is required.");
      return readConversationPage({
        traceId: sessionId, timeRange: sessionRecordRange(sinceMs, untilMs), cursor: cursor || null,
        exclude: Array.isArray(exclude) ? exclude : [],
        focalEventId: focalEventId || null,
        ...(maxMessages ? { maxMessages } : {}),
      });
    },
  },

  getSessionMessageCounts: {
    // Distinct message counts for many sessions in one call through the
    // conversation record summaries route. Resolves to a Map of session id ->
    // count (null for a session with no messages in the window). Throws with
    // `recordRouteMissing` when the gateway lacks the route, so the caller
    // can fall back to one count per session.
    execute: async ({ ids = [], sinceMs = null } = {}) => {
      const sessionIds = [...new Set((Array.isArray(ids) ? ids : []).map((id) => String(id || "").trim()).filter(Boolean))];
      if (!sessionIds.length) return new Map();
      const response = await authedFetch("/v1/conversations:readRecordSummaries", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ traceIds: sessionIds.map(uuidToEndpointId), timeRange: sessionRecordRange(sinceMs) }),
      });
      if (response.status === 404 || response.status === 405 || response.status === 501) {
        const error = new Error("The conversation record summaries read is not served here.");
        error.recordRouteMissing = true;
        error.status = response.status;
        throw error;
      }
      if (!response.ok) {
        const error = new Error(`Conversation record summaries could not be read (${response.status}).`);
        error.status = response.status;
        throw error;
      }
      const json = await response.json();
      const counts = new Map(sessionIds.map((id) => [id, null]));
      for (const summary of Array.isArray(json?.summaries) ? json.summaries : []) {
        const id = endpointIdToUuid(String(summary.traceId || "")).toLowerCase();
        const n = Number(summary.messageCount);
        if (counts.has(id) && Number.isFinite(n)) counts.set(id, n);
      }
      return counts;
    },
  },

  getSessionMessageCount: {
    // How many distinct messages a session's record holds: the figure the
    // record notice states, read for a session row while it is on screen (see sessionMessageCounts.js for the queue that bounds it).
    execute: async ({ id, sinceMs = null } = {}) => {
      const sessionId = String(id || "").trim();
      if (!sessionId) throw new Error("A trace id is required.");
      return countConversationMessages({ traceId: sessionId, timeRange: sessionRecordRange(sinceMs) });
    },
  },

  getTrace: {
    // The trace behind a `trace:<id>` tab. The id names either a REQUEST (one
    // captured call: the conversation up to it, plus the request's own facts)
    // or a SESSION (the whole conversation); the two lookups run together and
    // whichever answers decides. Both views share one shape (adaptTrace), one
    // record axis (the newest page here, earlier pages through getSessionPage)
    // and the session's span; a request adds its tokens, instructions, run and
    // capture facts. Null when neither lookup knows the id.
    //
    // Request path: EVENT-rooted detail fetch: the focal AiPrompt event +
    // joined PROMPT enrichment + cluster path. Follow-up queries run in
    // parallel: session_clusters (cluster activity in the same session) and
    // prevTraceInSession (last AiPrompt before this one).
    //
    // There is no analytics entity for canonical facet validation. The
    // detail panel renders parser-extracted facets directly, which can widen
    // the displayed chip set harmlessly.
    // `requestId`, when a caller opened a request row, names the request the
    // moment is about: sibling calls in one session can share a millisecond,
    // and the moment alone cannot tell them apart.
    execute: async ({ id, eventTime = "", requestId: requestHint = null, withoutRecord = false } = {}) => {
      if (!id) return null;
      // The id is a trace address (traceAddress.js): `<session>@<ms>` names
      // a session and a moment; a bare id is a request's or a session's, and
      // the prompt index says which below.
      const { sessionId: sessionHint, untilMs: boundMs } = parseTraceAddress(id);
      let requestId = sessionHint ? (requestHint ? String(requestHint) : null) : id;

      // A Discover run opened as a trace: synthetic ids resolve locally from
      // the discovery store's captured body — no analytics round-trip, and
      // the whole panel (context feed, x-ray, lanes, raw) works unchanged.
      if (isDiscoverTraceId(id)) {
        const parts = discoverTraceParts(id);
        if (!parts) return null;
        const parsed = parts.apiRequestRaw ? parseApiRequest(parts.apiRequestRaw) : { turns: [], tools: [], settings: null };
        const tools = [...new Set((parsed.tools || []).filter((t) => typeof t === "string"))].sort((a, b) => a.localeCompare(b));
        return adaptTrace({
          ...parts.focal,
          input_tokens: null, output_tokens: null,
          cache_read_tokens: null, cache_creation_tokens: null,
          inclusive_cache_read_tokens: null, reasoning_tokens: null,
          calls_total: null, calls_with_tokens: null, token_grain: "",
          capture_sources: [], prompt_capture_source: "", raw_capture: false,
          module_capture: false, module_capture_projected: false,
          filtered: false, message_type: "",
          cluster_status: "", cluster_ancestors: [],
        }, {
          api_request_raw: parts.apiRequestRaw,
          turns: parsed.turns ?? [],
          tools,
          toolDefs: parsed.toolDefs ?? [],
          settings: parsed.settings ?? { temperature: 0, maxOutputTokens: 0, stream: false, reasoningEffort: "" },
          prevTraceInSession: null,
          toolExecutions: [],
          partialBody: false,
          partialBodyReason: "",
          sessionOrigin: null,
        });
      }

      // Deep links carry only an id, so the prompt index supplies the event
      // timestamp used to bound the raw-event hydration. Every other lookup
      // here is keyed by the session or the request and runs over the index's
      // whole retention, stated as two instants (see indexRetentionRange).
      const wideRange = indexRetentionRange();



      let detailRange = detailRangeAround(eventTime);
      let spanPromise = null;
      let recordChecked = false;
      if (sessionHint && !requestId) {
        // A moment in a session with no request named: the focal request is
        // the latest at or before it. None there (a moment before the first
        // request, a session the index has no request for) leaves the
        // conversation up to the moment without request facts, or nothing
        // when the index knows no session.
        const [focalRow] = await runAnalyticsQuery(focalRequestSpecFor(sessionHint, boundMs, wideRange)).then(rowsToObjects).catch(() => []);
        if (!focalRow) {
          const span = await sessionSpanOrRecord(sessionHint, sessionSpanOf(await runAnalyticsQuery(sessionSpanSpecFor(sessionHint, wideRange)).then(rowsToObjects).catch(() => [])));
          return span ? wholeConversationTrace(sessionHint, span, wideRange, boundMs, { withoutRecord }) : null;
        }
        requestId = String(focalRow.request_id);
        detailRange = detailRangeAround(focalRow.event_time) ?? detailRange;
      } else if (!sessionHint) {
        // Which kind a bare id is: a request the prompt index dates, or a
        // session it counts requests for. Both are asked at once. A caller's
        // eventTime (a log row's) only bounds the request lookup; it does not
        // say which kind the id is, since a session row carries one too.
        spanPromise = runAnalyticsQuery(sessionSpanSpecFor(id, wideRange)).then(rowsToObjects).then(sessionSpanOf).catch(() => sessionSpanOf([]));
      }
      if (!detailRange) {
        const anchorSpec = {
          root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
          timeRange: wideRange,
          filter: {
            pred: {
              field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
              op: "OPERATOR_EQ", value: requestId,
            },
          },
          output: {
            dimensions: [{
              field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" },
              outputAlias: "event_time",
            }],
          },
          limit: 1,
        };
        const [[anchor], span] = await Promise.all([runAnalyticsQuery(anchorSpec).then(rowsToObjects), spanPromise]);
        detailRange = detailRangeAround(anchor?.event_time);
        if (!anchor && span && span.requestCount > 0) return wholeConversationTrace(id, span, wideRange, null, { withoutRecord });
        // The index knows no request or session by this id: a session its
        // record keeps (one older than the index) opens whole, without first
        // reading the event store, which keeps no longer than the index.
        if (!anchor && span) {
          const recorded = await sessionSpanOrRecord(id, span);
          if (recorded) return wholeConversationTrace(id, recorded, wideRange, null, { withoutRecord });
          recordChecked = true;
        }
      }

      const detailSpec = {
        root: {
          alias: "e", entity: "ENTITY_TYPE_EVENT",
          children: [{
            edge: "EDGE_TYPE_EVENT_HAS_PROMPT",
            mode: "JOIN_MODE_LEFT",
            target: {
              alias: "p", entity: "ENTITY_TYPE_PROMPT",
              // No skwach_major step_filter: the planner scopes PROMPT to
              // the production version server-side; the user-facing
              // dimension was removed in O-1447.
              stepFilter: [],
              children: [{
                edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
                mode: "JOIN_MODE_LEFT",
                target: {
                  alias: "ec", entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
                  children: [{
                    edge: "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER",
                    mode: "JOIN_MODE_LEFT",
                    target: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER", optional: true },
                  }],
                },
              }],
              // No per-prompt token child (the per-prompt usage edge was
              // removed in O-1805). Model falls back to the prompt-level
              // request-body model (DIMENSION_FIELD_PROMPT_MODEL); the detail
              // panel's token total aggregates the TOKEN store for this
              // prompt's trace (promptTokensSpec below).
            },
          }],
        },
        timeRange: detailRange ?? wideRange,
        filter: {
          pred: {
            field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },
            op: "OPERATOR_EQ", value: requestId,
          },
        },
        output: {
          dimensions: [
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },        outputAlias: "trace_id" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_ENDPOINT_ID" },     outputAlias: "endpoint_id" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_NOTIFICATION_ID" }, outputAlias: "session_id" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_TURN_KEY" }, outputAlias: "turn_key" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_CAPTURE_SOURCE" }, outputAlias: "capture_source" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_MODULE_CAPTURE" }, outputAlias: "module_capture" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_CAPTURE_PROJECTED" }, outputAlias: "capture_projected" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_RAW_CAPTURE" }, outputAlias: "raw_capture" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_REQUEST_EFFORT" }, outputAlias: "request_effort" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_REQUEST_SPEED" }, outputAlias: "request_speed" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_CONVERSATION_THREAD_ID" }, outputAlias: "conversation_thread_id" },

            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" },       outputAlias: "provider" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT" }, outputAlias: "enriched" },
            { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, outputAlias: "cluster_id" },
            { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" },       outputAlias: "cluster_name" },
            // Prompt-level request-body model (the response-resolved
            // TOKEN_MODEL isn't joined per-prompt). Coalesced into the
            // model label below.
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MODEL" },                    outputAlias: "model" },
            // skwach NON_PROMPT tombstone marker — same projection the list
            // query carries. Without it the detail panel can't tell a
            // non-prompt frame apart from an un-clustered prompt and shows a
            // perpetual "Pending" cluster chip instead of "Non-prompt".
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ANALYSIS_MODE" },          outputAlias: "analysisMode" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MESSAGE_TYPE" },           outputAlias: "message_type" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ANSWER_STATUS" },          outputAlias: "answer_status" },
            // The agentic-turn grouping key: every API call in the turn (main
            // thread + sub-agents) shares it. getTurnTraces joins on it, so the
            // Lanes view can list this turn's sibling traces.
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_SUBMISSION_ID" },          outputAlias: "submission_id" },
          ],
        },
        limit: 1,
      };

      // This prompt's token total (O-2021: the SUBMISSION rollup entity is
      // gone; totals aggregate the TOKEN store). PROMPT_HAS_TOKENS — the single
      // generic prompt→tokens edge — is now a GRAIN-AWARE collapse: it returns
      // the tokens at the session's FINEST available grain and surfaces which
      // grain answered via DIMENSION_FIELD_TOKEN_GRAIN ("turn" | "submission" |
      // "session" | "user_day"). So the detail panel shows the finest-grain
      // number we have, labeled by grain, instead of 0 for proxy/turn and
      // Codex/Gemini/session prompts (the old submission-only key). The edge is
      // planner-collapsed: the PROMPT root is filter-only, its predicate must
      // live in stepFilter, and all output comes from TOKEN. A single prompt's
      // session has exactly one finest grain → one row; limit 5 is defensive
      // headroom, not a real cap.
      const promptTokensSpec = {
        root: {
          alias: "p", entity: "ENTITY_TYPE_PROMPT",
          stepFilter: [{
            pred: {
              field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
              op: "OPERATOR_EQ", value: requestId,
            },
          }],
          children: [{
            edge: "EDGE_TYPE_PROMPT_HAS_TOKENS",
            mode: "JOIN_MODE_INNER",
            target: { alias: "tk", entity: "ENTITY_TYPE_TOKEN" },
          }],
        },
        timeRange: wideRange,
        output: {
          dimensions: [
            { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_GRAIN" }, outputAlias: "grain" },
          ],
          metrics: [
            { outputAlias: "input_tokens",  standard: { stepAlias: "tk", metric: "METRIC_TYPE_TOKEN_SUM_INPUT_TOKENS" } },
            { outputAlias: "output_tokens", standard: { stepAlias: "tk", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" } },
            // Same TOKEN store, same grain-aware collapse — just the rest of the
            // buckets so the detail chip can show cache health + reasoning, not
            // only in/out. cache_* is provider-predicated (Anthropic-only, where
            // it's disjoint from input); reasoning is a subset of output;
            // coverage is the measured-vs-total honesty signal. See tokenVolume.js.
            ...cacheTokenMetrics("tk"),
            ...inclusiveCacheMetric("tk"),
            ...reasoningTokenMetric("tk"),
            ...coverageMetrics("tk"),
          ],
        },
        limit: 5,
      };

      const promptTokenSourceSpec = traceCaptureSourceEnabled() ? {
        root: {
          alias: "p", entity: "ENTITY_TYPE_PROMPT",
          stepFilter: [{
            pred: {
              field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
              op: "OPERATOR_EQ", value: requestId,
            },
          }],
          children: [{
            edge: "EDGE_TYPE_PROMPT_HAS_TOKENS",
            mode: "JOIN_MODE_INNER",
            target: { alias: "tk", entity: "ENTITY_TYPE_TOKEN" },
          }],
        },
        timeRange: wideRange,
        output: {
          dimensions: [
            { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_SOURCE" }, outputAlias: "source" },
          ],
          metrics: [{
            outputAlias: "rows",
            computed: { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_UNIT_ID" }, op: "AGGREGATE_OP_COUNT" },
          }],
        },
        limit: 10,
      } : null;

      const [detailRowsRaw, promptTokenRows, promptTokenSourceRows] = await Promise.all([
        runDegradableQuery(detailSpec).then(rowsToObjects),
        runAnalyticsQuery(promptTokensSpec).then(rowsToObjects).catch(() => []),
        promptTokenSourceSpec ? runAnalyticsQuery(promptTokenSourceSpec).then(rowsToObjects).catch(() => []) : Promise.resolve([]),
      ]);
      const detailRows = detailRowsRaw;
      const focal = detailRows[0];
      if (!focal) {
        // No request by this id: the whole conversation when a bare id is a
        // session's (the prompt index's, else its conversation record's), else
        // nothing.
        if (!spanPromise || recordChecked) return null;
        const span = await sessionSpanOrRecord(id, await spanPromise);
        return span ? wholeConversationTrace(id, span, wideRange, null, { withoutRecord }) : null;
      }
      // This prompt's tokens at the session's finest available grain, plus the
      // grain that answered (O-2021 grain-aware collapse). One row per finest
      // grain → the first row is the answer.
      const promptTokens = promptTokenRows[0] ?? {};
      const tokenCaptureSources = [...new Set(
        (promptTokenSourceRows || []).map((r) => String(r.source || "").trim()).filter(Boolean),
      )].sort();

      const focalEventTime = focal.event_time;
      const sessionId = focal.session_id;

      // Exact request context is retired; retain call settings and the
      // independently paged conversation record for this observation.
      const conversationPromise = readInvocationParameters({
        traceId: sessionId, sourceEventId: requestId, timeRange: wideRange,
      }).catch((error) => ({ turns: [], tools: [], toolDefs: [], settings: null, partialBody: true,
        partialBodyReason: String(error?.message || error), requestContextRetired: true, invocation: null }));
      // The bound the record read stops at: the moment the address names, else
      // this capture's event time. A trace with no session, or no dated
      // capture, has no record to read.
      const focalAt = Number.isFinite(boundMs) ? boundMs : parseServerTimestamp(focal.event_time);
      const recordPromise = sessionId && Number.isFinite(focalAt)
        ? readConversationPage({ traceId: sessionId, timeRange: sessionRecordRange(null, focalAt), focalEventId: requestId })
          .then((page) => ({ page }), (error) => ({ error }))
        : Promise.resolve(null);

      // Parallel: session_clusters + prevTraceInSession + the session's span.
      const sessionClustersSpec = sessionId ? sessionClustersSpecFor(sessionId, wideRange) : null;
      const spanSpec = sessionId ? sessionSpanSpecFor(sessionId, wideRange) : null;

      const prevTraceSpec = sessionId && focalEventTime ? {
        root: { alias: "e", entity: "ENTITY_TYPE_EVENT" },
        timeRange: wideRange,
        filter: { andExpr: { children: [
          { pred: { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TYPE" },      op: "OPERATOR_EQ", value: "AiPrompt" } },
          { pred: { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_NOTIFICATION_ID" }, op: "OPERATOR_EQ", value: sessionId } },
          { pred: { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TIMESTAMP" }, op: "OPERATOR_LT", value: focalEventTime } },
        ]}},
        output: {
          dimensions: [
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },        outputAlias: "trace_id" },
            { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
          ],
        },
        orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_DESC" }],
        limit: 1,
      } : null;

      const turnKey = focal.turn_key;
      const toolExecSpec = turnKey && turnKey !== "00000000-0000-0000-0000-000000000000" ? {
        root: { alias: "t", entity: "ENTITY_TYPE_TRACE_TOOL_ACTIVITY" }, timeRange: detailRangeAround(focalEventTime),
        filter: { pred: { field: { stepAlias: "t", field: "DIMENSION_FIELD_TRACE_ACTIVITY_TRACE_ID" }, op: "OPERATOR_EQ", value: turnKey } },
        output: { dimensions: [
          ["TRACE_ACTIVITY_EVENT_ID", "tool_event_id"], ["TRACE_TOOL_TIMESTAMP", "tool_time"],
          // The provider's own tool_use id: the only identity this execution
          // and the captured tool_use block in the conversation share.
          ["TRACE_TOOL_USE_ID", "tool_use_id"],
          ["TRACE_TOOL_NAME", "name"], ["TRACE_TOOL_INPUT", "input"], ["TRACE_TOOL_RESULT", "result"],
          ["TRACE_TOOL_IS_ERROR", "isError"], ["TRACE_TOOL_INPUT_TRUNCATED", "inputTruncated"], ["TRACE_TOOL_RESULT_TRUNCATED", "resultTruncated"],
        ].map(([field, outputAlias]) => ({ field: { stepAlias: "t", field: `DIMENSION_FIELD_${field}` }, outputAlias })) },
        orderBy: [{ outputAlias: "tool_time", direction: "DIRECTION_ASC" }], limit: 50,
      } : null;

      // Session launch metadata is independent of the selected conversation.
      const originSpec = sessionOriginSpec(sessionId, wideRange);

      // The whole session's token spend, per (provider, model, run), for the
      // Lanes density strip. The strip attributes measured dollars onto this
      // conversation's turns (traceTurnDensity.js), and the conversation shown
      // is the SESSION's history — so the honest level is the session's spend,
      // not this prompt's own rollup (60x apart on long agent sessions). Rows
      // stay at run grain so the client can cut "through this run" when the
      // viewed capture isn't the session's newest; MAX(observed_at) is that
      // cut's ordering. TOKEN root + session_id filter mirrors getTurnTraces'
      // tokenSpec; grain double-count is the planner's problem (every TOKEN
      // scan is grain-resolved server-side). Newest-first so the defensive
      // limit truncates the session's OLDEST runs — an under-count, never a
      // missing current run.
      const sessionTokensSpec = sessionId ? sessionTokensSpecFor(sessionId, wideRange) : null;

      // GitHub Copilot captures share the coarse `microsoft` provider with
      // Microsoft Copilot; only the usage rows' client surface says which
      // product and surface ran, so read it for that provider alone.
      const clientSurfaceSpec = String(focal.provider || "").toLowerCase() === "microsoft"
        ? promptClientSurfaceSpecFor(requestId, wideRange)
        : null;

      const [sessionClusterRows, prevRows, toolExecRows, parsed, sessionOrigin, sessionTokenRowsRaw, recordRead, spanRows, clientSurfaceRows] = await Promise.all([
        sessionClustersSpec ? runDegradableQuery(sessionClustersSpec).then(rowsToObjects).catch(() => []) : Promise.resolve([]),
        prevTraceSpec        ? runAnalyticsQuery(prevTraceSpec).then(rowsToObjects).catch(() => [])        : Promise.resolve([]),
        toolExecSpec         ? runAnalyticsQuery(toolExecSpec).then(rowsToObjects).catch(() => [])         : Promise.resolve([]),
        conversationPromise,
        originSpec ? runAnalyticsQuery(originSpec).then((r) => shapeSessionOrigin(rowsToObjects(r)[0])).catch(() => null) : Promise.resolve(null),
        sessionTokensSpec ? runAnalyticsQuery(sessionTokensSpec).then(rowsToObjects).catch(() => []) : Promise.resolve([]),
        recordPromise,
        spanSpec ? runAnalyticsQuery(spanSpec).then(rowsToObjects).catch(() => []) : Promise.resolve([]),
        clientSurfaceSpec ? runAnalyticsQuery(clientSurfaceSpec).then(rowsToObjects).catch(() => []) : Promise.resolve([]),
      ]);
      const span = sessionSpanOf(spanRows);
      // The session-spend basis is an enrichment: absence (query failed, no
      // token observations, no session id) must leave the trace detail intact
      // — the strip then falls back to the prompt's own grain rollup, honestly
      // labeled. The zero UUID is the pre-feature submission sentinel, not a
      // run key (see adaptTrace) — map it to "" so no cut keys on it.
      const sessionTokenRows = normalizeSessionTokenRows(sessionTokenRowsRaw);
      const partialBody = Boolean(parsed.partialBody);
      const partialBodyReason = parsed.partialBodyReason || (partialBody ? "The source omitted some conversation content." : "");
      const promptCaptureSource = promptCaptureSourceFromEventData({ capture_source: focal.capture_source || parsed.invocation?.sourceCapture });
      // Read after the main batch: the gate needs the resolved capture source,
      // which is only known once `parsed` has landed.
      const browserCapture = promptCaptureSource === "browser_extension";
      const [sourceObservations, traceSurfaceRows] = await Promise.all([
        traceCaptureSourceEnabled() || browserCapture || promptCaptureSource === "openai_sync"
          ? fetchTraceSourceObservations(requestId, wideRange).catch(() => [])
          : Promise.resolve([]),
        browserCapture && sessionId
          ? runAnalyticsQuery(traceClientSurfaceSpecFor(sessionId, wideRange)).then(rowsToObjects).catch(() => [])
          : Promise.resolve([]),
      ]);
      const captureSources = mergeCaptureSources(promptCaptureSource, tokenCaptureSources);
      const sessionClusters = shapeSessionClusters(sessionClusterRows);
      const prev = prevRows[0] ? {
        traceId:   String(prevRows[0].trace_id),
        eventTime: prevRows[0].event_time,
        summary:   "",  // would need a second join; skip for first pass
      } : null;

      const toolExecutions = shapeToolExecutions(toolExecRows);

      const tools = (() => {
        // The top-level "tools" pill falls back to the parser's
        // offered-tools list — tools the agent had
        // access to. Wider than "called", but renders something
        // useful while we don't have an ARTIFACT entity.
        const seen = new Set(); const out = [];
        for (const t of parsed.tools || []) {
          if (typeof t === "string" && !seen.has(t)) { seen.add(t); out.push(t); }
        }
        return out.sort((a, b) => a.localeCompare(b));
      })();
      const settings = parsed.settings ?? { temperature: 0, maxOutputTokens: 0, stream: false, reasoningEffort: "" };
      // The trace's axis is the conversation up to this capture: the record's
      // newest page behind the request's instructions, in the shape the tab
      // reader keeps (sessionTabState), so Load earlier continues from here. A
      // page that could not be read leaves the instructions on the axis and
      // the record retryable, the same as a whole conversation whose page failed.
      const empty = { session: true, pending: false, loading: false, rejected: false, loaded: 0, total: null, nextCursor: null, exhausted: false, pages: 0, untilMs: focalAt };
      let turns = parsed.turns ?? [];
      let record = null;
      if (recordRead?.page) ({ turns, record } = applySessionPage({ data: { turns, record: empty } }, recordRead.page, { cursor: null }).data);
      else if (recordRead) record = { ...empty, rejected: true, reason: String(recordRead.error?.message || recordRead.error) };

      const trace = adaptTrace({
        trace_id:           focal.trace_id,
        endpoint_id:        focal.endpoint_id,
        endpoint_name:      "",
        session_id:         focal.session_id,
        event_time:         focal.event_time,
        provider:           focal.provider || "",
        submission_id:      focal.submission_id || "",
        // Prompt-level request-body model (the response-resolved TOKEN_MODEL
        // isn't joined per-prompt here).
        model:              focal.model || "",
        // This prompt's tokens at the session's FINEST available grain, via the
        // EDGE_TYPE_PROMPT_HAS_TOKENS grain-aware collapse over the TOKEN store
        // (O-2021). `token_grain` says which grain answered ("turn" is this
        // prompt's own tokens and summable; "submission"/"session"/"user_day"
        // are shared rollups — don't sum). The UI labels the row by grain.
        // Tokens pass through unmolested (null when no observation); adaptTrace
        // preserves that absence as null rather than coercing to 0.
        input_tokens:       promptTokens.input_tokens ?? null,
        output_tokens:      promptTokens.output_tokens ?? null,
        // Rest of the buckets from the same grain-aware collapse (null when the
        // provider doesn't report them — cache outside Anthropic, reasoning
        // outside OpenAI/DeepSeek — which adaptTrace preserves as null, not 0).
        cache_read_tokens:     promptTokens.cache_read_tokens ?? null,
        cache_creation_tokens: promptTokens.cache_creation_tokens ?? null,
        inclusive_cache_read_tokens: promptTokens.inclusive_cache_read_tokens ?? null,
        reasoning_tokens:      promptTokens.reasoning_tokens ?? null,
        calls_total:           promptTokens.calls_total ?? null,
        calls_with_tokens:     promptTokens.calls_with_tokens ?? null,
        token_grain:        promptTokens.grain || "",
        // Whole-session spend rows (provider x model x run) for the Lanes
        // density strip's session-level attribution. [] stays [] — "queried,
        // nothing measured" and "not queried" both land as null in adaptTrace.
        session_token_rows: sessionTokenRows,
        capture_sources:    captureSources,
        prompt_capture_source: promptCaptureSource,
        client_surface:     firstClientSurface(clientSurfaceRows) || reportedClientSurface(traceSurfaceRows),
        raw_capture:        focal.raw_capture,
        module_capture:     focal.module_capture,
        module_capture_projected: focal.capture_projected,
        // NON_PROMPT tombstone → "Non-prompt" cluster chip instead of a
        // never-resolving "Pending". adaptTrace lets `filtered` win over
        // cluster_status when mapping to clusterStatus.
        filtered:           focal.analysisMode === "NON_PROMPT",
        // SCAFFOLDING → terminal "Infrastructure" cluster badge (never clusters).
        message_type:       focal.message_type || "",
        answer_status:      focal.answer_status || "",
        cluster_id:         focal.cluster_id != null ? Number(focal.cluster_id) : 0,
        cluster_name:       focal.cluster_name || "",
        // Leave empty so adaptTrace derives the status from cluster_id, which
        // maps -1 → "outlier" correctly. The old `focal.cluster_id ? ...` treated
        // the truthy -1 outlier sentinel as "clustered" → the chip then rendered
        // NOTHING (the named-cluster path needs cluster_id > 0), so outliers
        // showed no cluster chip in the detail panel.
        cluster_status:     "",
        cluster_ancestors:  [],
        summary:            focal.enriched || "",
        session_clusters:   sessionClusters,
      }, {
        api_request_raw: "",
        requestEffort: focal.request_effort, requestSpeed: focal.request_speed,
        conversationThreadId: focal.conversation_thread_id || null,
        turns,
        record,
        tools,
        toolDefs:        parsed.toolDefs ?? [],
        settings,
        prevTraceInSession: prev,
        toolExecutions,
        partialBody,
        partialBodyReason,
        sessionOrigin,
        sourceObservations,
      });
      // The request this view is focused on, the moment it is bounded at, and
      // the conversation's span, the same facts the whole-conversation view
      // carries.
      trace.requestId = requestId;
      trace.requestContextRetired = Boolean(parsed.requestContextRetired);
      trace.untilMs = Number.isFinite(focalAt) ? focalAt : null;
      trace.requestCount = span.requestCount;
      trace.firstTimestamp = span.firstTimestamp;
      trace.lastTimestamp = span.lastTimestamp;
      return trace;
    },
  },

  // Every captured API call in one agentic turn, joined on the submission id
  // the turn's calls all share — the Lanes view's sub-agent thread listing.
  // Two bounded projections run in parallel: the PROMPT rows (id, time,
  // summary, model — no bodies, no cluster join) and the per-call TOKEN
  // observations (the four bands the lineage chaining needs). The stores
  // don't share a per-call key (token unit_id is the usage event, not the
  // prompt event), but both are one-row-per-call with occurrence timestamps
  // ~tens of ms apart against inter-call gaps of seconds, so a monotonic
  // nearest-time match joins them; a call outside the tolerance just carries
  // no bands and falls out of chaining rather than mis-joining.
  // Limits are sized to the biggest runs we have seen (420 calls) with
  // headroom; a run past them is truncated oldest-first and the result says
  // so (`truncated`), so the panel can state the listing is incomplete
  // instead of presenting a silent partial join.
  getTurnTraces: {
    execute: async ({ submissionId } = {}) => {
      const id = String(submissionId || "").trim();
      if (!id) return { rows: [], truncated: false };
      return tracesUnderKey(id, "DIMENSION_FIELD_PROMPT_SUBMISSION_ID", "DIMENSION_FIELD_TOKEN_SUBMISSION_ID");
    },
  },

  // Every captured API call of one SESSION, the whole conversation's runs at
  // once: what a trace opened on a session hands the thread wiring, so a
  // sub-agent spawned from any turn can be matched to its child and spliced or
  // opened, exactly as a trace opened on a request does for its one turn.
  getSessionTraces: {
    execute: async ({ sessionId } = {}) => {
      const id = String(sessionId || "").trim();
      if (!id) return { rows: [], truncated: false };
      return tracesUnderKey(id, "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID", "DIMENSION_FIELD_TOKEN_SESSION_ID");
    },
  },

  // Candidate captures around a moment on ONE endpoint, for joining a
  // spawned sub-agent that ran under a DIFFERENT submission: the lanes axis
  // spans a whole session of runs, so the viewed capture's submission misses
  // every other run's children. There is no prompt session dimension - the
  // session id lives in metadata - but the child ran on the same machine
  // inside the spawn's window, and the caller discriminates by OPENING match,
  // so endpoint + time is the right pond. Carries submission_id so a matched
  // first call can hop to ITS run for the full conversation.
  getEndpointTraces: {
    execute: async ({ endpointId, fromMs, toMs } = {}) => {
      const LIMIT = 1000;
      const id = String(endpointId || "").trim();
      const from = Number(fromMs);
      const to = Number(toMs);
      if (!id || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) return { rows: [], truncated: false };
      const timeRange = { absolute: { start: new Date(from).toISOString(), end: new Date(to).toISOString() } };
      const spec = {
        root: {
          alias: "p", entity: "ENTITY_TYPE_PROMPT",
          stepFilter: [{
            pred: {
              field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },
              op: "OPERATOR_EQ", value: id,
            },
          }],
        },
        timeRange,
        output: {
          dimensions: [
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },        outputAlias: "trace_id" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_SUBMISSION_ID" },   outputAlias: "submission_id" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT" }, outputAlias: "summary" },
          ],
        },
        orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }],
        limit: LIMIT,
      };
      // Token bands, same enrichment as getTurnTraces: the caller chains the
      // pond into cache-chain lineages to find conversation OPENERS, and two
      // conversations can interleave inside one submission (a sidechain is
      // captured under the spawning run) - only the bands can tell them apart.
      const tokenSpec = {
        root: {
          alias: "tk", entity: "ENTITY_TYPE_TOKEN",
          stepFilter: [{
            pred: {
              field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_ENDPOINT_ID" },
              op: "OPERATOR_EQ", value: id,
            },
          }],
        },
        timeRange,
        output: {
          dimensions: [
            { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_OBSERVED_AT" },                  outputAlias: "observed_at" },
            { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_INPUT_TOKENS" },                 outputAlias: "input_tokens" },
            { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_OUTPUT_TOKENS" },                outputAlias: "output_tokens" },
            { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_CACHED_INPUT_TOKENS" },          outputAlias: "cache_read_tokens" },
            { field: { stepAlias: "tk", field: "DIMENSION_FIELD_TOKEN_CACHE_CREATION_INPUT_TOKENS" },  outputAlias: "cache_creation_tokens" },
          ],
        },
        orderBy: [{ outputAlias: "observed_at", direction: "DIRECTION_ASC" }],
        limit: LIMIT,
      };
      const [promptRows, tokenRows] = await Promise.all([
        runAnalyticsQuery(spec).then(rowsToObjects),
        runAnalyticsQuery(tokenSpec).then(rowsToObjects).catch(() => []),
      ]);
      const rows = promptRows
        .map((r) => ({
          traceId: String(r.trace_id || ""),
          time: r.event_time ? parseServerTimestamp(r.event_time) : null,
          submissionId: String(r.submission_id || ""),
          summary: String(r.summary || ""),
          input: null, output: null, cacheRead: null, cacheWrite: null,
        }))
        .filter((r) => r.traceId && r.time != null)
        .sort((a, b) => a.time - b.time);
      const calls = tokenRows
        .map((r) => ({
          time: r.observed_at ? parseServerTimestamp(r.observed_at) : null,
          input: Number(r.input_tokens) || 0,
          output: Number(r.output_tokens) || 0,
          cacheRead: Number(r.cache_read_tokens) || 0,
          cacheWrite: Number(r.cache_creation_tokens) || 0,
        }))
        .filter((r) => r.time != null)
        .sort((a, b) => a.time - b.time);
      const JOIN_TOLERANCE_MS = 5_000;
      let ci = 0;
      for (const tr of rows) {
        while (ci < calls.length - 1 && Math.abs(calls[ci + 1].time - tr.time) <= Math.abs(calls[ci].time - tr.time)) ci++;
        const c = calls[ci];
        if (c && Math.abs(c.time - tr.time) <= JOIN_TOLERANCE_MS) {
          tr.input = c.input;
          tr.output = c.output;
          tr.cacheRead = c.cacheRead;
          tr.cacheWrite = c.cacheWrite;
          ci = Math.min(ci + 1, calls.length - 1);
        }
      }
      return { rows, truncated: promptRows.length >= LIMIT || tokenRows.length >= LIMIT };
    },
  },

  // Resolve a spawned conversation by its OPENING via the conversation
  // model's search surface, inside the window the caller can vouch for.
  // ok:false carries WHY, because the caller acts differently on each:
  // "over-scan" (the window read past the gateway's byte ceiling) is
  // narrowed and retried; "refused" (the typed-facts gate off in prod, RBAC,
  // an unsupported entity) is remembered for the trace and the
  // reconstruction ponds take over; "failed" (transport, aborted) and
  // "unbuildable" (prompt too short, no window) fall through once. None of
  // them means "no child".
  searchSpawnOpenings: {
    execute: async ({ prompt, taskName = null, endpointId = null, fromMs = null, toMs = null } = {}) => {
      const spec = spawnOpeningSearchSpec({ prompt, taskName, endpointId, fromMs, toMs });
      if (!spec) return { rows: [], ok: false, reason: "unbuildable" };
      try {
        const raw = await runAnalyticsQuery(spec).then(rowsToObjects);
        const rows = raw
          .map((r) => ({
            sourceEventId: String(r.source_event_id || ""),
            sessionId: String(r.session_id || ""),
            role: String(r.role || ""),
            partOrdinal: Number(r.part_ordinal) || 0,
            chunkOrdinal: Number(r.chunk_ordinal) || 0,
            time: r.at ? parseServerTimestamp(r.at) : null,
          }))
          .filter((r) => r.sourceEventId && r.time != null);
        return { rows, ok: true };
      } catch (err) {
        if (isOverScanRejection(err)) return { rows: [], ok: false, reason: "over-scan" };
        if (err?.analyticsInfrastructure || err?.name === "AbortError") return { rows: [], ok: false, reason: "failed" };
        return { rows: [], ok: false, reason: "refused" };
      }
    },
  },

  // Resolve a sealed spawn's conversation by its task token, through the
  // content-fingerprint index: the token is the one string a Codex spawn
  // call and its child's opening share byte for byte, so its other carrier
  // is the child. The index stamps a carriage at its first observation, so
  // the read speaks the padded window, as the joins dock's does. An empty
  // list is an answer (no conversation carries it - a child captured by an
  // agent that dropped the token); ok:false says the index could not ask.
  searchSealedTaskCarriers: {
    execute: async ({ sessionId, token, fromMs = null, toMs = null } = {}) => {
      if (!sessionId || !token || !Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
        return { rows: [], ok: false, reason: "unbuildable" };
      }
      try {
        if (!(await fingerprintEntityAvailable())) return { rows: [], ok: false, reason: "refused" };
        const { from, to } = paddedConversationRange(new Date(fromMs).toISOString(), new Date(toMs).toISOString());
        const carriers = await sealedTaskCarriers({ traceId: sessionId, token, startTime: from, endTime: to });
        const rows = carriers
          .map((c) => ({ sessionId: c.sessionId, ingested: c.ingested, time: c.at ? parseServerTimestamp(c.at) : null }))
          .filter((c) => c.sessionId && c.time != null);
        return { rows, ok: true };
      } catch {
        return { rows: [], ok: false, reason: "failed" };
      }
    },
  },

  // Scalar capture metadata for a bounded selection of prompts.
  getPromptMetadata: {
    execute: async ({ eventIds = [], range = null } = {}) => {
      const ids = (Array.isArray(eventIds) ? eventIds : []).map(String).filter(Boolean);
      if (!ids.length) return { rows: [], ok: true };
      if (ids.length > 1000) throw new Error("Select at most 1000 prompts.");
      // The analytics validator rejects an IN list longer than 1024; same cap and
      // union as getSignalTagValues.
      const IN_LIST_MAX = 1000;
      const batches = Array.from(
        { length: Math.ceil(ids.length / IN_LIST_MAX) },
        (_, i) => ids.slice(i * IN_LIST_MAX, (i + 1) * IN_LIST_MAX),
      );
      const runBatch = async (batchIds) => {
        const spec = {
          root: { alias: "e", entity: "ENTITY_TYPE_EVENT", children: [] },
          // A wide range on purpose: the caller already chose the ids, and a range
          // narrower than the selection would silently drop rows it asked for.
          timeRange: range || { relative: { durationSeconds: String(90 * 86400) } },
          filter: {
            pred: {
              field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },
              op: "OPERATOR_IN",
              value: batchIds,
            },
          },
          output: {
            dimensions: [
              { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_ID" },        outputAlias: "event_id" },
              { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_ENDPOINT_ID" },     outputAlias: "endpoint_id" },
              { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_NOTIFICATION_ID" }, outputAlias: "session_id" },
              { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_EVENT_CAPTURE_PROJECTED" }, outputAlias: "capture_projected" },
            ],
          },
          limit: batchIds.length,
        };
        return rowsToObjects(await runAnalyticsQuery(spec));
      };
      try {
        const rows = (await Promise.all(batches.map(runBatch))).flat();
        return { rows, ok: true };
      } catch (err) {
        console.warn("[getPromptMetadata] failed:", err);
        // `ok: false` rather than empty rows, so a caller can fall back to
        // per-prompt hydration instead of reading a failure as "no bodies".
        return { rows: [], ok: false };
      }
    },
  },

  getEvent: {
    execute: async (params = {}) => {
      const row = await readTelemetryEvent(params, { execute: runAnalyticsQuery });
      return row ? { ...shapeCommandRow(row), eventData: row.data } : null;
    },
  },

  getFleetPerson: { execute: (params) => readFleetPerson(params) },
  getFleetSource: { execute: (params) => readFleetSource(params) },
  getFleetAssociations: { execute: (params) => readFleetAssociations(params) },
  getFleetEndpointUse: { execute: (params) => readFleetEndpointUse(params) },
  // Fleet's split (lib/fleetConsoleReads.js, lib/fleetBreakdown.js).
  getFleetBreakdown: { execute: (params) => readFleetBreakdown(params) },
  getFleetLinks: { execute: (params) => readFleetLinks(params) },
  getInventoryTotal: { execute: async (params = {}) => ({ total: await executeInventoryTotal(params) }) },
  getFleetRuntimes: { execute: (params) => readFleetRuntimes(params) },
  // Fleet's endpoints: a page of the inventory, each with its sessions.
  getFleetEndpoints: { execute: async (params = {}) => {
    const page = await executeInventoryRows(params);
    return params.sortBy?.id === "sessions" ? page : withEndpointSessions(page, params);
  } },
  // Fleet's local inference: local sessions by model and endpoint, and the
  // runtimes seen and installed there (lib/fleetLocal.js).
  getFleetLocal: { execute: (params) => readFleetLocal(params) },
  // Some endpoints' sessions in a block by the agent that made them.
  getFleetEndpointAgents: { execute: (params) => readFleetEndpointAgents(params) },
  // Fleet's coverage, for the Endpoints page's totals (lib/fleetCoverage.js):
  // the endpoints in the inventory, when each was first seen and last heard
  // from (null where the status read is not served), and the sessions from
  // endpoints in the block and the block before.
  getFleetCoverage: { execute: async (params = {}) => {
    const [total, last, sessions] = await Promise.all([
      executeInventoryTotal({ range: "1M" }),
      executeLastSeenByEndpoint().catch(() => null),
      readEndpointSessions(params),
    ]);
    return { total, lastSeen: last?.byEndpoint ?? null, ...sessions };
  } },
  getFleetFirstSeen: { execute: async () => ({ firstSeen: (await executeFirstSeenByEndpoint()).byEndpoint }) },
  // The Overview's reads (lib/overviewReads.js): traces by hour as Findings
  // counts them, the agents they came from, their worst reviewed severity,
  // and where they came from. Loaded with the first, so this client's graph
  // holds none of the Overview's or Findings' modules.
  getOverviewTraces: { execute: async (params) => (await import("./overviewReads.js")).readOverviewTraces(params) },
  getOverviewAgents: { execute: async (params) => (await import("./overviewReads.js")).readOverviewAgents(params) },
  getOverviewSeverity: { execute: async (params) => (await import("./overviewReads.js")).readOverviewSeverity(params) },
  getOverviewReach: { execute: async (params) => (await import("./overviewReads.js")).readOverviewReach(params) },

  getInventoryAgentDistribution: {
    // Server-side fleet composition for the Inventory Surface "AI agents" tab
    // (see executeInventoryAgentDistribution). Takes { scopePath, range }: empty
    // scopePath aggregates the whole fleet, any scope runs over the endpoints
    // active in it via a PROMPT→ENDPOINT edge-join (no endpoint cap). Returns
    // { groups, endpointsTotal } - shaping is client-side.
    execute: (params = {}) => executeInventoryAgentDistribution(params),
  },
  getInventoryOsDistribution: {
    // Server-side OS-family composition for the Inventory Surface "Operating
    // systems" tab (see executeInventoryOsDistribution). Same { scopePath, range }
    // contract. Returns { groups: [{ product, major, count }] } — folded client-side.
    execute: (params = {}) => executeInventoryOsDistribution(params),
  },
  getInventoryRows: {
    // Per-endpoint Inventory lens table rows, served by the analytics graph
    // (see executeInventoryRows). Takes { scopePath, range, search, sortBy,
    // page, pageSize }: scope + free-text search + sort + pagination all
    // server-side. Returns { rows, total } — rows already flattened to the
    // shared inventory-table shape via flattenInventoryGraphRow.
    execute: (params = {}) => executeInventoryRows(params),
  },
  getInventoryActiveWithMetadata: {
    // Distinct endpoints with activity in scope AND a metadata row (server-side
    // PROMPT + INNER-join ENDPOINT; see executeInventoryActiveWithMetadata).
    // Same { scopePath, range } contract. Returns { count } — the header's
    // "No snapshot" tile subtracts it from "In scope" so that tile stays stable
    // and scalable instead of being derived from the loaded client rows.
    execute: (params = {}) => executeInventoryActiveWithMetadata(params),
  },
  getFirstSeenByEndpoint: {
    // Whole-fleet first-seen proxy for the Inventory Surface "First seen" rollout
    // chart (see executeFirstSeenByEndpoint). No params: returns
    // { byEndpoint: [{ id, firstSeenMs }] }. The surface intersects `id` with the
    // in-scope endpoint set to scope it, then buckets firstSeenMs by month.
    execute: () => executeFirstSeenByEndpoint(),
  },
  getRecentEndpointPage: {
    // Cursor-paginated endpoint liveness for the Connect page. The status
    // entity supplies the 30-day population and ordering; endpoint metadata
    // is fetched only for the returned ids.
    execute: (params = {}) => executeRecentEndpointPage(params),
  },
  getLastSeenByEndpoint: {
    // Whole-fleet last-contact data for the Inventory Surface "Last seen" chart
    // on the analytics:execute path (see executeLastSeenByEndpoint). No params: returns
    // { byEndpoint: [{ id, lastSeenMs }] }. The surface scopes `id` to the in-scope
    // set, refines it with the loaded rows' snapshotTime, then buckets by month.
    execute: () => executeLastSeenByEndpoint(),
  },
  getBrowserCaptureHealth: {
    // Per-browser capture posture for one endpoint, reported on the existing
    // heartbeat. The payload contains only bounded operational metadata.
    execute: (params = {}) => executeBrowserCaptureHealth(params),
  },
  getDimensionEntries: {
    // Ranked sparkline rows for the scoped-nav sidebar. See
    // executeDimensionEntries() below for the implementation —
    // factored out so buildColumnsWithFlowLinks can reuse the same
    // shape for its per-step entries columns. The welcome page's groups
    // read here too (executeGroupEntries), though no sidebar shows them.
    shortCircuit: ({ dimKey } = {}) =>
      !SUPPORTED_DIMS.has(dimKey) && dimKey !== GROUPS_DIM ? { entries: [], count: 0, total: 0, maxValue: 0 } : null,
    execute: (params = {}) => (params.dimKey === GROUPS_DIM ? executeGroupEntries(params) : executeDimensionEntries(params)),
  },

  getDimensionCount: {
    // Distinct count of `dim` values reachable under scope+range.
    // Implementation in executeDimensionCount() — shared with the
    // column-browser picker counts.
    shortCircuit: ({ dimKey } = {}) =>
      !SUPPORTED_DIMS.has(dimKey) ? 0 : null,
    execute: (params = {}) => executeDimensionCount(params),
  },

  getDimensionCounts: {
    // Batched form of getDimensionCount: returns counts for many dims
    // in a single analytics query (one row with N COUNT_DISTINCT
    // metrics). Used by ScopeSidebar to prefetch every top-level dim
    // header count up front without N separate round trips.
    execute: (params = {}) => executeDimensionCounts(params),
  },

  getDimEntrySparkline: {
    // Per-entry bucketed sparkline fetch, fired lazily by the sidebar
    // when a row scrolls into view (see EntrySparkline in
    // SidebarNavRuntime). Used by dims whose bulk discovery query
    // can't cheaply carry the bucket fan-out — today just
    // mcp_server, where the planner would otherwise blow up arrayJoin
    // on PROMPT_TOOL_CALL_NAMES × bucket.
    //
    // Returns { series: number[] } in the same densified shape as
    // the inline sparkline path so EntrySparkline can substitute it
    // in place. `query`: the steer the entries were read under, which the
    // line honours too (none where the PROMPT root can't carry it).
    execute: async ({ dimKey, id, parentScopePath = [], range, bucketCount = 24, query = "" } = {}) => {
      if (!dimKey || !id) return { series: [] };
      const apiScope = toApiScope(parentScopePath, range);
      const apiRange = toApiRange(range);
      const { fromMs, toMs } = rangeBoundsMs(apiRange);
      const bucketSeconds = bucketSecondsForRange(apiRange, bucketCount);
      // The dim's own scope-step expansion does the predicate work —
      // we just append the entry as a synthetic scope step and let
      // buildPromptRootAndFilter emit the right shape.
      const stepDim = DIM_SINGULAR[dimKey] ?? dimKey;
      const adapter = getDimAdapter(stepDim);
      if (adapter?.batchesEntrySparklines) {
        return batchedEntrySparkline({ adapter, dimKey, id, parentScopePath, range, bucketCount, query });
      }
      const { compiled: steer, active: steerActive, carryable } = await compileSteer(query);
      if (steerActive && !carryable) return { series: [] };
      const { root, filter, unsupported } = buildPromptRootAndFilter({
        scopePath: [...apiScope, { dim: stepDim, ids: [id] }],
        steer,
      });
      // Fail closed on a scope this PROMPT root can't carry (today: model) rather
      // than drawing a sparkline summed across all models (O-1805).
      if (unsupported.length) return { series: [] };
      const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [promptCount("value")],
        },
      };
      // Scope the per-entry sparkline to the signal's fired prompts in scope.
      const sparkFilter = combineAnd(filter, signalScopeFilter(parentScopePath));
      if (sparkFilter) spec.filter = sparkFilter;
      try {
        const rows = rowsToObjects(await runAnalyticsQuery(spec));
        const tagged = rows.map((r) => ({ id, bucket: r.bucket, value: r.value }));
        const densified = densifyBuckets(tagged, { fromMs, toMs, bucketCount, bucketSeconds });
        return { series: densified.get(id) ?? new Array(bucketCount).fill(0) };
      } catch (err) {
        console.warn(`[getDimEntrySparkline] failed for ${dimKey}/${id}:`, err);
        return { series: [] };
      }
    },
  },

  getSignalFiringSeries: {
    // Time-bucketed firing counts for the `signals` dimension: per bucket, the
    // distinct firing prompts, one densified series per alert signal (command).
    // Powers the firing-rate chart on the signals console (all alert signals)
    // and the per-signal detail lens (one command). Roots at PROMPT, INNER-joins
    // SignalTags (extraDims:["signal"]), groups by (st.namespace, bucket) and
    // buckets on the PROMPT event timestamp — SignalTags has no time column, so
    // a firing's time IS its prompt's time. The namespace filter bounds the
    // GROUP BY to the requested signals (never the whole tags table). Returns
    // { layers:[{label, series}] }; fails closed to empty on an inexpressible
    // scope (e.g. model) rather than charting a firing rate summed across it.
    execute: async ({ scopePath = [], commands = [], range, bucketCount = 48 } = {}) => {
      const cmds = (Array.isArray(commands) ? commands : []).filter(Boolean);
      if (!cmds.length) return { layers: [] };
      // A signal step in the parent scope would double-filter; the namespace
      // filter below is the scope. Drop any signal step before building the root.
      const apiScope = toApiScope(scopePath, range).filter((s) => s.dim !== "signal");
      const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["signal"] });
      if (unsupported.length) return { layers: [] };
      const apiRange = toApiRange(range);
      const bucketSeconds = bucketSecondsForRange(apiRange, bucketCount);
      const { fromMs, toMs } = rangeBoundsMs(apiRange);
      const NS_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" };
      const ST_EVENT_ID = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID" };
      const ns = [...new Set(cmds.map(namespaceForCommand).filter(Boolean))];
      if (!ns.length) return { layers: [] };
      const nsFilter = { pred: { field: NS_FIELD, op: ns.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ", value: ns.length > 1 ? ns : ns[0] } };
      const filterParts = [filter, nsFilter].filter(Boolean);
      const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        filter: filterParts.length === 1 ? filterParts[0] : { andExpr: { children: filterParts } },
        output: {
          dimensions: [
            { field: NS_FIELD, outputAlias: "ns" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [{ outputAlias: "value", computed: { field: ST_EVENT_ID, op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
        },
      };
      try {
        const rows = rowsToObjects(await runAnalyticsQuery(spec));
        const tagged = rows.map((r) => ({ id: String(r.ns ?? ""), bucket: r.bucket, value: r.value }));
        const densified = densifyBuckets(tagged, { fromMs, toMs, bucketCount, bucketSeconds });
        const layers = cmds.map((cmd) => ({
          label: labelForCommand(cmd),
          series: densified.get(namespaceForCommand(cmd)) ?? new Array(bucketCount).fill(0),
        }));
        return { layers };
      } catch (err) {
        console.warn("[getSignalFiringSeries] failed:", err);
        return { layers: [] };
      }
    },
  },

  getSignalEndpointFirings: {
    // The endpoints that fired an ENDPOINT-SUBJECT signal, for the endpoint
    // presentation of the signal detail lens (config_shadow today).
    //
    // Roots at SIGNAL_TAGS instead of PROMPT: an endpoint-subject tag is computed
    // from an endpoint event that has no prompt row, so every prompt-rooted
    // signal read (getTraces, getSignalFiringSeries, getSignalTagValues) is
    // structurally blind to these rows. `subject_id` IS the endpoint id here,
    // which is why the filter pins subject_type — the column is only an endpoint
    // id for endpoint-subject rows.
    //
    // One query serves the count, the table, and the chart: the caller buckets
    // `event_time` client-side rather than paying a second aggregate round-trip.
    // Hostnames come from a sibling ENDPOINT read, since signal_tags carries no
    // hostname and the graph has no ENDPOINT→SignalTags edge to join through.
    //
    // Scope note: a root signal_tags scan can't carry prompt-derived scope steps
    // (cluster, model, session…). An `endpoints` step is applied client-side; any
    // other narrowing step is reported back as `unscopedDims` so the caller can
    // say so in the UI rather than implying the list is scoped when it is not.
    execute: async ({ scopePath = [], namespace, range, limit = ENDPOINT_FIRING_ROW_LIMIT } = {}) => {
      const empty = { endpoints: [], series: [], total: 0, unscopedDims: [], ok: true };
      if (!namespace) return empty;

      // `toApiScope` singularizes dim keys through DIM_SINGULAR, so the sidebar's
      // `endpoints` step arrives here as `endpoint`. Matching the plural silently
      // never fires: the client-side narrowing is skipped AND the endpoint scope
      // gets reported back as one this view can't narrow by.
      const apiScope = toApiScope(scopePath, range).filter((s) => s.dim !== "signal");
      const isEndpointDim = (s) => s.dim === "endpoint" || s.dim === "endpoints";
      const endpointStep = apiScope.find(isEndpointDim);
      const allowedEndpoints = endpointStep
        ? new Set((Array.isArray(endpointStep.ids) ? endpointStep.ids : [endpointStep.id]).filter(Boolean).map(String))
        : null;
      const unscopedDims = apiScope.filter((s) => !isEndpointDim(s)).map((s) => s.dim);

      const apiRange = toApiRange(range);
      const ST = (field) => ({ stepAlias: "st", field });
      const spec = {
        root: { alias: "st", entity: "ENTITY_TYPE_SIGNAL_TAGS" },
        timeRange: rangeToTimeRange(apiRange),
        filter: {
          andExpr: {
            children: [
              { pred: { field: ST("DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE"), op: "OPERATOR_EQ", value: namespace } },
              { pred: { field: ST("DIMENSION_FIELD_SIGNAL_TAGS_SUBJECT_TYPE"), op: "OPERATOR_EQ", value: "endpoint" } },
            ],
          },
        },
        output: {
          dimensions: [
            { field: ST("DIMENSION_FIELD_SIGNAL_TAGS_SUBJECT_ID"), outputAlias: "endpoint_id" },
            { field: ST("DIMENSION_FIELD_SIGNAL_TAGS_TAG"), outputAlias: "tag" },
            { field: ST("DIMENSION_FIELD_SIGNAL_TAGS_VALUE"), outputAlias: "value" },
            { field: ST("DIMENSION_FIELD_SIGNAL_TAGS_EVENT_TIME"), outputAlias: "event_time" },
            { field: ST("DIMENSION_FIELD_SIGNAL_TAGS_EVENT_ID"), outputAlias: "event_id" },
          ],
        },
        limit,
      };

      let rows;
      try {
        rows = rowsToObjects(await runAnalyticsQuery(spec));
      } catch (err) {
        // A caller whose role is endpoint-row-scoped can't run a bare
        // signal_tags root scan (the table has no endpoint column, so the
        // planner refuses rather than under-enforcing). Surface ok:false so the
        // lens shows an explicit state instead of a misleading empty list.
        console.warn("[getSignalEndpointFirings] failed:", err);
        return { ...empty, ok: false };
      }

      const byEndpoint = new Map();
      const times = [];
      for (const r of rows) {
        const id = String(r?.endpoint_id ?? "");
        if (!id) continue;
        if (allowedEndpoints && !allowedEndpoints.has(id)) continue;
        const ms = parseServerTimestamp(r?.event_time);
        if (Number.isFinite(ms)) times.push(ms);
        let ep = byEndpoint.get(id);
        if (!ep) {
          ep = { endpointId: id, hostname: "", firings: 0, lastFiredMs: null, findings: [] };
          byEndpoint.set(id, ep);
        }
        ep.firings += 1;
        if (Number.isFinite(ms) && (ep.lastFiredMs === null || ms > ep.lastFiredMs)) ep.lastFiredMs = ms;
        ep.findings.push({
          tag: String(r?.tag ?? ""),
          value: String(r?.value ?? ""),
          eventId: String(r?.event_id ?? ""),
          firedAtMs: Number.isFinite(ms) ? ms : null,
        });
      }

      // Newest-firing first — the ranking an analyst triages by.
      const endpoints = Array.from(byEndpoint.values()).sort(
        (a, b) => (b.lastFiredMs ?? 0) - (a.lastFiredMs ?? 0),
      );
      let groupedTotal = 0;
      for (const ep of endpoints) {
        ep.findings.sort((a, b) => (b.firedAtMs ?? 0) - (a.firedAtMs ?? 0));
        ep.groupedFindings = groupFindings(ep.findings);
        groupedTotal += ep.groupedFindings.length;
      }

      await attachEndpointHostnames(endpoints, apiRange);

      return {
        endpoints,
        series: bucketTimestamps(times, apiRange),
        // Three different quantities, each labelled where it renders. `total` is
        // raw tag rows (what the observation chart plots), `groupedTotal` the
        // distinct problems after collapsing re-fires, `endpoints.length` the
        // machines affected. They differ by design — one condition can be observed
        // many times — and reporting one under another's label is what made the
        // old view read 1 / 10 / 10 for the same signal.
        total: rows.length,
        groupedTotal,
        endpointCount: endpoints.length,
        unscopedDims,
        ok: true,
      };
    },
  },

  getMemorySignalAvailable: {
    // Presence probe for the Memories dim. The `memory` signal is per-tenant
    // (manifest deploy_to: none) and there's no resource_paths fallback, so a
    // tenant without it deployed has no memory data — the sidebar hides the dim
    // until this resolves true, else it renders an empty surface. Tenant-wide over
    // a wide fixed window (see hasMemorySignal); returns a boolean. Self-corrects:
    // deploy the signal for a tenant and the dim appears on next load.
    execute: () => hasMemorySignal(),
  },

  getFileCounterparties: {
    // "Who else touched this file?" — the cross-session join behind the Lanes
    // file panel. Answers it from RAW OS TELEMETRY rather than from prompts,
    // because that is the only substrate where both sides of an agent-to-agent
    // file exchange are visible at once:
    //
    //   - an Anthropic agent's writes go through its Write tool and emit NO
    //     FileState write, but its `resource_paths` name the file;
    //   - an OpenAI/Codex agent in code mode names the file in neither — its
    //     file work never reaches a tool call at all — but its process DOES
    //     emit FileState and the command lines that opened the file.
    //
    // So a `resource_paths` search finds only the Anthropic side, and this
    // search finds both. Measured on staging against a real two-agent file
    // channel: it recovers the Codex counterparty that the prompt-side search
    // returns nothing for.
    //
    // COST — `event_data` is a HighCardinality String with no json_path, so this
    // is a genuine ILIKE over the raw payload and TIME is the expensive axis, not
    // breadth. Measured under ILIKE (one filename, staging; single samples, and
    // run-to-run variance is real — treat the ORDERING as the durable finding,
    // not the absolute figures):
    //     endpoint + 12h    0.26s     62k rows      <- default
    //     tenant  + 12h     1.89s    1.0M rows      <- "search all endpoints"
    //     endpoint + 7d     2.21s    2.1M rows
    //     tenant  + 7d     12.76s    4.0M rows      <- never do this implicitly
    // Hence: default to the focal trace's own window, let the caller widen
    // ENDPOINT scope freely, and never widen the window on its behalf.
    //
    // Returns { rows: [{ session_id, endpoint_id, writes, reads, commands,
    // first_seen, last_seen }] }, authors first. Polarity is derived
    // client-side: FileState carries either `first_write_time` or
    // `first_read_time`, and neither is a filterable column.
    //
    // The OS search is no longer alone: two more channels run beside it under
    // the SAME window and endpoint bounds — the enriched prompt rows that name
    // the file in `resource_paths` (each hit an openable turn), and the
    // conversation model's content search (which sees tool results and captured
    // responses). Each channel fails independently: the OS spine erroring fails
    // the search, a side channel erroring reports itself unavailable, because
    // "the channel could not answer" and "the channel found nothing" are
    // different claims and only one of them is about the agent.
    execute: async (args = {}) => {
      const request = fileCounterpartiesSpec(args);
      if (!request) return { rows: [], ok: false };
      const { searchText, ...spec } = request;
      const callsSpec = fileMentionTurnsSpec(args);
      const side = (s) => (s
        ? runAnalyticsQuery(s).then((r) => ({ ok: true, rows: rowsToObjects(r) }))
          .catch((err) => ({ ok: false, error: String(err?.message || err) }))
        : Promise.resolve({ ok: false, error: "unbounded" }));
      const contentSide = runPaddedSearchWithRetry((pad) => conversationFileSearchSpec({ ...args, pad }))
        .then((r) => (r == null ? { ok: false, error: "unbounded" } : { ok: true, rows: rowsToObjects(r) }))
        .catch((err) => ({ ok: false, error: String(err?.message || err) }));
      try {
        const [activity, callsRes, contentRes] = await Promise.all([
          readTelemetryPage(spec, { search: searchText, execute: runAnalyticsQuery }), side(callsSpec), contentSide,
        ]);
        const raw = activity.map((row) => ({
          session_id: row.sessionId, endpoint_id: row.endpointId, event_type: row.eventType,
          event_time: row.ts, data: row.data,
        }));
        const truncated = countedAtCap(raw.length, spec.limit);
        const folded = foldFileCounterparties(raw);
        // Resolve BEFORE merging: the merge keys on session ids, and a Codex
        // host id only matches its conversation's calls/content evidence after
        // the host has been resolved to the conversation.
        const osRows = await resolveHostedConversations(folded, args);
        const { focal, rows: merged } = mergeCounterpartyChannels({
          os: osRows,
          calls: callsRes.ok ? foldFileMentions(callsRes.rows) : [],
          // The fold checks matches against the SAME basename the search
          // LIKE'd for — a suffix collision (focal `b2.md` matched inside
          // another conversation's `prompt-b2.md`) is dropped here.
          content: contentRes.ok ? foldContentMentions(contentRes.rows, { basename: fileBasenameOf(args.path) }) : [],
          focalSessionId: args.sessionId ?? null,
        });
        const labelled = await labelCounterpartySessions(
          focal ? [focal, ...merged] : merged, args);
        const focalOut = focal ? labelled.shift() : null;
        return {
          rows: labelled,
          focal: focalOut,
          truncated,
          channels: {
            calls: callsRes.ok ? { ok: true } : { ok: false, error: callsRes.error },
            content: contentRes.ok ? { ok: true } : { ok: false, error: contentRes.error },
          },
          ok: true,
        };
      } catch (err) {
        return { rows: [], ok: false, error: String(err?.message || err) };
      }
    },
  },

  getFileContentProbes: {
    // The content-overlap search: given a few literal lines of the focal
    // file's reconstructed content, which OTHER conversations carried the same
    // content — in a tool result, a pasted message, or a captured response?
    // This is the first content-based join between sessions: it can relate two
    // agents that never touched the same path, because the relationship it
    // tests is the CONTENT moving between them.
    //
    // One bounded query per probe (the search view takes exactly one term per
    // query), probes bounded by the caller's derivation: up to NEEDLE_COUNT
    // id-shaped needles plus up to PROBE_COUNT distinctive lines. Returns
    // per-session "matched k of n" — distinct probes matched, which one
    // repeated snippet cannot inflate. A probe matching more sessions than
    // PROBE_SESSION_CAP is reported diffuse and counts toward neither side.
    execute: async ({ probes = [], sessionId = null, ...bounds } = {}) => {
      // "Did the content travel?" means travel to SOMEONE ELSE: the focal
      // session's own echoes must not satisfy any fallback gate, or the
      // search stops at its own reflection (found live — the writer's Write
      // input echo blocked the cross-endpoint widen).
      const othersOf = (fold) => fold.filter((p) => p.session_id !== sessionId);
      const specs = (probes || []).map((probe) => contentProbeSpec({ probe, ...bounds }));
      if (!specs.length || specs.some((s) => !s)) return { rows: [], ok: false };
      try {
        const results = await Promise.all((probes || []).map((probe) =>
          runPaddedSearchWithRetry((pad) => contentProbeSpec({ probe, ...bounds, pad }))
            .then((r) => ({ ok: true, rows: rowsToObjects(r) }))
            .catch((err) => ({ ok: false, error: String(err?.message || err) }))));
        // Every probe failing means the search could not run (most likely the
        // deployment has no conversation model) — report that, not "no overlap".
        if (results.every((r) => !r.ok)) {
          return { rows: [], ok: false, error: results[0]?.error || "search unavailable" };
        }
        let fold = foldProbeMatches(results);
        // "k of n" must be computed from the SAME result set the adopted fold
        // was built from, or the numbers drift apart when a fallback runs.
        let used = results;
        let via = "conversations";
        let scope = bounds.allEndpoints ? "all-endpoints" : "endpoint";
        if (!othersOf(fold).length && !bounds.allEndpoints) {
          // A scoped zero is not the end of the question: the counterparty
          // that read this content can sit under ANOTHER endpoint id — found
          // live, where the reading agent's conversation was registered
          // against a second endpoint of the same exchange. The probes are
          // selective literal lines, so tenant breadth is cheap; run them
          // wide before answering "nowhere".
          const wideResults = await Promise.all((probes || []).map((probe) =>
            runPaddedSearchWithRetry((pad) => contentProbeSpec({ probe, ...bounds, allEndpoints: true, pad }))
              .then((r) => ({ ok: true, rows: rowsToObjects(r) }))
              .catch((err) => ({ ok: false, error: String(err?.message || err) }))));
          const wideFold = foldProbeMatches(wideResults);
          if (othersOf(wideFold).length) {
            // The WHOLE fold — focal entry included — comes from the wide
            // results. Widening can push a probe over the diffuseness cap, so
            // a focal entry carried over from the narrow fold would report a
            // "k of n" under a different n than every other row in the same
            // table. The wide scope is a superset of the narrow one, so an
            // index-found focal is found again here.
            //
            // A focal found ONLY by the raw-bodies pass is the exception: its
            // content is not in the conversation index anywhere, so the
            // widened index query cannot re-find it. Keep its evidence, but
            // strip the counts — they were computed under a different
            // partition and must not render beside the wide denominator.
            const focalEntry = fold.find((p) => p.session_id === sessionId);
            const wideHasFocal = wideFold.some((p) => p.session_id === sessionId);
            fold = !wideHasFocal && focalEntry
              ? [...wideFold, { ...focalEntry, matched: 0, of: 0 }]
              : wideFold;
            used = wideResults;
            via = "conversations";
            scope = "all-endpoints";
          }
        }
        const { counted, diffuse } = partitionProbeResults(used);
        return { rows: fold, of: counted.length, diffuse: diffuse.length, via, scope, ok: true };
      } catch (err) {
        return { rows: [], ok: false, error: String(err?.message || err) };
      }
    },
  },

  getConversationTurnTimes: {
    // Assistant-reply timestamps for one conversation, from the normalized
    // conversation model — the anchors that let the lanes date their turn
    // axis (traceTurnTimes) without a body fetch per prior request. Returns
    // { ok, times: [epochMs ascending] }; ok:false where the deployment has
    // no conversation model, which callers must read as "cannot date", never
    // as "instantaneous".
    execute: (args = {}) => fetchConversationTurnTimes(args),
  },

  getHostSessionForThread: {
    // The host process session a hosted conversation's OS telemetry is filed
    // under (see fetchHostSessionForThread). One bounded LIKE over ProcessStart
    // markers; returns { ok, hostSessionId } with null when nothing matched.
    execute: (args = {}) => fetchHostSessionForThread(args),
  },

  getTraceResponse: {
    // The response to the focal request, from the normalized conversation
    // model. A trace is a REQUEST body — every reply it shows was recovered
    // from a later request's echo, so the reply to the request being viewed
    // has never been visible. Responses are captured as assistant messages
    // now; this fetches the one that answers the focal request.
    //
    // Status is four-valued and the distinctions are the feature:
    //   captured    — a response message with content, returned as parts
    //   none        — the model has no observed response for this invocation
    //   missing     — a response was OBSERVED but its message has not landed
    //   unavailable — the deployment cannot answer (no conversation model, or
    //                 no sensitive-read permission); says nothing about the agent
    execute: (args = {}) => fetchTraceResponse(args),
  },

  getSignalTagValues: {
    // Read the signal_tags rows for one (namespace, tag) across a scope — the
    // value each matching event emitted (an mcp_server's {surface,instructions,
    // fingerprint} JSON, a skill's SKILL.md body, a tool's {name,description,schema},
    // …). This is how the functional skill/MCP/tool detail lenses read the backend
    // signal pipeline instead of re-extracting in-browser per view-load. The `tag`
    // (the skill/server/tool/memory name) IS the identity, so the matching
    // skills/mcp_servers/tools/memories scope step is dropped before building the
    // root — it would over-narrow (tools are OFFERED on more prompts than they're
    // CALLED on; a memory step would redundantly re-gate the same tag), whereas the
    // tag already selects the prompts that carried this body/surface/definition.
    // Returns { rows: [{ event_id, event_time, value }] }, newest first.
    execute: async ({ scopePath = [], namespace, tag, range, limit = 500, eventIds = null } = {}) => {
      if (!namespace || !tag) return { rows: [] };
      const apiScope = toApiScope(scopePath, range)
        .filter((s) => s.dim !== "skill" && s.dim !== "mcp_server" && s.dim !== "tool_call" && s.dim !== "memory");
      const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["signal"] });
      if (unsupported.length) return { rows: [] };
      const apiRange = toApiRange(range);
      const NS_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" };
      const TAG_FIELD = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG" };
      const timeRange = rangeToTimeRange(apiRange);
      const dimensions = [
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, outputAlias: "event_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
        { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_VALUE" }, outputAlias: "value" },
      ];

      // Optional event-id set — restricts the bodies to a specific set of events
      // (e.g. the skill lens hydrates versions from just the events currently in
      // its trace-list window, so chips can never advertise a version the list
      // isn't showing). The analytics validator rejects an `IN` list longer than
      // 1024, so batch the ids under that and union the results — the caller can
      // page an arbitrarily large window without the query failing closed.
      const IN_LIST_MAX = 1000;
      const ids = Array.isArray(eventIds) ? eventIds.map(String) : null;
      const batches = ids && ids.length
        ? Array.from({ length: Math.ceil(ids.length / IN_LIST_MAX) }, (_, i) => ids.slice(i * IN_LIST_MAX, (i + 1) * IN_LIST_MAX))
        : [null];

      const runBatch = async (batchIds) => {
        const idPred = batchIds
          ? { pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, op: "OPERATOR_IN", value: batchIds } }
          : null;
        const preds = [
          filter,
          { pred: { field: NS_FIELD, op: "OPERATOR_EQ", value: namespace } },
          { pred: { field: TAG_FIELD, op: "OPERATOR_EQ", value: tag } },
          // Skill/MCP usage-only tags have no definition payload. Exclude them in
          // the query so they cannot consume the capability window's row limit.
          namespace === "mcp_server" || namespace === "skill"
            ? { pred: { field: { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_VALUE" }, op: "OPERATOR_NE", value: "" } }
            : null,
          idPred,
        ].filter(Boolean);
        const spec = {
          root,
          timeRange,
          filter: preds.length === 1 ? preds[0] : { andExpr: { children: preds } },
          output: { dimensions },
          // Bound each batch to its own id count so a batch is never truncated
          // (an event can carry >1 body row); no-id reads keep the caller's limit.
          limit: batchIds ? Math.max(limit, batchIds.length * 2) : limit,
        };
        return runSignalValueRows(spec, async (s) => rowsToObjects(await runAnalyticsQuery(s)), [namespace]);
      };

      try {
        const rows = (await Promise.all(batches.map(runBatch))).flat();
        // Sort newest-first client-side (orderBy references an output alias, not
        // a field; event_time is a plain ISO string so a string sort is correct).
        rows.sort((a, b) => String(b.event_time ?? "").localeCompare(String(a.event_time ?? "")));
        return { rows, ok: true };
      } catch (err) {
        console.warn("[getSignalTagValues] failed:", err);
        // `ok: false` (not just empty rows) lets callers distinguish a failure
        // from a genuinely empty result — the skill lens releases the events it
        // requested so a later window retries, rather than marking them fetched.
        return { rows: [], ok: false };
      }
    },
  },

  getFsSignalAvailable: {
    // Presence probe for the Files X-ray tab. The `fs` signal is per-tenant
    // (manifest deploy_to: none) and the tab now reads it directly with no
    // hydration fallback, so the tab is hidden until this resolves true — else
    // it renders an empty tree. Tenant-wide over a wide fixed window (see
    // hasFsSignal); returns a boolean. Self-corrects: deploy the signal for a
    // tenant and the tab appears on next load. Mirrors getMemorySignalAvailable.
    execute: () => hasFsSignal(),
  },

  getFsSignalOps: {
    // The Files X-ray's data source: the backend `fs` signal (fs.write authorship
    // + fs.read observations) over a scope, replacing per-prompt hydration and the
    // event_data ILIKE blame scan with one indexed signal_tags read. The value
    // JSON already carries the effective diff / numbered obs, so the caller maps
    // each row straight onto a file op (see fsProvenance.fsRowsToPromptEvents /
    // pickFsAuthor). Two call shapes:
    //   • file tree — namespaces:["fs.write","fs.read"], eventIds:<window ids>
    //   • line blame — namespaces:["fs.write"], tag:<rpath(file)>, range:<window>
    // Returns { rows: [{ event_id, endpoint_id, session_id, event_time,
    // namespace, tag, value }], ok }. ok:false (not just empty rows) on a scope
    // the PROMPT root can't carry or a query failure, so the tab can fall back to
    // its idle/empty state rather than treating a failure as "no files".
    execute: async ({ scopePath = [], range, namespaces = ["fs.write", "fs.read"], tag = null, eventIds = null, limit = 8000 } = {}) => {
      const nss = (Array.isArray(namespaces) ? namespaces : [namespaces]).filter(Boolean);
      if (!nss.length) return { rows: [], ok: true };
      const apiScope = toApiScope(scopePath, range);
      const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["signal"] });
      if (unsupported.length) return { rows: [], ok: false };
      const apiRange = toApiRange(range);
      const timeRange = rangeToTimeRange(apiRange);
      const NS = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_NAMESPACE" };
      const TAG = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_TAG" };
      const VALUE = { stepAlias: "st", field: "DIMENSION_FIELD_SIGNAL_TAGS_VALUE" };
      const P_EVENT = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" };
      const dimensions = [
        { field: P_EVENT, outputAlias: "event_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" }, outputAlias: "endpoint_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, outputAlias: "session_id" },
        { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
        { field: NS, outputAlias: "namespace" },
        { field: TAG, outputAlias: "tag" },
        { field: VALUE, outputAlias: "value" },
      ];
      const nsPred = nss.length === 1
        ? { pred: { field: NS, op: "OPERATOR_EQ", value: nss[0] } }
        : { pred: { field: NS, op: "OPERATOR_IN", value: nss } };
      const tagPred = tag ? { pred: { field: TAG, op: "OPERATOR_EQ", value: tag } } : null;

      // Batch a large window event-id set under the analytics IN-list ceiling
      // (1024) and union — the scan window is ~SCAN_LIMIT ids so this is one
      // batch today, but stays correct if the window ever grows.
      const IN_LIST_MAX = 1000;
      const ids = Array.isArray(eventIds) ? eventIds.map(String) : null;
      const batches = ids && ids.length
        ? Array.from({ length: Math.ceil(ids.length / IN_LIST_MAX) }, (_, i) => ids.slice(i * IN_LIST_MAX, (i + 1) * IN_LIST_MAX))
        : [null];

      const runBatch = async (batchIds) => {
        const idPred = batchIds ? { pred: { field: P_EVENT, op: "OPERATOR_IN", value: batchIds } } : null;
        const preds = [filter, nsPred, tagPred, idPred].filter(Boolean);
        const spec = {
          root,
          timeRange,
          filter: preds.length === 1 ? preds[0] : { andExpr: { children: preds } },
          output: { dimensions },
          // A single event can carry up to ~2×_MAX_OPS instance tags (writes +
          // reads); size each batch generously so it isn't truncated mid-event.
          limit: batchIds ? Math.max(limit, batchIds.length * 128) : limit,
        };
        return rowsToObjects(await runAnalyticsQuery(spec));
      };

      try {
        const rows = (await Promise.all(batches.map(runBatch))).flat();
        return { rows, ok: true };
      } catch (err) {
        console.warn("[getFsSignalOps] failed:", err);
        return { rows: [], ok: false };
      }
    },
  },

  getOverviewActivity: {
    // Top-K agents with per-bucket sparkline. Single analytics query
    // grouping PROMPT by (provider, bucket_time); FE picks top-N
    // agents by sum and densifies sparse rows into fixed-length
    // arrays. A scope dim the API cannot express fails closed.
    execute: async ({ scopePath = [], bucketCount = 48, topN = 5, metric = "prompts", range } = {}) => {
      const apiScope = toApiScope(scopePath, range);
      if (unsupportedScopeDims(apiScope).length > 0) return emptyActivityLens();

      const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope });
      // Fail closed on a scope this PROMPT root can't carry (today: model) instead
      // of charting activity summed across all models (O-1805).
      if (unsupported.length) return emptyActivityLens();
      const apiRange = toApiRange(range);
      const bucketSeconds = bucketSecondsForRange(apiRange, bucketCount);
      const { fromMs, toMs } = rangeBoundsMs(apiRange);

      // Each bucket × agent cell counts:
      //   "prompts" counts distinct logical prompt IDs
      //   "sessions"  → uniq(notification_id)       — distinct sessions active
      //   "endpoints" → uniq(endpoint_id)            — distinct endpoints active
      // Anything else falls through to prompts. Without the
      // "endpoints" branch the "Active endpoints" tab was rendering
      // raw prompt counts (which is why a single bucket reported
      // 10K+ "endpoints" against a 60-endpoint org).
      const metricSource = metric === "sessions"
        ? { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, op: "AGGREGATE_OP_COUNT_DISTINCT" }
        : metric === "endpoints"
        ? { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },     op: "AGGREGATE_OP_COUNT_DISTINCT" }
        : promptCount("value").computed;

      const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" },          outputAlias: "agent" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" },  outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [{ outputAlias: "value", computed: metricSource }],
        },
      };
      if (filter) spec.filter = filter;
      const rows = rowsToObjects(await runAnalyticsQuery(spec));

      return overviewActivityFromRows(rows, {
        valueAlias: "value",
        bucketCount,
        bucketSeconds,
        fromMs,
        toMs,
        topN,
      });
    },
  },

  getOverviewActivityBundle: {
    // Same activity surface as getOverviewActivity, but returns all three
    // lenses from a single analytics query. The previous React callsite
    // launched one request for sessions, one for prompts, and one for
    // endpoints; each used the same root, filter, buckets, and grouping.
    execute: async ({ scopePath = [], bucketCount = 48, topN = 5, range, query = "" } = {}) => {
      const apiScope = toApiScope(scopePath, range);
      // The query-language refinement is PROMPT-rooted scope; `hasQuery` forces
      // the token layer onto the PROMPT→TOKEN collapse (a query can't be
      // re-anchored onto TOKEN columns) so it tracks the same filtered view.
      // Compile ONCE here and apply the result to the agent + token specs below
      // (cheap pure work, but no reason to parse/compile the same string 3×).
      const compiledQuery = await resolveAndCompile(query);
      const hasQuery = Boolean(compiledQuery.filter);
      // Unsupported scopes fail closed so the activity surface never renders
      // unscoped data and a refining query is never silently dropped.
      if (unsupportedScopeDims(apiScope).length > 0) {
        return { sessions: emptyActivityLens(), prompts: emptyActivityLens(), endpoints: emptyActivityLens(), tokens: emptyActivityLens() };
      }

      const apiRange = toApiRange(range);
      const bucketSeconds = bucketSecondsForRange(apiRange, bucketCount);
      const { fromMs, toMs } = rangeBoundsMs(apiRange);

      // Agent-grouped pass: sessions / prompts / endpoints. Group key
      // is PROMPT.provider so layers are per-tool ("Claude", "Cursor").
      // These layers root at PROMPT and can't carry a model scope (O-1805) — fail
      // them closed to empty below (the token layer still honors model via
      // TOKEN directly), so a pinned model scope never silently widens the
      // count layers across all models.
      const { root: agentRoot, filter: agentFilter, unsupported: agentUnsupported } = buildPromptRootAndFilter({ scopePath: apiScope });
      const agentSpec = {
        root: agentRoot,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" },          outputAlias: "agent" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" },  outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [
            {
              outputAlias: "sessions",
              computed: {
                field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" },
                op: "AGGREGATE_OP_COUNT_DISTINCT",
              },
            },
            promptCount("prompts"),
            {
              outputAlias: "endpoints",
              computed: {
                field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },
                op: "AGGREGATE_OP_COUNT_DISTINCT",
              },
            },
          ],
        },
      };
      if (agentFilter) agentSpec.filter = agentFilter;
      // Fold the query in (PROMPT-rooted): ANDs the compiled filter + adds any
      // cross-entity joins onto the agent-count spec.
      const agentSpecFinal = applyQueryToSpec(agentSpec, compiledQuery);

      // Token pass: group by TOKEN.model. This doesn't join tokens onto the
      // PROMPT root (the per-prompt usage edge was removed in O-1805) — it
      // ROOTS at ENTITY_TYPE_TOKEN directly (O-2021), which carries both
      // MODEL and the token-sum metrics natively. Scope is re-anchored onto
      // TOKEN's own endpoint/session/provider columns; a scope dim
      // TOKEN can't express (cluster/tool/…) fails closed to an
      // empty token layer (the session/prompt/endpoint lenses still render).
      // Bucketing uses OBSERVED_AT (the token entity's time field) rather than
      // the prompt's issue time. Sums skip NULL token columns natively and the
      // planner applies FINAL + finest-grain resolution. Cache sums are
      // provider-predicated (cacheTokenMetrics) so each layer is its
      // provider's true reported volume. Empty-string `model`
      // rows (no model resolved) get dropped in overviewActivityFromRows.
      // Direct on TOKEN for endpoint/session/agent/model; else fall back
      // to the PROMPT_HAS_TOKENS collapse so prompt-analysis
      // scopes (cluster/tool/file/mcp/skill) keep a per-model token layer
      // instead of failing closed to empty. Output (model + bucket + token sums)
      // stays on `pu` either way; the collapse puts the scope in PROMPT's
      // stepFilter, so no top-level filter on the source.
      const tokenDirect = buildUsageRootAndFilter({ scopePath: apiScope });
      let tokenRoot = tokenDirect.root;
      let tokenFilter = tokenDirect.filter;
      let tokenUnsupported = tokenDirect.unsupported;
      // Force the collapse when a query is active (TOKEN-direct can't carry a
      // PROMPT-rooted query filter) as well as for TOKEN-inexpressible scope.
      if (tokenUnsupported.length || hasQuery) {
        const tokenCollapsed = buildUsageCollapse({ scopePath: apiScope });
        if (tokenCollapsed.unsupported.length) {
          tokenUnsupported = tokenCollapsed.unsupported;
        } else {
          // The query rides the PROMPT source's stepFilter (collapse contract).
          // identity can't ride the collapse → blank the token layer.
          const refined = applyQueryToCollapseRoot(tokenCollapsed.root, compiledQuery);
          if (!refined.ok) {
            tokenUnsupported = ["identity"];
          } else {
            tokenRoot = refined.root;
            tokenFilter = null;
            tokenUnsupported = [];
          }
        }
      }
      const tokenSpec = tokenUnsupported.length ? null : {
        root: tokenRoot,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [
            { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_MODEL" },          outputAlias: "model" },
            { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_OBSERVED_AT" },     outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [
            {
              outputAlias: "input_tokens",
              standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_INPUT_TOKENS" },
            },
            {
              outputAlias: "output_tokens",
              standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" },
            },
            ...cacheTokenMetrics("pu"),
            ...inclusiveCacheMetric("pu"),
            ...reasoningTokenMetric("pu"),
            ...coverageMetrics("pu"),
          ],
        },
      };
      if (tokenSpec && tokenFilter) tokenSpec.filter = tokenFilter;

      // Cost rollup: the same token sums grouped by (provider, model) with NO
      // time bucket. Token COST has to be priced at this grain and summed —
      // pi-ai's per-model rates differ, so a blended scope total can't be
      // priced. Same root/filter/range as tokenSpec (so generation pin +
      // scope CTE plumbing ride along identically); cache sums stay
      // provider-predicated, so each group is its provider's true volume and
      // the four cost bands map 1:1 onto pi-ai's {input,output,cacheRead,
      // cacheWrite}. Priced client-side (summarizeTokenCost) since pricing
      // lives in the bundled pi-ai catalog, not the analytics API.
      // Skip the rollup entirely when cost estimates are killed (one gate,
      // tokenCost.js) — no wasted query, and summarizeTokenCost([]) below
      // returns an unpriced summary so the surfaces stay dark anyway.
      const costSpec = (tokenUnsupported.length || !tokenCostEnabled()) ? null : {
        root: tokenRoot,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [
            { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_PROVIDER" }, outputAlias: "provider" },
            { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_MODEL" },     outputAlias: "model" },
          ],
          metrics: [
            { outputAlias: "input_tokens",  standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_INPUT_TOKENS" } },
            { outputAlias: "output_tokens", standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" } },
            ...cacheTokenMetrics("pu"),
            ...inclusiveCacheMetric("pu"),
          ],
        },
      };
      if (costSpec && tokenFilter) costSpec.filter = tokenFilter;

      // One agent-grouped query carries all three count lenses
      // (sessions/endpoints COUNT_DISTINCT, prompts COUNT). Under a cluster
      // scope it's bounded by the server-side cluster_subtree walk (O-1624),
      // so the COUNT_DISTINCT lenses no longer hit the 1024 IN cap — no
      // prompts-only fallback needed. A query failure degrades to empty.
      const [agentRows, tokenRows, costRows] = await Promise.all([
        // A model scope can't be carried on the PROMPT root (O-1805) — fail the
        // agent-count layers closed to empty rather than count across all models.
        agentUnsupported.length ? Promise.resolve([]) : runAnalyticsQuery(agentSpecFinal).then(rowsToObjects).catch(() => []),
        // tokenSpec is null only for a scope neither TOKEN-direct nor the
        // collapse can express (none today — cluster/tool now collapse) —
        // render an empty token layer in that case.
        tokenSpec ? runAnalyticsQuery(tokenSpec).then(rowsToObjects).catch(() => []) : Promise.resolve([]),
        // Per-(provider, model) sums for the cost estimate. Degrades to [] (no
        // cost shown) on failure, independent of the token-volume layer.
        costSpec ? runAnalyticsQuery(costSpec).then(rowsToObjects).catch(() => []) : Promise.resolve([]),
      ]);
      const base = { bucketCount, bucketSeconds, fromMs, toMs, topN };
      return {
        sessions: overviewActivityFromRows(agentRows, { ...base, valueAlias: "sessions" }),
        prompts: overviewActivityFromRows(agentRows, { ...base, valueAlias: "prompts" }),
        endpoints: overviewActivityFromRows(agentRows, { ...base, valueAlias: "endpoints" }),
        // The chart still stacks THROUGHPUT by model (TOKEN_VOLUME_ALIASES);
        // `breakdown` carries the scope-level per-bucket sums + coverage that
        // the trace-list summary bar renders as input / output / work with the
        // cache / reasoning / coverage detail on hover (sumTokenBreakdown owns
        // the work-vs-throughput accounting so the summary bar doesn't).
        tokens: (() => {
          const lens = overviewActivityFromRows(tokenRows, {
            ...base,
            idAlias: "model",
            valueAlias: TOKEN_VOLUME_ALIASES,
          });
          lens.breakdown = sumTokenBreakdown(tokenRows);
          // Estimated USD cost, priced per (provider, model) from the bundled
          // pi-ai catalog and summed (summarizeTokenCost). `total`/`byModel`
          // feed the summary-bar total, the legend total, and each layer's hover;
          // `pricedShare` / `suppressTotal` carry how much of the range's
          // throughput actually resolved to a rate — a surface that renders
          // `total` without honouring `suppressTotal` will show an
          // uncatalogued model's traffic as near-free.
          lens.cost = summarizeTokenCost(costRows);
          // Hang a per-model type breakdown off each by-model layer so the
          // legend chip's hover can show where that model's tokens went (with
          // per-bucket sparklines). The stacked total stays unchanged.
          const perModel = modelTypeBreakdown(tokenRows, base);
          for (const l of lens.layers) l.byType = perModel.get(l.id) || null;
          return lens;
        })(),
      };
    },
  },

  getClusterLeafActivity: {
    // Whole-tree LEAF-level prompt counts for the cluster anatomy map:
    // one leaf-grouped COUNT under the scope's NON-cluster steps. The
    // map renders the entire taxonomy — the selected cluster is a
    // highlight, not a filter — so cluster steps are deliberately
    // dropped (same reasoning as rollupClusterActivity's drill rollup,
    // which this mirrors). Inexpressible scopes degrade to [] and the
    // map falls back to structure-only shading.
    execute: async ({ scopePath = [], range, includeParents = false } = {}) => {
      const apiScope = toApiScope(scopePath, range).filter((s) => s.dim !== "cluster");
      const apiRange = toApiRange(range);
      if (unsupportedScopeDims(apiScope).length > 0) return [];
      const { root, filter, unsupported } = buildEntriesRoot({ apiScope, dim: "cluster", needsLabelJoin: false });
      if (unsupported.length) return [];
      const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [{ field: dimFieldRef("cluster"), outputAlias: "id" }],
          metrics: [promptCount("value")],
        },
        // Leaf-cluster cardinality is bounded (hundreds) — one page.
        limit: 5000,
      };
      if (filter) spec.filter = filter;
      // Parent heat is the sum of its leaves unless a prompt sits in two
      // leaves. The distinct total over the same rows equals the sum of the
      // per-leaf counts exactly when none does, so one extra read decides
      // whether the per-parent counts below are needed.
      const totalSpec = { root, timeRange: spec.timeRange, output: { metrics: [promptCount("total")] } };
      if (filter) totalSpec.filter = filter;
      try {
        const [leafRows, totalRows] = await Promise.all([
          runAnalyticsQuery(spec),
          includeParents ? runAnalyticsQuery(totalSpec).catch(() => null) : null,
        ]);
        const leaves = rowsToObjects(leafRows)
          .map((row) => ({ id: String(row.id), value: Number(row.value) || 0 }));
        if (!includeParents) return leaves;
        const tree = await loadClusterTree();
        if (!tree?.isHierShape) return leaves;
        const leafSum = leaves.reduce((sum, leaf) => sum + leaf.value, 0);
        const total = totalRows ? Number(rowsToObjects(totalRows)[0]?.total) : NaN;
        if (leaves.length < spec.limit && leafSum === total) return leaves;
        const parents = [...tree.childrenOf].filter(([, children]) => children.length > 0).map(([id]) => id);
        const rows = await countPromptGroups({
          root, filter, timeRange: rangeToTimeRange(apiRange),
          groups: clusterPromptGroups(parents), runQuery: runAnalyticsQuery, rowsToObjects,
        });
        return [...leaves, ...rows.map((row) => ({ ...row, includesDescendants: true }))];
      } catch (err) {
        console.warn("[getClusterLeafActivity] leaf activity failed:", err);
        return [];
      }
    },
  },

  getScanVolume: {
    // Scan-window scrubber backdrop: UNGROUPED per-time-bucket prompt counts +
    // token sums over the whole scope+range. lib/scanVolume.js resamples them
    // into RANK space client-side, so the brush — a fixed prompt-count window —
    // keeps constant width wherever it sits (the rail is rank-linear, not
    // time-linear; see useScanWindow).
    //
    // Signals steps are dropped from the scope before the filter is built:
    // a signal has no server predicate (signalsDim predicate → null) and
    // getTraces' adapter branch no-ops it, so the window the scrubber pages
    // is the PARENT scope's prompts — the backdrop must count the same
    // population. Any other inexpressible scope degrades to an empty shape
    // (the scrubber falls back to a plain rail rather than drawing a graph
    // of the wrong population).
    execute: async ({ scopePath = [], range, bucketCount = 96 } = {}) => {
      const volumeScope = (scopePath || []).filter((s) => s?.dim !== "signals");
      const apiScope = toApiScope(volumeScope, range);
      const apiRange = toApiRange(range);
      const bucketSeconds = bucketSecondsForRange(apiRange, bucketCount);
      const { fromMs, toMs } = rangeBoundsMs(apiRange);
      const empty = shapeScanVolume({ bucketCount: 0, bucketSeconds, fromMs, toMs });
      if (unsupportedScopeDims(apiScope).length > 0) return empty;

      // Count pass: PROMPT-rooted, same builder as the trace list / overview
      // bundle, no grouping — one row per time bucket.
      const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope });
      if (unsupported.length) return empty;
      const countSpec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [
            promptCount("count"),
          ],
        },
      };
      if (filter) countSpec.filter = filter;

      // Token pass: TOKEN direct, falling back to the collapse —
      // mirrors getOverviewActivityBundle's token pass minus the model
      // grouping. A scope neither can express just loses the token backdrop
      // (the scrubber falls back to prompt-count bars).
      const tokenDirect = buildUsageRootAndFilter({ scopePath: apiScope });
      let tokenRoot = tokenDirect.root;
      let tokenFilter = tokenDirect.filter;
      let tokenUnsupported = tokenDirect.unsupported;
      if (tokenUnsupported.length) {
        const tokenCollapsed = buildUsageCollapse({ scopePath: apiScope });
        if (tokenCollapsed.unsupported.length) {
          tokenUnsupported = tokenCollapsed.unsupported;
        } else {
          tokenRoot = tokenCollapsed.root;
          tokenFilter = null;
          tokenUnsupported = [];
        }
      }
      const tokenSpec = tokenUnsupported.length ? null : {
        root: tokenRoot,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [
            { field: { stepAlias: "pu", field: "DIMENSION_FIELD_TOKEN_OBSERVED_AT" }, outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [
            { outputAlias: "input_tokens",  standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_INPUT_TOKENS" } },
            { outputAlias: "output_tokens", standard: { stepAlias: "pu", metric: "METRIC_TYPE_TOKEN_SUM_OUTPUT_TOKENS" } },
            ...cacheTokenMetrics("pu"),
            ...inclusiveCacheMetric("pu"),
          ],
        },
      };
      if (tokenSpec && tokenFilter) tokenSpec.filter = tokenFilter;

      const [countRows, tokenRows] = await Promise.all([
        runAnalyticsQuery(countSpec).then(rowsToObjects).catch(() => []),
        tokenSpec ? runAnalyticsQuery(tokenSpec).then(rowsToObjects).catch(() => []) : Promise.resolve([]),
      ]);
      return shapeScanVolume({ countRows, tokenRows, bucketCount, bucketSeconds, fromMs, toMs });
    },
  },

  getContrastMatrix: {
    // Migrated to analytics API with FE-side TV-divergence scoring.
    //
    // Approximation: rather than scoring divergence over the full N×M
    // cross-tab (doesn't fit in the browser at production scale; see
    // RankBy proto extension PR), we candidate-prune by activity
    // first: top RANK_K rows × top RANK_M cols by raw count, then
    // compute TV-divergence on that bounded matrix. K=60, M=30 →
    // 1800-cell ceiling, ~50KB JSON. Edge cases where a low-activity
    // row would have ranked highly by divergence get missed; in
    // practice the long tail produces noisy distributions that
    // wouldn't be useful to surface anyway.
    //
    // Unsupported scopes and axes return an empty matrix.
    execute: async ({
      scopePath = [], rowDim = "endpoints", colDim = "clusters",
      rowLimit = 14, colLimit = 8, selfId = null, selfColId = null, range,
    } = {}) => {
      const apiScope = toApiScope(scopePath, range);
      const rowDimS = DIM_SINGULAR[rowDim] ?? rowDim;
      const colDimS = DIM_SINGULAR[colDim] ?? colDim;
      // Synthetic dims have no group-by FieldRef, so they fail closed too.
      const supported = isAnalyticsSupportedDim(rowDimS)
        && isAnalyticsSupportedDim(colDimS)
        && !dimIsSynthetic(rowDimS)
        && !dimIsSynthetic(colDimS)
        && rowDimS !== colDimS
        && unsupportedScopeDims(apiScope).length === 0;
      if (!supported) {
        return contrastEmpty(rowDimS, colDimS);
      }
      return contrastViaAnalytics({
        apiScope, rowDimS, colDimS,
        rowLimit, colLimit, selfId, selfColId,
        apiRange: toApiRange(range),
      });
    },
  },

  fuzzyAnchors: {
    // Anchor fingerprint for "Workflow context" — fetched once per
    // anchor set so each per-dim candidate query (and the FE's cluster
    // bonus computation) can share the result. Returns the dim-value
    // sets + per-(dim, value) anchor event_time arrays needed for
    // time-decayed scoring.
    shortCircuit: ({ anchorIds = [] } = {}) =>
      anchorIds.length === 0 ? null : null,
    execute: async ({ anchorIds = [], range } = {}) => {
      if (!anchorIds.length) return null;
      const apiRange = toApiRange(range);
      const anchorRoot = {
        alias: "p", entity: "ENTITY_TYPE_PROMPT",
        children: [{
          edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
          mode: "JOIN_MODE_LEFT",
          target: {
            alias: "ec", entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
            stepFilter: [{
              pred: { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, op: "OPERATOR_GT", value: 0 },
            }],
          },
        }],
      };
      const anchorSpec = {
        root: anchorRoot,
        timeRange: rangeToTimeRange(apiRange),
        filter: { pred: {
          field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
          op: "OPERATOR_IN", value: anchorIds.map(String),
        }},
        output: {
          dimensions: [
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },        outputAlias: "trace_id" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, outputAlias: "session_id" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },     outputAlias: "endpoint_id" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" },        outputAlias: "provider" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
            { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, outputAlias: "cluster_id" },
          ],
        },
      };
      const anchorRows = rowsToObjects(await runAnalyticsQuery(anchorSpec));
      if (anchorRows.length === 0) return null;

      // Map<dimValue, number[]> serializes cleanly across the cache
      // boundary as plain arrays so callers don't need Map plumbing.
      const sessionTimesMap = mapAnchorsByValue(anchorRows, "session_id");
      const endpointTimesMap = mapAnchorsByValue(anchorRows, "endpoint_id");
      const sessionTimes = Object.fromEntries(sessionTimesMap);
      const endpointTimes = Object.fromEntries(endpointTimesMap);
      const uniqSessions  = Object.keys(sessionTimes);
      const uniqEndpoints = Object.keys(endpointTimes);
      const uniqClusters  = [...new Set(anchorRows.map((r) => r.cluster_id).filter((c) => c != null && Number(c) > 0).map(Number))];
      const uniqProviders = [...new Set(anchorRows.map((r) => r.provider).filter(Boolean).map(String))];

      return {
        sessionTimes, endpointTimes,
        uniqSessions, uniqEndpoints, uniqClusters, uniqProviders,
      };
    },
  },

  fuzzyCandidatesByDim: {
    // Per-dimension candidate fetch + scoring. The FE issues one of
    // these per enabled chip (session / endpoint / agent), each with
    // its own loading state; results are merged client-side. Cluster
    // has no single-step IN predicate so it's not handled here — the
    // FE applies the cluster bonus during the merge step using
    // `fuzzyAnchors.uniqClusters`.
    //
    // Each entry returns { trace, dimScore, dim } — the FE accumulates
    // scores across dims by trace_id and applies the threshold cutoff
    // on the merged score, which lets borderline matches that score
    // 4 on session AND 4 on endpoint still surface as a combined 8.
    shortCircuit: ({ anchorIds = [], dim } = {}) =>
      (!anchorIds.length || !dim) ? [] : null,
    execute: async ({ anchorIds = [], dim, anchors, range, limit = 200 } = {}) => {
      if (!anchorIds.length || !dim || !anchors) return [];
      const apiRange = toApiRange(range);

      // Reconstitute Map<dimValue, number[]> from the JSON-friendly
      // shape that came back from fuzzyAnchors / went through the
      // useQuery cache.
      const sessionTimes = new Map(Object.entries(anchors.sessionTimes ?? {}));
      const endpointTimes = new Map(Object.entries(anchors.endpointTimes ?? {}));
      const uniqProviders = new Set(anchors.uniqProviders ?? []);

      let dimPred;
      if (dim === "session") {
        if (!anchors.uniqSessions?.length) return [];
        dimPred = inPred("p", "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID", anchors.uniqSessions);
      } else if (dim === "endpoint") {
        if (!anchors.uniqEndpoints?.length) return [];
        dimPred = inPred("p", "DIMENSION_FIELD_PROMPT_ENDPOINT_ID", anchors.uniqEndpoints);
      } else if (dim === "agent") {
        if (!uniqProviders.size) return [];
        dimPred = inPred("p", "DIMENSION_FIELD_PROMPT_PROVIDER", [...uniqProviders]);
      } else {
        return [];
    }

      const candidateRoot = {
        alias: "p", entity: "ENTITY_TYPE_PROMPT",
        children: [{
          edge: "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER",
          mode: "JOIN_MODE_LEFT",
          target: {
            alias: "ec", entity: "ENTITY_TYPE_ILO_EVENT_CLUSTER",
            stepFilter: [{
              pred: { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, op: "OPERATOR_GT", value: 0 },
            }],
            children: [{
              edge: "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER",
              mode: "JOIN_MODE_LEFT",
              target: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER", optional: true },
            }],
          },
        }],
        // No per-prompt token child (the per-prompt usage edge was removed in
        // O-1805), so the response-resolved model isn't reachable here. Context
        // rows show the prompt-level request-body model (DIMENSION_FIELD_
        // PROMPT_MODEL), the same fallback the primary trace list uses.
      };
      const candidateOutput = {
        dimensions: [
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },        outputAlias: "trace_id" },
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, outputAlias: "session_id" },
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },     outputAlias: "endpoint_id" },
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_PROVIDER" },        outputAlias: "provider" },
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "event_time" },
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT" }, outputAlias: "enriched" },
          // Prompt-level request-body model (the response-resolved
          // TOKEN_MODEL isn't joined per-prompt). Carried into `model`
          // on the row below.
          { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_MODEL" }, outputAlias: "model" },
          { field: { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" }, outputAlias: "cluster_id" },
          { field: { stepAlias: "c",  field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" },     outputAlias: "cluster_name" },
        ],
      };

      const spec = {
        root: candidateRoot,
        timeRange: rangeToTimeRange(apiRange),
        filter: {
          andExpr: { children: [
            { notExpr: { child: inPred("p", "DIMENSION_FIELD_PROMPT_EVENT_ID", anchorIds.map(String)) } },
            dimPred,
          ]},
        },
        output: candidateOutput,
        limit: Math.max(limit * 4, 200),
      };
      const candidates = rowsToObjects(await runDegradableQuery(spec));

      const LN2 = Math.log(2);
      const decay = (deltaSec) => deltaSec > 300 ? 0 : Math.exp(-LN2 * deltaSec / 60);
      const out = [];
      for (const r of candidates) {
        let dimScore = 0;
        if (dim === "session" && r.session_id && sessionTimes.has(String(r.session_id))) {
          const minDt = minAbsDelta(sessionTimes.get(String(r.session_id)), candidateEventTimeSec(r));
          dimScore = 50 * decay(minDt);
        } else if (dim === "endpoint" && r.endpoint_id && endpointTimes.has(String(r.endpoint_id))) {
          const minDt = minAbsDelta(endpointTimes.get(String(r.endpoint_id)), candidateEventTimeSec(r));
          dimScore = 20 * decay(minDt);
        } else if (dim === "agent" && r.provider && uniqProviders.has(String(r.provider))) {
          dimScore = 2;
        }
        if (dimScore <= 0) continue;
        out.push({
          trace_id: r.trace_id,
          dimScore,
          dim,
          // Carry the raw row through so the FE merge can adaptTrace
          // once with full data (and read cluster_id for the cluster
          // bonus) without round-tripping back to the server. `model` is
          // the prompt-level request-body model (the response-resolved
          // TOKEN_MODEL isn't joined per-prompt); adaptTrace reads `t.model`.
          row: { ...r, model: r.model || "" },
        });
      }
      out.sort((a, b) => b.dimScore - a.dimScore);
      return out.slice(0, Math.max(limit * 4, 200));
    },
  },

  getProjectTree: {
    // Projects have no analytics entity. Preserve stale-link response shape.
    execute: async () => {
      const empty = { tree: { name: "", dirs: [], files: [], hits: 0, endpoints: [] }, files: [] };
      return empty;
    },
  },

  getCommands: {
    // Page normalized activity, then hydrate only those event IDs from their
    // typed families. Keep the existing scope and focus-window resolution;
    // scopes analytics cannot express fail closed.
    execute: async ({ scopePath = [], range, limit = TELEMETRY_PAGE_LIMIT, sort = "newest", search = "", aroundTraceId = "", aroundSessionId = "", aroundT = "", aroundPrevT = "", windowAfterT = "", windowBeforeT = "", eventTypes = COMMAND_EVENT_TYPES } = {}) => {
      // Empty selection short-circuits — the analytics planner rejects an
      // empty IN list (ValidateError::EmptyInList); returning everything would
      // violate the caller's explicit empty selection.
      if (!eventTypes?.length) {
        const out = []; out.total = 0; out.totalIsExact = true; return out;
      }
      const apiScope = toApiScope(scopePath, range);
      if (unsupportedScopeDims(apiScope).length > 0) {
        const out = []; out.total = 0; out.totalIsExact = true; return out;
      }

      const apiRange = toApiRange(range);

      // Bucket scope into direct endpoint/session predicates
      // and indirect (everything else — pre-fetch matching session
      // set because the proto's join modes don't express SQL
      // semi-join semantics directly; INNER would multiply activity
      // rows by per-session prompt count). Indirect now covers
      // agent, cluster, tool_call, file — the latter two via the
      // PROMPT array fields we just wired. The IN-list grows at
      // most to the planner's MAX_IN_LIST_LEN (1024); broad
      // cross-cuts that exceed that fail closed below.
      const directScope = apiScope.filter((s) => s.dim === "endpoint" || s.dim === "session");
      const indirectScope = apiScope.filter((s) => s.dim !== "endpoint" && s.dim !== "session");

      let sessionInPred = null;
      if (indirectScope.length > 0) {
        const { root: pRoot, filter: pFilter, unsupported: pUnsupported } = buildPromptRootAndFilter({
          scopePath: indirectScope, extraDims: indirectScope.some((s) => s.dim === "cluster") ? ["cluster"] : [],
        });
        // Fail closed on a scope this PROMPT root can't carry (today: model): an
        // unscoped session set would widen the trace list across all models (O-1805).
        if (pUnsupported.length) { const out = []; out.total = 0; out.totalIsExact = true; return out; }
        const sessionsSpec = {
          root: pRoot,
          timeRange: rangeToTimeRange(apiRange),
          output: {
            dimensions: [{ field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, outputAlias: "session_id" }],
            metrics: [{
              outputAlias: "_n",
              computed: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, op: "AGGREGATE_OP_COUNT" },
            }],
          },
          // No order_by/limit — distinct session set under the scope.
        };
        if (pFilter) sessionsSpec.filter = pFilter;
        const sessionRows = rowsToObjects(await runAnalyticsQuery(sessionsSpec));
        const sessionIds = [...new Set(sessionRows.map((r) => String(r.session_id)).filter(Boolean))];
        if (sessionIds.length === 0) {
          const out = []; out.total = 0; out.totalIsExact = true; return out;
        }
        sessionInPred = chunkedInPredicate(
          { stepAlias: "e", field: "DIMENSION_FIELD_TELEMETRY_TIMELINE_SESSION_ID" },
          sessionIds,
        );
      }

      // aroundTraceId mode: clamp the activity range to "the agent's
      // last response window." Replaces — not intersects — the
      // session/range filter. The caller (ScopedPage) pre-resolves
      // sessionId / t / prevT from the already-loaded trace so this
      // path doesn't re-derive them via separate queries that could
      // disagree with getTrace's prev-in-session answer (which gates
      // the chevron). When the caller cannot pre-resolve it, the analytics
      // lookup below derives the clamp.
      // 10-minute pre-window when the focal prompt is the session's first.
      const lookbackPrevT = (t) => new Date(new Date(t).getTime() - 10 * 60_000).toISOString();
      let aroundClamp = null;
      const trimmedAroundId = aroundTraceId?.trim();
      if (trimmedAroundId && aroundSessionId && aroundT) {
        aroundClamp = {
          sessionId: String(aroundSessionId),
          t: aroundT,
          prevT: aroundPrevT || lookbackPrevT(aroundT),
        };
      } else if (trimmedAroundId) {
        const focalSpec = {
          root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
          timeRange: { relative: { durationSeconds: String(90 * 86400) } },
          filter: { pred: {
            field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" },
            op: "OPERATOR_EQ", value: trimmedAroundId,
          }},
          output: {
            dimensions: [
              { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, outputAlias: "session_id" },
              { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "t" },
            ],
          },
          limit: 1,
        };
        const [focal] = rowsToObjects(await runAnalyticsQuery(focalSpec));
        if (!focal) {
          const out = []; out.total = 0; out.totalIsExact = true; return out;
        }
        const prevSpec = {
          root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
          timeRange: { relative: { durationSeconds: String(90 * 86400) } },
          filter: { andExpr: { children: [
            { pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" }, op: "OPERATOR_EQ", value: focal.session_id }},
            { pred: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, op: "OPERATOR_LT", value: focal.t }},
          ]}},
          output: {
            dimensions: [{ field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "t" }],
          },
          orderBy: [{ outputAlias: "t", direction: "DIRECTION_DESC" }],
          limit: 1,
        };
        const [prev] = rowsToObjects(await runAnalyticsQuery(prevSpec));
        aroundClamp = {
          sessionId: String(focal.session_id),
          t: focal.t,
          prevT: prev?.t ?? lookbackPrevT(focal.t),
        };
      }

      const filterParts = [{
        pred: {
          field: { stepAlias: "e", field: "DIMENSION_FIELD_TELEMETRY_TIMELINE_EVENT_KIND" },
          op: "OPERATOR_IN",
          value: eventTypes.map((type) => {
            const kind = TELEMETRY_EVENT_KINDS[type];
            if (!kind) throw new Error("Unsupported activity type.");
            return kind;
          }),
        },
      }];
      if (aroundClamp) {
        // aroundTraceId replaces session/range filters — the user is
        // looking at the side-effects of one specific trace.
        filterParts.push({ pred: {
          field: { stepAlias: "e", field: "DIMENSION_FIELD_TELEMETRY_TIMELINE_SESSION_ID" },
          op: "OPERATOR_EQ", value: aroundClamp.sessionId,
        }});
        filterParts.push({ pred: {
          field: { stepAlias: "e", field: "DIMENSION_FIELD_TELEMETRY_TIMELINE_EVENT_TIME" },
          op: "OPERATOR_LTE", value: aroundClamp.t,
        }});
        filterParts.push({ pred: {
          field: { stepAlias: "e", field: "DIMENSION_FIELD_TELEMETRY_TIMELINE_EVENT_TIME" },
          op: "OPERATOR_GT", value: aroundClamp.prevT,
        }});
      } else {
        const scopeParts = [];
        for (const step of directScope) {
          const ids = step.ids?.length ? step.ids : step.id != null ? [step.id] : [];
          if (!ids.length) continue;
          const field = step.dim === "endpoint"
            ? "DIMENSION_FIELD_TELEMETRY_TIMELINE_ENDPOINT_ID"
            : "DIMENSION_FIELD_TELEMETRY_TIMELINE_SESSION_ID";
          scopeParts.push({
            pred: {
              field: { stepAlias: "e", field },
              op: ids.length > 1 ? "OPERATOR_IN" : "OPERATOR_EQ",
              value: ids.length > 1 ? ids.map(String) : String(ids[0]),
            },
          });
        }
        if (sessionInPred) scopeParts.push(sessionInPred);
        filterParts.push(...scopeParts);

      }
      // Directional time bounds for the command log's Context filter.
      // Pure time predicates — compose with whatever scope filters apply
      // (cross-session). Skipped when aroundClamp is in play (the
      // aroundClamp branch already applies tighter time bounds). Match
      // the aroundClamp branch's operator choice (GT / LTE) so the
      // analytics gateway sees the same shape it tolerates there.
      if (!aroundClamp && windowAfterT) {
        filterParts.push({ pred: {
          field: { stepAlias: "e", field: "DIMENSION_FIELD_TELEMETRY_TIMELINE_EVENT_TIME" },
          op: "OPERATOR_GT", value: windowAfterT,
        }});
      }
      if (!aroundClamp && windowBeforeT) {
        filterParts.push({ pred: {
          field: { stepAlias: "e", field: "DIMENSION_FIELD_TELEMETRY_TIMELINE_EVENT_TIME" },
          op: "OPERATOR_LTE", value: windowBeforeT,
        }});
      }
      const filter = filterParts.length === 1 ? filterParts[0] : { andExpr: { children: filterParts } };

      const orderBy = sort === "oldest"
        ? [{ outputAlias: "ts", direction: "DIRECTION_ASC" }]
        : sort === "tool"
        ? [{ outputAlias: "family", direction: "DIRECTION_ASC" }, { outputAlias: "eventType", direction: "DIRECTION_ASC" }, { outputAlias: "ts", direction: "DIRECTION_DESC" }]
        : [{ outputAlias: "ts", direction: "DIRECTION_DESC" }];
      orderBy.push({ outputAlias: "id", direction: "DIRECTION_ASC" });
      const pageLimit = Math.max(1, Math.min(TELEMETRY_PAGE_LIMIT, Math.floor(Number(limit) || 0)));
      // Fetch one sentinel row so the execution log can expose its existing
      // "Load more" affordance when a page is full. The typed activity reader
      // caps individual queries at 1,000 rows, so a 1,000-row page is the
      // terminal page we can request here.
      const probeLimit = pageLimit < TELEMETRY_PAGE_LIMIT ? pageLimit + 1 : pageLimit;
      const spec = {
        root: { alias: "e", entity: "ENTITY_TYPE_TELEMETRY_TIMELINE" },
        timeRange: aroundClamp ? { relative: { durationSeconds: String(90 * 86400) } } : rangeToTimeRange(apiRange),
        filter,
        output: { dimensions: telemetryDimensions() },
        orderBy,
        limit: probeLimit,
      };
      const fetched = await readTelemetryPage(spec, { search, execute: runAnalyticsQuery });
      const hasMore = pageLimit < TELEMETRY_PAGE_LIMIT
        ? fetched.length > pageLimit
        : fetched.length >= pageLimit;
      const rows = fetched.slice(0, pageLimit).map(shapeCommandRow);
      rows.total = hasMore ? pageLimit + 1 : rows.length;
      rows.totalIsExact = !hasMore;
      return rows;
    },
  },

  getOverviewMetrics: {
    // Scalar totals (requests / endpoints / sessions) for the activity-
    // chart hero row. Migrated to the analytics API: a single
    // PROMPT-rooted aggregate with three computed metrics. Empty scope
    // / single-dim equality scope all translate cleanly. Unsupported
    // scopes fail closed rather than silently widening the query.
    execute: async ({ scopePath = [], range, query = "", byTrace = false } = {}) => {
      // toApiScope converts plural sidebar keys ("endpoints", "clusters")
      // into the singular keys DIM_INFO is keyed by. Without this,
      // buildPromptRootAndFilter silently drops every scope step and
      // the hero counters render unscoped totals.
      const apiScope = toApiScope(scopePath, range);
      const { root, filter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope });
      // With `byTrace` (the fleet map), sessions are counted as the workspace's
      // session lists list them, from the capture frames where the scope and
      // query read them (see compileFrameScope), so over a range longer than
      // the prompt index keeps the total still matches the lists. Machines are
      // then the larger of the frames' count and the index's: a connector's
      // capture names no machine in its frames, and the index keeps a month,
      // so each counts fewer than there are, never more. Requests stay the
      // index's count. Explore's header leaves it off and counts the index, as
      // its breakdowns and request list do.
      const framed = byTrace ? frameTotals({ scopePath, range, query }) : Promise.resolve(null);
      // Fail closed on a scope this PROMPT root can't carry (today: model) instead
      // of rendering hero totals summed across all models (O-1805).
      if (unsupported.length) {
        const totals = await framed;
        return { requests: 0, endpoints: totals?.endpoints ?? 0, sessions: totals?.sessions ?? 0 };
      }
      const baseSpec = {
        root,
        timeRange: rangeToTimeRange(toApiRange(range)),
        output: {
          metrics: [
            promptCount("requests"),
            {
              outputAlias: "endpoints",
              computed: {
                field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" },
                op: "AGGREGATE_OP_COUNT_DISTINCT",
              },
            },
            {
              outputAlias: "sessions",
              computed: {
                field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID" },
                op: "AGGREGATE_OP_COUNT_DISTINCT",
              },
            },
          ],
        },
      };
      if (filter) baseSpec.filter = filter;
      // The query-language refinement is PROMPT-rooted scope — resolve labels,
      // then AND the compiled filter on (+ any cross-entity joins) so the hero
      // counts track the filtered view.
      const spec = applyQueryToSpec(baseSpec, await resolveAndCompile(query));
      const [json, totals] = await Promise.all([runAnalyticsQuery(spec), framed]);
      const [row = {}] = rowsToObjects(json);
      return {
        requests:  Number(row.requests)  || 0,
        endpoints: Math.max(totals?.endpoints ?? 0, Number(row.endpoints) || 0),
        sessions:  totals?.sessions ?? (Number(row.sessions) || 0),
      };
    },
  },

  getCellScopeClusterMatrix: {
    // Right-panel siblings × clusters co-occurrence on cell hover.
    // Two analytics queries: (1) endpoints touching hovered cluster
    // in the bucket; (2) endpoint × cluster counts for those siblings
    // in the same bucket. Cluster names from a third parallel query.
    shortCircuit: ({ clusterId, bucketStart = 0, bucketEnd = 0 } = {}) => {
      if (!clusterId || !(bucketEnd > bucketStart)) {
        return {
          siblings: [], clusters: [], matrix: [], maxCell: 0,
          bucketStart, bucketEnd, breadthDim: "endpoints",
          selfBreadthValue: null, hoveredClusterId: clusterId ?? null,
        };
      }
      return null;
    },
    execute: async ({ scopePath = [], clusterId, bucketStart, bucketEnd, siblingLimit = 15, clusterLimit = 10 } = {}) => {
      const empty = {
        siblings: [], clusters: [], matrix: [], maxCell: 0,
        bucketStart, bucketEnd, breadthDim: "endpoints",
        selfBreadthValue: null, hoveredClusterId: clusterId,
      };
      // Bucket-time-range queries. The whole hover panel is bucket-
      // scoped — top-level filter still applies.
      const bucketRange = {
        from: new Date(bucketStart).toISOString(),
        to:   new Date(bucketEnd).toISOString(),
      };
      const apiScope = toApiScope(scopePath, bucketRange);
      const cid = Number(clusterId);

      const { root, filter: scopeFilter, unsupported } = buildPromptRootAndFilter({ scopePath: apiScope, extraDims: ["cluster"] });
      if (unsupported.length) return empty;
      const clusterRef   = { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" };
      const endpointRef  = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" };
      const promptIdRef  = { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" };

      // Q1: hovered_endpoints — distinct endpoints touching `cid` in bucket.
      const hoveredSpec = {
        root,
        timeRange: rangeToTimeRange(bucketRange),
        filter: combineAnd(scopeFilter, { pred: { field: clusterRef, op: "OPERATOR_EQ", value: cid } }),
        output: {
          dimensions: [{ field: endpointRef, outputAlias: "endpoint_id" }],
          metrics: [{
            outputAlias: "value",
            computed: { field: promptIdRef, op: "AGGREGATE_OP_COUNT_DISTINCT" },
          }],
        },
      };
      const hoveredRows = rowsToObjects(await runAnalyticsQuery(hoveredSpec));
      const hoveredEndpointIds = hoveredRows.map((r) => String(r.endpoint_id));
      if (hoveredEndpointIds.length === 0) return empty;

      // Q2: matrix — endpoint × cluster counts for those endpoints in bucket.
      const matrixSpec = {
        root,
        timeRange: rangeToTimeRange(bucketRange),
        filter: combineAnd(scopeFilter, { pred: {
          field: endpointRef, op: "OPERATOR_IN", value: hoveredEndpointIds,
        }}),
        output: {
          dimensions: [
            { field: endpointRef, outputAlias: "endpoint_id" },
            { field: clusterRef,  outputAlias: "cluster_id" },
          ],
          metrics: [{
            outputAlias: "c",
            computed: { field: promptIdRef, op: "AGGREGATE_OP_COUNT_DISTINCT" },
          }],
        },
      };
      const matrixRows = rowsToObjects(await runAnalyticsQuery(matrixSpec));
      if (matrixRows.length === 0) return empty;

      // Marginals count the union directly; source observations may occupy
      // different cells for the same logical prompt.
      const marginalSpec = (field, outputAlias) => ({
        ...matrixSpec,
        output: { dimensions: [{ field, outputAlias }], metrics: [promptCount("total")] },
      });
      const [endpointRows, clusterRows] = await Promise.all([
        runAnalyticsQuery(marginalSpec(endpointRef, "endpoint_id")).then(rowsToObjects),
        runAnalyticsQuery(marginalSpec(clusterRef, "cluster_id")).then(rowsToObjects),
      ]);
      const epTotals = new Map(endpointRows.map((row) => [String(row.endpoint_id), Number(row.total) || 0]));
      const clTotals = new Map(clusterRows.map((row) => [String(Number(row.cluster_id)), Number(row.total) || 0]));
      const topSiblings = [...epTotals.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, siblingLimit)
        .map(([id, count]) => ({ id, count, name: id }));  // lazy label cache fills name
      const hoveredClId = String(cid);
      const otherClusters = [...clTotals.entries()]
        .filter(([id]) => id !== hoveredClId)
        .sort((a, b) => b[1] - a[1])
        .slice(0, Math.max(0, clusterLimit - 1));
      const topClusters = [
        { id: hoveredClId, total: clTotals.get(hoveredClId) || 0, name: hoveredClId },
        ...otherClusters.map(([id, total]) => ({ id, total, name: id })),
      ];

      // Q3 (parallel-friendly but ordered after Q2 since needs cluster ids): names.
      try {
        const idsForLookup = topClusters.map((c) => Number(c.id)).filter(Number.isFinite);
        if (idsForLookup.length > 0) {
          const namesSpec = {
            root: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER" },
            timeRange: rangeToTimeRange(bucketRange),
            filter: { pred: {
              field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_ID" },
              op: "OPERATOR_IN", value: idsForLookup,
            }},
            output: {
              dimensions: [
                { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_ID" },   outputAlias: "cluster_id" },
                { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" }, outputAlias: "name" },
              ],
            },
          };
          const nameRows = rowsToObjects(await runAnalyticsQuery(namesSpec));
          const nameMap = new Map(nameRows.map((r) => [String(r.cluster_id), String(r.name || r.cluster_id)]));
          for (const c of topClusters) c.name = nameMap.get(c.id) ?? c.name;
        }
      } catch {}

      // Build the matrix.
      const epIdx = new Map(topSiblings.map((s, i) => [s.id, i]));
      const clIdx = new Map(topClusters.map((c, i) => [c.id, i]));
      const matrix = topSiblings.map(() => new Array(topClusters.length).fill(0));
      let maxCell = 0;
      for (const r of matrixRows) {
        const i = epIdx.get(String(r.endpoint_id));
        const j = clIdx.get(String(Number(r.cluster_id)));
        if (i != null && j != null) {
          matrix[i][j] = Number(r.c);
          if (matrix[i][j] > maxCell) maxCell = matrix[i][j];
        }
      }

      const selfBreadthValue = apiScope.length > 0 && apiScope[apiScope.length - 1]?.dim === "endpoint"
        ? apiScope[apiScope.length - 1].id
        : null;

      return {
        siblings: topSiblings, clusters: topClusters, matrix, maxCell,
        bucketStart, bucketEnd,
        breadthDim: "endpoints",
        selfBreadthValue,
        hoveredClusterId: clusterId,
      };
    },
  },

  getClusterSwimlaneData: {
    // Cluster × time-bucket matrix + per-cell endpoint breadth, plus
    // bundle-mode coherence shading.
    //
    // Migrated to analytics API: rank top-K clusters via count, then
    // densify per (cluster, bucket) with two metrics (count,
    // count_distinct(endpoint_id)). Cluster names come from a third
    // parallel query against ILO_CLUSTER.
    //
    // Coherence is computed via a fourth parallel query that emits
    // per-(endpoint, cluster, bucket) cells filtered to top-K, then
    // reduced JS-side in two linear passes — no pairwise scan, no
    // sketches. Only bundle mode is supported on the analytics path
    // (the proto can't express the array operations tenant/scoped
    // need at fleet scale). Unsupported scopes fail closed.
    execute: async ({ scopePath = [], bucketCount = 24, maxRows = 12, range, coherenceMode = "tenant" } = {}) => {
      const apiScope = toApiScope(scopePath, range);
      if (unsupportedScopeDims(apiScope).length > 0) {
        const { fromMs, toMs } = rangeBoundsMs(toApiRange(range));
        return swimlaneEmpty({ bucketCount, fromMs, toMs, coherenceMode });
      }
      const apiRange = toApiRange(range);
      const { fromMs, toMs } = rangeBoundsMs(apiRange);
      const bucketSeconds = bucketSecondsForRange(apiRange, bucketCount);

      const { root, filter, unsupported } = buildPromptRootAndFilter({
        scopePath: apiScope, extraDims: ["cluster"],
      });
      // Fail closed on a scope this PROMPT root can't carry (today: model) instead
      // of ranking clusters over activity summed across all models (O-1805).
      if (unsupported.length) return swimlaneEmpty({ bucketCount, fromMs, toMs, coherenceMode });
      const clusterIdRef = { stepAlias: "ec", field: "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_ID" };

      // Q1: rank top-K clusters under scope+range.
      const rankSpec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        output: {
          dimensions: [{ field: clusterIdRef, outputAlias: "cluster_id" }],
          metrics: [promptCount("value")],
        },
        orderBy: [{ outputAlias: "value", direction: "DIRECTION_DESC" }],
        limit: maxRows,
      };
      if (filter) rankSpec.filter = filter;
      const rankRows = rowsToObjects(await runAnalyticsQuery(rankSpec));
      const clusterIds = rankRows.map((r) => Number(r.cluster_id)).filter(Number.isFinite);
      if (clusterIds.length === 0) {
        return swimlaneEmpty({ bucketCount, fromMs, toMs, coherenceMode });
      }

      // Q2 + Q3 in parallel: densify per (cluster, bucket), and look up names.
      const inFilter = combineAnd(filter, { pred: {
        field: clusterIdRef, op: "OPERATOR_IN", value: clusterIds,
      }});
      const matrixSpec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        filter: inFilter,
        output: {
          dimensions: [
            { field: clusterIdRef, outputAlias: "cluster_id" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [
            promptCount("prompts"),
            { outputAlias: "endpoints", computed: { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" }, op: "AGGREGATE_OP_COUNT_DISTINCT" } },
          ],
        },
      };
      const namesSpec = {
        root: { alias: "c", entity: "ENTITY_TYPE_ILO_CLUSTER" },
        timeRange: rangeToTimeRange(apiRange),
        filter: { pred: {
          field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_ID" },
          op: "OPERATOR_IN", value: clusterIds,
        }},
        output: {
          dimensions: [
            { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_ID" },   outputAlias: "cluster_id" },
            { field: { stepAlias: "c", field: "DIMENSION_FIELD_ILO_CLUSTER_CLUSTER_NAME" }, outputAlias: "name" },
          ],
        },
      };
      // Q4: per-(endpoint, cluster, bucket) cells filtered to top-K.
      // Same root/filter/bucketing as Q2 — just an extra endpoint
      // group-by dim. Because the IN-top-K predicate is folded in at
      // the query layer, COUNT_DISTINCT(cluster_id) per (endpoint,
      // bucket) over these rows IS |sig ∩ top_K| for that bucket —
      // exactly what bundle needs. We don't actually emit that count
      // here; we count rows JS-side instead (one row per cell already
      // implies distinctness across the three group-by dims).
      const bundleSpec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        filter: inFilter,
        output: {
          dimensions: [
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_ENDPOINT_ID" }, outputAlias: "endpoint_id" },
            { field: clusterIdRef, outputAlias: "cluster_id" },
            { field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP" }, outputAlias: "bucket", bucketSeconds },
          ],
          metrics: [promptCount("n")],
        },
      };
      const [matrixRows, nameRows, bundleRows] = await Promise.all([
        runAnalyticsQuery(matrixSpec).then(rowsToObjects),
        runAnalyticsQuery(namesSpec).then(rowsToObjects).catch(() => []),
        runAnalyticsQuery(bundleSpec).then(rowsToObjects).catch(() => []),
      ]);

      const promptDense = densifyBuckets(
        matrixRows.map((r) => ({ id: r.cluster_id, bucket: r.bucket, value: r.prompts })),
        { fromMs, toMs, bucketCount, bucketSeconds },
      );
      const endpointDense = densifyBuckets(
        matrixRows.map((r) => ({ id: r.cluster_id, bucket: r.bucket, value: r.endpoints })),
        { fromMs, toMs, bucketCount, bucketSeconds },
      );
      const nameByCluster = new Map(nameRows.map((r) => [String(r.cluster_id), String(r.name || r.cluster_id)]));
      const matrix = clusterIds.map((cid) => promptDense.get(String(cid)) ?? new Array(bucketCount).fill(0));
      const breadthMatrix = clusterIds.map((cid) => endpointDense.get(String(cid)) ?? new Array(bucketCount).fill(0));
      let globalMax = 0, breadthMax = 0;
      for (let r = 0; r < clusterIds.length; r++) {
        for (let b = 0; b < bucketCount; b++) {
          if (matrix[r][b] > globalMax) globalMax = matrix[r][b];
          if (breadthMatrix[r][b] > breadthMax) breadthMax = breadthMatrix[r][b];
        }
      }

      // Bundle reduction. Two single-pass maps:
      //   sigSize[`ep|b`]       = |sig ∩ top_K| for that (endpoint, bucket)
      //   cellEndpoints[`ri|b`] = endpoints touching cluster ri in bucket b
      // Then per cell (ri, b): avg over members of (sigSize − 1) / (K − 1).
      // Membership in cell(ri, b) guarantees the focal cluster is in the
      // member's signature, so |sig \ {focal}| = sigSize − 1 — no per-cluster
      // loop, no Set intersections. Total work is O(bundleRows).
      const stepMs = Math.max(1, bucketSeconds * 1000);
      const bucketIndexOf = (b) => {
        // parseServerTimestamp, not Date.parse — same UTC-vs-local trap as
        // densifyBuckets: a bare ClickHouse bucket string parsed as local
        // would shift the swimlane cell to the wrong column.
        const ms = parseServerTimestamp(b);
        if (!Number.isFinite(ms)) return -1;
        return Math.max(0, Math.min(bucketCount - 1, Math.floor((ms - fromMs) / stepMs)));
      };
      const ridxByCluster = new Map(clusterIds.map((c, i) => [String(c), i]));
      const sigSize = new Map();
      const cellEndpoints = new Map();
      for (const row of bundleRows) {
        const b = bucketIndexOf(row.bucket);
        if (b < 0) continue;
        const ri = ridxByCluster.get(String(row.cluster_id));
        if (ri == null) continue;
        const ep = String(row.endpoint_id);
        const sk = `${ep}|${b}`;
        sigSize.set(sk, (sigSize.get(sk) || 0) + 1);
        const ck = `${ri}|${b}`;
        let set = cellEndpoints.get(ck);
        if (!set) { set = new Set(); cellEndpoints.set(ck, set); }
        set.add(ep);
      }
      const coherenceMatrix = clusterIds.map(() => new Array(bucketCount).fill(0));
      const Kdenom = Math.max(1, clusterIds.length - 1);
      for (const [ck, eps] of cellEndpoints) {
        const [ri, b] = ck.split("|").map(Number);
        let sum = 0;
        for (const ep of eps) sum += (sigSize.get(`${ep}|${b}`) || 1) - 1;
        coherenceMatrix[ri][b] = sum / (eps.size * Kdenom);
      }

      return {
        rankedClusters: clusterIds.map((cid) => ({
          id: String(cid),
          name: nameByCluster.get(String(cid)) ?? String(cid),
        })),
        matrix,
        breadthMatrix,
        coherenceMatrix,
        coOccurrence:    [],
        siblingScopes:   [],
        breadthDim:      "endpoints",
        selfBreadthValue: null,
        globalMax,
        breadthMax,
        minTime: fromMs,
        maxTime: toMs,
        bucketCount,
        subRowsByCluster: {},
        subDim:  "endpoints",
        coherenceMode,
        siblingsOf: null,
      };
    },
  },

  buildColumnsWithFlowLinks: {
    // Finder-style ScopeColumns: alternating picker/entries columns.
    // Composed from executeDimensionCount (picker counts per dim) and
    // executeDimensionEntries (entries column for the step's chosen
    // dim) in a single Promise.all. The adjacent-column flow links for
    // the top (scroll=0) window are seeded in this same round-trip via
    // seedFlowLinksForCols; getFlowLinks then only runs for windows the
    // user scrolls to past the seed.
    execute: async ({ scopePath = [], trailDim = null, skipColumns = 0, columnQueries = {}, seedRows, range } = {}) => {
      const COL_PAGE_LIMIT = 200;
      // Caller's viewport-derived seed size (visible rows + buffer). No upper
      // cap — it's bounded by the viewport already; we only floor it (and fall
      // back to the default when a caller doesn't estimate).
      const effectiveSeedRows = Math.max(
        FLOW_SEED_MIN_ROWS,
        Math.floor(Number(seedRows) || FLOW_SEED_DEFAULT_ROWS),
      );
      const queryFor = (depth) => {
        const q = columnQueries[depth] || {};
        return {
          offset: Number(q.offset) || 0,
          limit:  Number(q.limit)  || COL_PAGE_LIMIT,
          search: String(q.search || "").trim(),
          sort:   q.sort || "activity",
        };
      };

      // Only the first page — top of every column, default page size — is
      // worth seeding flow links for: that's the window the finder paints on
      // a fresh reveal. Once any column has been paged past its first page (a
      // deeper scroll bumped its limit, or a nonzero offset), the top-window
      // seed would be off screen and discarded client-side, so we skip it and
      // let the viewport-driven getFlowLinks fetch exactly the rows the user
      // scrolled to. Keeps scrolling pay-as-you-go: fetch only what's needed
      // to satisfy the current scroll area, nothing more. (search/sort stay
      // at offset 0 + default limit, so they still seed — the view resets to
      // the top.)
      const isFirstPage = Object.values(columnQueries).every(
        (q) => (Number(q?.offset) || 0) === 0 && (Number(q?.limit) || COL_PAGE_LIMIT) <= COL_PAGE_LIMIT,
      );

      // One picker per scope depth (0..N), plus the trail picker at N+1.
      const prefixScopes = [];
      for (let i = 0; i <= scopePath.length; i++) {
        prefixScopes.push(scopePath.slice(0, i));
      }

      // Per-depth picker counts. Batch the analytics-supported direct
      // fields into one aggregate query and cluster counts into a second
      // joined aggregate query; unsupported dims still fall back one-by-one.
      const pickerPromises = prefixScopes.map((prefix) =>
        executeDimensionCounts({ dimKeys: PICKER_DIM_PLURALS, parentScopePath: prefix, range })
          .then((counts) => PICKER_DIM_PLURALS.map((dimPlural) => ({
            id: dimPlural,
            count: typeof counts[dimPlural] === "number" ? counts[dimPlural] : "Unavailable",
          })))
          .catch(() => PICKER_DIM_PLURALS.map((dimPlural) => ({ id: dimPlural, count: "Unavailable" })))
      );

      // Per-step entries column — ranked sparkline for the step's dim
      // under the prefix scope.
      const entriesPromises = scopePath.map((step, i) => {
        const q = queryFor(i);
        return executeDimensionEntries({
          dimKey: step.dim,
          parentScopePath: prefixScopes[i],
          offset: q.offset,
          limit:  q.limit,
          search: q.search,
          sort:   q.sort,
          range,
        });
      });

      // Trail entries — only when trailDim is set.
      const trailQ = queryFor(scopePath.length);
      const trailPromise = trailDim
        ? executeDimensionEntries({
            dimKey: trailDim,
            parentScopePath: scopePath,
            offset: trailQ.offset,
            limit:  trailQ.limit,
            search: trailQ.search,
            sort:   trailQ.sort,
            range,
          })
        : Promise.resolve(null);

      const [pickerResults, entriesResults, trailEntries] = await Promise.all([
        Promise.all(pickerPromises),
        Promise.all(entriesPromises),
        trailPromise,
      ]);

      // Assemble alternating picker/entries cols.
      const cols = [];
      for (let i = 0; i < scopePath.length; i++) {
        const step = scopePath[i];
        cols.push(buildPickerCol({
          depth: i,
          items: pickerResults[i],
          selectedDimKey: step.dim,
          scopePath,
        }));
        const q = queryFor(i);
        cols.push(buildEntriesCol({
          depth: i,
          dimPlural: step.dim,
          entries: entriesResults[i],
          offset: q.offset, limit: q.limit,
          step,
        }));
      }
      cols.push(buildPickerCol({
        depth: scopePath.length,
        items: pickerResults[scopePath.length],
        selectedDimKey: trailDim,
        scopePath,
      }));
      if (trailEntries && trailDim) {
        cols.push(buildEntriesCol({
          depth: scopePath.length,
          dimPlural: trailDim,
          entries: trailEntries,
          offset: trailQ.offset, limit: trailQ.limit,
          step: null,
        }));
      }

      const displayCols = skipColumns > 0 ? cols.slice(skipColumns) : cols;

      // Seed the adjacent-column flow links for the top window in this
      // same round-trip so the finder paints links with the columns
      // instead of flipping the dimension column to a second "LOADING
      // LINKS" pass once columns land — but only on a first-page fetch
      // (see isFirstPage). A link failure must never block the columns
      // from rendering — degrade to an unseeded response and let the
      // client's viewport effect fetch them the old way.
      let flowLinks = [];
      let flowSeeded = false;
      if (isFirstPage) {
        try {
          flowLinks = await seedFlowLinksForCols(displayCols, scopePath, range, toApiRange(range), effectiveSeedRows);
          flowSeeded = true;
        } catch (err) {
          console.warn("[buildColumnsWithFlowLinks] flow-link seed failed; columns render unseeded:", err);
        }
      }
      return { cols: displayCols, flowLinks, flowSeeded, flowSeedRows: effectiveSeedRows };
    },
  },

  getFlowLinks: {
    // Pairwise co-occurrence counts between adjacent dim columns. Each
    // pair fans out to one analytics query; pairs run in parallel.
    // Pairs with an unsupported axis return no links.
    shortCircuit: ({ pairs = [] } = {}) =>
      Array.isArray(pairs) && pairs.length === 0 ? { pairs: [] } : null,
    execute: async ({ scopePath = [], pairs = [], range, scoped = false } = {}) => {
      const apiRange = toApiRange(range);
      const results = await Promise.all(
        pairs.map((pair) => resolveFlowPair(pair, scopePath, range, apiRange, scoped)),
      );
      return { pairs: results };
    },
  },
};

export async function httpQuery(method, params) {
  const def = HTTP_METHODS[method];
  if (!def) throw new Error(`No HTTP route for method: ${method}`);
  if (def.shortCircuit) {
    const sc = def.shortCircuit(params ?? {});
    if (sc !== null && sc !== undefined) return sc;
  }
  if (typeof def.execute !== "function") throw new Error(`No executor for method: ${method}`);
  return def.execute(params ?? {});
}

export function isHttpMethod(method) {
  return Object.prototype.hasOwnProperty.call(HTTP_METHODS, method);
}
